import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Location } from "@angular/common";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ReportService } from "../report-system/core/services/report.service";
import { DataShareService } from "../report-system/core/services/data-share.service";
import { UserManagementService } from "../user-management/user-management.service";
import {
  DashboardGroupWithReportsResponse,
  DashboardReportData,
  DashboardReportParameter,
  DashboardWithReportsItem,
  SavedDocumentListItem,
  SavedDocumentDetail,
  SavedIntervalWindow,
} from "../report-system/core/utils/types/report.types";
import { firstValueFrom, Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import {
  GoogleDocsError,
  checkGoogleDriveAvailable,
  getCachedGoogleToken,
  isGoogleDocsMarkedUnavailable,
  preloadGoogleIdentity,
  requestGoogleToken,
  setGoogleDocsUnavailable,
  uploadDocxAsGoogleDoc,
} from "./google-docs-export";

// One report whose chosen frequency range differs from its original configured
// period — shown in the "Apply" change-review dialog.
interface PdfReportChange {
  id: number;
  name: string;
  originalFrom: string;
  originalTo: string;
  newFrom: string;
  newTo: string;
}

// Changed reports grouped by frequency, with an overall count.
interface PdfApplyChanges {
  daily: PdfReportChange[];
  weekly: PdfReportChange[];
  monthly: PdfReportChange[];
  total: number;
}

/**
 * Where a dragged visual would land. "split" cuts a text block at the drop caret
 * so the visual can sit between two lines of it; "block" places it between two
 * existing siblings. top/left/width are the insertion line in viewport space.
 */
interface VisualDropTarget {
  mode: "split" | "block";
  container: HTMLElement;
  before: HTMLElement | null;
  block: HTMLElement | null;
  caret: Range | null;
  top: number;
  left: number;
  width: number;
}

@Component({
  selector: "app-downloadpdf",
  templateUrl: "./downloadpdf.component.html",
  styleUrls: ["./downloadpdf.component.css"],
  standalone: false,
})
export class DownloadpdfComponent implements OnInit, AfterViewInit, OnDestroy {
  private reportContentCache = new Map<number, string>();
  private selectedResizableWrapper: HTMLElement | null = null;
  @ViewChildren("pageEditor") pageEditors!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild("pagesWorkspaceRef") pagesWorkspaceRef!: ElementRef<HTMLDivElement>;
  @ViewChild("blockPickerRef") blockPickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("fontPickerRef") fontPickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("sizePickerRef") sizePickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("lineSpacingPickerRef") lineSpacingPickerRef!: ElementRef<HTMLDivElement>;

  pages: Array<{ id: number; html: string }> = [
    {
      id: 1,
      html: "<h2>Document Title</h2><p>Start writing your document here.</p>",
    },
  ];
  activePageIndex = 0;
  // Page currently in the scroll viewport — drives the "Page X of Y" footer.
  // Kept separate from activePageIndex, which reflow pushes to the last page.
  currentPageIndex = 0;
  private nextPageId = 2;
  // Number of fixed leading pages that sit OUTSIDE the reflow flow before the
  // flowing content begins. MOR reports reserve 1 (the full-bleed cover on page
  // 1); reflow never backfills it. The Table of Contents is NOT counted here — it
  // leads the flowing content as a page-exclusive block so it can span multiple
  // pages. 0 means content starts on page 1 (all non-MOR reports).
  private reservedLeadingPages = 0;

  activeBlock = "p";
  selectedFont = "Arial";
  selectedFontSize = 16;
  selectedLineSpacing = 1.5;
  // Active inline-format states for the toolbar (reflect the current selection).
  isBold = false;
  isItalic = false;
  isUnderline = false;
  isStrike = false;
  private toolbarSyncPending = false;
  // Selection captured before a focus-stealing control (e.g. the native color
  // picker) clears it, so the chosen format can be applied to the right text.
  private savedSelectionRange: Range | null = null;
  // Caret preservation across a reflow. Reflow moves DOM nodes between page
  // editors, which drops the caret (it jumps to the top of the page and vanishes
  // mid-typing). A zero-width marker is dropped at the caret before reflow and
  // travels with its block; once reflow settles the caret is re-placed at the
  // marker. See insertCaretMarker() / restorePendingCaret().
  private caretMarkerSeq = 0;
  private pendingCaretMarkerId: string | null = null;
  // Set after inserting a table so we scroll to it once reflow fully settles
  // (pagination is async and may move the table to a different page first).
  private pendingTableReveal = false;
  // A just-dropped chart/table/image, revealed once repagination settles.
  private pendingVisualReveal: HTMLElement | null = null;
  // Native drag-and-drop is only left alone WITHIN a single page, where the
  // browser really does move the content. Every page is its own editing host, so
  // a native drop onto a different page COPIES the dragged content and leaves
  // the original behind — that is where the duplicates came from. Those drops
  // are taken over below and performed as a real move; the range is captured at
  // dragstart because the drop event no longer has the source selection.
  private nativeDragSourceIndex: number | null = null;
  private nativeDragRange: Range | null = null;
  /** Transient message explaining a refused drop. Rendered above the pages. */
  dropNotice = "";
  private dropNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  // Which wrappers/handles already carry their listeners. Deliberately NOT
  // dataset flags: those serialise into pages[i].html, so a re-hydrated document
  // came back marked as bound while its listeners were gone — the wrapper then
  // ignored every click and drag.
  private boundWrappers = new WeakSet<HTMLElement>();
  private boundHandles = new WeakSet<HTMLElement>();
  // Chart wrappers whose snapshot labels have already been normalised.
  private normalizedSnapshots = new WeakSet<HTMLElement>();
  textColor = "#111827";
  highlightColor = "#ffff00";
  wordCount = 0;
  characterCount = 0;
  editorLoading = true;
  // Shown before anything is loaded/rendered. The user must choose how to start;
  // "Continue with Existing" runs the current dashboard-group render flow.
  entryDialogOpen = true;

  // Set when the group opened is a MOR (Monthly Operation Report) type, flagged
  // via the ?isMor=1 query param from the report list. MOR reports require the
  // user to name the MOR before proceeding (both "Apply" and "Continue"); the
  // entry dialog then surfaces a required "MOR Name" field above the frequency
  // ranges.
  isMorReport = false;
  // The MOR cover title, entered line by line (Line 1, Line 2, …). Line 1 is
  // required; further lines are optional and filled top-down (a line can't be
  // filled while an earlier one is blank). Empty lines are never rendered. The
  // lines are stacked on the cover with a size/weight hierarchy. For a MOR the
  // three lines are prefilled on open — see buildDefaultMorNameLines().
  morNameLines: string[] = ["Monthly Operation Report"];
  morNameError = "";
  private readonly maxMorNameLines = 6;
  // A single MOR name input by default, with a button beside it that reveals the
  // line-by-line builder. MOR groups open with the builder already revealed so
  // the prefilled lines 2 and 3 are visible and editable, not hidden behind the
  // button.
  morMultiLineOpen = false;

  // Entry dialog → left "data configuration" section. On open we collect every
  // parameter code in the group and ask the backend for the date range it holds
  // data for, then prefill "Data from" = oldest, "Data to" = newest.
  dataConfigLoading = false;
  dataConfigError = "";
  // The left section offers three frequency groups (Daily / Weekly / Monthly),
  // each with its own "Data from / Data to" range. On Apply / Continue, each
  // report is scoped by the range of ITS OWN configured frequency — daily-
  // configured reports use the Daily range, monthly reports the Monthly range,
  // and a frequency with no reports is simply never applied.
  readonly pdfIntervalOptions: Array<{
    value: "daily" | "weekly" | "monthly";
    label: string;
  }> = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];
  intervalRanges: Record<
    "daily" | "weekly" | "monthly",
    { from: string; to: string }
  > = {
    daily: { from: "", to: "" },
    weekly: { from: "", to: "" },
    monthly: { from: "", to: "" },
  };
  // Each frequency group has a collapsible "Reports" list — all closed by
  // default. Opening one reveals the reports configured at that frequency.
  expandedIntervals: Record<"daily" | "weekly" | "monthly", boolean> = {
    daily: false,
    weekly: false,
    monthly: false,
  };
  // The group's reports bucketed by their configured frequency, listed inside the
  // matching group. Populated once the group payload loads.
  intervalReports: Record<
    "daily" | "weekly" | "monthly",
    Array<{ id: number; name: string }>
  > = { daily: [], weekly: [], monthly: [] };
  // Text reports (headings / narrative blocks) have no data range, so they're
  // kept out of the frequency accordions and surfaced as a single count instead.
  textReportCount = 0;
  dataBoundsOldest = ""; // min selectable date (oldest available)
  dataBoundsNewest = ""; // max selectable date (newest available)
  // The window actually applied to the render, PER frequency. A report is clipped
  // to the window of its own configured frequency. Empty = no window for that
  // frequency (render every embedded value, as before).
  private exportWindows: Record<
    "daily" | "weekly" | "monthly",
    { from: string; to: string }
  > = {
    daily: { from: "", to: "" },
    weekly: { from: "", to: "" },
    monthly: { from: "", to: "" },
  };
  // Overall span across the per-frequency windows — used only for the saved-
  // document metadata (dataFrom / dataTo) and hydrate, not for clipping.
  private exportWindowFrom = "";
  private exportWindowTo = "";

  // "Apply" refetches each report's data for the chosen period (sequentially,
  // in render order) and shows a 1/N progress counter. Text reports are skipped.
  applyInProgress = false;
  applyCurrent = 0;
  applyTotal = 0;
  applyError = "";

  // "Apply" change review: a report is "changed" when the range chosen for its
  // frequency differs from the period it was originally configured with. When any
  // report changed, Apply first asks the user to review the changes or apply
  // directly; "Review" shows the per-frequency breakdown below.
  applyConfirmOpen = false; // the review-or-apply prompt
  applyReviewOpen = false; // the detailed per-frequency change list
  applyReviewError = ""; // validation error for an edited per-report range
  applyChanges: PdfApplyChanges = {
    daily: [],
    weekly: [],
    monthly: [],
    total: 0,
  };
  // Per-report range overrides set by editing a row in the review dialog. Keyed
  // by reportId; when present, a report is fetched/clipped to this range instead
  // of its frequency's range.
  private reportWindows = new Map<number, { from: string; to: string }>();

  // Entry dialog → right "Saved Documents" section. Documents the user previously
  // saved (for this site) that can be reopened and edited.
  savedDocsLoading = false;
  savedDocsError = ""; // load failure — gates the list
  savedDocActionError = ""; // open/delete failure — shown without hiding the list
  savedDocuments: SavedDocumentListItem[] = [];
  savedDocSearch = ""; // filters the saved-documents list by name/owner
  savedDocOpeningId: string | number | null = null;
  // Identity of the document currently in the editor. Set when a saved document
  // is opened (or after the first save) so re-saving updates it in place instead
  // of creating a new one. Null = a brand-new, never-saved document.
  private currentDocumentId: string | number | null = null;
  private currentDocumentName: string | null = null;
  // Per-row kebab (•••) menu: which row's menu is open, which is awaiting a
  // delete confirmation, and which is mid-delete.
  savedDocMenuId: string | number | null = null;
  savedDocConfirmDeleteId: string | number | null = null;
  savedDocDeletingId: string | number | null = null;
  // Viewport coords for the open menu so it can be position:fixed and escape the
  // scrollable list (otherwise the list clips it and scrolls to reveal it).
  savedDocMenuPos: { top: number; right: number } | null = null;
  // Group payload fetched for the data config, reused on "Continue" so the
  // render flow doesn't refetch the (potentially multi-MB) with-reports payload.
  private cachedGroupForRender: {
    id: number;
    data: DashboardGroupWithReportsResponse;
  } | null = null;

  exportInProgress = false;
  exportProgress = 0;
  exportProgressText = "Preparing export...";
  exportDialogOpen = false;
  exportFileName = "";
  private exportCancelled = false;
  /** Outcome of "Open in Google Docs" when it can't open the tab itself. */
  googleDocsResult: { url?: string; error?: string; comingSoon?: boolean } | null = null;
  /** The Drive API was found disabled for this app: the button reads "Coming
   *  soon" until a later re-check (see google-docs-export.ts). */
  googleDocsComingSoon = isGoogleDocsMarkedUnavailable();
  private googleDocsAbort: AbortController | null = null;
  imageToolbarVisible = false;
  imageToolbarTop = 0;
  // Distance from the viewport's right edge: the toolbar is anchored to the
  // selected visual's top-right corner so it grows leftwards when expanded.
  imageToolbarRight = 0;
  // Collapsed by default so the chip does not cover the content behind it;
  // the user opens the full button row from the corner toggle.
  imageToolbarExpanded = false;
  chartEditorOpen = false;
  chartEditorMessage = "";
  chartEditorTitle = "";
  chartEditorTitleSize = 24;
  chartEditorTitleColor = "#f8fafc";
  chartEditorBgStart = "#1f2937";
  chartEditorBgCenter = "#2b3444";
  chartEditorBgEnd = "#3f3f46";
  chartEditorBgOuter = "#111827";
  chartEditorDateLabelMode: "all" | "auto" = "auto";
  chartEditorDateLabelRotation = -40;
  chartEditorValueLabelSize = 11;
  chartEditorValueLabelRotation = 0;
  chartEditorLegendSize = 15;
  chartEditorXAxisLabelSize = 12;
  chartEditorYAxisLabelSize = 12;
  chartEditorTitlePadTop = 0;
  chartEditorTitlePadRight = 0;
  chartEditorTitlePadBottom = 0;
  chartEditorTitlePadLeft = 0;
  chartEditorMultiAxis = false;
  chartEditorSeriesAxis: boolean[] = [];
  chartEditorSeriesNames: string[] = [];
  chartEditorSeriesColors: string[] = [];
  chartEditorPreviewDataUrl = "";
  chartEditorChartType:
    | "bar" | "line" | "dot" | "bar_label" | "line_label"
    | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" = "bar";
  readonly chartTypeOptions: Array<{
    value: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "table";
    label: string;
  }> = [
    { value: "bar", label: "Bar" },
    { value: "line", label: "Line" },
    { value: "dot", label: "Dot" },
    { value: "bar_label", label: "Bar Label" },
    { value: "line_label", label: "Line Label" },
    { value: "table", label: "Table" },
    { value: "gauge", label: "Gauge" },
    { value: "pie", label: "Pie" },
    { value: "donut", label: "Donut" },
  ];
  tableEditorOpen = false;
  tableEditorMessage = "";
  tableEditorTitleBg = "#0b2345";
  tableEditorHeaderBg = "#021b39";
  tableEditorOddRowBg = "#15335a";
  tableEditorEvenRowBg = "#001a3a";
  tableEditorTextColor = "#f8fafc";
  tableEditorBorderColor = "#2b3f59";
  tableEditorTitleAlign: "left" | "center" | "right" = "left";
  tableEditorTitleSize = 13;
  tableEditorPreviewHtml: SafeHtml = "";
  private chartEditorBasePayload: {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // The text painted on the snapshot. Distinct from `title` (the report name,
      // which the interval detection keys off): "" paints no title, and undefined
      // falls back to `title`. See drawChartSnapshotDataUrl.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null = null;
  private chartEditorTargetWrapper: HTMLElement | null = null;
  private tableEditorTargetWrapper: HTMLElement | null = null;
  dialogType: "none" | "link" | "table" = "none";
  linkUrl = "";
  tableRows = 2;
  tableCols = 2;
  fontDropdownOpen = false;
  blockDropdownOpen = false;
  sizeDropdownOpen = false;
  lineSpacingDropdownOpen = false;
  private lastLoadedDashboardGroupId: number | null = null;
  // ActivatedRoute's observables are owned by the router and never complete, so
  // this one outlives the component unless it is torn down by hand. It also has
  // to be replaced rather than added to: loadDashboardGroupFromQuery is entered
  // again on a second Apply, which used to stack another live subscription.
  private queryParamsSub: Subscription | null = null;
  private readonly maxAutoPages = 80;

  // --- Local draft persistence (autosave to localStorage) ---
  // No backend "save document" endpoint exists, so edits are persisted locally,
  // keyed per dashboard group. This never touches stored reports/dashboards.
  private readonly draftVersion = 1;
  // Drafts are stored in IndexedDB (handles large chart-snapshot documents that
  // exceed the ~5 MB localStorage quota). localStorage is kept only as a
  // read-migration source and a tiny-document fallback.
  private readonly draftDbName = "downloadpdf-drafts";
  private readonly draftStoreName = "drafts";
  private draftDb: IDBDatabase | null = null;
  private documentReady = false;
  private draftAutosaveTimer: ReturnType<typeof setTimeout> | null = null;
  // When the current burst of edits must be persisted by, whatever else arrives.
  private draftAutosaveDeadline = 0;
  private static readonly DRAFT_AUTOSAVE_DELAY_MS = 1500;
  private static readonly DRAFT_AUTOSAVE_MAX_WAIT_MS = 4000;
  // Set when an edit asks to autosave while a reflow is mid-flight (the page
  // model is intentionally stale during pagination). reflowFrom re-arms the
  // autosave once it settles so we never persist a half-paginated document.
  private draftAutosavePending = false;
  // True once the user explicitly restored a draft, so a still-pending API load
  // must not overwrite their restored document.
  private userRestoredDraft = false;
  private pendingDraft: {
    pages: Array<{ id: number; html: string }>;
    savedAt: number;
    activePageIndex: number;
  } | null = null;
  draftRestoreAvailable = false;
  draftSaving = false;
  draftSaveError = false;
  draftSavedAt: number | null = null;
  // Drives the auto-save status indicator in the toolbar.
  draftState: "idle" | "unsaved" | "saving" | "saved" | "error" = "idle";

  // --- Structural table editing (floating toolbar) ---
  tableToolbarVisible = false;
  tableToolbarTop = 0;
  tableToolbarLeft = 0;
  private selectedTableCell: HTMLTableCellElement | null = null;
  // Cell fill color + the cells captured (from the current selection) to fill.
  cellFillColor = "#fde68a";
  private cellColorTargets: HTMLTableCellElement[] = [];

  // --- Hover "+" controls to add a column (right) / row (bottom) on any table ---
  tableControlsVisible = false;
  tableColAddTop = 0;
  tableColAddLeft = 0;
  tableRowAddTop = 0;
  tableRowAddLeft = 0;
  private hoveredTable: HTMLElement | null = null;
  private tableControlsHideTimer: ReturnType<typeof setTimeout> | null = null;
  private tableHoverRafPending = false;
  // --- Hover "−" controls to delete the column (top) / row (left) under the
  // cursor on any table, including report tables (applied across page fragments) ---
  tableDelControlsVisible = false;
  tableDelColTop = 0;
  tableDelColLeft = 0;
  tableDelRowTop = 0;
  tableDelRowLeft = 0;
  private hoveredCell: HTMLTableCellElement | null = null;
  // True while reflowFrom() is paginating. During reflow the live DOM is the
  // source of truth, so the pageEditors.changes subscription must not re-hydrate
  // from the (stale) page model — that would re-inject content into editors
  // reflow just emptied, duplicating reports.
  private reflowInProgress = false;
  // The full render order (reports as they lay out, group-wide deduped), captured
  // at render time. The Table of Contents is driven by MOR Heading reports: each
  // becomes a bold top-level entry and the normal reports that follow it become
  // numbered children. Only what comes AFTER the TOC block is listed. Page numbers
  // are resolved later from the paginated DOM by refreshTableOfContents().
  private tocOrder: Array<{
    reportId: number;
    name: string;
    isToc: boolean;
    isMorHead: boolean;
  }> = [];
  // For MOR reports: the dashboard→reports grouping used to build the page-2
  // Table of Contents. Each dashboard becomes a numbered heading (1., 2., …) and
  // its reports the numbered children (1.1, 1.2, …). Page numbers are resolved
  // from the paginated DOM by refreshTableOfContents() (reused via the shared
  // data-toc-page-for / data-report-id markers).
  private morTocGroups: Array<{
    name: string;
    reports: Array<{ id: number; name: string }>;
  }> = [];
  private readonly defaultChartPalette = [
    "#4f7de8",
    "#7cc85e",
    "#2dd4bf",
    "#f59e0b",
    "#ef4444",
    "#a78bfa",
  ];

  readonly blockOptions: Array<{ value: "p" | "h1" | "h2" | "h3"; label: string }> = [
    { value: "p", label: "Paragraph" },
    { value: "h1", label: "Heading 1" },
    { value: "h2", label: "Heading 2" },
    { value: "h3", label: "Heading 3" },
  ];

  readonly fontFamilies = [
    "Arial",
    "Arial Black",
    "Bahnschrift",
    "Book Antiqua",
    "Calibri",
    "Cambria",
    "Candara",
    "Century Gothic",
    "Comic Sans MS",
    "Consolas",
    "Constantia",
    "Corbel",
    "Courier New",
    "Franklin Gothic Medium",
    "Garamond",
    "Georgia",
    "Impact",
    "Lucida Console",
    "Lucida Sans Unicode",
    "Microsoft Sans Serif",
    "Palatino Linotype",
    "Segoe Print",
    "Segoe Script",
    "Segoe UI",
    "Tahoma",
    "Times New Roman",
    "Trebuchet MS",
    "Verdana",
  ];

  readonly fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36];
  readonly lineSpacingOptions = [1, 1.15, 1.5, 2, 2.5, 3];

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportService,
    private location: Location,
    private router: Router,
    private sanitizer: DomSanitizer,
    private dataShareService: DataShareService,
    private userService: UserManagementService
  ) {}

  ngOnInit(): void {
    // Defer all loading/rendering until the user picks an option in the entry
    // dialog. Drop the editor loader so the dialog (z-index 4001) isn't covered
    // by the loader overlay (z-index 5000) while it's open.
    this.entryDialogOpen = true;
    this.editorLoading = false;

    // MOR groups arrive with ?isMor=1 and must be named before proceeding. The
    // name is prefilled with the usual three-line cover title, with the builder
    // revealed so all three lines are editable up front.
    this.isMorReport =
      this.route.snapshot.queryParamMap.get("isMor") === "1";
    if (this.isMorReport) {
      this.morNameLines = this.buildDefaultMorNameLines();
      this.morMultiLineOpen = true;
    }

    // While the dialog is open, fetch the group's reports to learn its data date
    // range and prefill the left "data configuration" section.
    const dashboardGroupId = Number(
      this.route.snapshot.queryParamMap.get("dashboardGroupId")
    );
    if (dashboardGroupId && !Number.isNaN(dashboardGroupId)) {
      this.loadDataConfiguration(dashboardGroupId);
    }

    // Right section: list documents the user previously saved for this site.
    this.loadSavedDocuments();
  }

  /** Saved documents matching the search box (by name or owner). */
  get filteredSavedDocuments(): SavedDocumentListItem[] {
    const query = this.savedDocSearch.trim().toLowerCase();
    if (!query) {
      return this.savedDocuments;
    }
    return this.savedDocuments.filter((doc) => {
      const name = (doc.documentName || "").toLowerCase();
      const owner = String(doc.createdByName || doc.createdBy || "").toLowerCase();
      return name.includes(query) || owner.includes(query);
    });
  }

  /** Entry dialog → right section: load the site's saved documents. */
  private loadSavedDocuments(): void {
    const siteId = this.dataShareService.getSiteId();
    if (!siteId) {
      this.savedDocuments = [];
      return;
    }
    this.savedDocsLoading = true;
    this.savedDocsError = "";
    this.reportService.listDocuments(siteId).subscribe({
      next: (response) => {
        this.savedDocsLoading = false;
        this.savedDocuments =
          response?.success && Array.isArray(response.data) ? response.data : [];
      },
      error: () => {
        this.savedDocsLoading = false;
        this.savedDocsError = "Could not load saved documents.";
      },
    });
  }

  /** Open a previously saved document and hydrate it into the editor. */
  openSavedDocument(doc: SavedDocumentListItem): void {
    if (this.savedDocOpeningId !== null) {
      return;
    }
    this.savedDocActionError = "";
    this.savedDocOpeningId = doc.id;
    this.reportService.getDocument(doc.id).subscribe({
      next: (response) => {
        const detail = response?.data;
        if (!response?.success || !detail) {
          this.failOpenSavedDocument();
          return;
        }
        // Large documents come back as a presigned S3 URL instead of inline
        // pages (a multi-MB response would exceed the API limit) — fetch it.
        if (!Array.isArray(detail.pages) && detail.downloadUrl) {
          void this.hydrateFromDownloadUrl(detail);
          return;
        }
        if (!Array.isArray(detail.pages)) {
          this.failOpenSavedDocument();
          return;
        }
        this.hydrateFromSavedDocument(detail);
      },
      error: () => {
        this.failOpenSavedDocument();
      },
    });
  }

  /** Fetch a large document's JSON (incl. pages) from its presigned S3 URL. */
  private async hydrateFromDownloadUrl(
    detail: SavedDocumentDetail
  ): Promise<void> {
    try {
      const res = await fetch(detail.downloadUrl as string);
      if (!res.ok) {
        throw new Error(`document download failed (${res.status})`);
      }
      const json = await res.json();
      const merged: SavedDocumentDetail = { ...detail, ...json };
      if (!Array.isArray(merged.pages)) {
        this.failOpenSavedDocument();
        return;
      }
      this.hydrateFromSavedDocument(merged);
    } catch (error) {
      console.error("[PDF] open saved document failed", error);
      this.failOpenSavedDocument();
    }
  }

  /** Shared failure path when a saved document can't be opened. */
  private failOpenSavedDocument(): void {
    this.savedDocOpeningId = null;
    this.savedDocActionError = "Could not open that document.";
    this.loadSavedDocuments();
  }

  /** Toggle a row's kebab (•••) menu; closes any other open menu. */
  toggleSavedDocMenu(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    this.savedDocConfirmDeleteId = null;
    if (this.savedDocMenuId === doc.id) {
      this.closeSavedDocMenu();
      return;
    }
    // Anchor the fixed-position menu just under the kebab button. The dialog has
    // a CSS transform, so position:fixed is relative to the dialog box (not the
    // viewport) — compute the offset relative to the dialog accordingly.
    const btn = event.currentTarget as HTMLElement | null;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const dialog = btn.closest(".entry-choice-dialog") as HTMLElement | null;
      const base = dialog ? dialog.getBoundingClientRect() : null;
      const baseTop = base ? base.top : 0;
      const baseRight = base ? base.right : window.innerWidth;
      this.savedDocMenuPos = {
        top: rect.bottom - baseTop + 4,
        right: baseRight - rect.right,
      };
    }
    this.savedDocMenuId = doc.id;
  }

  /** Close the kebab menu (and any pending delete confirmation). */
  closeSavedDocMenu(): void {
    this.savedDocMenuId = null;
    this.savedDocConfirmDeleteId = null;
    this.savedDocMenuPos = null;
  }

  /** Menu → Open. */
  onSavedDocMenuOpen(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    this.closeSavedDocMenu();
    this.openSavedDocument(doc);
  }

  /** True when the current user saved this document (owner-only delete). */
  private isOwnDocument(doc: SavedDocumentListItem): boolean {
    const userId = this.userService.getAuthenticatedUserId();
    return (
      !!userId &&
      doc.createdBy !== null &&
      doc.createdBy !== undefined &&
      String(doc.createdBy) === String(userId)
    );
  }

  /** Menu → Delete: block non-owners up front, else ask for confirmation. */
  onSavedDocMenuDelete(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    if (!this.isOwnDocument(doc)) {
      this.savedDocActionError = "You can only delete documents you saved.";
      this.closeSavedDocMenu();
      return;
    }
    this.savedDocConfirmDeleteId = doc.id;
  }

  /** Inline confirm → cancel. */
  cancelSavedDocDelete(event: Event): void {
    event.stopPropagation();
    this.savedDocConfirmDeleteId = null;
  }

  /** Inline confirm → delete the document. */
  confirmSavedDocDelete(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    if (this.savedDocDeletingId !== null) {
      return;
    }
    this.savedDocActionError = "";
    this.savedDocDeletingId = doc.id;
    this.reportService.deleteDocument(doc.id).subscribe({
      next: (response) => {
        this.savedDocDeletingId = null;
        if (response?.success === false) {
          this.savedDocActionError = "Could not delete that document.";
          this.closeSavedDocMenu();
          this.loadSavedDocuments();
          return;
        }
        // Drop it from the list without a full refetch.
        this.savedDocuments = this.savedDocuments.filter(
          (item) => item.id !== doc.id
        );
        this.closeSavedDocMenu();
      },
      error: (err) => {
        this.savedDocDeletingId = null;
        // Backend enforces owner-only delete → 403 for another user's document.
        this.savedDocActionError =
          err?.status === 403
            ? "You can only delete documents you saved."
            : "Could not delete that document.";
        this.closeSavedDocMenu();
        // Re-render the list so it stays in sync after the failed attempt.
        this.loadSavedDocuments();
      },
    });
  }

  /**
   * Replace the editor content with a saved document's pages (mirrors
   * restoreDraft). Skips the dashboard-group render path entirely.
   */
  private hydrateFromSavedDocument(detail: SavedDocumentDetail): void {
    // A fresh dashboard load must not overwrite the document the user just opened.
    this.userRestoredDraft = true;
    this.entryDialogOpen = false;
    this.documentReady = false;
    this.editorLoading = true;

    // Remember which document this is so re-saving updates it in place.
    this.currentDocumentId = detail.id;
    this.currentDocumentName = detail.documentName;
    // Restore the saved data window so it persists across reopen → re-save.
    this.exportWindowFrom = detail.dataFrom || "";
    this.exportWindowTo = detail.dataTo || "";
    // Prefer the per-frequency ranges when the document carries them; otherwise
    // fall back to applying the single overall range to every frequency.
    const savedWindows = new Map(
      (detail.intervalWindows || []).map((win) => [win.interval, win])
    );
    for (const opt of this.pdfIntervalOptions) {
      const win = savedWindows.get(opt.value);
      const from = win?.from || detail.dataFrom || "";
      const to = win?.to || detail.dataTo || "";
      if (from) {
        this.intervalRanges[opt.value].from = from;
      }
      if (to) {
        this.intervalRanges[opt.value].to = to;
      }
      this.exportWindows[opt.value] = {
        from: this.intervalRanges[opt.value].from,
        to: this.intervalRanges[opt.value].to,
      };
    }

    this.pages = (detail.pages || []).map((page, index) => ({
      id: Number(page?.id) || index + 1,
      html: String(page?.html || ""),
    }));
    if (!this.pages.length) {
      this.pages = [{ id: 1, html: "" }];
    }
    this.nextPageId =
      this.pages.reduce(
        (max, page) => Math.max(max, Number(page.id) || 0),
        0
      ) + 1;
    this.activePageIndex = Math.min(
      Math.max(0, detail.activePageIndex || 0),
      this.pages.length - 1
    );
    this.savedDocOpeningId = null;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(0);
      this.focusActivePage();
      this.updateDocumentStats();
      this.editorLoading = false;
      this.documentReady = true;
      this.draftState = "saved";
    }, 0);
  }

  /** Toggle an interval accordion open/closed (independent of the others). */
  toggleIntervalAccordion(value: "daily" | "weekly" | "monthly"): void {
    this.expandedIntervals[value] = !this.expandedIntervals[value];
  }

  /**
   * Bucket the group's DATA reports by their own configured frequency so each
   * accordion lists only the reports that render at that interval. Text reports
   * (headings / narrative blocks) carry no data range, so they're excluded from
   * the buckets and counted separately (textReportCount). Reordered/deduped the
   * same way the renderer lays them out.
   */
  private groupReportsByInterval(): void {
    const buckets: Record<
      "daily" | "weekly" | "monthly",
      Array<{ id: number; name: string }>
    > = { daily: [], weekly: [], monthly: [] };
    let textCount = 0;
    const group = this.cachedGroupForRender?.data;
    if (group) {
      for (const report of this.collectRenderReports(group)) {
        if (this.isTextReport(report)) {
          textCount += 1;
          continue;
        }
        const bucket = this.normalizeIntervalBucket(report.interval);
        if (bucket) {
          buckets[bucket].push({
            id: report.reportId,
            name: report.reportName || `Report ${report.reportId}`,
          });
        }
      }
    }
    this.textReportCount = textCount;
    this.intervalReports = buckets;
  }

  /**
   * Map a report's configured frequency onto one of the three accordion buckets.
   * Sub-daily intervals (hourly/minute) collapse into Daily; anything
   * unrecognised is dropped rather than mis-bucketed.
   */
  private normalizeIntervalBucket(
    interval: string | null | undefined
  ): "daily" | "weekly" | "monthly" | null {
    switch (interval) {
      case "monthly":
        return "monthly";
      case "weekly":
        return "weekly";
      case "daily":
      case "15minutes":
      case "30minutes":
      case "1hour":
        return "daily";
      default:
        return null;
    }
  }

  /** Entry dialog close (×): leave the editor without rendering anything. */
  closeEntryDialog(): void {
    this.entryDialogOpen = false;
    void this.closeEditor();
  }

  /**
   * The MOR name the entry dialog opens with: the report title, the site it's
   * for, and the month it covers (the previous one) — e.g. opened for BPCL
   * Mumbai in July 2026:
   *
   *   Monthly Operation Report
   *   for BPCL Mumbai
   *   - June 2026
   *
   * These are only defaults; the user can edit or remove any line, and a cleared
   * line simply never renders on the cover. The site line is dropped when no site
   * name is known rather than rendering "for " with nothing after it.
   */
  private buildDefaultMorNameLines(): string[] {
    const siteName = (
      this.userService.getCurrentSitename() ||
      this.userService.getSiteDetail()?.[0]?.site_name ||
      ""
    ).trim();
    const { month, year } = this.getPreviousMonth();
    const lines = ["Monthly Operation Report"];
    if (siteName) {
      lines.push(`for ${siteName}`);
    }
    lines.push(`- ${month} ${year}`);
    return lines;
  }

  /**
   * The previous calendar month, with its own year so January rolls back to the
   * prior year (opened Jan 2026 → December 2025).
   */
  private getPreviousMonth(): { month: string; year: number } {
    const now = new Date();
    // Last day of the previous month (first of this month, minus one day).
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    prevMonth.setDate(prevMonth.getDate() - 1);
    return {
      month: prevMonth.toLocaleString("en-US", { month: "long" }),
      year: prevMonth.getFullYear(),
    };
  }

  /**
   * For a MOR group the document must be named before it can be generated. Line 1
   * is required and the lines must be filled in order (no content after a blank
   * line). Sets `morNameError` and returns false on failure; a no-op (returns
   * true) for non-MOR groups.
   */
  private validateMorName(): boolean {
    if (!this.isMorReport) {
      return true;
    }
    const lines = this.morNameLines.map((line) => (line || "").trim());
    if (!lines[0]) {
      this.morNameError = "Enter the first MOR name line to continue.";
      return false;
    }
    // No gaps: once a blank line appears, no later line may carry content.
    let blankSeen = false;
    for (const line of lines) {
      if (!line) {
        blankSeen = true;
        continue;
      }
      if (blankSeen) {
        this.morNameError =
          "Fill the lines in order — a later line can't be filled while an earlier one is empty.";
        return false;
      }
    }
    this.morNameError = "";
    return true;
  }

  /** trackBy for the MOR name line inputs — index-stable so editing a line
   *  doesn't recreate its input (which would drop focus mid-type). */
  trackByIndex(index: number): number {
    return index;
  }

  /** Whether a new (blank) MOR name line may be added: only once the last line
   *  has content, and below the max line count. */
  canAddMorNameLine(): boolean {
    if (this.morNameLines.length >= this.maxMorNameLines) {
      return false;
    }
    const last = this.morNameLines[this.morNameLines.length - 1] || "";
    return last.trim().length > 0;
  }

  /** Add another blank line for the user to fill (Line N+1). */
  addMorNameLine(): void {
    if (!this.canAddMorNameLine()) {
      return;
    }
    this.morNameLines.push("");
    this.morNameError = "";
  }

  /** Remove an added line (never Line 1). */
  removeMorNameLine(index: number): void {
    if (index <= 0 || index >= this.morNameLines.length) {
      return;
    }
    this.morNameLines.splice(index, 1);
    this.morNameError = "";
  }

  /** A line input is locked until the line above it has content — this enforces
   *  the top-down fill order (can't fill Line 3 while Line 2 is blank). */
  isMorNameLineDisabled(index: number): boolean {
    if (index <= 0) {
      return false;
    }
    return !(this.morNameLines[index - 1] || "").trim();
  }

  onMorNameLineChange(): void {
    this.morNameError = "";
  }

  /** Reveal the line-by-line title builder (multi-line MOR name). */
  openMorMultiLine(): void {
    this.morMultiLineOpen = true;
    if (!this.morNameLines.length) {
      this.morNameLines = [""];
    }
  }

  /** Collapse back to the single MOR name input. Added lines are kept (they still
   *  render on the cover); the user can remove them with the per-line ×. */
  closeMorMultiLine(): void {
    this.morMultiLineOpen = false;
    this.morNameError = "";
  }

  /** The MOR name lines to render on the cover: the leading run of non-empty
   *  lines (validation guarantees no interior gaps), trimmed. */
  private morNameLinesForRender(): string[] {
    const out: string[] = [];
    for (const line of this.morNameLines) {
      const trimmed = (line || "").trim();
      if (!trimmed) {
        break;
      }
      out.push(trimmed);
    }
    return out;
  }

  /** Entry dialog → proceed with the current (existing) render behaviour. */
  continueWithExisting(): void {
    // MOR reports must be named before the editor opens.
    if (!this.validateMorName()) {
      return;
    }
    // Apply each frequency's range to the render (reports are clipped to the
    // window of their own configured frequency). Empty inputs → no clipping.
    this.commitExportWindows();
    this.entryDialogOpen = false;
    this.loadDashboardGroupFromQuery();
  }

  /** The display label for a frequency bucket (for validation messages). */
  private intervalLabel(bucket: "daily" | "weekly" | "monthly"): string {
    return (
      this.pdfIntervalOptions.find((opt) => opt.value === bucket)?.label ??
      bucket
    );
  }

  /**
   * The per-frequency applied ranges to store on a saved document, so its card
   * can show "Daily 04-01→04-30 · Monthly 06-01→06-30" instead of a single
   * min→max span. Only frequencies that actually have reports and a set range are
   * included (an untouched, report-less Weekly is left out).
   */
  private buildSavedIntervalWindows(): SavedIntervalWindow[] {
    const windows: SavedIntervalWindow[] = [];
    for (const opt of this.pdfIntervalOptions) {
      const hasReports = this.intervalReports[opt.value].length > 0;
      const applied = this.exportWindows[opt.value];
      if (hasReports && applied && applied.from && applied.to) {
        windows.push({
          interval: opt.value,
          label: opt.label,
          from: applied.from,
          to: applied.to,
        });
      }
    }
    return windows;
  }

  /**
   * Freeze the per-frequency ranges the user picked as the windows applied to the
   * render, and derive the overall span used for saved-document metadata.
   */
  private commitExportWindows(): void {
    const froms: string[] = [];
    const tos: string[] = [];
    for (const opt of this.pdfIntervalOptions) {
      const range = this.intervalRanges[opt.value];
      this.exportWindows[opt.value] = { from: range.from, to: range.to };
      if (range.from) {
        froms.push(range.from);
      }
      if (range.to) {
        tos.push(range.to);
      }
    }
    this.exportWindowFrom = froms.length
      ? froms.reduce((a, b) => (a < b ? a : b))
      : "";
    this.exportWindowTo = tos.length
      ? tos.reduce((a, b) => (a > b ? a : b))
      : "";
  }

  /**
   * "Apply": validate the per-frequency ranges, then detect which reports have a
   * range different from their original configured period. If any changed, ask
   * the user to review the changes or apply directly; otherwise run immediately.
   */
  async applyDataConfiguration(): Promise<void> {
    if (this.applyInProgress) {
      return;
    }
    // MOR reports must be named before the data configuration can be applied.
    if (!this.validateMorName()) {
      return;
    }
    const group = this.cachedGroupForRender?.data;
    if (!group) {
      this.applyError = "Report data isn't loaded yet.";
      return;
    }

    // Same dedup/order the renderer uses, minus text reports (no data to fetch).
    const reports = this.collectRenderReports(group).filter(
      (report) => !this.isTextReport(report)
    );

    // Validate the range of every frequency that actually has reports; ignore
    // frequencies with no reports (e.g. Weekly here) — their range is unused.
    const usedBuckets = new Set<"daily" | "weekly" | "monthly">();
    for (const report of reports) {
      const bucket = this.normalizeIntervalBucket(report.interval);
      if (bucket) {
        usedBuckets.add(bucket);
      }
    }
    for (const bucket of usedBuckets) {
      const range = this.intervalRanges[bucket];
      if (!range.from || !range.to) {
        this.applyError = `Choose both dates for the ${this.intervalLabel(
          bucket
        )} range.`;
        return;
      }
      if (range.from > range.to) {
        this.applyError = `${this.intervalLabel(
          bucket
        )}: 'Data from' must be on or before 'Data to'.`;
        return;
      }
    }

    this.applyError = "";
    // Fresh Apply → drop any per-report overrides from a previous review session.
    this.reportWindows.clear();

    // Change detection: which reports' chosen range differs from the period they
    // were originally configured with. If any, gate Apply behind a review prompt.
    this.applyChanges = this.computeReportChanges(reports);
    if (this.applyChanges.total > 0) {
      this.applyReviewOpen = false;
      this.applyReviewError = "";
      this.applyConfirmOpen = true;
      return;
    }

    await this.runApply();
  }

  /**
   * Build the per-frequency list of reports whose chosen range differs from the
   * period they were originally configured with (report.period). A frequency with
   * no reports or no range set contributes nothing.
   */
  private computeReportChanges(reports: DashboardReportData[]): PdfApplyChanges {
    const changes: PdfApplyChanges = {
      daily: [],
      weekly: [],
      monthly: [],
      total: 0,
    };
    for (const report of reports) {
      const bucket = this.normalizeIntervalBucket(report.interval);
      if (!bucket) {
        continue;
      }
      const range = this.intervalRanges[bucket];
      if (!range.from || !range.to) {
        continue;
      }
      const originalFrom = this.toDateInputValue(report.period?.startDate);
      const originalTo = this.toDateInputValue(report.period?.endDate);
      if (originalFrom !== range.from || originalTo !== range.to) {
        changes[bucket].push({
          id: report.reportId,
          name: report.reportName || `Report ${report.reportId}`,
          originalFrom,
          originalTo,
          newFrom: range.from,
          newTo: range.to,
        });
        changes.total += 1;
      }
    }
    return changes;
  }

  /** Review dialog → show the detailed per-frequency change breakdown. */
  onApplyReview(): void {
    this.applyReviewOpen = true;
  }

  /** Review dialog → dismiss without applying (stay in the entry dialog). */
  onApplyCancel(): void {
    this.applyConfirmOpen = false;
    this.applyReviewOpen = false;
    this.applyReviewError = "";
  }

  /**
   * Review dialog → apply. Validate any per-report ranges the user edited in the
   * review, capture them as overrides, then run. A report the user edited is
   * fetched/clipped to its own range instead of its frequency's range.
   */
  onApplyProceed(): void {
    for (const opt of this.pdfIntervalOptions) {
      for (const change of this.applyChanges[opt.value]) {
        if (!change.newFrom || !change.newTo) {
          this.applyReviewError = `Choose both dates for "${change.name}".`;
          this.applyReviewOpen = true;
          return;
        }
        if (change.newFrom > change.newTo) {
          this.applyReviewError = `"${change.name}": 'from' must be on or before 'to'.`;
          this.applyReviewOpen = true;
          return;
        }
      }
    }
    this.applyReviewError = "";

    // Capture each changed report's (possibly edited) range as a per-report
    // override so runApply / clipping use it instead of the frequency range.
    this.reportWindows.clear();
    for (const opt of this.pdfIntervalOptions) {
      for (const change of this.applyChanges[opt.value]) {
        this.reportWindows.set(change.id, {
          from: change.newFrom,
          to: change.newTo,
        });
      }
    }

    this.applyConfirmOpen = false;
    this.applyReviewOpen = false;
    void this.runApply();
  }

  /**
   * Refetch each report's data for the range of ITS OWN configured frequency
   * (daily reports use the Daily range, monthly reports the Monthly range) and
   * render with it. A frequency with no reports is never applied. Reports are
   * processed sequentially in render order with a 1/N progress counter. Text
   * reports carry no time-series data, so they're skipped. Each report keeps its
   * existing configuration — only the period and fetched values change.
   */
  private async runApply(): Promise<void> {
    const group = this.cachedGroupForRender?.data;
    if (!group) {
      return;
    }
    const reports = this.collectRenderReports(group).filter(
      (report) => !this.isTextReport(report)
    );

    this.applyTotal = reports.length;
    this.applyCurrent = 0;
    this.applyInProgress = true;

    try {
      for (let i = 0; i < reports.length; i += 1) {
        const report = reports[i];
        this.applyCurrent = i + 1;
        // Prefer a per-report override (edited in the review) over the frequency
        // range. No matching range (unrecognised interval) → leave as-is.
        const bucket = this.normalizeIntervalBucket(report.interval);
        const range =
          this.reportWindows.get(report.reportId) ||
          (bucket ? this.intervalRanges[bucket] : null);
        if (!range || !range.from || !range.to) {
          continue;
        }
        try {
          const res = await firstValueFrom(
            this.reportService.getReportDataByDateRange(
              report.reportId,
              range.from,
              range.to
            )
          );
          if (res?.success && res.data) {
            this.mergeRefetchedReportData(report, res.data);
          }
        } catch {
          // A single report's refetch failed — keep its embedded data and move
          // on rather than aborting the whole batch.
        }
      }

      // Freeze the per-frequency windows so any report that failed to refetch is
      // at least clipped to its own frequency's requested range.
      this.commitExportWindows();
      this.entryDialogOpen = false;
      this.loadDashboardGroupFromQuery();
    } finally {
      this.applyInProgress = false;
    }
  }

  /**
   * The reports the renderer will lay out, in order and deduplicated group-wide
   * (mirrors renderDashboardGroupIntoDocument). Returns live references into the
   * group so mutating their values/period reaches the render.
   */
  private collectRenderReports(
    group: DashboardGroupWithReportsResponse
  ): DashboardReportData[] {
    const list: DashboardReportData[] = [];
    const seen = new Set<string>();
    for (const dashboard of group.dashboards || []) {
      const reports = this.getUniqueReports(
        this.orderReportsByLayout(dashboard)
      );
      for (const report of reports) {
        const key = this.getReportDedupKey(report);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        list.push(report);
      }
    }
    return list;
  }

  /**
   * Merge a date-range refetch response back into a report: replace each
   * parameter's values (matched by paramCode) and the report period, keeping all
   * other configuration. Flag the report so the render redraws it fresh from the
   * new data instead of reusing a now-stale chart snapshot.
   */
  private mergeRefetchedReportData(
    report: DashboardReportData,
    data: {
      period?: { startDate?: string; endDate?: string };
      parameters?: Array<{
        paramCode?: string;
        formula?: string;
        values?: Array<{ start: string; end: string; value: number | null }>;
      }>;
    }
  ): void {
    if (data.period?.startDate && data.period?.endDate) {
      report.period = {
        startDate: data.period.startDate,
        endDate: data.period.endDate,
      };
    }
    // A paramCode is not unique within a report: OPEX consumption/cost tables
    // carry the same code twice (formula totalConsumption vs totalCost). Keying
    // by code alone gave both the same values, so match on formula first, then
    // fall back to the next unused entry with that code in response order.
    type Refetched = NonNullable<typeof data.parameters>[number];
    const byCode = new Map<string, Refetched[]>();
    for (const param of data.parameters || []) {
      if (param?.paramCode) {
        const list = byCode.get(param.paramCode) || [];
        list.push(param);
        byCode.set(param.paramCode, list);
      }
    }
    const used = new Set<Refetched>();
    const formulaKey = (f: unknown) => String(f || "").trim().toLowerCase();
    for (const param of report.parameters || []) {
      const candidates = byCode.get(param.paramCode);
      if (!candidates?.length) {
        continue;
      }
      const unused = candidates.filter((c) => !used.has(c));
      const match =
        unused.find((c) => formulaKey(c.formula) === formulaKey(param.formula)) ||
        unused[0] ||
        candidates[candidates.length - 1];
      used.add(match);
      param.values = match.values || [];
    }
    (report as unknown as { __windowClipped?: boolean }).__windowClipped = true;
  }

  /**
   * Clip each report's parameter values to the window of ITS OWN configured
   * frequency (daily reports to the Daily window, monthly to the Monthly window)
   * so the rendered PDF only contains data in that range. Returns the group
   * unchanged when no window is set for any frequency. Reports whose data is
   * actually clipped are flagged so the render bypasses any stale chart snapshot.
   */
  private clipReportsToWindow(
    group: DashboardGroupWithReportsResponse
  ): DashboardGroupWithReportsResponse {
    const windows = this.exportWindows;
    const hasAnyWindow = this.pdfIntervalOptions.some((opt) => {
      const w = windows[opt.value];
      return w.from || w.to;
    });
    if (!hasAnyWindow) {
      return group;
    }

    // Deep clone so the cached payload (reused on re-render) is never mutated.
    const clone: DashboardGroupWithReportsResponse = JSON.parse(
      JSON.stringify(group)
    );
    for (const dashboard of clone.dashboards || []) {
      for (const report of dashboard.reports || []) {
        // Prefer a per-report override (edited in the review); otherwise the
        // window for this report's own frequency. No matching window
        // (unrecognised interval) → leave the report's data untouched.
        const bucket = this.normalizeIntervalBucket(report.interval);
        const window =
          this.reportWindows.get(report.reportId) ||
          (bucket ? windows[bucket] : null);
        if (!window || (!window.from && !window.to)) {
          continue;
        }
        const from = window.from;
        const to = window.to;
        let clipped = false;
        for (const param of report.parameters || []) {
          if (!Array.isArray(param.values)) {
            continue;
          }
          const before = param.values.length;
          param.values = param.values.filter((v) => {
            // Compare the YYYY-MM-DD date portions directly. Lexicographic order
            // matches chronological order for that format and sidesteps any
            // local-vs-UTC timezone drift at the window edges.
            const match = String(v?.start ?? "").match(/\d{4}-\d{2}-\d{2}/);
            // Keep values we can't date-parse rather than silently dropping them.
            if (!match) {
              return true;
            }
            const day = match[0];
            if (from && day < from) {
              return false;
            }
            if (to && day > to) {
              return false;
            }
            return true;
          });
          if (param.values.length !== before) {
            clipped = true;
          }
        }
        if (clipped) {
          (report as unknown as { __windowClipped?: boolean }).__windowClipped =
            true;
        }
      }
    }
    return clone;
  }

  /**
   * Entry dialog, left section: fetch the group's reports, collect every
   * parameter code, then ask the backend for the oldest/newest dates that have
   * data so we can prefill the "Data from / Data to" range.
   */
  private loadDataConfiguration(dashboardGroupId: number): void {
    this.dataConfigLoading = true;
    this.dataConfigError = "";
    this.reportService.getDashboardGroupWithReports(dashboardGroupId).subscribe({
      next: (response) => {
        const group = response?.data;
        if (!response?.success || !group) {
          this.dataConfigLoading = false;
          this.dataConfigError = "Could not load report data.";
          return;
        }
        // Cache so "Continue" can render without refetching this payload.
        this.cachedGroupForRender = { id: dashboardGroupId, data: group };
        // Bucket the reports by interval for the accordions.
        this.groupReportsByInterval();
        const codes = this.collectReportCodes(group);
        if (!codes.length) {
          this.dataConfigLoading = false;
          this.dataConfigError = "No data codes found for this report.";
          return;
        }
        this.fetchDataDateBounds(group.siteId, codes);
      },
      error: () => {
        this.dataConfigLoading = false;
        this.dataConfigError = "Could not load report data.";
      },
    });
  }

  /** Unique parameter codes across every report/parameter in the group. */
  private collectReportCodes(
    group: DashboardGroupWithReportsResponse
  ): string[] {
    const codes = new Set<string>();
    for (const dashboard of group.dashboards || []) {
      for (const report of dashboard.reports || []) {
        for (const param of report.parameters || []) {
          if (param?.paramCode) {
            codes.add(param.paramCode);
          }
        }
      }
    }
    return Array.from(codes);
  }

  /** Ask the backend for the data date range and prefill the inputs. */
  private fetchDataDateBounds(siteId: number, codes: string[]): void {
    this.reportService.getReportDataDateBounds({ siteId, codes }).subscribe({
      next: (response) => {
        this.dataConfigLoading = false;
        const bounds = response?.data;
        if (!response?.success || !bounds) {
          this.dataConfigError = "Could not determine the available data range.";
          return;
        }
        this.dataBoundsOldest = this.toDateInputValue(bounds.oldestDate);
        this.dataBoundsNewest = this.toDateInputValue(bounds.newestDate);
        // Prefill every interval's range: from = oldest, to = newest available.
        for (const opt of this.pdfIntervalOptions) {
          this.intervalRanges[opt.value].from = this.dataBoundsOldest;
          this.intervalRanges[opt.value].to = this.dataBoundsNewest;
        }
      },
      error: () => {
        this.dataConfigLoading = false;
        this.dataConfigError = "Could not determine the available data range.";
      },
    });
  }

  /** Normalise an ISO timestamp or date string to a YYYY-MM-DD input value. */
  private toDateInputValue(value: string | null | undefined): string {
    if (!value) {
      return "";
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return "";
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  ngAfterViewInit(): void {
    document.execCommand("defaultParagraphSeparator", false, "p");

    this.pageEditors.changes.subscribe(() => {
      // While reflow is paginating, the live DOM is authoritative and the page
      // model is intentionally stale. Re-hydrating here would re-inject stale
      // content into editors reflow has just emptied (duplicating reports), so
      // skip until reflow settles (reflowFrom() syncs the model on completion).
      if (this.reflowInProgress) {
        return;
      }
      this.hydratePagesToDom();
      this.prepareResizableImages();
      this.syncPagesFromDom();
      setTimeout(() => this.updateDocumentStats(), 0);
    });

    this.hydratePagesToDom();
    this.prepareResizableImages();
    setTimeout(() => {
      this.reflowFrom(0);
      this.updateDocumentStats();
      this.focusActivePage();
    }, 0);
  }

  ngOnDestroy(): void {
    // Tear down any in-flight drag first: it owns document-level listeners and a
    // ghost node that must not outlive the view.
    this.finishVisualDrag(false);
    // A deferred repagination must not fire into a destroyed view. The DOM is
    // still read below, so nothing typed is lost by dropping it.
    this.cancelInputReflow();
    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = null;
    // Flush any pending edits to the local draft before the view goes away.
    if (this.documentReady) {
      // Only re-read the model from the DOM when pagination is settled. Mid-reflow
      // the DOM is half-distributed; the last settled sync is the safe snapshot.
      if (!this.reflowInProgress) {
        this.syncPagesFromDom();
      }
      void this.saveDraft(false);
    }
    if (this.draftAutosaveTimer) {
      clearTimeout(this.draftAutosaveTimer);
      this.draftAutosaveTimer = null;
    }
    this.draftAutosaveDeadline = 0;
    if (this.statsTimer) {
      clearTimeout(this.statsTimer);
      this.statsTimer = null;
    }
    if (this.tableControlsHideTimer) {
      clearTimeout(this.tableControlsHideTimer);
      this.tableControlsHideTimer = null;
    }
    if (this.dropNoticeTimer) {
      clearTimeout(this.dropNoticeTimer);
      this.dropNoticeTimer = null;
    }
  }

  // Commands that can change content height and therefore require repagination.
  // Everything else (bold/italic/underline/color/align/link…) only restyles the
  // current selection, so we skip the expensive reflow + full-document sync.
  private static readonly LAYOUT_COMMANDS = new Set<string>([
    "fontName",
    "fontSize",
    "formatBlock",
    "insertImage",
    "insertHTML",
    "insertUnorderedList",
    "insertOrderedList",
    "indent",
    "outdent",
    "insertHorizontalRule",
    "insertParagraph",
    "removeFormat",
    "undo",
    "redo",
  ]);

  format(command: string, value?: string): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    // Focus the selection's own editable host — it may be an editable table
    // island nested in the page editor. Refocusing the wrong host collapses the
    // selection, so the command would no-op on table content.
    this.focusSelectionEditableHost(editor);
    document.execCommand(command, false, value);

    if (DownloadpdfComponent.LAYOUT_COMMANDS.has(command)) {
      this.prepareResizableImages();
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    } else if (this.selectionSpansMultiplePages()) {
      // A selection spanning pages was just restyled across all of them in the
      // DOM; syncing only the active page would drop the off-page formatting on
      // the next rehydrate. Persist every page the selection touched.
      this.syncPagesFromDom();
      this.scheduleDraftAutosave();
    } else {
      // Non-layout command: only the active page's markup changed.
      this.syncActivePageFromDom();
    }
    this.updateDocumentStats();
  }

  /** True when the current selection spans more than one page editor. */
  private selectionSpansMultiplePages(): boolean {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      return false;
    }
    const range = sel.getRangeAt(0);
    const pageOf = (node: Node | null): Element | null => {
      const el =
        node && node.nodeType === Node.ELEMENT_NODE
          ? (node as Element)
          : node?.parentElement || null;
      return el?.closest(".page-editor") || null;
    };
    const startPage = pageOf(range.startContainer);
    const endPage = pageOf(range.endContainer);
    return !!startPage && !!endPage && startPage !== endPage;
  }

  setBlock(blockTag: string): void {
    this.activeBlock = blockTag;
    if (blockTag === "p") {
      this.format("formatBlock", "<p>");
      return;
    }

    this.format("formatBlock", `<${blockTag}>`);
  }

  get activeBlockLabel(): string {
    return (
      this.blockOptions.find((item) => item.value === this.activeBlock)?.label ||
      "Paragraph"
    );
  }

  openLinkDialog(): void {
    // The selection is captured on the button's mousedown (before focus moves to
    // the button and collapses it) — see the template. Re-saving here on click
    // would overwrite it with the now-collapsed selection, so we don't.
    this.dialogType = "link";
    this.linkUrl = "";
  }

  /**
   * Remove the hyperlink covering the current selection. Restores the selection
   * captured on mousedown (the button click would otherwise have collapsed it),
   * and when the caret merely sits inside a link with nothing selected, expands
   * the selection to the whole anchor so execCommand("unlink") has a target.
   */
  unlinkSelection(): void {
    this.restoreEditorSelectionForInsert();
    this.expandSelectionToEnclosingAnchor();
    this.format("unlink");
    this.savedSelectionRange = null;
  }

  /** If the selection is collapsed inside an <a>, select the whole anchor. */
  private expandSelectionToEnclosingAnchor(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (!range.collapsed) {
      return;
    }
    const node = range.commonAncestorContainer;
    const element =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    const anchor = element?.closest("a");
    if (!anchor) {
      return;
    }
    const anchorRange = document.createRange();
    anchorRange.selectNodeContents(anchor);
    selection.removeAllRanges();
    selection.addRange(anchorRange);
  }

  setFontFamily(fontFamily: string): void {
    this.selectedFont = fontFamily;
    this.format("fontName", fontFamily);
    this.fontDropdownOpen = false;
  }

  toggleFontDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.fontDropdownOpen = !this.fontDropdownOpen;
    this.blockDropdownOpen = false;
    this.sizeDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  setFontSize(size: number): void {
    this.selectedFontSize = size;

    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    // Focus the selection's own editable host (may be an editable table island)
    // so the size applies to the selected table text, not a collapsed caret.
    this.focusSelectionEditableHost(editor);
    // Use the legacy <font size="7"> form deterministically. With styleWithCSS
    // ON, execCommand("fontSize") instead emits `font-size: xxx-large` (the
    // size-7 keyword, ~48px). If that marker is missed it renders enormous,
    // blowing the page height up into many blank/overflowing pages. Forcing CSS
    // off here guarantees the <font size="7"> we convert below.
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand("fontSize", false, "7");

    // A selection can span page boundaries, so the temporary size-7 markers may
    // be created in MORE than the active page. Convert them in EVERY editor and
    // also catch the CSS keyword form as a fallback, so none survive at the
    // browser's ~48px size-7 default.
    const editors = this.pageEditors?.toArray() || [];
    for (const ref of editors) {
      const pageEl = ref.nativeElement;
      pageEl.querySelectorAll('font[size="7"]').forEach((tag) => {
        tag.removeAttribute("size");
        (tag as HTMLElement).style.fontSize = `${size}px`;
      });
      pageEl
        .querySelectorAll<HTMLElement>('[style*="xxx-large"]')
        .forEach((tag) => {
          if (tag.style.fontSize.trim() === "xxx-large") {
            tag.style.fontSize = `${size}px`;
          }
        });
    }

    this.syncPagesFromDom();
    // A multi-page selection may grow content on a page earlier than the caret,
    // so start the forward repagination at the earliest page that now overflows
    // (the forward pass stops at the first page that fits, so a stale start index
    // would miss the overflow). Falls back to the active page when nothing
    // overflows (e.g. the size was reduced); pull-up still runs from the top.
    const firstOverflow = editors.findIndex(
      (ref) =>
        ref.nativeElement.scrollHeight > ref.nativeElement.clientHeight + 1
    );
    this.reflowFrom(
      firstOverflow === -1 ? this.activePageIndex : firstOverflow
    );
    this.updateDocumentStats();
    this.sizeDropdownOpen = false;
  }

  setTextColor(color: string): void {
    this.textColor = color;
    this.applyTextColor();
  }

  setHighlightColor(color: string): void {
    this.highlightColor = color;
    this.applyHighlight();
  }

  /**
   * Apply the currently selected text color to the selection without reopening
   * the colour picker. Lets the user re-apply the same colour on a single click
   * (the picker is only needed to change the colour).
   */
  applyTextColor(): void {
    this.restoreSavedSelection();
    this.format("foreColor", this.textColor);
    this.savedSelectionRange = null;
  }

  /** Apply the currently selected highlight colour on a single click. */
  applyHighlight(): void {
    this.restoreSavedSelection();
    this.format("hiliteColor", this.highlightColor);
    this.savedSelectionRange = null;
  }

  /**
   * Capture the current editor selection before a focus-stealing control (the
   * native color input opens an OS dialog) collapses it. Called on the control's
   * mousedown, which fires while the editor selection is still intact.
   */
  saveSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element =
      container.nodeType === Node.ELEMENT_NODE
        ? (container as Element)
        : container.parentElement;
    const editor = element?.closest(".page-editor") as HTMLElement | null;
    if (!editor) {
      // No editor selection right now — drop any stale saved range so we don't
      // later restore a selection that no longer exists.
      this.savedSelectionRange = null;
      return;
    }
    this.savedSelectionRange = range.cloneRange();
  }

  /** Re-apply the saved selection so the next command targets that text. */
  private restoreSavedSelection(): HTMLDivElement | null {
    const range = this.savedSelectionRange;
    if (!range) {
      return null;
    }
    // Bail out if the saved range points at nodes that were removed/replaced
    // (e.g. by a reflow) — restoring a detached range throws and would abort the
    // whole insert.
    const container = range.startContainer as Node & { isConnected?: boolean };
    if (!container || container.isConnected === false) {
      this.savedSelectionRange = null;
      return null;
    }
    const el =
      container.nodeType === Node.ELEMENT_NODE
        ? (container as Element)
        : container.parentElement;
    const editor = el?.closest(".page-editor") as HTMLDivElement | null;
    if (!editor) {
      this.savedSelectionRange = null;
      return null;
    }
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
    }
    editor.focus();
    const selection = window.getSelection();
    if (selection) {
      try {
        selection.removeAllRanges();
        selection.addRange(range);
      } catch {
        this.savedSelectionRange = null;
        return null;
      }
    }
    return editor;
  }

  insertHorizontalRule(): void {
    this.format("insertHorizontalRule");
  }

  insertImage(): void {
    // Intentionally left empty. Use local file picker via insertImageFromFile.
  }

  insertImageFromFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageDataUrl = String(reader.result || "");
      if (imageDataUrl) {
        this.format("insertImage", imageDataUrl);
      }
    };
    reader.readAsDataURL(file);
    input.value = "";
  }

  openTableDialog(): void {
    // Capture the caret before the dialog steals focus, so the table is inserted
    // at the cursor position instead of failing for lack of a selection.
    this.saveSelection();
    this.dialogType = "table";
    this.tableRows = 2;
    this.tableCols = 2;
  }

  confirmDialog(): void {
    if (this.dialogType === "link") {
      if (this.linkUrl.trim()) {
        this.restoreEditorSelectionForInsert();
        this.format("createLink", this.linkUrl.trim());
      }
      this.savedSelectionRange = null;
      this.closeDialog();
      return;
    }

    if (this.dialogType === "table") {
      const rows = Number(this.tableRows);
      const cols = Number(this.tableCols);

      if (!rows || !cols || rows < 1 || cols < 1) {
        return;
      }

      const tableStyle =
        "width:100%;border-collapse:collapse;table-layout:fixed;margin:12px 0;";
      const cellStyle =
        "border:1px solid #cfd6dd;padding:8px;min-width:48px;";
      let tableHtml = `<table class="doc-table" style="${tableStyle}"><tbody>`;
      for (let r = 0; r < rows; r += 1) {
        tableHtml += "<tr>";
        for (let c = 0; c < cols; c += 1) {
          tableHtml += `<td style="${cellStyle}">&nbsp;</td>`;
        }
        tableHtml += "</tr>";
      }
      tableHtml += "</tbody></table><p><br></p>";

      // Insert the table directly into the DOM at block level. execCommand
      // ("insertHTML") is unreliable for block elements after focus has moved to
      // a dialog, so we do it deterministically instead.
      this.restoreEditorSelectionForInsert();
      this.insertBlockHtmlAtCaret(tableHtml);
      this.prepareResizableImages();
      this.syncPagesFromDom();
      // Pagination is async and may move the table to another page; defer the
      // scroll-to-table until reflow fully settles (handled in reflowFrom).
      this.pendingTableReveal = true;
      this.reflowFrom(this.activePageIndex);
      this.updateDocumentStats();
      this.savedSelectionRange = null;
      this.closeDialog();
    }
  }

  /** Scroll the just-inserted table into view and drop the caret in it. */
  private revealInsertedTable(): void {
    const table = document.querySelector(
      'table[data-just-inserted="1"]'
    ) as HTMLElement | null;
    if (!table) {
      return;
    }
    table.removeAttribute("data-just-inserted");

    const editor = table.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
      this.currentPageIndex = idx;
    }

    // Put the caret in the first cell first, then scroll the table into view so
    // the focus change doesn't override our scroll position.
    const firstCell = table.querySelector("td,th") as HTMLElement | null;
    if (firstCell) {
      this.placeCaretAtStart(firstCell);
    }
    // Scroll the workspace so the whole .doc-page holding the table is visible.
    const docPage = table.closest(".doc-page") as HTMLElement | null;
    const target = docPage || table;
    try {
      target.scrollIntoView({ block: "center", behavior: "auto" });
    } catch {
      target.scrollIntoView();
    }
    this.syncPagesFromDom();
  }

  /**
   * Insert block-level HTML at the editor's top level, just after the block that
   * currently holds the caret (or at the end if there is no caret). Avoids the
   * invalid nesting and silent no-ops that execCommand("insertHTML") can cause.
   */
  private insertBlockHtmlAtCaret(html: string): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    const temp = document.createElement("div");
    temp.innerHTML = html;
    // Mark the new table so we can scroll to it after pagination settles.
    const newTable = temp.querySelector("table");
    if (newTable) {
      newTable.setAttribute("data-just-inserted", "1");
    }
    const newNodes = Array.from(temp.childNodes);
    if (!newNodes.length) {
      return;
    }

    // Determine where to insert: `anchor` is the node to insert BEFORE
    // (null = append to the end of the editor).
    let anchor: ChildNode | null = null;
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      selection.anchorNode &&
      editor.contains(selection.anchorNode)
    ) {
      const anchorNode = selection.anchorNode;
      if (anchorNode === editor) {
        // Caret sits directly on the editor; use the offset to pick the child.
        anchor = editor.childNodes[selection.anchorOffset] || null;
      } else {
        // Climb to the editor's direct child holding the caret, insert after it.
        let node: Node | null = anchorNode;
        while (node && node.parentNode && node.parentNode !== editor) {
          node = node.parentNode;
        }
        anchor = node && node.parentNode === editor ? node.nextSibling : null;
      }
    }

    for (let i = 0; i < newNodes.length; i += 1) {
      const node = newNodes[i];
      if (anchor) {
        editor.insertBefore(node, anchor);
      } else {
        editor.appendChild(node);
      }
      anchor = node.nextSibling;
    }
  }

  /**
   * Restore the selection captured before the dialog opened so the insert lands
   * at the right spot. Falls back to focusing the active editor (caret at end)
   * when there is no saved selection, so insertHTML always has a target.
   */
  private restoreEditorSelectionForInsert(): HTMLElement | null {
    const restored = this.restoreSavedSelection();
    if (restored) {
      return restored;
    }
    const editor = this.getActiveEditor();
    if (!editor) {
      return null;
    }
    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.placeCaretAtEnd(editor);
    }
    return editor;
  }

  closeDialog(): void {
    this.dialogType = "none";
  }

  toggleBlockDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.blockDropdownOpen = !this.blockDropdownOpen;
    this.fontDropdownOpen = false;
    this.sizeDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  toggleSizeDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.sizeDropdownOpen = !this.sizeDropdownOpen;
    this.fontDropdownOpen = false;
    this.blockDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  toggleLineSpacingDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.lineSpacingDropdownOpen = !this.lineSpacingDropdownOpen;
    this.fontDropdownOpen = false;
    this.blockDropdownOpen = false;
    this.sizeDropdownOpen = false;
  }

  selectLineSpacing(value: number): void {
    this.selectedLineSpacing = value;
    this.applyLineSpacingToSelection(value);
    this.lineSpacingDropdownOpen = false;
  }

  private applyLineSpacingToSelection(value: number): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    const targetBlocks = new Set<HTMLElement>();

    const addBlock = (node: Node | null): void => {
      if (!node) {
        return;
      }
      const element = (node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement) as Element | null;
      const block = element?.closest(
        "p,div,li,h1,h2,h3,h4,h5,h6,blockquote,td,th"
      ) as HTMLElement | null;
      if (block && editor.contains(block)) {
        targetBlocks.add(block);
      }
    };

    if (range.collapsed) {
      addBlock(selection.anchorNode);
    } else {
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT);
      let current = walker.currentNode as Element | null;
      while (current) {
        if (range.intersectsNode(current)) {
          const tag = current.tagName.toLowerCase();
          if (
            tag === "p" || tag === "div" || tag === "li" ||
            tag === "h1" || tag === "h2" || tag === "h3" ||
            tag === "h4" || tag === "h5" || tag === "h6" ||
            tag === "blockquote" || tag === "td" || tag === "th"
          ) {
            targetBlocks.add(current as HTMLElement);
          }
        }
        current = walker.nextNode() as Element | null;
      }
      addBlock(range.startContainer);
      addBlock(range.endContainer);
    }

    if (!targetBlocks.size) {
      const fallback = editor.querySelector("p,div,li,h1,h2,h3,h4,h5,h6,blockquote") as HTMLElement | null;
      if (fallback) {
        targetBlocks.add(fallback);
      }
    }

    const normalized = Number(value) > 0 ? Number(value) : 1.5;
    targetBlocks.forEach((block) => {
      block.style.lineHeight = `${normalized}`;
    });

    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  selectBlock(option: "p" | "h1" | "h2" | "h3"): void {
    this.setBlock(option);
    this.blockDropdownOpen = false;
  }

  selectFontSize(size: number): void {
    this.setFontSize(size);
    this.sizeDropdownOpen = false;
  }

  setActivePage(index: number): void {
    this.activePageIndex = index;
    // Reflect the page the user just interacted with in the footer immediately.
    this.currentPageIndex = index;
  }

  private scrollRafPending = false;

  /** Track the page in the scroll viewport so the footer shows the real page. */
  onWorkspaceScroll(): void {
    // Throttle to one update per animation frame — scroll fires very often.
    if (this.scrollRafPending) {
      return;
    }
    this.scrollRafPending = true;
    requestAnimationFrame(() => {
      this.scrollRafPending = false;
      this.updateVisiblePageIndex();
      // Keep the per-table "+" controls glued to their table while scrolling.
      if (this.tableControlsVisible && this.hoveredTable?.isConnected) {
        this.showTableControls(this.hoveredTable);
      } else if (this.tableControlsVisible) {
        this.tableControlsVisible = false;
      }
      // Keep the per-cell "−" delete controls glued to their cell too.
      if (this.tableDelControlsVisible && this.hoveredCell?.isConnected) {
        this.showTableDeleteControls(this.hoveredCell);
      } else if (this.tableDelControlsVisible) {
        this.tableDelControlsVisible = false;
      }
    });
  }

  /** Pick the doc-page whose center is closest to the viewport center. */
  private updateVisiblePageIndex(): void {
    const workspace = this.pagesWorkspaceRef?.nativeElement;
    if (!workspace) {
      return;
    }
    const pages = Array.from(
      workspace.querySelectorAll(".doc-page")
    ) as HTMLElement[];
    if (!pages.length) {
      return;
    }

    const workspaceRect = workspace.getBoundingClientRect();
    const viewportCenterY = workspaceRect.top + workspace.clientHeight / 2;

    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < pages.length; i += 1) {
      const rect = pages[i].getBoundingClientRect();
      const pageCenter = rect.top + rect.height / 2;
      const distance = Math.abs(pageCenter - viewportCenterY);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    if (bestIndex !== this.currentPageIndex) {
      this.currentPageIndex = bestIndex;
    }
  }

  insertPageBreak(): void {
    const insertAt = this.activePageIndex + 1;
    this.pages.splice(insertAt, 0, { id: this.nextPageId++, html: "<p><br></p>" });
    setTimeout(() => {
      this.activePageIndex = insertAt;
      this.focusActivePage();
    }, 0);
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const rawTarget = event.target as EventTarget | null;
    const targetNode = rawTarget as Node | null;
    const targetEl =
      rawTarget instanceof Element
        ? rawTarget
        : (targetNode?.parentElement as Element | null);
    const insideBlock = !!(
      targetNode && this.blockPickerRef?.nativeElement?.contains(targetNode)
    );
    const insideFont = !!(
      targetNode && this.fontPickerRef?.nativeElement?.contains(targetNode)
    );
    const insideSize = !!(
      targetNode && this.sizePickerRef?.nativeElement?.contains(targetNode)
    );
    const insideLineSpacing = !!(
      targetNode && this.lineSpacingPickerRef?.nativeElement?.contains(targetNode)
    );
    const pathElements = this.getEventPathElements(event);
    const insideResizable =
      pathElements.some((el) => el.classList?.contains("resizable-image")) ||
      (targetEl ? !!targetEl.closest(".resizable-image") : false);
    const insideImageToolbar =
      pathElements.some((el) => el.classList?.contains("image-quick-toolbar")) ||
      (targetEl ? !!targetEl.closest(".image-quick-toolbar") : false);

    if (!insideBlock) {
      this.blockDropdownOpen = false;
    }
    if (!insideFont) {
      this.fontDropdownOpen = false;
    }
    if (!insideSize) {
      this.sizeDropdownOpen = false;
    }
    if (!insideLineSpacing) {
      this.lineSpacingDropdownOpen = false;
    }
    // Close the saved-document kebab menu on any outside click.
    if (this.savedDocMenuId !== null) {
      const insideSavedMenu = targetEl
        ? !!targetEl.closest(".entry-saved-menu, .entry-saved-kebab")
        : false;
      if (!insideSavedMenu) {
        this.closeSavedDocMenu();
      }
    }
    if (!insideResizable && !insideImageToolbar) {
      this.selectResizableWrapper(null);
    }

    const insideTableToolbar =
      pathElements.some((el) => el.classList?.contains("table-quick-toolbar")) ||
      (targetEl ? !!targetEl.closest(".table-quick-toolbar") : false);
    const insideTableCell = targetEl ? !!targetEl.closest("td,th") : false;
    if (!insideTableToolbar && !insideTableCell) {
      this.tableToolbarVisible = false;
      this.selectedTableCell = null;
    }
  }

  @HostListener("window:resize")
  @HostListener("window:scroll")
  onViewportChange(): void {
    this.updateFloatingImageToolbar();
    if (this.tableToolbarVisible) {
      this.updateTableToolbarFromSelection();
    }
    this.updateVisiblePageIndex();
  }

  // Reflect the current selection's formatting back into the toolbar (block type,
  // font, size, line spacing, and bold/italic/underline/strike states) so the
  // controls show what the selected text actually is. Throttled to one update
  // per frame because selectionchange fires very frequently.
  /**
   * Delete / Backspace removes the selected chart, table or image. Until now the
   * only way to remove one was the floating toolbar's "Del" button: visuals are
   * filtered out of the block walk Backspace uses, so the key simply did nothing
   * in front of one. This covers the case where the key arrives with no caret in
   * a page editor (the user clicked the visual and focus sits elsewhere); when an
   * editor does have focus its own handler gets there first, and this then finds
   * nothing selected.
   */
  @HostListener("document:keydown", ["$event"])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== "Delete" && event.key !== "Backspace") {
      return;
    }
    if (event.defaultPrevented) {
      return; // the page editor's own handler already dealt with it
    }
    if (!this.shouldDeleteSelectedVisual(event)) {
      return;
    }
    event.preventDefault();
    this.deleteSelectedVisualFromKeyboard();
  }

  @HostListener("document:selectionchange")
  onSelectionChange(): void {
    if (this.toolbarSyncPending) {
      return;
    }
    this.toolbarSyncPending = true;
    requestAnimationFrame(() => {
      this.toolbarSyncPending = false;
      this.syncToolbarFromSelection();
    });
  }

  private syncToolbarFromSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.anchorNode) {
      return;
    }

    const anchor = selection.anchorNode;
    const element =
      anchor.nodeType === Node.ELEMENT_NODE
        ? (anchor as Element)
        : anchor.parentElement;
    if (!element) {
      return;
    }

    // Only sync when the selection is inside an editor; otherwise leave the
    // toolbar untouched (e.g. when focus is on a toolbar control or dialog).
    const editor = element.closest(".page-editor") as HTMLElement | null;
    if (!editor) {
      return;
    }

    // Block type (Paragraph / Heading 1-3).
    const block = element.closest(
      "h1,h2,h3,h4,h5,h6,p,div,li,blockquote"
    ) as HTMLElement | null;
    if (block) {
      const tag = block.tagName.toLowerCase();
      this.activeBlock =
        tag === "h1" || tag === "h2" || tag === "h3" ? tag : "p";
    }

    // Computed font / size / line spacing from the nearest element.
    const styleTarget = (element instanceof HTMLElement
      ? element
      : block) as HTMLElement | null;
    if (styleTarget) {
      const computed = window.getComputedStyle(styleTarget);

      const family = this.matchFontFamily(computed.fontFamily);
      if (family) {
        this.selectedFont = family;
      }

      const sizePx = Math.round(parseFloat(computed.fontSize) || 0);
      if (sizePx > 0) {
        this.selectedFontSize = sizePx;
      }

      const spacing = this.computeLineSpacingRatio(computed, sizePx);
      if (spacing) {
        this.selectedLineSpacing = spacing;
      }
    }

    // Inline format states for the B / I / U / S buttons.
    try {
      this.isBold = document.queryCommandState("bold");
      this.isItalic = document.queryCommandState("italic");
      this.isUnderline = document.queryCommandState("underline");
      this.isStrike = document.queryCommandState("strikeThrough");
    } catch {
      /* queryCommandState can throw if no active editable context */
    }
  }

  /** Match a computed font-family stack against our known font list. */
  private matchFontFamily(computedFamily: string): string | null {
    if (!computedFamily) {
      return null;
    }
    const first = computedFamily
      .split(",")[0]
      .trim()
      .replace(/^["']|["']$/g, "");
    const match = this.fontFamilies.find(
      (font) => font.toLowerCase() === first.toLowerCase()
    );
    return match || null;
  }

  /** Derive a line-spacing ratio, snapping to a known option when close. */
  private computeLineSpacingRatio(
    computed: CSSStyleDeclaration,
    fontSizePx: number
  ): number | null {
    const lineHeight = computed.lineHeight;
    if (!lineHeight || lineHeight === "normal") {
      return null;
    }
    const lineHeightPx = parseFloat(lineHeight);
    if (!lineHeightPx || !fontSizePx) {
      return null;
    }
    const ratio = lineHeightPx / fontSizePx;
    let nearest = this.lineSpacingOptions[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.lineSpacingOptions.length; i += 1) {
      const distance = Math.abs(this.lineSpacingOptions[i] - ratio);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = this.lineSpacingOptions[i];
      }
    }
    return bestDistance <= 0.15 ? nearest : Math.round(ratio * 100) / 100;
  }

  removeFormatting(): void {
    this.format("removeFormat");
    this.format("unlink");
  }

  onPageInput(index: number): void {
    this.activePageIndex = index;
    this.updateDocumentStats(); // already debounced internally
    this.updateTableToolbarFromSelection();

    // Typing has to stay cheap. The repagination path below re-walks every page's
    // images (decoding each chart's payload and forcing a layout read per chart)
    // and serialises every page's innerHTML — 10-15 MB once chart snapshots are
    // embedded. Running that per keystroke is what made Backspace and Enter lag
    // on a long report.
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (editor && editor.scrollHeight > editor.clientHeight + 1) {
      // The page is over its limit right now, and .page-editor clips: anything
      // more the user types would be invisible. Repaginate immediately.
      this.runInputReflow(index);
      return;
    }

    // The page still fits, so pagination cannot be wrong yet — a keystroke only
    // changed THIS page's markup. Sync just that, and let pagination catch up
    // when typing pauses: deleting may have opened a gap worth closing, and the
    // next character may tip the page over.
    this.syncActivePageFromDom();
    this.scheduleInputReflow(index);
  }

  /**
   * Full repagination after an edit: refresh image wrappers (a paste or a drop
   * can have introduced some), snapshot every page, and reflow from the edited
   * page with the caret marked so it survives the block moves.
   */
  private runInputReflow(index: number): void {
    this.cancelInputReflow();
    this.prepareResizableImages();
    this.syncPagesFromDom();
    // Mark the caret before reflow so it survives the DOM node moves reflow makes
    // between page editors (otherwise it jumps to the top of the page and vanishes
    // mid-typing). restorePendingCaret() re-places it once reflow settles.
    this.pendingCaretMarkerId = this.insertCaretMarker();
    this.reflowFrom(index);
  }

  /**
   * Coalesce repagination while the user is still typing — but never postpone it
   * indefinitely.
   *
   * A plain debounce was wrong for the gesture this editor is used with: holding
   * Backspace repeats the key roughly thirty times a second, so the timer was
   * reset before it could ever fire and pagination did not run until the key was
   * released. The blank space the user was trying to close up simply sat there,
   * then jumped all at once. The deadline caps how long a continuous burst can
   * defer it, so content keeps shifting up while the key is held.
   */
  private scheduleInputReflow(index: number): void {
    const now = Date.now();
    if (!this.inputReflowDeadline) {
      this.inputReflowDeadline =
        now + DownloadpdfComponent.INPUT_REFLOW_MAX_WAIT_MS;
    }
    if (this.inputReflowTimer) {
      clearTimeout(this.inputReflowTimer);
    }
    const wait = Math.max(
      0,
      Math.min(
        DownloadpdfComponent.INPUT_REFLOW_DELAY_MS,
        this.inputReflowDeadline - now
      )
    );
    this.inputReflowTimer = setTimeout(() => {
      this.inputReflowTimer = null;
      this.inputReflowDeadline = 0;
      this.runInputReflow(index);
    }, wait);
  }

  private cancelInputReflow(): void {
    if (this.inputReflowTimer) {
      clearTimeout(this.inputReflowTimer);
      this.inputReflowTimer = null;
    }
    this.inputReflowDeadline = 0;
  }

  /** Run a deferred repagination now — before anything snapshots the document. */
  private flushInputReflow(): void {
    if (!this.inputReflowTimer) {
      return;
    }
    this.runInputReflow(this.activePageIndex);
  }

  /**
   * Drop a zero-width marker span at the current caret and return its id. The
   * marker is moved along with its containing block when reflow shuffles content
   * between page editors, so the caret can be re-placed exactly where it was. Any
   * marker left over from an in-flight reflow is cleared first so at most one
   * exists. Returns null when the caret isn't inside a page editor.
   */
  private insertCaretMarker(): string | null {
    // Clear any stale marker from a still-settling reflow before adding a new one.
    document
      .querySelectorAll("[data-caret-marker]")
      .forEach((node) => node.remove());

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    if (!el || !el.closest(".page-editor")) {
      return null;
    }

    const marker = document.createElement("span");
    const id = `pdf-caret-${this.caretMarkerSeq++}`;
    marker.id = id;
    marker.setAttribute("data-caret-marker", "1");
    // Zero footprint so it never affects the height measurements reflow relies on.
    marker.style.cssText =
      "display:inline;padding:0;margin:0;border:0;width:0;height:0;font-size:0;line-height:0;";

    const at = range.cloneRange();
    at.collapse(true);
    try {
      at.insertNode(marker);
    } catch {
      return null;
    }
    return id;
  }

  /**
   * Re-place the caret at the pending marker (set before a reflow) and remove the
   * marker. Follows the marker wherever reflow moved it — including onto another
   * page editor — and focuses that editable host so typing continues seamlessly.
   */
  private restorePendingCaret(): void {
    const id = this.pendingCaretMarkerId;
    if (!id) {
      return;
    }
    this.pendingCaretMarkerId = null;
    const marker = document.getElementById(id);
    if (!marker || !marker.parentNode) {
      return;
    }
    const parent = marker.parentNode;
    const host = (marker.closest('[contenteditable="true"]') ||
      marker.closest(".page-editor")) as HTMLElement | null;
    const index = Array.prototype.indexOf.call(parent.childNodes, marker);
    marker.remove();

    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    try {
      if (host && document.activeElement !== host) {
        // preventScroll: focusing a page editor otherwise scrolls it into view,
        // which is a second, uncontrolled source of the view jumping. Leave the
        // scrolling to scrollCaretIntoView, which only moves when the caret is
        // actually off screen and then only by the minimum needed.
        host.focus({ preventScroll: true });
      }
      const range = document.createRange();
      const safeIndex = Math.min(Math.max(index, 0), parent.childNodes.length);
      range.setStart(parent, safeIndex);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      // Keep the active page in step with where the caret actually ended up,
      // rather than guessing during the forward sweep.
      const caretPage = this.pageIndexOf(parent);
      if (caretPage >= 0) {
        this.activePageIndex = caretPage;
      }
    } catch {
      // Position became invalid (node removed by reflow) — leave focus on host.
    }
  }

  /** Strip any caret-preservation markers from serialized page HTML so they never
   *  leak into the saved/exported document. Cheap no-op when none are present. */
  private stripCaretMarkers(html: string): string {
    if (!html || html.indexOf("data-caret-marker") === -1) {
      return html;
    }
    return html.replace(/<span[^>]*data-caret-marker[^>]*>\s*<\/span>/gi, "");
  }

  /**
   * Intercept pastes so we never inject raw clipboard HTML (Word/Excel/web pages
   * carry scripts, event handlers, and heavy inline styles that corrupt the
   * pagination engine). We sanitize to a safe subset and insert that instead.
   */
  onEditorPaste(event: ClipboardEvent, index: number): void {
    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }

    event.preventDefault();
    this.activePageIndex = index;

    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }
    editor.focus();

    const html = clipboard.getData("text/html");
    const text = clipboard.getData("text/plain");

    // Guard against pasting enormous HTML (e.g. a page full of base64 images).
    // Injecting multiple MB into the 6-7 MB editor DOM can hang the browser and
    // overwhelm pagination. Above the cap, fall back to plain text.
    const MAX_PASTE_HTML = 1_500_000;
    const MAX_PASTE_TEXT = 500_000;

    let inserted = false;
    if (html && html.trim()) {
      const clean = this.sanitizePastedHtml(html);
      if (clean && clean.trim() && clean.length <= MAX_PASTE_HTML) {
        document.execCommand("insertHTML", false, clean);
        inserted = true;
      }
    }
    if (!inserted && text) {
      // insertText preserves line breaks and is always safe.
      document.execCommand(
        "insertText",
        false,
        text.length > MAX_PASTE_TEXT ? text.slice(0, MAX_PASTE_TEXT) : text
      );
      inserted = true;
    }
    if (!inserted) {
      return;
    }

    // Pasted content can change height and add images/tables → full layout pass.
    this.prepareResizableImages();
    this.syncPagesFromDom();
    // Keep the caret at the end of the pasted content through the reflow.
    this.pendingCaretMarkerId = this.insertCaretMarker();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  /**
   * Make Backspace able to remove the blank lines above (or below) rendered
   * blocks like charts/tables. Those gaps are empty <p><br></p> spacers; the
   * browser's default Backspace can't delete them because the element before is
   * non-editable (the chart/table), so the merge is a no-op. Here we detect that
   * case and remove the empty spacer ourselves, then reflow (which backfills).
   */
  onEditorKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== "Backspace" && event.key !== "Delete") {
      return;
    }
    // A selected chart / table / image is what the key acts on, wherever the
    // caret happens to be — including when the visual sits on another page.
    if (this.shouldDeleteSelectedVisual(event)) {
      event.preventDefault();
      this.deleteSelectedVisualFromKeyboard();
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      return; // let the default handle real selections / mid-text edits
    }
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return;
    }
    const range = selection.getRangeAt(0);
    const block = this.getCaretBlock(range.startContainer, editor);
    if (!block) {
      return;
    }

    if (event.key === "Backspace") {
      if (!this.isCaretAtBlockStart(range, block)) {
        return; // normal backspace inside text
      }
      const prevBlock = this.findAdjacentBlock(block, index, "previous");

      // The current line is blank → delete it and pull the caret to the previous block.
      if (this.isBlockEmpty(block) && prevBlock) {
        event.preventDefault();
        this.deleteSpacerBlock(block, index);
        setTimeout(() => this.placeCaretAtEndOfBlock(prevBlock), 0);
        return;
      }

      // The current line has content but an empty spacer sits directly above it
      // (e.g. between a chart and this text) → remove that spacer.
      if (prevBlock && this.isBlockEmpty(prevBlock) && !this.isNonEditableBlock(prevBlock)) {
        event.preventDefault();
        this.deleteSpacerBlock(prevBlock, index);
        setTimeout(() => this.placeCaretAtStart(block), 0);
        return;
      }

      // A chart / table / image immediately above the caret is what Backspace
      // acts on — checked BEFORE any text join, so a join can never step silently
      // over a visual. findAdjacentBlock cannot see one: collectEditableBlocks
      // filters visuals out, so it skips PAST the chart to the text above it,
      // finds that text non-empty and gives up, which is why Backspace in front
      // of a chart did nothing at all. findAdjacentUnit keeps visuals, and
      // crosses pages. The first press selects, the next removes: the node goes
      // outside execCommand, so the browser's undo stack cannot bring a chart
      // back and one stray keypress must not be enough to lose it.
      const prevUnit = this.findAdjacentUnit(block, index, "previous");
      if (prevUnit && this.isVisualUnit(prevUnit.el)) {
        event.preventDefault();
        this.selectAdjacentVisual(prevUnit.el, prevUnit.pageIndex);
        return;
      }

      // A heading keeps its own identity through a Backspace. Left to the
      // browser, backspacing at the start of a heading MERGES its text into the
      // paragraph above and the title silently becomes body text; merged the
      // other way, body text is promoted to a title. So a merge across a block
      // type boundary is refused.
      //
      // Relocating is a different operation, though, and it is the one the user
      // is asking for at a page boundary: moving a block to the page above keeps
      // its tag, so nothing is restyled. Refusing both is what left Backspace
      // doing nothing at all between pages — nearly every page here begins with a
      // report heading, so nearly every boundary hit this.
      if (prevBlock && this.joinWouldRestyle(block, prevBlock)) {
        event.preventDefault();
        if (!editor.contains(prevBlock)) {
          this.pullBlockToPreviousPage(block, index);
        }
        return;
      }

      // Caret at the very start of the FIRST block on a later page, with content
      // above on the PREVIOUS page: browsers can't Backspace-join across separate
      // page editors, so do it ourselves — merge this block into the last block
      // of the previous page, then re-paginate.
      if (
        prevBlock &&
        !editor.contains(prevBlock) &&
        !this.isNonEditableBlock(prevBlock) &&
        !this.isNonEditableBlock(block)
      ) {
        event.preventDefault();
        this.mergeBlockIntoPrevious(block, prevBlock, index);
        return;
      }
      return;
    }

    // Delete (forward): remove an empty spacer line immediately below the caret.
    if (event.key === "Delete") {
      if (!this.isCaretAtBlockEnd(range, block)) {
        return;
      }
      const nextBlock = this.findAdjacentBlock(block, index, "next");
      if (nextBlock && this.isBlockEmpty(nextBlock) && !this.isNonEditableBlock(nextBlock)) {
        event.preventDefault();
        this.deleteSpacerBlock(nextBlock, index);
        setTimeout(() => this.placeCaretAtEndOfBlock(block), 0);
        return;
      }

      // Mirror of Backspace: a visual sitting after the caret is reachable too,
      // across a page boundary as well.
      const nextUnit = this.findAdjacentUnit(block, index, "next");
      if (nextUnit && this.isVisualUnit(nextUnit.el)) {
        event.preventDefault();
        this.selectAdjacentVisual(nextUnit.el, nextUnit.pageIndex);
        return;
      }

      // Pulling the next block up into this one destroys a heading exactly as
      // Backspace does — a heading below the caret would lose its title styling
      // on the way up. Refuse it the same way.
      if (nextBlock && this.joinWouldRestyle(block, nextBlock)) {
        event.preventDefault();
      }
    }
  }

  /**
   * A self-contained visual: a chart, a data table or an image. These move,
   * resize and delete as one piece and hold no caret position of their own.
   */
  private isVisualUnit(el: Element | null | undefined): boolean {
    return (
      el instanceof HTMLElement &&
      (el.classList.contains("resizable-image") ||
        el.classList.contains("report-chart") ||
        el.classList.contains("report-table-block"))
    );
  }

  /** A block that holds text directly, with nothing structural nested in it. */
  private isLeafEditableBlock(el: HTMLElement): boolean {
    if (!DownloadpdfComponent.LEAF_BLOCK_TAGS.has(el.tagName)) {
      return false;
    }
    if (
      el.querySelector(
        "p,li,div,table,.report-chart,.report-table-block,.resizable-image"
      )
    ) {
      return false;
    }
    return !this.isNonEditableBlock(el);
  }

  /**
   * Every unit on a page in document order — leaf text blocks AND visuals.
   *
   * The counterpart to collectEditableBlocks, which filters visuals out entirely.
   * Here a visual is kept, and treated as opaque: the walk never descends into
   * one, so a report table's editable cells cannot masquerade as separate units.
   */
  private collectPageUnits(editor: HTMLElement): HTMLElement[] {
    const units: HTMLElement[] = [];
    const visit = (parent: Element): void => {
      const children = Array.from(parent.children) as HTMLElement[];
      for (let i = 0; i < children.length; i += 1) {
        const child = children[i];
        if (this.isVisualUnit(child)) {
          units.push(child);
          continue; // opaque — never look inside
        }
        if (this.isLeafEditableBlock(child)) {
          units.push(child);
          continue;
        }
        visit(child);
      }
    };
    visit(editor);
    return units;
  }

  /**
   * The unit immediately before or after a block in document order, crossing page
   * boundaries. Unlike findAdjacentBlock this returns visuals, and it scans on
   * past a page that holds no units at all rather than stopping at the neighbour.
   */
  private findAdjacentUnit(
    block: HTMLElement,
    index: number,
    direction: "previous" | "next"
  ): { el: HTMLElement; pageIndex: number } | null {
    const editors = this.pageEditors?.toArray() || [];
    const editor = editors[index]?.nativeElement;
    if (!editor) {
      return null;
    }
    const units = this.collectPageUnits(editor);
    const pos = units.indexOf(block);
    if (pos === -1) {
      return null;
    }

    if (direction === "previous") {
      if (pos > 0) {
        return { el: units[pos - 1], pageIndex: index };
      }
      for (let p = index - 1; p >= 0; p -= 1) {
        const earlier = this.collectPageUnits(editors[p].nativeElement);
        if (earlier.length) {
          return { el: earlier[earlier.length - 1], pageIndex: p };
        }
      }
      return null;
    }

    if (pos < units.length - 1) {
      return { el: units[pos + 1], pageIndex: index };
    }
    for (let p = index + 1; p < editors.length; p += 1) {
      const later = this.collectPageUnits(editors[p].nativeElement);
      if (later.length) {
        return { el: later[0], pageIndex: p };
      }
    }
    return null;
  }

  /**
   * Arm the visual the caret has just backed into: select it so the user can see
   * what the next Delete/Backspace will remove, scrolling to it — with the same
   * flash a dropped visual gets — when it lives on a page other than the one
   * being read.
   */
  private selectAdjacentVisual(unit: HTMLElement, pageIndex: number): void {
    const wrapper =
      (unit.closest(".resizable-image") as HTMLElement | null) ||
      (unit.querySelector(".resizable-image") as HTMLElement | null) ||
      unit;
    this.selectResizableWrapper(wrapper);
    if (pageIndex !== this.currentPageIndex) {
      this.revealMovedVisual(wrapper);
      return;
    }
    setTimeout(() => this.updateFloatingImageToolbar(), 0);
  }

  /**
   * Whether Delete/Backspace should remove the selected visual instead of acting
   * on text.
   *
   * The caret test is the load-bearing one. A report table is an EDITABLE island
   * inside its own (selected) wrapper, so a wrapper is selected while the user is
   * legitimately typing in a cell — without this, Backspace in a table cell would
   * delete the whole table. A caret inside the wrapper therefore keeps the key; a
   * caret anywhere else does not, which is what lets the user click a chart on one
   * page and press Delete while the caret still sits on another.
   */
  private shouldDeleteSelectedVisual(event: KeyboardEvent): boolean {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper || !wrapper.isConnected) {
      return false;
    }
    if (
      this.entryDialogOpen ||
      this.exportDialogOpen ||
      this.chartEditorOpen ||
      this.tableEditorOpen ||
      this.dialogType !== "none" ||
      this.exportInProgress
    ) {
      return false;
    }
    const target = event.target as Element | null;
    if (target && target.closest("input,textarea,select")) {
      return false; // a form field owns its own backspace
    }
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const anchor = selection.anchorNode;
      const el =
        anchor && anchor.nodeType === Node.ELEMENT_NODE
          ? (anchor as Element)
          : anchor?.parentElement || null;
      if (el && wrapper.contains(el)) {
        return false; // caret is inside the visual (a table cell) → text wins
      }
    }
    return true;
  }

  /**
   * Remove the selected visual from the keyboard, repaginating from the page it
   * was on rather than the caret's page — the two differ whenever the visual was
   * reached across a page boundary, and the gap that needs closing is the one it
   * leaves behind.
   */
  private deleteSelectedVisualFromKeyboard(): void {
    const wrapper = this.selectedResizableWrapper;
    const page = wrapper ? this.pageIndexOf(wrapper) : -1;
    if (page >= 0) {
      this.activePageIndex = page;
    }
    this.deleteSelectedVisual();
  }

  /** Nearest editable block element that contains the caret node. */
  private getCaretBlock(node: Node | null, editor: HTMLElement): HTMLElement | null {
    const el =
      node && node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node?.parentElement || null;
    const block = el?.closest(
      "p,li,h1,h2,h3,h4,h5,h6,blockquote,div"
    ) as HTMLElement | null;
    if (!block || !editor.contains(block) || block === editor) {
      return null;
    }
    // Ignore structural containers (sections, imported wrappers, chart holders).
    if (
      block.querySelector(
        "p,li,table,.report-chart,.report-table-block,.resizable-image"
      )
    ) {
      return null;
    }
    return block;
  }

  /** A block with no visible text and no media counts as an empty spacer. */
  private isBlockEmpty(el: HTMLElement): boolean {
    if (
      el.querySelector(
        "img,table,hr,canvas,svg,.report-chart,.report-table-block,.resizable-image"
      )
    ) {
      return false;
    }
    const text = String(el.textContent || "")
      .replace(/ /g, " ")
      .replace(/[​-‍﻿]/g, "")
      .trim();
    return text.length === 0;
  }

  /** A heading block (h1–h6), whose type a join must not dissolve. */
  private isHeadingBlock(el: HTMLElement | null | undefined): boolean {
    return !!el && /^H[1-6]$/.test(el.tagName);
  }

  /**
   * True when joining these two blocks would silently restyle one of them: a
   * heading merged into a paragraph loses its title formatting and becomes body
   * text, and a paragraph merged into a heading is promoted to a title. Either
   * way a structure the user set up disappears on one keypress with nothing on
   * screen to explain it, so joins across a type boundary are refused. Two blocks
   * of the same type join normally, as always.
   */
  private joinWouldRestyle(block: HTMLElement, other: HTMLElement): boolean {
    const blockIsHeading = this.isHeadingBlock(block);
    const otherIsHeading = this.isHeadingBlock(other);
    if (blockIsHeading !== otherIsHeading) {
      return true; // heading ↔ paragraph
    }
    // Two headings of different levels: the text would take on the other level.
    return blockIsHeading && block.tagName !== other.tagName;
  }

  /** True when nothing editable can merge into this block (chart/table/etc.). */
  private isNonEditableBlock(el: HTMLElement): boolean {
    return (
      el.getAttribute("contenteditable") === "false" ||
      !!el.querySelector(
        ".report-chart, .report-table-block, .resizable-image, img, table"
      )
    );
  }

  private isCaretAtBlockStart(range: Range, block: HTMLElement): boolean {
    const pre = range.cloneRange();
    pre.selectNodeContents(block);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().replace(/ /g, " ").length === 0;
  }

  private isCaretAtBlockEnd(range: Range, block: HTMLElement): boolean {
    const post = range.cloneRange();
    post.selectNodeContents(block);
    post.setStart(range.endContainer, range.endOffset);
    return post.toString().replace(/ /g, " ").length === 0;
  }

  /** All leaf editable blocks of an editor, in document order. */
  private collectEditableBlocks(editor: HTMLElement): HTMLElement[] {
    return (
      Array.from(
        editor.querySelectorAll("p,li,h1,h2,h3,h4,h5,h6,blockquote,div")
      ) as HTMLElement[]
    ).filter(
      (el) =>
        !el.querySelector(
          "p,li,div,table,.report-chart,.report-table-block,.resizable-image"
        ) && !this.isNonEditableBlock(el)
    );
  }

  /**
   * Join a block at the top of a page into the last block of the previous page
   * (cross-page Backspace merge that the browser can't do across separate
   * contenteditables), then re-paginate and restore the caret at the join.
   */
  private mergeBlockIntoPrevious(
    block: HTMLElement,
    prevBlock: HTMLElement,
    index: number
  ): void {
    // The first node we move marks the join point for the caret.
    const joinAnchor = block.firstChild as ChildNode | null;
    while (block.firstChild) {
      prevBlock.appendChild(block.firstChild);
    }
    block.remove();
    this.syncPagesFromDom();
    this.reflowFrom(Math.max(0, index - 1));
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel) {
        return;
      }
      const r = document.createRange();
      if (joinAnchor && joinAnchor.isConnected) {
        r.setStartBefore(joinAnchor);
      } else {
        r.selectNodeContents(prevBlock);
        r.collapse(false);
      }
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      // The join point is on the PREVIOUS page — scroll back to it.
      this.scrollCaretIntoView();
    }, 0);
  }

  /** Previous/next editable block in document order, crossing page boundaries. */
  private findAdjacentBlock(
    block: HTMLElement,
    index: number,
    direction: "previous" | "next"
  ): HTMLElement | null {
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return null;
    }
    const blocks = this.collectEditableBlocks(editor);
    const pos = blocks.indexOf(block);
    if (pos === -1) {
      return null;
    }
    if (direction === "previous") {
      if (pos > 0) {
        return blocks[pos - 1];
      }
      const prevEditor =
        this.pageEditors?.toArray()?.[index - 1]?.nativeElement;
      if (prevEditor) {
        const prevBlocks = this.collectEditableBlocks(prevEditor);
        return prevBlocks.length ? prevBlocks[prevBlocks.length - 1] : null;
      }
      return null;
    }
    if (pos < blocks.length - 1) {
      return blocks[pos + 1];
    }
    const nextEditor = this.pageEditors?.toArray()?.[index + 1]?.nativeElement;
    if (nextEditor) {
      const nextBlocks = this.collectEditableBlocks(nextEditor);
      return nextBlocks.length ? nextBlocks[0] : null;
    }
    return null;
  }

  /** Remove an empty spacer block (and its now-empty report section), reflow. */
  private deleteSpacerBlock(block: HTMLElement, index: number): void {
    const parentSection = block.closest(
      "section.report-block"
    ) as HTMLElement | null;
    block.remove();
    if (
      parentSection &&
      !parentSection.querySelector(
        "img,table,.report-chart,.report-table-block,.resizable-image"
      ) &&
      !String(parentSection.textContent || "").trim()
    ) {
      parentSection.remove();
    }
    this.activePageIndex = index;
    this.syncPagesFromDom();
    // From the edited page, not the top of the document. Deleting a blank line
    // cannot make an EARLIER page overflow, and the backward pass sweeps from the
    // start regardless — reflowing everything from page 1 on every Backspace was
    // pure cost, and on a long report a visible stall.
    this.reflowFrom(Math.max(0, index - 1));
    this.updateDocumentStats();
  }

  // ----------------------------------------------------------------------------
  // Drag-to-move for charts / tables / images.
  //
  // Native HTML5 drag-and-drop cannot be trusted here: every page is its own
  // contenteditable (so a native drop COPIES the node into the target page and
  // leaves a duplicate behind), the units are `<span contenteditable="false">`
  // wrappers that browsers do not consistently treat as drag sources, and the
  // mousedown that selects/measures a wrapper cancels Chrome's pending drag
  // before it ever starts. So dragging runs on plain mouse events instead: arm
  // on mousedown, start after a few pixels of travel, follow the cursor with a
  // ghost plus an insertion line, and move the real node on mouseup. The native
  // handlers below now exist only to neutralise a drag the browser starts anyway.
  // ----------------------------------------------------------------------------

  // Tags collectPageUnits treats as candidate leaf text blocks. Mirrors the
  // selector collectEditableBlocks queries with.
  private static readonly LEAF_BLOCK_TAGS = new Set<string>([
    "P", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "DIV",
  ]);

  private static readonly VISUAL_DRAG_THRESHOLD_PX = 4;
  private static readonly VISUAL_DRAG_SCROLL_ZONE_PX = 56;
  private static readonly VISUAL_DRAG_SCROLL_STEP_PX = 16;
  // How far outside a page a release still counts as landing on that page.
  private static readonly VISUAL_DROP_MARGIN_PX = 48;

  private visualDrag: {
    unit: HTMLElement;
    originOpacity: string;
    // The page the unit started on, so repagination can begin there instead of
    // re-distributing the whole document from page 1.
    originPageIndex: number;
    startX: number;
    startY: number;
    grabOffsetX: number;
    grabOffsetY: number;
    pointerX: number;
    pointerY: number;
    started: boolean;
    ghost: HTMLElement | null;
    indicator: HTMLElement | null;
    target: VisualDropTarget | null;
    // Why the current pointer position cannot take the unit, if it cannot.
    rejection: string | null;
    scrollFrame: number | null;
  } | null = null;

  onEditorDragStart(event: DragEvent, index: number): void {
    const target = event.target instanceof Element ? event.target : null;
    this.nativeDragSourceIndex = null;
    this.nativeDragRange = null;

    if (
      target &&
      this.getDraggableUnit(target) &&
      !this.isTextEditingTarget(target)
    ) {
      // Charts / tables / images belong to the pointer drag. A native drag
      // running alongside it is what copied them into the target page. Text
      // inside an editable island is excluded: the pointer drag does not arm
      // there, so blocking here too would leave that text unmovable.
      event.preventDefault();
      return;
    }

    // A text drag. Keep the exact range: if it is dropped on another page the
    // browser would copy it, so the move has to be performed by hand.
    const selection = window.getSelection();
    this.nativeDragRange =
      selection && selection.rangeCount
        ? selection.getRangeAt(0).cloneRange()
        : null;
    this.nativeDragSourceIndex = index;
    this.activePageIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onEditorDragOver(event: DragEvent, index: number): void {
    if (this.nativeDragSourceIndex === index) {
      return; // same page: the browser moves it correctly, leave it alone
    }
    // Claim the drop so the default insert (which copies) can never run.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onEditorDrop(event: DragEvent, index: number): void {
    const sourceIndex = this.nativeDragSourceIndex;
    const range = this.nativeDragRange;
    if (sourceIndex === index) {
      return; // a same-page native move: correct as it stands
    }

    // Cancel the browser's own drop. Across editing hosts it inserts a COPY and
    // leaves the original in place, which is the duplication being reported.
    event.preventDefault();
    event.stopPropagation();
    this.nativeDragSourceIndex = null;
    this.nativeDragRange = null;

    if (sourceIndex === null || !range) {
      this.showDropNotice(
        "Only content already in this document can be dropped here — use Insert or paste instead."
      );
      return;
    }
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      this.showDropNotice("That is not a place this content can go.");
      return;
    }
    this.moveRangeToPoint(
      range,
      editor,
      event.clientX,
      event.clientY,
      index,
      sourceIndex
    );
  }

  onEditorDragEnd(): void {
    this.nativeDragSourceIndex = null;
    this.nativeDragRange = null;
  }

  /**
   * Move dragged text to another page: extract it from the source range and
   * re-insert it at the drop caret. Extract-then-insert moves the very same
   * nodes, so the content can only ever exist once — unlike the native
   * cross-editor drop, which copies.
   */
  private moveRangeToPoint(
    range: Range,
    editor: HTMLElement,
    clientX: number,
    clientY: number,
    index: number,
    sourceIndex: number
  ): void {
    const sourceNode = range.startContainer;
    const sourceBlock =
      sourceNode.nodeType === Node.ELEMENT_NODE
        ? (sourceNode as HTMLElement)
        : sourceNode.parentElement;

    const dropRange = this.caretRangeFromPoint(clientX, clientY);
    if (!dropRange || !editor.contains(dropRange.startContainer)) {
      this.showDropNotice("Drop inside the page text to move it there.");
      return;
    }

    let fragment: DocumentFragment;
    try {
      fragment = range.extractContents();
    } catch {
      this.showDropNotice(
        "That selection could not be moved — use Cut and Paste instead."
      );
      return;
    }
    if (!fragment.childNodes.length) {
      return;
    }
    dropRange.insertNode(fragment);
    this.pruneEmptyBlock(sourceBlock, null);

    // Leave the caret where the text landed and mark it, so it is still there
    // (and visible) after pagination has moved blocks around.
    const selection = window.getSelection();
    if (selection) {
      dropRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(dropRange);
    }
    this.pendingCaretMarkerId = this.insertCaretMarker();

    this.activePageIndex = index;
    this.prepareResizableImages();
    this.syncPagesFromDom();
    // From the earliest page involved, not the whole document — see
    // commitVisualMove for why a full re-distribution is disorienting.
    this.reflowFrom(Math.max(0, Math.min(sourceIndex, index)));
    this.updateDocumentStats();
  }

  /** The caret position under the cursor, across the two browser spellings. */
  private caretRangeFromPoint(clientX: number, clientY: number): Range | null {
    const doc = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (
        x: number,
        y: number
      ) => { offsetNode: Node; offset: number } | null;
    };
    if (typeof doc.caretRangeFromPoint === "function") {
      return doc.caretRangeFromPoint(clientX, clientY);
    }
    const position = doc.caretPositionFromPoint
      ? doc.caretPositionFromPoint(clientX, clientY)
      : null;
    if (!position) {
      return null;
    }
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }

  /** Flash a short explanation of a drop that was refused. */
  private showDropNotice(message: string): void {
    this.dropNotice = message;
    if (this.dropNoticeTimer) {
      clearTimeout(this.dropNoticeTimer);
    }
    this.dropNoticeTimer = setTimeout(() => {
      this.dropNoticeTimer = null;
      this.dropNotice = "";
    }, 4500);
  }

  dismissDropNotice(): void {
    if (this.dropNoticeTimer) {
      clearTimeout(this.dropNoticeTimer);
      this.dropNoticeTimer = null;
    }
    this.dropNotice = "";
  }

  /**
   * The element that should move as one unit when dragged: a whole chart/table
   * report section, otherwise the outermost resizable image wrapper. (Inline
   * images inside a text report move on their own, not the whole text block.)
   */
  private getDraggableUnit(el: Element): HTMLElement | null {
    const chartOrTable = el.closest(
      ".report-chart, .report-chart-image, .report-table-block"
    ) as HTMLElement | null;
    if (chartOrTable) {
      const section = chartOrTable.closest(
        "section.report-block"
      ) as HTMLElement | null;
      if (section) {
        return section;
      }
    }
    let wrapper = el.closest(".resizable-image") as HTMLElement | null;
    while (wrapper) {
      const parent = wrapper.parentElement?.closest(
        ".resizable-image"
      ) as HTMLElement | null;
      if (!parent) {
        break;
      }
      wrapper = parent;
    }
    return wrapper;
  }

  /**
   * True when the pointer went down inside an editable text island (a report
   * table's editable content, say). There the gesture belongs to text selection,
   * so body-dragging is not armed — the grip handle still moves the block.
   */
  private isTextEditingTarget(target: Element): boolean {
    const host = target.closest('[contenteditable="true"]') as HTMLElement | null;
    return !!host && !host.classList.contains("page-editor");
  }

  /**
   * Arm a move-drag on `unit`. Nothing visible happens until the pointer travels
   * VISUAL_DRAG_THRESHOLD_PX, so a plain click still just selects the visual.
   * `immediate` (the grip handle) skips that threshold.
   */
  private armVisualDrag(
    unit: HTMLElement | null,
    event: MouseEvent,
    immediate = false
  ): void {
    if (!unit || !unit.isConnected || event.button !== 0) {
      return;
    }
    // Never leave two drags armed, whatever order the mousedown handlers ran in.
    this.finishVisualDrag(false);

    const rect = unit.getBoundingClientRect();
    const scale = rect.width > 0 ? Math.min(1, 280 / rect.width) : 1;
    this.visualDrag = {
      unit,
      originOpacity: unit.style.opacity || "",
      originPageIndex: this.pageIndexOf(unit),
      startX: event.clientX,
      startY: event.clientY,
      grabOffsetX: (event.clientX - rect.left) * scale,
      grabOffsetY: (event.clientY - rect.top) * scale,
      pointerX: event.clientX,
      pointerY: event.clientY,
      started: false,
      ghost: null,
      indicator: null,
      target: null,
      rejection: null,
      scrollFrame: null,
    };

    // The unit is a contenteditable="false" island, so the default action here
    // is a text selection that would fight the drag the whole way. Take it.
    event.preventDefault();

    // Capture phase: nothing in the editor can swallow the gesture halfway.
    document.addEventListener("mousemove", this.onVisualDragMove, true);
    document.addEventListener("mouseup", this.onVisualDragUp, true);
    document.addEventListener("keydown", this.onVisualDragKey, true);
    window.addEventListener("blur", this.onVisualDragAbort);

    if (immediate) {
      this.beginVisualDrag();
      this.updateVisualDrag(event.clientX, event.clientY);
    }
  }

  private onVisualDragMove = (event: MouseEvent): void => {
    const drag = this.visualDrag;
    if (!drag) {
      return;
    }
    drag.pointerX = event.clientX;
    drag.pointerY = event.clientY;

    // The button was released somewhere we never saw the mouseup (outside the
    // window, over a plugin surface…). Settle the drag rather than getting stuck.
    if (drag.started && event.buttons === 0) {
      this.finishVisualDrag(!!drag.target);
      return;
    }

    if (!drag.started) {
      const travelled = Math.max(
        Math.abs(event.clientX - drag.startX),
        Math.abs(event.clientY - drag.startY)
      );
      if (travelled < DownloadpdfComponent.VISUAL_DRAG_THRESHOLD_PX) {
        return;
      }
      this.beginVisualDrag();
    }

    // Stop the browser turning the gesture into a text selection or native drag.
    event.preventDefault();
    this.updateVisualDrag(event.clientX, event.clientY);
  };

  private onVisualDragUp = (event: MouseEvent): void => {
    const drag = this.visualDrag;
    if (!drag) {
      return;
    }
    if (drag.started) {
      // Swallow this mouseup and the click it spawns, so the page/wrapper click
      // handlers do not re-select and re-paginate on top of the drop.
      event.preventDefault();
      event.stopPropagation();
      const swallowClick = (click: MouseEvent) => {
        click.preventDefault();
        click.stopPropagation();
      };
      document.addEventListener("click", swallowClick, {
        capture: true,
        once: true,
      });
      // If no click follows (the pointer left the source element), drop the trap
      // so it cannot eat an unrelated click later.
      setTimeout(
        () => document.removeEventListener("click", swallowClick, true),
        0
      );
    }
    this.finishVisualDrag(true);
  };

  private onVisualDragKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.visualDrag) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.finishVisualDrag(false);
  };

  private onVisualDragAbort = (): void => {
    this.finishVisualDrag(false);
  };

  private beginVisualDrag(): void {
    const drag = this.visualDrag;
    if (!drag || drag.started) {
      return;
    }
    drag.started = true;

    // A live caret/selection inside the page fights the drag; drop it.
    const selection = window.getSelection();
    if (selection && selection.rangeCount) {
      selection.removeAllRanges();
    }
    // The floating toolbar would sit right over the drop zone.
    this.imageToolbarVisible = false;
    this.imageToolbarExpanded = false;

    drag.ghost = this.createDragGhost(drag.unit);
    drag.indicator = this.createDropIndicator();
    drag.unit.style.opacity = "0.35";
    document.body.style.cursor = "grabbing";
    drag.scrollFrame = requestAnimationFrame(this.visualDragScrollStep);
  }

  /**
   * A scaled clone that follows the cursor. Styled inline rather than by class:
   * page content is injected as raw innerHTML, so it sits outside Angular's
   * per-component style scoping and a class alone is not guaranteed to bite.
   */
  private createDragGhost(unit: HTMLElement): HTMLElement {
    const rect = unit.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const scale = Math.min(1, 280 / width);
    const ghost = unit.cloneNode(true) as HTMLElement;
    ghost
      .querySelectorAll(".image-handle, [data-caret-marker]")
      .forEach((node) => node.remove());
    ghost.classList.remove("is-selected");
    ghost.removeAttribute("id");
    ghost.style.cssText +=
      ";position:fixed;left:0;top:0;margin:0;z-index:1400;pointer-events:none;" +
      `width:${Math.round(width)}px;max-width:none;max-height:240px;overflow:hidden;` +
      `opacity:0.9;transform-origin:top left;transform:scale(${scale});` +
      "background:#ffffff;border:1px solid #93c5fd;border-radius:8px;" +
      "box-shadow:0 18px 38px rgba(15,23,42,0.32);";
    this.getDragLayer().appendChild(ghost);
    return ghost;
  }

  private createDropIndicator(): HTMLElement {
    const line = document.createElement("div");
    line.className = "visual-drop-indicator";
    line.style.cssText =
      "position:fixed;left:0;top:0;width:0;height:3px;z-index:1401;" +
      "pointer-events:none;display:none;background:#2563eb;border-radius:3px;" +
      "box-shadow:0 0 0 1px rgba(37,99,235,0.2), 0 2px 6px rgba(37,99,235,0.35);";

    const cap = document.createElement("span");
    cap.style.cssText =
      "position:absolute;left:-4px;top:-3px;width:9px;height:9px;" +
      "border-radius:50%;background:#2563eb;" +
      "box-shadow:0 0 0 2px rgba(255,255,255,0.9);";
    line.appendChild(cap);

    this.getDragLayer().appendChild(line);
    return line;
  }

  /**
   * Ghost and indicator live in the workspace (never inside a page editor), so
   * syncPagesFromDom() can never capture them into the saved document.
   */
  private getDragLayer(): HTMLElement {
    return this.pagesWorkspaceRef?.nativeElement || document.body;
  }

  private updateVisualDrag(clientX: number, clientY: number): void {
    const drag = this.visualDrag;
    if (!drag || !drag.started) {
      return;
    }
    if (drag.ghost) {
      drag.ghost.style.left = `${Math.round(clientX - drag.grabOffsetX)}px`;
      drag.ghost.style.top = `${Math.round(clientY - drag.grabOffsetY)}px`;
    }
    this.refreshVisualDropTarget();
  }

  /** Recompute where the unit would land from the last known pointer position. */
  private refreshVisualDropTarget(): void {
    const drag = this.visualDrag;
    if (!drag || !drag.started) {
      return;
    }
    const editor = this.editorAtPoint(drag.pointerX, drag.pointerY);
    const rejection = this.describeDropRejection(editor, drag.unit);
    const target = editor
      ? this.computeDropTarget(editor, drag.pointerX, drag.pointerY, drag.unit)
      : null;

    if (rejection || !target) {
      drag.target = null;
      drag.rejection = rejection || "Drop inside a page to move it there.";
      if (drag.indicator) {
        drag.indicator.style.display = "none";
      }
      document.body.style.cursor = "not-allowed";
      return;
    }

    drag.target = target;
    drag.rejection = null;
    document.body.style.cursor = "grabbing";
    this.positionDropIndicator(target);
  }

  /**
   * Why the unit cannot be dropped where the cursor currently is, or null when
   * it can. The message is what the user is shown if they release there.
   */
  private describeDropRejection(
    editor: HTMLElement | null,
    unit: HTMLElement
  ): string | null {
    if (!editor) {
      return "Drop inside a page to move it there.";
    }
    const page = editor.closest(".doc-page") as HTMLElement | null;
    if (page && page.classList.contains("cover-page")) {
      return "The cover page is a fixed layout and cannot take other content.";
    }
    return null;
  }

  /** Scroll the workspace while the cursor is held near its top/bottom edge. */
  private visualDragScrollStep = (): void => {
    const drag = this.visualDrag;
    if (!drag || !drag.started) {
      return;
    }
    const workspace = this.pagesWorkspaceRef?.nativeElement;
    if (workspace) {
      const rect = workspace.getBoundingClientRect();
      const zone = DownloadpdfComponent.VISUAL_DRAG_SCROLL_ZONE_PX;
      const step = DownloadpdfComponent.VISUAL_DRAG_SCROLL_STEP_PX;
      let delta = 0;
      if (drag.pointerY < rect.top + zone) {
        delta = -step;
      } else if (drag.pointerY > rect.bottom - zone) {
        delta = step;
      }
      if (delta) {
        const before = workspace.scrollTop;
        workspace.scrollTop = before + delta;
        if (workspace.scrollTop !== before) {
          // The pages moved under a stationary cursor — re-aim the indicator.
          this.refreshVisualDropTarget();
        }
      }
    }
    drag.scrollFrame = requestAnimationFrame(this.visualDragScrollStep);
  };

  /**
   * The page editor under the cursor. Falls back to the vertically nearest page
   * so a release in the gap between pages (or in a page's header/footer) still
   * lands somewhere sensible instead of being thrown away.
   */
  private editorAtPoint(clientX: number, clientY: number): HTMLElement | null {
    const editors = (this.pageEditors?.toArray() || []).map(
      (ref) => ref.nativeElement as HTMLElement
    );
    if (!editors.length) {
      return null;
    }
    let nearest: HTMLElement | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < editors.length; i += 1) {
      const editor = editors[i];
      const box = (editor.closest(".doc-page") as HTMLElement | null) || editor;
      const rect = box.getBoundingClientRect();
      if (
        clientY >= rect.top &&
        clientY <= rect.bottom &&
        clientX >= rect.left &&
        clientX <= rect.right
      ) {
        return editor;
      }
      // Only the gutter immediately around a page counts as that page; further
      // out there is no sensible target and the drop is refused instead.
      if (
        clientX < rect.left - DownloadpdfComponent.VISUAL_DROP_MARGIN_PX ||
        clientX > rect.right + DownloadpdfComponent.VISUAL_DROP_MARGIN_PX
      ) {
        continue;
      }
      const distance =
        clientY < rect.top ? rect.top - clientY : Math.max(0, clientY - rect.bottom);
      if (
        distance <= DownloadpdfComponent.VISUAL_DROP_MARGIN_PX &&
        distance < nearestDistance
      ) {
        nearestDistance = distance;
        nearest = editor;
      }
    }
    return nearest;
  }

  /**
   * Where the unit lands. A position between two LINES of text is preferred, so
   * a visual can be dropped anywhere in a paragraph and the text closes around
   * it; only when no line can be resolved (empty area, a table, the gap between
   * blocks) does it fall back to sitting between whole blocks.
   */
  private computeDropTarget(
    editor: HTMLElement,
    clientX: number,
    clientY: number,
    unit: HTMLElement
  ): VisualDropTarget | null {
    return (
      this.computeLineDropTarget(editor, clientX, clientY, unit) ||
      this.computeBlockDropTarget(editor, clientX, clientY, unit)
    );
  }

  /**
   * A drop position between two lines of a text block. The caret under the
   * cursor is re-probed at the line's leading or trailing edge, so the block is
   * cut at a line boundary rather than mid-word.
   */
  private computeLineDropTarget(
    editor: HTMLElement,
    clientX: number,
    clientY: number,
    unit: HTMLElement
  ): VisualDropTarget | null {
    const caret = this.caretRangeFromPoint(clientX, clientY);
    if (!caret || !editor.contains(caret.startContainer)) {
      return null;
    }
    if (unit.contains(caret.startContainer)) {
      return null; // the cursor is over the thing being dragged
    }

    const block = this.nearestTextBlock(caret.startContainer, editor);
    if (
      !block ||
      block === unit ||
      unit.contains(block) ||
      block.contains(unit) ||
      this.isAtomicBlock(block)
    ) {
      return null; // charts, tables and images move whole and are never cut
    }
    const container = block.parentElement;
    if (!container) {
      return null;
    }

    // A collapsed range reports the line box it sits on, which is the gap the
    // insertion line has to be drawn in.
    const lineRect = caret.getBoundingClientRect();
    if (!lineRect.height) {
      return null;
    }
    const beforeLine = clientY < (lineRect.top + lineRect.bottom) / 2;
    const midY = (lineRect.top + lineRect.bottom) / 2;

    const style = window.getComputedStyle(block);
    const blockRect = block.getBoundingClientRect();
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const left = blockRect.left + padLeft;
    const width = Math.max(24, blockRect.width - padLeft - padRight);

    // Probing at the line edge snaps to the first/last caret position on that
    // line, so the split lands between lines instead of inside a word.
    const snapped = this.caretRangeFromPoint(
      beforeLine ? left + 1 : left + width - 1,
      midY
    );
    const splitCaret =
      snapped && block.contains(snapped.startContainer) ? snapped : caret;

    return {
      mode: "split",
      container,
      before: null,
      block,
      caret: splitCaret,
      top: beforeLine ? lineRect.top : lineRect.bottom,
      left,
      width,
    };
  }

  /**
   * The fallback: between two whole blocks. Reordering stays inside the unit's
   * own container while the cursor is still over it, so a chart shuffled within
   * a report section is not hoisted out of it.
   */
  private computeBlockDropTarget(
    editor: HTMLElement,
    clientX: number,
    clientY: number,
    unit: HTMLElement
  ): VisualDropTarget | null {
    let container: HTMLElement = editor;
    const originParent = unit.parentElement;
    if (originParent && originParent !== editor && editor.contains(originParent)) {
      const rect = originParent.getBoundingClientRect();
      if (
        clientY >= rect.top &&
        clientY <= rect.bottom &&
        clientX >= rect.left &&
        clientX <= rect.right
      ) {
        container = originParent;
      }
    }

    const siblings = this.dropSiblings(container, unit);
    let before: HTMLElement | null = null;
    for (let i = 0; i < siblings.length; i += 1) {
      const rect = siblings[i].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) {
        before = siblings[i];
        break;
      }
    }

    const rect = container.getBoundingClientRect();
    const style = window.getComputedStyle(container);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const padTop = parseFloat(style.paddingTop) || 0;
    const last = siblings.length ? siblings[siblings.length - 1] : null;
    const top = before
      ? before.getBoundingClientRect().top
      : last
      ? last.getBoundingClientRect().bottom
      : rect.top + padTop;

    return {
      mode: "block",
      container,
      before,
      block: null,
      caret: null,
      top,
      left: rect.left + padLeft,
      width: Math.max(24, rect.width - padLeft - padRight),
    };
  }

  /** The block-level element a caret sits in, stopping at the page editor. */
  private nearestTextBlock(node: Node, editor: HTMLElement): HTMLElement | null {
    let el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node.parentElement;
    while (el && el !== editor) {
      const display = window.getComputedStyle(el).display;
      if (display !== "inline" && display !== "contents") {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  /** Blocks that move as a whole and must never be cut in half. */
  private isAtomicBlock(block: HTMLElement): boolean {
    return (
      !!block.closest(".resizable-image") ||
      block.matches(
        "table, thead, tbody, tr, td, th, section.report-block, .report-chart, .report-table-block"
      ) ||
      !!block.querySelector("table, .report-chart, .report-table-block")
    );
  }

  /** Children of `container` the unit can be positioned against. */
  private dropSiblings(container: HTMLElement, unit: HTMLElement): HTMLElement[] {
    return (Array.from(container.children) as HTMLElement[]).filter((child) => {
      if (child === unit || unit.contains(child) || child.contains(unit)) {
        return false;
      }
      if (
        child.classList.contains("image-handle") ||
        child.classList.contains("visual-drop-indicator") ||
        child.hasAttribute("data-caret-marker")
      ) {
        return false;
      }
      const rect = child.getBoundingClientRect();
      return rect.height > 0 || rect.width > 0;
    });
  }

  private positionDropIndicator(target: VisualDropTarget): void {
    const drag = this.visualDrag;
    const line = drag?.indicator;
    if (!drag || !line) {
      return;
    }
    // Clamp into the page so a line at the very top/bottom stays on screen.
    const page =
      (target.container.closest(".page-editor") as HTMLElement | null) ||
      target.container;
    const bounds = page.getBoundingClientRect();

    line.style.display = "block";
    line.style.left = `${Math.round(target.left)}px`;
    line.style.width = `${Math.round(target.width)}px`;
    line.style.top = `${Math.round(
      Math.min(Math.max(target.top, bounds.top + 1), bounds.bottom - 4)
    )}px`;
  }

  /**
   * Tear the drag down and, when `commit` is set and a target was resolved, move
   * the node. Always safe to call — it is the single exit for every path
   * (mouseup, Escape, window blur, a new drag arming, component destroy).
   */
  private finishVisualDrag(commit: boolean): void {
    const drag = this.visualDrag;
    if (!drag) {
      return;
    }
    this.visualDrag = null;

    document.removeEventListener("mousemove", this.onVisualDragMove, true);
    document.removeEventListener("mouseup", this.onVisualDragUp, true);
    document.removeEventListener("keydown", this.onVisualDragKey, true);
    window.removeEventListener("blur", this.onVisualDragAbort);
    if (drag.scrollFrame !== null) {
      cancelAnimationFrame(drag.scrollFrame);
    }
    if (drag.ghost) {
      drag.ghost.remove();
    }
    if (drag.indicator) {
      drag.indicator.remove();
    }
    document.body.style.cursor = "";
    drag.unit.style.opacity = drag.originOpacity;

    if (!drag.started) {
      return; // a plain click: selection already handled it
    }
    // The node never left its place during the drag, so a cancel needs no undo.
    if (!commit || !drag.target) {
      if (commit) {
        this.showDropNotice(
          drag.rejection || "Drop inside a page to move it there."
        );
      }
      this.updateFloatingImageToolbar();
      return;
    }
    if (this.applyDropTarget(drag.unit, drag.target)) {
      this.commitVisualMove(drag.unit, drag.originPageIndex);
    } else {
      this.updateFloatingImageToolbar();
    }
  }

  private applyDropTarget(unit: HTMLElement, target: VisualDropTarget): boolean {
    if (target.mode === "split" && target.block && target.caret) {
      return this.splitBlockAndInsert(unit, target.block, target.caret);
    }
    return this.moveUnitInto(unit, target.container, target.before);
  }

  /**
   * Whether nothing but whitespace separates the caret from the start or the end
   * of the block — in which case there is nothing to split off.
   */
  private caretAtBlockEdge(
    caret: Range,
    block: HTMLElement
  ): "start" | "end" | null {
    const media = "img,table,.report-chart,.report-table-block,.resizable-image";
    const head = document.createRange();
    head.setStart(block, 0);
    head.setEnd(caret.startContainer, caret.startOffset);
    if (!head.toString().trim() && !head.cloneContents().querySelector(media)) {
      return "start";
    }
    const tail = document.createRange();
    tail.setStart(caret.startContainer, caret.startOffset);
    tail.setEnd(block, block.childNodes.length);
    if (!tail.toString().trim() && !tail.cloneContents().querySelector(media)) {
      return "end";
    }
    return null;
  }

  /**
   * Cut `block` in two at `caret` and seat the unit between the halves, so a
   * visual can land between any two lines and the surrounding text closes back
   * around it. Whichever half the cut leaves empty (a drop at the very start or
   * end of the block) is dropped, so no blank paragraph is left behind.
   */
  private splitBlockAndInsert(
    unit: HTMLElement,
    block: HTMLElement,
    caret: Range
  ): boolean {
    const container = block.parentElement;
    if (
      !container ||
      !block.isConnected ||
      block === unit ||
      unit.contains(block) ||
      unit.contains(caret.startContainer)
    ) {
      return false;
    }

    const edge = this.caretAtBlockEdge(caret, block);
    if (edge === "start") {
      return this.moveUnitInto(unit, container, block);
    }
    if (edge === "end") {
      return this.moveUnitInto(
        unit,
        container,
        block.nextElementSibling as HTMLElement | null
      );
    }

    const previousParent = unit.parentElement;
    // The unit may be sitting inside the very block being split; take it out
    // first so the tail extraction cannot carry it away.
    if (block.contains(unit)) {
      unit.remove();
    }

    let tailFragment: DocumentFragment;
    try {
      const tail = document.createRange();
      tail.setStart(caret.startContainer, caret.startOffset);
      tail.setEnd(block, block.childNodes.length);
      tailFragment = tail.extractContents();
    } catch {
      // An unsplittable position: fall back to dropping above the block.
      return this.moveUnitInto(unit, container, block);
    }

    const tailBlock = block.cloneNode(false) as HTMLElement;
    tailBlock.removeAttribute("id");
    tailBlock.appendChild(tailFragment);

    container.insertBefore(unit, block.nextSibling);
    container.insertBefore(tailBlock, unit.nextSibling);

    this.pruneEmptyBlock(block, null);
    this.pruneEmptyBlock(tailBlock, null);
    if (previousParent !== container) {
      this.pruneEmptyBlock(previousParent, container);
    }
    return true;
  }

  /**
   * Relocate the real node (never a clone, so nothing is duplicated) and clean
   * up a block left empty behind it. Returns false when the move is a no-op or
   * would nest the unit inside itself.
   */
  private moveUnitInto(
    unit: HTMLElement,
    container: HTMLElement,
    before: HTMLElement | null
  ): boolean {
    if (!unit.isConnected || !container.isConnected) {
      return false;
    }
    if (unit === container || unit.contains(container)) {
      return false;
    }
    if (before && (before === unit || unit.contains(before))) {
      return false;
    }
    if (
      unit.parentElement === container &&
      (before
        ? unit.nextElementSibling === before
        : unit === container.lastElementChild)
    ) {
      return false; // already sits exactly there
    }

    const previousParent = unit.parentElement;
    if (before) {
      container.insertBefore(unit, before);
    } else {
      container.appendChild(unit);
    }
    this.pruneEmptyBlock(previousParent, container);
    return true;
  }

  /** Drop a wrapper the moved unit just emptied (a bare <p>, a report section). */
  private pruneEmptyBlock(
    block: HTMLElement | null,
    keep: HTMLElement | null
  ): void {
    if (
      !block ||
      block === keep ||
      !block.isConnected ||
      block.classList.contains("page-editor") ||
      block.classList.contains("resizable-image")
    ) {
      return;
    }
    const hasMedia = block.querySelector(
      "img,table,.report-chart,.report-table-block,.resizable-image"
    );
    if (hasMedia || String(block.textContent || "").trim()) {
      return;
    }
    block.remove();
  }

  /** Repaginate, persist and reselect after a unit has been moved. */
  private commitVisualMove(unit: HTMLElement, fromPageIndex = -1): void {
    const index = this.pageIndexOf(unit);
    if (index >= 0) {
      this.activePageIndex = index;
    }

    this.prepareResizableImages();
    this.syncPagesFromDom();
    // Repaginate from the earliest page the move touched, NOT from page 1.
    // A full re-distribution re-lays out settled pages above the drop as well,
    // which is what made the dropped item appear to jump somewhere else.
    const target = index >= 0 ? index : this.activePageIndex;
    const from =
      fromPageIndex >= 0 ? Math.min(fromPageIndex, target) : target;
    this.reflowFrom(Math.max(0, from));
    this.updateDocumentStats();

    // Keep the moved visual selected. If it does not fit on the page it was
    // dropped on, pagination still has to carry it forward — so mark it for a
    // reveal, and once reflow settles the view scrolls to wherever it landed
    // and flashes it, instead of leaving the user to hunt for it.
    const wrapper = unit.classList.contains("resizable-image")
      ? unit
      : (unit.querySelector(".resizable-image") as HTMLElement | null);
    if (wrapper) {
      this.selectResizableWrapper(wrapper);
    }
    this.pendingVisualReveal = unit;
    setTimeout(() => this.updateFloatingImageToolbar(), 0);
  }

  /** Index of the page a node currently sits on, or -1. */
  private pageIndexOf(node: Node | null): number {
    const el =
      node && node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node?.parentElement || null;
    const editor = el?.closest(".page-editor") as HTMLElement | null;
    if (!editor) {
      return -1;
    }
    const editors = this.pageEditors?.toArray() || [];
    return editors.findIndex((ref) => ref.nativeElement === editor);
  }

  /**
   * Show the user where a dropped item ended up: scroll to it only if it is not
   * already fully in view, then ring it briefly. The ring is an overlay in the
   * workspace, never a style on the content, so it cannot be captured into the
   * saved document.
   */
  private revealMovedVisual(unit: HTMLElement): void {
    if (!unit.isConnected) {
      return;
    }
    const index = this.pageIndexOf(unit);
    if (index >= 0) {
      this.activePageIndex = index;
      this.currentPageIndex = index;
    }

    const workspace = this.pagesWorkspaceRef?.nativeElement;
    const rect = unit.getBoundingClientRect();
    if (workspace) {
      const view = workspace.getBoundingClientRect();
      const fullyVisible = rect.top >= view.top && rect.bottom <= view.bottom;
      if (!fullyVisible) {
        try {
          unit.scrollIntoView({ block: "center", behavior: "auto" });
        } catch {
          unit.scrollIntoView();
        }
      }
    }

    const box = unit.getBoundingClientRect();
    if (!box.width && !box.height) {
      return;
    }
    const flash = document.createElement("div");
    flash.className = "visual-drop-flash";
    flash.style.cssText =
      "position:fixed;pointer-events:none;z-index:1399;border-radius:6px;" +
      "border:2px solid #2563eb;box-shadow:0 0 0 4px rgba(37,99,235,0.18);" +
      "opacity:1;transition:opacity 0.8s ease 0.6s;" +
      `left:${Math.round(box.left - 3)}px;top:${Math.round(box.top - 3)}px;` +
      `width:${Math.round(box.width + 6)}px;height:${Math.round(box.height + 6)}px;`;
    this.getDragLayer().appendChild(flash);
    requestAnimationFrame(() => {
      flash.style.opacity = "0";
    });
    setTimeout(() => flash.remove(), 1800);

    this.updateFloatingImageToolbar();
  }

  /**
   * Where the caret is on screen. A collapsed range reports no rect at all in
   * some positions (an empty block, a fresh line), so fall back through
   * getClientRects → getBoundingClientRect → the block that holds it.
   */
  private getCaretRect(): DOMRect | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    const rects = range.getClientRects();
    if (rects.length && (rects[0].height || rects[0].width)) {
      return rects[0];
    }
    const box = range.getBoundingClientRect();
    if (box.height || box.width) {
      return box;
    }

    const node = range.startContainer;
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    const block = el?.closest(
      "p,li,h1,h2,h3,h4,h5,h6,blockquote,div,td,th"
    ) as HTMLElement | null;
    return block ? block.getBoundingClientRect() : null;
  }

  /**
   * Keep the caret in view after the document has moved underneath it.
   *
   * Every page is a fixed-height sheet, so repagination shifts blocks between
   * pages and a Backspace can join content onto the page above — the caret then
   * routinely lands somewhere the viewport is not, and the screen sits still
   * while the user edits a position they cannot see. This scrolls by the smallest
   * amount that brings the caret back inside a comfortable margin.
   *
   * Two things it deliberately does NOT do. It never scrolls when the caret is
   * already comfortably visible, so ordinary typing cannot make the page jitter.
   * And it only follows a caret the user is actually working at — the document
   * must be past its initial render and a page editor must hold focus — so the
   * pagination that runs while a document is first laid out (which parks the
   * caret on the last page it fills) cannot drag the view off the opening page.
   */
  private scrollCaretIntoView(): void {
    if (!this.documentReady) {
      return;
    }
    const workspace = this.pagesWorkspaceRef?.nativeElement;
    if (!workspace) {
      return;
    }
    const active = document.activeElement as Element | null;
    if (!active || !active.closest(".page-editor")) {
      return; // focus is on the toolbar, a dialog, or nothing at all
    }
    // Only follow a caret sitting on the page the editor treats as active. The
    // two come apart after a visual is deleted from the keyboard: activePageIndex
    // is the page the chart was on, while the caret can still be a stale position
    // on a page the user scrolled away from long ago. Following that would yank
    // the view somewhere they were not looking — worse than not scrolling.
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const caretPage = this.pageIndexOf(selection.getRangeAt(0).startContainer);
    if (caretPage >= 0 && caretPage !== this.activePageIndex) {
      return;
    }
    const caret = this.getCaretRect();
    if (!caret) {
      return;
    }

    const view = workspace.getBoundingClientRect();
    // Leave a line or two of context around the caret, but never demand more
    // room than a short viewport can give.
    const margin = Math.min(80, Math.max(0, workspace.clientHeight / 4));
    let delta = 0;
    if (caret.top < view.top + margin) {
      delta = caret.top - (view.top + margin);
    } else if (caret.bottom > view.bottom - margin) {
      delta = caret.bottom - (view.bottom - margin);
    }
    if (!delta) {
      return;
    }
    workspace.scrollTop += delta;
    // The workspace's own scroll handler refreshes the "Page X of Y" footer.
  }

  /** Place the caret at the end of a block and focus its editor. */
  private placeCaretAtEndOfBlock(block: HTMLElement): void {
    if (!block || !block.isConnected) {
      return;
    }
    const editor = block.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const editorIndex = editors.findIndex(
      (ref) => ref.nativeElement === editor
    );
    if (editorIndex >= 0) {
      this.activePageIndex = editorIndex;
    }
    if (editor) {
      editor.focus({ preventScroll: true });
    }
    this.placeCaretAtEnd(block);
    // Backspace joins content onto the block above, which is often on the page
    // above — follow it rather than leaving the view behind.
    this.scrollCaretIntoView();
  }

  /**
   * Reduce arbitrary clipboard HTML to a safe, lightweight subset:
   *  - strip scripts/handlers/javascript: (via sanitizeImportedHtml),
   *  - drop Office/markup cruft (style, meta, o:p, comments, …),
   *  - unwrap disallowed tags but keep their text,
   *  - keep only a small whitelist of attributes and inline style properties.
   */
  private sanitizePastedHtml(html: string): string {
    const base = this.sanitizeImportedHtml(html);
    if (!base) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = base;

    // Remove elements that should never survive a paste.
    container
      .querySelectorAll("style, meta, link, title, head, script, xml, o\\:p")
      .forEach((el) => el.remove());

    // Remove comments (Word emits conditional-comment blocks).
    const commentWalker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_COMMENT
    );
    const comments: Comment[] = [];
    let comment = commentWalker.nextNode();
    while (comment) {
      comments.push(comment as Comment);
      comment = commentWalker.nextNode();
    }
    comments.forEach((node) => node.parentNode?.removeChild(node));

    const allowedTags = new Set<string>([
      "P", "BR", "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "DEL",
      "SUP", "SUB", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE",
      "UL", "OL", "LI", "A", "SPAN", "DIV", "TABLE", "THEAD", "TBODY",
      "TFOOT", "TR", "TD", "TH", "HR", "IMG", "FONT", "PRE", "CODE",
    ]);

    // Document order → parents are processed before children, so unwrapping a
    // disallowed parent still lets its (now lifted) children be cleaned later.
    const elements = Array.from(container.querySelectorAll("*"));
    for (let i = 0; i < elements.length; i += 1) {
      const el = elements[i] as HTMLElement;
      const tag = el.tagName.toUpperCase();
      if (!allowedTags.has(tag)) {
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        }
        continue;
      }
      this.cleanPastedElementAttributes(el);
    }

    this.relaxNonBreakingSpaces(container);
    return container.innerHTML;
  }

  /** Keep only safe attributes + a whitelist of inline style properties. */
  private cleanPastedElementAttributes(el: HTMLElement): void {
    const tag = el.tagName.toUpperCase();
    const keep = new Set<string>();
    if (tag === "A") {
      keep.add("href");
      keep.add("target");
      keep.add("rel");
    } else if (tag === "IMG") {
      keep.add("src");
      keep.add("alt");
    } else if (tag === "TD" || tag === "TH") {
      keep.add("colspan");
      keep.add("rowspan");
    }

    const attributes = Array.from(el.attributes);
    for (let i = 0; i < attributes.length; i += 1) {
      const name = attributes[i].name.toLowerCase();
      if (name === "style") {
        continue;
      }
      if (!keep.has(name)) {
        el.removeAttribute(attributes[i].name);
      }
    }

    const style = el.getAttribute("style");
    if (style) {
      const safe = this.filterInlineStyle(style);
      if (safe) {
        el.setAttribute("style", safe);
      } else {
        el.removeAttribute("style");
      }
    }

    if (tag === "A") {
      const href = el.getAttribute("href") || "";
      if (/^\s*javascript:/i.test(href)) {
        el.removeAttribute("href");
      }
    }
    if (tag === "IMG") {
      const src = el.getAttribute("src") || "";
      if (/^\s*javascript:/i.test(src)) {
        el.remove();
      }
    }
  }

  /** Allow only a small set of visual style properties; reject any url()/expression. */
  private filterInlineStyle(style: string): string {
    const allowedProps = new Set<string>([
      "color", "background-color", "font-weight", "font-style", "font-size",
      "text-align", "text-decoration", "line-height", "font-family",
    ]);

    return style
      .split(";")
      .map((decl) => decl.trim())
      .filter(Boolean)
      .filter((decl) => {
        const idx = decl.indexOf(":");
        if (idx < 0) {
          return false;
        }
        const prop = decl.slice(0, idx).trim().toLowerCase();
        const value = decl.slice(idx + 1).toLowerCase();
        if (!allowedProps.has(prop)) {
          return false;
        }
        if (
          value.includes("url(") ||
          value.includes("expression") ||
          value.includes("javascript:")
        ) {
          return false;
        }
        return true;
      })
      .join("; ");
  }

  onEditorClick(event: MouseEvent, index: number): void {
    this.activePageIndex = index;

    const rawTarget = event.target as EventTarget | null;
    const target =
      rawTarget instanceof Element
        ? rawTarget
        : (rawTarget as Node | null)?.parentElement || null;
    if (!target) {
      return;
    }
    // Show the structural-table toolbar when a cell is clicked; hide otherwise.
    this.updateTableToolbarForNode(target);

    const existingWrapper =
      this.resolveWrapperFromEvent(event) || this.resolveWrapperFromTarget(target);
    const clickedImageFromEvent = this.resolveImageFromEvent(event);
    const clickedImageFromTarget = this.resolveImageFromTarget(target);
    if (existingWrapper) {
      event.stopPropagation();
      this.selectResizableWrapper(existingWrapper);
      return;
    }

    const clickedImage = clickedImageFromEvent || clickedImageFromTarget;

    if (!clickedImage) {
      return;
    }

    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return;
    }

    this.enableResizableImagesInEditor(editor);
    const wrapped =
      this.resolveWrapperFromEvent(event) ||
      this.resolveWrapperFromTarget(target) ||
      (clickedImage.closest(".resizable-image") as HTMLElement | null);
    if (wrapped) {
      event.stopPropagation();
      this.selectResizableWrapper(wrapped);
      return;
    }
  }

  onEditorMouseDown(event: MouseEvent, index: number): void {
    this.activePageIndex = index;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }

    // Grabbing a cell's right/bottom border resizes the column/row instead of
    // placing the caret or selecting the surrounding chart/table wrapper.
    if (this.maybeStartTableResize(event, target)) {
      return;
    }

    let wrapper =
      this.resolveWrapperFromEvent(event) || this.resolveWrapperFromTarget(target);
    const mousedownImageFromEvent = this.resolveImageFromEvent(event);
    const mousedownImageFromTarget = this.resolveImageFromTarget(target);
    if (!wrapper) {
      const clickedImage = mousedownImageFromEvent || mousedownImageFromTarget;

      if (!clickedImage) {
        return;
      }

      const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
      if (!editor) {
        return;
      }

      // Ensure direct image clicks become selectable immediately.
      this.enableResizableImagesInEditor(editor);
      wrapper =
        this.resolveWrapperFromEvent(event) ||
        this.resolveWrapperFromTarget(target) ||
        (clickedImage.closest(".resizable-image") as HTMLElement | null);
      if (!wrapper) {
        return;
      }
    }

    this.selectResizableWrapper(wrapper);

    const handleEl = target.closest(".image-resize-handle") as HTMLElement | null;
    const dragDir = String(handleEl?.getAttribute("data-resize-dir") || "");

    if (dragDir) {
      event.preventDefault();
      event.stopPropagation();
      this.startResizeDrag(wrapper, event, dragDir);
      return;
    }

    // Anything else on a chart / table / image starts a move-drag once the
    // pointer travels a few pixels; a plain click still only selects.
    //
    // The containment test is what keeps a drag from being armed by a click that
    // merely happened on the same page. Arming calls preventDefault(), so a
    // wrapper resolved for an unrelated click makes that click place no caret —
    // the text around a chart silently stops being editable. Requiring the
    // pointer to be inside the wrapper keeps that contained to a genuine hit on
    // the visual, whatever the resolvers return.
    if (
      wrapper.contains(target) &&
      !target.closest(".image-handle") &&
      !this.isTextEditingTarget(target)
    ) {
      this.armVisualDrag(this.getDraggableUnit(target) || wrapper, event);
    }
  }

  private statsTimer: ReturnType<typeof setTimeout> | null = null;
  // Repagination is deferred while the user is still typing — see onPageInput.
  private inputReflowTimer: ReturnType<typeof setTimeout> | null = null;
  // When the current burst of edits must be paginated by, whatever else arrives.
  private inputReflowDeadline = 0;
  private static readonly INPUT_REFLOW_DELAY_MS = 250;
  private static readonly INPUT_REFLOW_MAX_WAIT_MS = 600;

  // Word/character counts are footer-only display. Reading innerText of every
  // page (which forces layout) and scanning a huge string on every toolbar action
  // makes large documents hang, so defer it until edits settle.
  private updateDocumentStats(): void {
    if (this.statsTimer) {
      clearTimeout(this.statsTimer);
    }
    this.statsTimer = setTimeout(() => {
      this.statsTimer = null;
      this.computeDocumentStats();
    }, 300);
  }

  private computeDocumentStats(): void {
    const plainText = this.pageEditors
      ? this.pageEditors
          .toArray()
          .map((ref) => ref.nativeElement.innerText || "")
          .join(" ")
          .trim()
      : "";

    this.characterCount = plainText.length;
    this.wordCount = plainText ? plainText.split(/\s+/).length : 0;
  }

  private getActiveEditor(): HTMLDivElement | null {
    const editors = this.pageEditors?.toArray() || [];
    return editors[this.activePageIndex]?.nativeElement || null;
  }

  /**
   * Focus the editable host that owns the current selection so an execCommand
   * targets the right text. The selection may sit in an editable table island
   * (`.report-table-block[contenteditable=true]`) nested in the page editor;
   * focusing an ancestor instead would collapse the selection. Only focuses when
   * the host isn't already active (to avoid collapsing it), and falls back to the
   * page editor when the selection isn't inside any editable region.
   */
  private focusSelectionEditableHost(fallback: HTMLElement): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const node = selection.getRangeAt(0).startContainer;
      const el =
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element)
          : node.parentElement;
      const host = el?.closest(
        '[contenteditable="true"]'
      ) as HTMLElement | null;
      if (host) {
        if (document.activeElement !== host) {
          host.focus();
        }
        return;
      }
    }
    fallback.focus();
  }

  private focusActivePage(): void {
    const editor = this.getActiveEditor();
    if (editor) {
      editor.focus();
    }
  }

  private reflowFrom(startIndex: number): void {
    if (!this.pageEditors || !this.pageEditors.length) {
      return;
    }

    this.reflowInProgress = true;
    // Consolidate any same-page table fragments first so a table is never shown
    // split twice on one page; the forward pass below re-splits across pages.
    const allEditors = this.pageEditors.toArray();
    let earliestMerged = -1;
    for (let e = 0; e < allEditors.length; e += 1) {
      if (this.mergeAdjacentTableFragments(allEditors[e].nativeElement)) {
        if (earliestMerged === -1) {
          earliestMerged = e;
        }
      }
    }
    // If we merged fragments, start the forward pass at the earliest affected
    // page so the now-taller table is re-split from there.
    const forwardStart =
      earliestMerged === -1
        ? startIndex
        : Math.min(startIndex, earliestMerged);

    // Pre-create all the blank pages the overflow will need in ONE shot. Without
    // this, moveOverflowForward creates a single page, returns to wait for Angular
    // to render it, then the whole reflow re-runs — an O(pages) async cascade that
    // re-measures the entire (multi-MB) document each cycle. After this pre-pass,
    // the forward sweep below distributes everything in a single synchronous run.
    if (this.ensureBlankPageCapacity()) {
      setTimeout(() => this.reflowFrom(startIndex), 0);
      return;
    }

    const waitingForNewPage = this.moveOverflowForward(forwardStart);
    if (waitingForNewPage) {
      // Reflow continues asynchronously after the new page renders; stay marked
      // in-progress so the changes subscription keeps deferring to the live DOM.
      return;
    }
    // Pull content back up to fill any gaps left when content shrank (e.g. a
    // smaller font, deletion). Scan from the first non-reserved page so the
    // leading reserved pages (MOR cover) are never backfilled with report
    // content. moveOverflowForward already ensured no page overflows, so this
    // only fills genuine free space.
    this.pullContentBackward(this.reservedLeadingPages);
    this.removeTrailingEmptyPages();
    this.syncPagesFromDom();
    // Pagination is final for this pass — refresh any Table of Contents page
    // numbers so they always reflect where reports actually landed (covers
    // initial render, edits, and reports dragged between pages).
    this.refreshTableOfContents();
    this.reflowInProgress = false;
    // Reflow is done moving nodes around; put the caret back where the user was
    // typing (the marker travelled with its block, even onto another page).
    this.restorePendingCaret();
    // ...and bring the view with it. Repagination may have carried the caret's
    // block to a different page entirely; without this the screen stays put and
    // the user goes on editing somewhere they cannot see. A pending visual reveal
    // is queued below and runs after this, so a drop still wins the scroll.
    this.scrollCaretIntoView();
    // An edit asked to autosave while we were paginating; now that the model is
    // consistent again, honour it.
    if (this.draftAutosavePending) {
      this.draftAutosavePending = false;
      this.scheduleDraftAutosave();
    }
    // Page count may have changed; refresh the footer's current-page tracker.
    setTimeout(() => this.updateVisiblePageIndex(), 0);
    // Reflow has fully settled — now scroll to a just-inserted table (if any),
    // since pagination may have moved it to its final page.
    if (this.pendingTableReveal) {
      this.pendingTableReveal = false;
      setTimeout(() => this.revealInsertedTable(), 0);
    }
    // Same for a dragged chart/table/image: pagination has finished shuffling,
    // so now point the user at where it actually came to rest.
    const moved = this.pendingVisualReveal;
    if (moved) {
      this.pendingVisualReveal = null;
      setTimeout(() => this.revealMovedVisual(moved), 0);
    }
  }

  /**
   * Pre-create the blank pages the current overflow will need, in one shot, so
   * moveOverflowForward can distribute content in a single synchronous sweep
   * instead of the create-one-page-then-wait-and-rerun cascade.
   *
   * Returns true when it added pages (caller should wait a tick for Angular to
   * render them, then re-run reflow). Returns false when enough capacity already
   * exists — which is what stops it re-adding on the re-run: the pages it created
   * are now counted as available trailing blanks, so the deficit is zero.
   */
  private ensureBlankPageCapacity(): boolean {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return false;
    }

    let overflowPx = 0;
    let usable = 0;
    for (let i = 0; i < editors.length; i += 1) {
      const el = editors[i].nativeElement;
      usable = Math.max(usable, el.clientHeight || 0);
      const o = el.scrollHeight - el.clientHeight;
      // Only count overflow that adding pages can actually relieve. A page whose
      // overflow comes from a single indivisible block (taller than the page,
      // not splittable) will stay overflowing no matter how many pages we add —
      // counting it would make us pre-create pages every reflow forever.
      if (o > 1 && this.pageOverflowIsRelievable(el)) {
        overflowPx += o;
      }
    }
    if (overflowPx <= 1 || usable <= 0) {
      return false;
    }

    // Trailing blank pages can already absorb part of the overflow.
    let trailingBlanks = 0;
    for (let i = editors.length - 1; i >= 0; i -= 1) {
      if (this.isBlankPageEl(editors[i].nativeElement)) {
        trailingBlanks += 1;
      } else {
        break;
      }
    }

    // Over-provision slightly. Content never packs to 100% of a page (block
    // boundaries leave slack), and splitting a too-tall block ADDS height (each
    // continuation re-adds a wrapper), so a raw overflow/usable estimate
    // under-shoots and forces several extra reflow cycles. Use 88% of the page
    // as the effective capacity and add one page of headroom; any surplus blank
    // pages are trimmed by removeTrailingEmptyPages at the end.
    const pagesNeeded = Math.ceil(overflowPx / (usable * 0.88)) + 1;
    const room = Math.max(0, this.maxAutoPages - this.pages.length);
    const toAdd = Math.min(Math.max(0, pagesNeeded - trailingBlanks), room);
    for (let i = 0; i < toAdd; i += 1) {
      this.pages.push({ id: this.nextPageId++, html: "<p><br></p>" });
    }
    return toAdd > 0;
  }

  /**
   * True when a page's overflow can be reduced by adding pages — i.e. it has
   * more than one block (some can move forward), or its single block is one we
   * can split across the boundary (a text report with >1 inner paragraph, or a
   * data table with >1 row). A lone indivisible block taller than the page is
   * NOT relievable: it will be left clipped, so its overflow must not drive
   * page creation.
   */
  private pageOverflowIsRelievable(el: HTMLElement): boolean {
    if (el.children.length !== 1) {
      return el.children.length > 1;
    }
    const only = el.firstElementChild as HTMLElement | null;
    if (!only) {
      return false;
    }
    if (only.classList.contains("report-block")) {
      const inner = only.querySelector(
        ":scope > .imported-report-content"
      ) as HTMLElement | null;
      if (inner && inner.children.length > 1) {
        return true;
      }
      const rows = only.querySelectorAll(
        "table.report-data-table tbody > tr"
      );
      if (rows.length > 1) {
        return true;
      }
      // Any other multi-part section (a heading + a chart + a description) can
      // still be divided at its direct children, so its overflow IS relievable.
      if (only.children.length > 1) {
        return true;
      }
    }
    return false;
  }

  private moveOverflowForward(startIndex: number): boolean {
    const editors = this.pageEditors.toArray();

    // When the document has a page-exclusive block (e.g. the Table of Contents),
    // a later page may need isolating even though earlier pages are settled, so
    // we can't take the "cascade settled → stop scanning" shortcut below.
    const docHasExclusive = editors.some((e) =>
      Array.from(e.nativeElement.children).some((c) => this.isExclusiveBlock(c))
    );

    for (let index = startIndex; index < editors.length; index += 1) {
      const currentEditor = editors[index].nativeElement;
      let guard = 0;
      let pushedForward = false;
      this.removeTrailingWhitespaceNodes(currentEditor);

      while (
        (currentEditor.scrollHeight > currentEditor.clientHeight + 1 ||
          this.pageHasUnisolatedExclusive(currentEditor)) &&
        currentEditor.lastElementChild &&
        guard < 300
      ) {
        guard += 1;
        // Whether this page genuinely overflows (vs. only needing a page-exclusive
        // block isolated). Splitting tables/text is only valid for real overflow;
        // for isolation we move whole blocks forward instead.
        const overflowing =
          currentEditor.scrollHeight > currentEditor.clientHeight + 1;
        const nextIndex = index + 1;
        const nextEditor = editors[nextIndex]?.nativeElement;

        if (!nextEditor) {
          if (this.pages.length >= this.maxAutoPages) {
            return false;
          }

          this.pages.push({ id: this.nextPageId++, html: "<p><br></p>" });
          setTimeout(() => this.reflowFrom(index), 0);
          return true;
        }

        const movingNode = currentEditor.lastElementChild;
        if (!movingNode) {
          break;
        }

        // Decide whether to row-split a table or move it forward whole.
        // - If the table is the ONLY thing on this page yet the page still
        //   overflows, it cannot fit on any page whole → split it.
        // - If it is taller than the usable page height → split.
        // - If it is being pushed off the bottom but there is still MEANINGFUL
        //   room above it on this page, split it so those rows fill the space
        //   instead of leaving a large blank gap above the table.
        // - Only when the remaining room is too small to hold a sensible chunk
        //   of rows do we move the WHOLE table forward (avoids an ugly split
        //   that would orphan just a header / a row or two).
        let tableNeedsSplit = false;
        if (
          overflowing &&
          movingNode instanceof HTMLElement &&
          movingNode.classList.contains("table-report-block")
        ) {
          const editorStyle = window.getComputedStyle(currentEditor);
          const padY =
            (parseFloat(editorStyle.paddingTop) || 0) +
            (parseFloat(editorStyle.paddingBottom) || 0);
          const usableHeight = currentEditor.clientHeight - padY;
          const isAloneOnPage = movingNode === currentEditor.firstElementChild;
          const tallerThanPage = movingNode.offsetHeight > usableHeight;
          // Free vertical space that was available for the table on this page
          // (page height minus the content above it).
          const freeSpace =
            currentEditor.clientHeight -
            (currentEditor.scrollHeight - movingNode.offsetHeight);
          // ~ table header + a couple of data rows.
          const MIN_FILL_SPACE = 140;
          tableNeedsSplit =
            isAloneOnPage || tallerThanPage || freeSpace >= MIN_FILL_SPACE;
        }

        if (
          tableNeedsSplit &&
          this.splitTableBlockOverflow(movingNode as HTMLElement, nextEditor)
        ) {
          pushedForward = true;
          this.removeTrailingWhitespaceNodes(currentEditor);
          continue;
        }

        // Runaway guard: when the ONLY block left on this page STILL makes the
        // page overflow, it cannot fit on any page — whether from its own height
        // or its margins (offsetHeight excludes margins, so a sub-page-height
        // block with a bottom margin still overflows). Pushing it forward just
        // marches it through blank pages until maxAutoPages (the "15 real pages
        // but 80 total" symptom). Split a text report block by flowing its
        // trailing paragraphs onto the next page (no content lost); only when it
        // genuinely can't be split do we leave it here (clipped) so the rest of
        // the document still paginates.
        if (movingNode === currentEditor.firstElementChild) {
          if (
            movingNode instanceof HTMLElement &&
            movingNode.classList.contains("report-block") &&
            (this.splitTextReportBlockOverflow(movingNode, nextEditor) ||
              this.splitReportBlockChildOverflow(movingNode, nextEditor))
          ) {
            pushedForward = true;
            this.removeTrailingWhitespaceNodes(currentEditor);
            continue;
          }
          break;
        }

        nextEditor.insertBefore(movingNode, nextEditor.firstChild);
        pushedForward = true;
        this.removeTrailingWhitespaceNodes(currentEditor);

        // Deliberately NOT moving the caret to the next page here.
        //
        // This used to advance activePageIndex and drop the caret at the end of
        // nextEditor to "keep typing flow natural". Because activePageIndex was
        // reassigned INSIDE the loop, the following iteration matched the very
        // same test and advanced again — so a cascade marched it to the far end
        // of the document, queuing a caret move for every page it crossed. The
        // last of those timeouts won, landing the caret (and, through focus(),
        // the scroll position) on a page the user had nothing to do with. That is
        // the jump to a "random" page after any edit.
        //
        // The caret marker handles this properly and precisely: it travels with
        // the user's own block, so if that block is the one that moved forward
        // the caret follows it there and nowhere else. See insertCaretMarker /
        // restorePendingCaret.
      }

      // Cascade settled: this page fit without pushing anything to the next, so
      // every later page is unchanged. Stop instead of scanning all remaining
      // pages (a forced layout read each) — the key win for large documents.
      // Skip this shortcut when an exclusive block exists, since a later page may
      // still need isolating independently of earlier pages.
      if (!pushedForward && !docHasExclusive) {
        break;
      }
    }

    return false;
  }

  private splitTableBlockOverflow(
    sourceBlock: HTMLElement,
    nextEditor: HTMLElement
  ): boolean {
    const sourceBody = sourceBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody) {
      return false;
    }

    const sourceRows = sourceBody.querySelectorAll("tr");
    if (sourceRows.length <= 1) {
      return false;
    }

    // The table id lives on the inner .report-table-block, not on the section
    // wrapper — read it via getTableBlockId. Reading the (absent) attribute off
    // the section yielded "" for every table, so a split fragment latched onto
    // ANY table at the top of the next page and merged two unrelated tables.
    const tableId = this.getTableBlockId(sourceBlock);
    let targetBlock = null as HTMLElement | null;
    const firstNextChild = nextEditor.firstElementChild as HTMLElement | null;
    if (
      firstNextChild &&
      this.isTableSection(firstNextChild) &&
      tableId &&
      this.getTableBlockId(firstNextChild) === tableId
    ) {
      targetBlock = firstNextChild;
    }

    if (!targetBlock) {
      targetBlock = sourceBlock.cloneNode(true) as HTMLElement;
      // Mark the carried-over fragment "(cont.)" so a table spanning pages doesn't
      // read as a second, unrelated table. Tables no longer render their own title
      // bar (the report-name heading above them covers it), so the heading carries
      // the marker; documents saved back when tables had a title bar still have
      // one, and it takes precedence so those keep marking it where they always did.
      const targetTitle = (targetBlock.querySelector(".report-chart-title") ||
        targetBlock.querySelector(".report-name-heading")) as HTMLElement | null;
      if (targetTitle) {
        const baseTitle = String(targetTitle.textContent || "").trim();
        if (baseTitle && !/\(cont\.\)$/i.test(baseTitle)) {
          targetTitle.textContent = `${baseTitle} (cont.)`;
        }
      }
      const targetBody = targetBlock.querySelector(
        "table.report-data-table tbody"
      ) as HTMLElement | null;
      if (!targetBody) {
        return false;
      }
      targetBody.innerHTML = "";
      nextEditor.insertBefore(targetBlock, nextEditor.firstChild);
    }

    const destinationBody = targetBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!destinationBody) {
      return false;
    }

    const rowToMove = sourceBody.lastElementChild as HTMLElement | null;
    if (!rowToMove) {
      return false;
    }

    destinationBody.insertBefore(rowToMove, destinationBody.firstChild);
    return true;
  }

  /**
   * Split a too-tall TEXT report block (a `.report-block` whose body is an
   * `.imported-report-content` div of paragraphs) across the page boundary by
   * moving its LAST inner block into a continuation copy at the top of the next
   * page. Moves one inner block per call (like splitTableBlockOverflow moves one
   * row) and returns true so the caller re-measures and calls again as needed.
   *
   * Returns false when there is nothing left to split (≤1 inner block, e.g. a
   * single oversized paragraph), so the caller can fall back to leaving the block
   * in place rather than looping forever.
   */
  private splitTextReportBlockOverflow(
    sourceBlock: HTMLElement,
    nextEditor: HTMLElement
  ): boolean {
    const sourceInner = sourceBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!sourceInner || sourceInner.children.length <= 1) {
      return false;
    }

    const reportId = String(sourceBlock.getAttribute("data-report-id") || "");
    let targetBlock: HTMLElement | null = null;
    const firstNextChild = nextEditor.firstElementChild as HTMLElement | null;
    if (
      firstNextChild &&
      firstNextChild.classList.contains("report-block") &&
      firstNextChild.getAttribute("data-report-cont") === "1" &&
      String(firstNextChild.getAttribute("data-report-id") || "") === reportId
    ) {
      targetBlock = firstNextChild;
    }

    if (!targetBlock) {
      // Shallow clone keeps the <section> tag/classes/attributes but no children;
      // give it a fresh empty content body and mark it as a continuation.
      targetBlock = sourceBlock.cloneNode(false) as HTMLElement;
      targetBlock.setAttribute("data-report-cont", "1");
      const targetInner = document.createElement("div");
      targetInner.className = sourceInner.className;
      targetBlock.appendChild(targetInner);
      nextEditor.insertBefore(targetBlock, nextEditor.firstChild);
    }

    const destInner = targetBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!destInner) {
      return false;
    }

    const nodeToMove = sourceInner.lastElementChild as HTMLElement | null;
    if (!nodeToMove) {
      return false;
    }
    destInner.insertBefore(nodeToMove, destInner.firstChild);
    return true;
  }

  /**
   * Inverse of moveOverflowForward: pull content up from later pages to fill
   * free space on earlier pages. Runs after the forward pass (so no page
   * overflows) and only moves an element/row when it still fits — so it can
   * never create an overflow, it only closes gaps left when content shrank.
   */
  private pullContentBackward(startIndex: number): void {
    if (!this.pageEditors || !this.pageEditors.length) {
      return;
    }
    const editors = this.pageEditors.toArray();

    for (let index = Math.max(0, startIndex); index < editors.length; index += 1) {
      const currentEditor = editors[index]?.nativeElement;
      if (!currentEditor) {
        continue;
      }

      // Pull from the next page that actually HOLDS something, rather than
      // blindly from index + 1.
      //
      // Taking the immediate neighbour is what left blank pages sitting between
      // content. A run of two or more empty pages stalled the sweep: the first
      // empty page's neighbour is also empty, so nothing was ever pulled into it,
      // and it stayed blank for good while every page after it compacted
      // normally. Skipping the empties makes them bubble to the end of the
      // document instead, where removeTrailingEmptyPages trims them away.
      //
      // The donor is re-selected whenever it runs dry, so this page keeps filling
      // from SUCCESSIVE later pages until it is genuinely full. Binding a single
      // donor for the whole sweep is what left a half-empty page in the middle of
      // the document after a burst of Backspace/Enter: a donor holding less than
      // the free space above it emptied into this page, the sweep moved on with
      // the gap still open, and that now-empty donor became the next target —
      // where the currentWasEmpty branch below skips the fit test, so it swallowed
      // the whole following section and cemented the gap on the page above it.
      let donorIndex = index;
      let donor: HTMLElement | null = null;
      let guard = 0;
      while (guard < 600) {
        if (!donor || !donor.firstElementChild) {
          donorIndex += 1;
          while (
            donorIndex < editors.length &&
            !editors[donorIndex].nativeElement.firstElementChild
          ) {
            donorIndex += 1;
          }
          donor = editors[donorIndex]?.nativeElement || null;
        }
        if (!donor || !donor.firstElementChild) {
          break; // nothing but empty pages left — they are trailing, so trimmed
        }
        // Every `break` below means "this page is full", so it correctly ends the
        // donor walk too; only a drained donor advances it.
        const nextEditor = donor;
        guard += 1;
        const candidate = nextEditor.firstElementChild as HTMLElement;

        // Never merge across a page-exclusive boundary: a page-exclusive block
        // (e.g. the Table of Contents) must stay alone on its page, so don't pull
        // it up onto a page with other content, and don't pull other content up
        // onto a page whose last block is page-exclusive.
        if (this.isExclusiveBlock(candidate)) {
          // ...but it MAY move up onto a page that is empty: it is alone either
          // way, and refusing stranded a blank page in front of it. Once it has
          // moved, the branch below keeps anything else off that page.
          if (currentEditor.firstElementChild) {
            break;
          }
        } else if (this.isExclusiveBlock(currentEditor.lastElementChild)) {
          break;
        }

        // Split table continued onto the next page: merge its rows back into the
        // matching table that ends the current page, one row at a time. The
        // table id lives on the inner .report-table-block, not the section.
        const lastCurrent = currentEditor.lastElementChild as HTMLElement | null;
        const candidateTableId = this.isTableSection(candidate)
          ? this.getTableBlockId(candidate)
          : "";
        if (
          candidateTableId &&
          lastCurrent &&
          this.isTableSection(lastCurrent) &&
          candidateTableId === this.getTableBlockId(lastCurrent)
        ) {
          const rowMoved = this.mergeTableRowBackward(
            candidate,
            lastCurrent,
            currentEditor
          );
          if (!rowMoved) {
            break; // not even one more row fits → stop on this page
          }
          if (this.isTableBlockEmpty(candidate)) {
            // Drop the drained table shell only. Anything else the continuation
            // still holds (a description paragraph, a trailing spacer) stays put
            // and merges back on a later pass — removing the whole section here
            // would take that content with it.
            this.removeDrainedTableShell(candidate);
          }
          continue;
        }

        // Split TEXT report continued onto the next page: merge its paragraphs
        // back into the matching report block ending the current page, one at a
        // time, so a report re-joins when earlier content shrinks (the table
        // path above does this for rows; this is the text-report equivalent).
        const candReportContId =
          candidate.classList.contains("report-block") &&
          candidate.getAttribute("data-report-cont") === "1"
            ? String(candidate.getAttribute("data-report-id") || "")
            : "";
        if (
          candReportContId &&
          lastCurrent &&
          lastCurrent.classList.contains("report-block") &&
          String(lastCurrent.getAttribute("data-report-id") || "") ===
            candReportContId
        ) {
          // Rich-text reports merge at paragraph level (the finest granularity
          // available); every other section — a chart, or a mixed one — merges
          // one direct child at a time. Note the two emptiness tests differ and
          // are NOT interchangeable: isReportBlockContentEmpty reports "empty"
          // for a section that has no .imported-report-content at all, so using
          // it on the child path would delete a chart continuation that still
          // holds its content.
          if (
            this.mergeTextParagraphBackward(candidate, lastCurrent, currentEditor)
          ) {
            if (this.isReportBlockContentEmpty(candidate)) {
              candidate.remove(); // fully merged back; drop the empty continuation
            }
            continue;
          }
          if (
            this.mergeReportChildBackward(candidate, lastCurrent, currentEditor)
          ) {
            if (!candidate.firstElementChild) {
              candidate.remove(); // fully merged back; drop the empty continuation
            }
            continue;
          }
          break; // not even one more unit fits → stop on this page
        }

        // Generic block: tentatively move it up and keep it only if it fits.
        const currentWasEmpty = !currentEditor.firstElementChild;
        currentEditor.appendChild(candidate);
        this.removeTrailingWhitespaceNodes(nextEditor);
        if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
          // A block too tall for ANY page still belongs on this one when the page
          // was otherwise empty. No later page can hold it either, so reverting
          // only leaves a blank page here with the block marooned behind it.
          // Keeping it cannot oscillate: the forward pass already refuses to
          // march a lone unsplittable block onwards.
          if (currentWasEmpty) {
            continue;
          }
          nextEditor.insertBefore(candidate, nextEditor.firstChild);
          // It does not fit WHOLE — but part of it may. A report section is a
          // heading plus a chart/table plus a description; moving it as one unit
          // is what left a large gap at the foot of this page. Flow as many of
          // its leading children up as fit, then continue so the row/paragraph
          // merges above carry on filling whatever room is left.
          if (this.pullReportBlockPrefixBackward(candidate, currentEditor)) {
            continue;
          }
          break;
        }
      }
    }
  }

  /** Move the first body row of a split table continuation back to the page
   *  above, keeping it only if it still fits. Returns false when it doesn't. */
  private mergeTableRowBackward(
    sourceBlock: HTMLElement,
    destBlock: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const sourceBody = sourceBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    const destBody = destBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody || !destBody) {
      return false;
    }
    const row = sourceBody.firstElementChild as HTMLElement | null;
    if (!row) {
      return false;
    }

    destBody.appendChild(row);
    if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
      // Doesn't fit → put the row back at the front of the continuation.
      sourceBody.insertBefore(row, sourceBody.firstChild);
      return false;
    }
    return true;
  }

  private isTableBlockEmpty(block: HTMLElement): boolean {
    const body = block.querySelector("table.report-data-table tbody");
    return !body || body.children.length === 0;
  }

  /** Move the first inner paragraph of a split TEXT continuation back to the
   *  matching report block above, keeping it only if it still fits. Returns
   *  false when it doesn't (caller stops merging on this page). */
  private mergeTextParagraphBackward(
    sourceCont: HTMLElement,
    destBlock: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const sourceInner = sourceCont.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    const destInner = destBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!sourceInner || !destInner) {
      return false;
    }
    const para = sourceInner.firstElementChild as HTMLElement | null;
    if (!para) {
      return false;
    }
    destInner.appendChild(para);
    if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
      // Doesn't fit → put it back at the front of the continuation.
      sourceInner.insertBefore(para, sourceInner.firstChild);
      return false;
    }
    return true;
  }

  /** True when a report block's content body has no remaining inner blocks. */
  private isReportBlockContentEmpty(block: HTMLElement): boolean {
    const inner = block.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    return !inner || inner.children.length === 0;
  }

  /**
   * Children that must not be stranded at the foot of a page without the content
   * they introduce. A report-name heading alone at the bottom of a page, with its
   * chart or table overleaf, reads as a mistake — so every split carries it
   * forward with the block it belongs to.
   */
  private isKeepWithNextChild(el: Element | null | undefined): boolean {
    return (
      el instanceof HTMLElement &&
      (el.classList.contains("report-name-heading") ||
        el.classList.contains("report-chart-title"))
    );
  }

  /**
   * Split a report section at its DIRECT CHILDREN by flowing the last one into a
   * continuation at the top of the next page. This is the general fallback for
   * sections the finer splitters cannot touch: a chart section is a heading + a
   * snapshot image + a description, with no inner content body to divide
   * (splitTextReportBlockOverflow) and no table rows (splitTableBlockOverflow),
   * so before this it could only ever move as one indivisible unit — which is
   * what left the page above it blank.
   *
   * Moves one child per call, like the row/paragraph splitters, so the caller
   * re-measures and calls again as needed. Never empties the source: that would
   * march the section forward through blank pages, the very failure the caller's
   * runaway guard exists to prevent.
   */
  private splitReportBlockChildOverflow(
    sourceBlock: HTMLElement,
    nextEditor: HTMLElement
  ): boolean {
    if (
      !sourceBlock.classList.contains("report-block") ||
      sourceBlock.children.length <= 1
    ) {
      return false;
    }

    const reportId = String(sourceBlock.getAttribute("data-report-id") || "");
    let targetBlock: HTMLElement | null = null;
    const firstNextChild = nextEditor.firstElementChild as HTMLElement | null;
    if (
      firstNextChild &&
      firstNextChild.classList.contains("report-block") &&
      firstNextChild.getAttribute("data-report-cont") === "1" &&
      String(firstNextChild.getAttribute("data-report-id") || "") === reportId
    ) {
      targetBlock = firstNextChild;
    }
    const createdTarget = !targetBlock;
    if (!targetBlock) {
      // Shallow clone keeps the <section> tag/classes/attributes but no children.
      targetBlock = sourceBlock.cloneNode(false) as HTMLElement;
      targetBlock.removeAttribute("id");
      targetBlock.setAttribute("data-report-cont", "1");
      nextEditor.insertBefore(targetBlock, nextEditor.firstChild);
    }

    const dest = targetBlock;
    const moved: HTMLElement[] = [];
    const takeLast = (): boolean => {
      const node = sourceBlock.lastElementChild as HTMLElement | null;
      if (!node) {
        return false;
      }
      dest.insertBefore(node, dest.firstChild);
      moved.push(node);
      return true;
    };

    if (takeLast()) {
      // A heading introduces whatever just moved — it travels with it.
      while (this.isKeepWithNextChild(sourceBlock.lastElementChild)) {
        if (!takeLast()) {
          break;
        }
      }
    }

    // Nothing moved, or the source was emptied (which would move the section
    // whole and re-open the march-through-blank-pages failure) — undo.
    if (!moved.length || !sourceBlock.firstElementChild) {
      for (let i = moved.length - 1; i >= 0; i -= 1) {
        sourceBlock.appendChild(moved[i]);
      }
      if (createdTarget) {
        dest.remove();
      }
      return false;
    }
    return true;
  }

  /**
   * Inverse of splitReportBlockChildOverflow: move the first direct child of a
   * continuation back into the matching section above, keeping it only if it
   * still fits. Returns false when it doesn't, so the caller stops on this page.
   */
  private mergeReportChildBackward(
    sourceCont: HTMLElement,
    destBlock: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const child = sourceCont.firstElementChild as HTMLElement | null;
    if (!child) {
      return false;
    }
    destBlock.appendChild(child);
    if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
      // Doesn't fit → put it back at the front of the continuation.
      sourceCont.insertBefore(child, sourceCont.firstChild);
      return false;
    }
    return true;
  }

  /**
   * A report section on the next page that will not fit on this one WHOLE gives
   * up as many of its leading children as do fit, so the page above is filled
   * instead of being left half empty. What stays behind becomes a continuation
   * of the part that moved up.
   *
   * This is the backward counterpart of splitReportBlockChildOverflow, and the
   * reason a chart that cannot fit no longer leaves the page above it blank.
   * Returns true when it moved something — the caller then continues, so the
   * row/paragraph merges can keep filling the remaining room.
   */
  private pullReportBlockPrefixBackward(
    candidate: HTMLElement,
    currentEditor: HTMLElement,
    respectKeepWithNext = true
  ): boolean {
    if (
      !candidate.classList.contains("report-block") ||
      candidate.children.length <= 1
    ) {
      return false; // nothing to divide — it moves whole or not at all
    }

    const head = candidate.cloneNode(false) as HTMLElement;
    head.removeAttribute("id");
    currentEditor.appendChild(head);

    const moved: HTMLElement[] = [];
    while (candidate.firstElementChild) {
      const child = candidate.firstElementChild as HTMLElement;
      head.appendChild(child);
      if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
        candidate.insertBefore(child, candidate.firstChild);
        break;
      }
      moved.push(child);
    }

    if (!moved.length) {
      head.remove();
      return false;
    }

    // Everything fit after all (trailing whitespace was the only overflow), so
    // the section effectively moved whole — drop the now-empty original.
    if (!candidate.firstElementChild) {
      candidate.remove();
      return true;
    }

    // What is left behind is now a continuation of the part on the page above.
    const wasContinuation = candidate.getAttribute("data-report-cont") === "1";
    candidate.setAttribute("data-report-cont", "1");

    // Give the part that moved an empty counterpart of whatever blocked it, so
    // the existing row / paragraph merges can flow content in behind it one unit
    // at a time instead of stopping at this whole-child boundary.
    //
    // This runs BEFORE the keep-with-next check below, and that order is the
    // whole point: whether content follows the heading is exactly what decides
    // whether the heading is stranded. A report section is a heading plus a
    // table, and only the heading ever fits in a part-full page — so checking
    // keep-with-next first put the heading straight back and gave up, and a table
    // too big to fit whole was never divided. Seeding first lets its rows follow
    // the heading up, which is what "split the table automatically" means here.
    const flowed = this.seedContinuationContainer(candidate, head, currentEditor);
    if (flowed) {
      return true;
    }

    // Nothing followed, so a heading really would be stranded here. Put it back —
    // unless the caller is acting on an explicit user gesture, where moving
    // nothing at all is the worse outcome. See pullBlockToPreviousPage.
    if (respectKeepWithNext) {
      while (this.isKeepWithNextChild(head.lastElementChild)) {
        candidate.insertBefore(
          head.lastElementChild as HTMLElement,
          candidate.firstChild
        );
        moved.pop();
      }
      if (!moved.length) {
        if (!wasContinuation) {
          candidate.removeAttribute("data-report-cont");
        }
        head.remove();
        return false;
      }
    }
    return true;
  }

  /**
   * Move the section holding the caret onto the page above, taking as much of it
   * as fits and leaving the rest behind as a continuation.
   *
   * Used when a Backspace at the top of a page cannot MERGE the block into the
   * one above without restyling it. Relocating preserves every block's tag, so
   * the content still shifts between pages and a heading stays a heading.
   *
   * The keep-with-next rule is deliberately suspended here. It exists to stop
   * automatic pagination stranding a heading at the foot of a page; an explicit
   * Backspace at that boundary is the user overriding that preference, and
   * honouring it would leave the key doing nothing at all. A later automatic
   * reflow can only pull MORE content up to join it, never push it back.
   */
  private pullBlockToPreviousPage(block: HTMLElement, index: number): boolean {
    const editors = this.pageEditors?.toArray() || [];
    const currentEditor = editors[index]?.nativeElement;
    const prevEditor = editors[index - 1]?.nativeElement;
    if (!currentEditor || !prevEditor) {
      return false;
    }

    // The top-level unit on this page that contains the caret's block.
    let unit: HTMLElement | null = block;
    while (unit && unit.parentElement !== currentEditor) {
      unit = unit.parentElement as HTMLElement | null;
    }
    if (!unit || unit !== currentEditor.firstElementChild) {
      return false; // something else sits above it here — nothing to shift
    }

    // Keep the caret with its block through the repagination that follows.
    this.pendingCaretMarkerId = this.insertCaretMarker();

    let moved = this.pullReportBlockPrefixBackward(unit, prevEditor, false);
    if (!moved) {
      // Not a divisible section: move it whole, but only if it actually fits.
      prevEditor.appendChild(unit);
      if (prevEditor.scrollHeight > prevEditor.clientHeight + 1) {
        currentEditor.insertBefore(unit, currentEditor.firstChild);
      } else {
        moved = true;
      }
    }

    if (!moved) {
      // Nothing could move — drop the marker we speculatively placed, since no
      // reflow will run to consume it.
      this.pendingCaretMarkerId = null;
      document
        .querySelectorAll("[data-caret-marker]")
        .forEach((node) => node.remove());
      return false;
    }

    this.syncPagesFromDom();
    this.reflowFrom(Math.max(0, index - 1));
    return true;
  }

  /**
   * The child that stopped pullReportBlockPrefixBackward is often a container
   * that could itself be divided — a text body of paragraphs, or a data table of
   * rows. Put an empty counterpart of it on the page above so the existing
   * mergeTextParagraphBackward / mergeTableRowBackward can flow its contents back
   * one unit at a time. The seed is proved by moving one unit into it and is
   * removed again when none fits, so a failed attempt never leaves an empty table
   * shell or content div behind on the page.
   */
  private seedContinuationContainer(
    candidate: HTMLElement,
    head: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const next = candidate.firstElementChild as HTMLElement | null;
    if (!next) {
      return false;
    }

    if (next.classList.contains("imported-report-content")) {
      const seed = document.createElement("div");
      seed.className = next.className;
      head.appendChild(seed);
      if (!this.mergeTextParagraphBackward(candidate, head, currentEditor)) {
        seed.remove();
        return false;
      }
      return true;
    }

    const tableBlock = (
      next.classList.contains("report-table-block")
        ? next
        : next.querySelector(".report-table-block")
    ) as HTMLElement | null;
    if (!tableBlock) {
      return false;
    }
    const seedTable = tableBlock.cloneNode(true) as HTMLElement;
    seedTable.removeAttribute("id");
    const body = seedTable.querySelector("table.report-data-table tbody");
    if (!body) {
      return false;
    }
    body.innerHTML = ""; // the header stays; the rows flow back into it
    // Mirror how the table sits inside the section (bare, or inside its resize
    // wrapper) so the seeded copy lays out exactly like the original.
    let host = seedTable;
    if (next !== tableBlock) {
      host = next.cloneNode(false) as HTMLElement;
      host.removeAttribute("id");
      host.appendChild(seedTable);
    }
    head.appendChild(host);
    if (!this.mergeTableRowBackward(candidate, head, currentEditor)) {
      host.remove();
      return false;
    }
    return true;
  }

  /**
   * Remove a table continuation's drained shell (its tbody is now empty) without
   * taking the rest of the section with it. The section itself is dropped only
   * once nothing else remains in it, so a description paragraph or trailing
   * spacer that has not merged back yet survives to be pulled up on a later pass.
   */
  private removeDrainedTableShell(section: HTMLElement): void {
    const block = section.querySelector(
      ".report-table-block"
    ) as HTMLElement | null;
    if (block) {
      const wrapper = block.closest(".resizable-image") as HTMLElement | null;
      const host = wrapper && section.contains(wrapper) ? wrapper : block;
      host.remove();
    }
    const hasContent = Array.from(section.children).some(
      (child) =>
        !this.isKeepWithNextChild(child) &&
        (!!child.querySelector("img,table,.report-chart,.resizable-image") ||
          !!String(child.textContent || "").trim())
    );
    if (!hasContent) {
      section.remove();
    }
  }

  /** True when this top-level node is a report data-table block (or wraps one). */
  private isTableSection(el: HTMLElement): boolean {
    return (
      el.classList.contains("table-report-block") ||
      !!el.querySelector(".report-table-block")
    );
  }

  /** The table id (lives on the inner .report-table-block, not the section). */
  private getTableBlockId(section: HTMLElement): string {
    const inner = section.querySelector(
      ".report-table-block"
    ) as HTMLElement | null;
    return String((inner || section).getAttribute("data-table-id") || "");
  }

  /**
   * A split table must never have two fragments on the SAME page. Merge any
   * consecutive table sections that share a table id back into the first one
   * (dropping the "(cont.)" duplicate). If the merged table then overflows, the
   * forward pass re-splits it cleanly across a page boundary.
   */
  private mergeAdjacentTableFragments(editor: HTMLElement): boolean {
    let merged = false;
    let child = editor.firstElementChild as HTMLElement | null;
    while (child) {
      const next = child.nextElementSibling as HTMLElement | null;
      if (
        next &&
        this.isTableSection(child) &&
        this.isTableSection(next) &&
        this.getTableBlockId(child) &&
        this.getTableBlockId(child) === this.getTableBlockId(next)
      ) {
        this.mergeAllTableRows(next, child);
        next.remove();
        merged = true;
        // Stay on `child` so a third fragment also merges in.
        continue;
      }
      child = next;
    }
    return merged;
  }

  /** Move every body row from one table section into another, in order. */
  private mergeAllTableRows(source: HTMLElement, dest: HTMLElement): void {
    const sourceBody = source.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    const destBody = dest.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody || !destBody) {
      return;
    }
    while (sourceBody.firstElementChild) {
      destBody.appendChild(sourceBody.firstElementChild);
    }
  }

  private removeTrailingWhitespaceNodes(container: HTMLElement): void {
    while (
      container.lastChild &&
      container.lastChild.nodeType === Node.TEXT_NODE &&
      !(container.lastChild.textContent || "").trim()
    ) {
      container.removeChild(container.lastChild);
    }
  }

  private removeTrailingEmptyPages(): void {
    this.syncPagesFromDom();

    // Keep the reserved leading pages plus at least one content page, so an empty
    // document can't strip the reserved (blank) MOR cover pages from the end.
    const minPages = this.reservedLeadingPages + 1;
    while (this.pages.length > minPages) {
      const lastPage = this.pages[this.pages.length - 1];

      // A page is only removable when it has neither text NOR media. Checking
      // plain text alone would treat a page holding just a chart/image/table
      // (which legitimately carry no text) as empty and delete real content.
      if (!this.isBlankPageHtml(lastPage.html)) {
        break;
      }

      this.pages.pop();
      if (this.activePageIndex >= this.pages.length) {
        this.activePageIndex = this.pages.length - 1;
      }
    }
  }

  private syncPagesFromDom(): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }

    // Read back at most as many pages as the model holds.
    //
    // The model and the DOM can disagree in exactly one direction: a page has
    // just been removed from the model and Angular has not re-rendered yet, so
    // its editor is still in the DOM awaiting teardown. Reading that editor back
    // RE-CREATED the page — which is why removeTrailingEmptyPages never actually
    // removed anything: the very next line in reflowFrom put every blank page it
    // had just dropped straight back, in the editor and so in the export too.
    //
    // The other direction is safe and expected: ensureBlankPageCapacity grows the
    // model first and the DOM catches up, so bounding by the editors is correct
    // there and the surplus model entries are left untouched.
    const count = this.pages.length
      ? Math.min(editors.length, this.pages.length)
      : editors.length;
    for (let i = 0; i < count; i += 1) {
      if (!this.pages[i]) {
        this.pages[i] = { id: this.nextPageId++, html: "" };
      }
      this.pages[i].html = this.stripCaretMarkers(
        editors[i].nativeElement.innerHTML
      );
    }

    this.scheduleDraftAutosave();
  }

  // Sync only the active page's markup. Used by non-layout edits so we avoid
  // reading innerHTML of every page on each keystroke/format in large documents.
  private syncActivePageFromDom(): void {
    const editors = this.pageEditors?.toArray() || [];
    const editor = editors[this.activePageIndex]?.nativeElement;
    if (!editor) {
      return;
    }
    if (!this.pages[this.activePageIndex]) {
      this.pages[this.activePageIndex] = { id: this.nextPageId++, html: "" };
    }
    this.pages[this.activePageIndex].html = this.stripCaretMarkers(
      editor.innerHTML
    );

    this.scheduleDraftAutosave();
  }

  private hydratePagesToDom(force = false): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }

    for (let i = 0; i < editors.length; i += 1) {
      const modelHtml = this.pages[i]?.html || "";
      const el = editors[i].nativeElement;
      if (force || (!el.innerHTML.trim() && modelHtml)) {
        el.innerHTML = modelHtml;
      }
      this.enableResizableImagesInEditor(el);
    }
  }

  private prepareResizableImages(): void {
    const editors = this.pageEditors?.toArray() || [];
    for (let i = 0; i < editors.length; i += 1) {
      this.enableResizableImagesInEditor(editors[i].nativeElement);
    }
  }

  private enableResizableImagesInEditor(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(".resizable-image")
    ) as HTMLElement[];
    for (let i = 0; i < wrappers.length; i += 1) {
      this.bindResizableWrapper(wrappers[i]);
    }

    const images = Array.from(editor.querySelectorAll("img"));

    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      // Images are the one natively draggable node in a page. Left enabled, the
      // browser starts its own drag mid-gesture and COPIES the image into the
      // drop target; the pointer drag above is what moves visuals.
      img.setAttribute("draggable", "false");
      const parent = img.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }
      // The MOR cover image is a fixed, full-bleed page background — never wrap
      // it for resize/selection (that would give it a fixed width and break the
      // edge-to-edge fit).
      if (img.classList.contains("mor-cover-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image";
      wrapper.contentEditable = "false";

      const attrWidth = img.getAttribute("width");
      const cssWidth = img.style.width;
      if (attrWidth && /^\d+$/.test(attrWidth)) {
        wrapper.style.width = `${attrWidth}px`;
      } else if (attrWidth) {
        wrapper.style.width = attrWidth;
      } else if (cssWidth) {
        wrapper.style.width = cssWidth;
      } else if (img.clientWidth > 0) {
        wrapper.style.width = `${img.clientWidth}px`;
      }

      img.removeAttribute("width");
      img.removeAttribute("height");

      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);
      this.bindResizableWrapper(wrapper);
    }

    // Some report charts are not plain <img> (svg/canvas/custom DOM).
    // Wrap whole chart blocks so toolbar resize works consistently.
    const chartBlocks = Array.from(
      editor.querySelectorAll(".report-chart, .report-table-block")
    ) as HTMLElement[];

    for (let i = 0; i < chartBlocks.length; i += 1) {
      const block = chartBlocks[i];
      const parent = block.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image resizable-block";
      wrapper.contentEditable = "false";

      const widthFromBlock = block.getBoundingClientRect().width;
      const widthFromParent = parent.getBoundingClientRect().width;
      const baseWidth = Math.max(120, Math.round(widthFromBlock || widthFromParent || 0));
      if (baseWidth > 0) {
        wrapper.style.width = `${baseWidth}px`;
        wrapper.dataset.widthPx = String(baseWidth);
      }

      parent.insertBefore(wrapper, block);
      wrapper.appendChild(block);

      // Report TABLES keep an editable content island inside the (non-editable)
      // resize wrapper, so the document toolbar (font size, bold, align, color…)
      // can format the title and cell text. Chart blocks stay non-editable —
      // they're rendered images, not text.
      if (block.classList.contains("report-table-block")) {
        block.contentEditable = "true";
      }

      this.bindResizableWrapper(wrapper);
    }

    this.normalizeTemporalChartSnapshotsInEditor(editor);

    // Charts/images render fully on load instead of being clipped until clicked.
    this.fitResizableWrappersToPage(editor);
  }

  /**
   * Make charts/images fit the printable page the moment they are hydrated,
   * instead of overflowing the page (which `overflow:hidden` then clips) until
   * the user clicks them. A 900px chart snapshot only fits because of a fragile
   * `max-width:100%` cap that fails during initial hydration/pagination; here we
   * enforce it deterministically:
   *   1. force every chart box (wrapper / report-chart / inner span / image) to
   *      `box-sizing:border-box` + `max-width:100%` so padding/borders can never
   *      add width and push content off the page, and
   *   2. clamp each outermost wrapper to the page CONTENT width (clientWidth minus
   *      the editor's horizontal padding — the earlier bug used clientWidth, which
   *      still left the wrapper ~112px too wide).
   */
  private fitResizableWrappersToPage(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(".resizable-image")
    ) as HTMLElement[];
    for (let i = 0; i < wrappers.length; i += 1) {
      const wrapper = wrappers[i];
      // Only size the outermost wrapper; nested chart wrappers flow fluidly inside it.
      const parent = wrapper.parentElement;
      const nested = !!(parent && parent.closest(".resizable-image"));
      this.fitResizableWrapperWidth(wrapper, nested);
    }

    // Chart snapshots are data-URL images that may report their final layout only
    // after decoding. Re-fit once each finishes so a late decode cannot leave the
    // wrapper oversized and clipped.
    const images = Array.from(
      editor.querySelectorAll(".resizable-image img")
    ) as HTMLImageElement[];
    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      if (img.dataset.fitBound === "1" || img.complete) {
        continue;
      }
      img.dataset.fitBound = "1";
      img.addEventListener(
        "load",
        () => {
          const editorEl = img.closest(".page-editor") as HTMLElement | null;
          if (editorEl) {
            this.fitResizableWrappersToPage(editorEl);
          }
        },
        { once: true }
      );
    }
  }

  private pageContentWidth(pageEditor: HTMLElement): number {
    const style = window.getComputedStyle(pageEditor);
    const padX =
      (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return Math.max(0, pageEditor.clientWidth - padX);
  }

  private fitResizableWrapperWidth(wrapper: HTMLElement, nested: boolean): void {
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    if (!pageEditor) {
      return;
    }
    const contentWidth = this.pageContentWidth(pageEditor);
    if (contentWidth <= 0) {
      return;
    }

    const isChartWrapper = !!wrapper.querySelector(
      ".report-chart, .chart-snapshot-image"
    );

    // Border-box + max-width:100% on every level guarantees no descendant (and no
    // padding/border) can push past its container, so the single fixed outer width
    // below is enough to keep the whole chart on the page.
    if (isChartWrapper) {
      const boxes = [
        wrapper,
        ...(Array.from(
          wrapper.querySelectorAll(
            ".report-chart, .chart-image-resizable, .chart-snapshot-image"
          )
        ) as HTMLElement[]),
      ];
      for (let i = 0; i < boxes.length; i += 1) {
        boxes[i].style.boxSizing = "border-box";
        boxes[i].style.maxWidth = "100%";
      }
      const img = wrapper.querySelector("img") as HTMLImageElement | null;
      if (img) {
        img.style.setProperty("width", "100%", "important");
        img.style.setProperty("max-width", "100%", "important");
        img.style.setProperty("height", "auto", "important");
      }
    } else {
      wrapper.style.boxSizing = "border-box";
      wrapper.style.maxWidth = "100%";
    }

    // Nested wrappers stay fluid (width:100% of the fitted outer wrapper); only the
    // outermost wrapper gets a concrete width clamped to the page content box.
    if (nested) {
      wrapper.style.width = "100%";
      return;
    }

    // Charts are full-width snapshots by design and the document is regenerated
    // fresh on every open, so pin them to the content width (a measured width can
    // be unreliable while the page is still laying out). Plain images keep their
    // own width but are never allowed to exceed the content box.
    const currentWidth = this.getWrapperWidthPx(wrapper);
    const targetWidth = isChartWrapper
      ? Math.floor(contentWidth)
      : Math.max(120, Math.floor(Math.min(currentWidth, contentWidth)));
    wrapper.style.width = `${targetWidth}px`;
    wrapper.dataset.widthPx = String(targetWidth);
    wrapper.style.maxWidth = "100%";
  }

  private normalizeTemporalChartSnapshotsInEditor(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(
        ".resizable-image.chart-image-resizable[data-chart-payload], .resizable-image[data-chart-payload]"
      )
    ) as HTMLElement[];

    for (let i = 0; i < wrappers.length; i += 1) {
      const wrapper = wrappers[i];
      // Once per wrapper. Decoding a payload is a decodeURIComponent + JSON.parse
      // over the chart's whole series, and a chart whose labels need normalising
      // is redrawn on canvas — far too much to repeat for every chart on every
      // page each time the editor re-walks its images. A WeakSet, deliberately
      // NOT a dataset flag: those serialise into pages[i].html, so a re-hydrated
      // document would come back already marked and never normalise at all.
      if (this.normalizedSnapshots.has(wrapper)) {
        continue;
      }
      this.normalizedSnapshots.add(wrapper);

      const payload = this.readChartPayloadFromWrapper(wrapper);
      if (!payload) {
        continue;
      }

      const title = String(payload.title || "");
      const isDailyChart = /\bdaily\b/i.test(title);
      const isTimestampChart = /\bhourly\b/i.test(title) || /\braw\b/i.test(title);
      if (!isDailyChart && !isTimestampChart) {
        continue;
      }

      const current = Array.isArray(payload.chartData?.categories)
        ? payload.chartData.categories.map((c) => String(c || ""))
        : [];
      if (!current.length) {
        continue;
      }

      const normalized = isDailyChart
        ? this.normalizeDailyCategorySequence(
            current.map((c) => this.normalizeCategoryLabelForInterval(c, "daily"))
          )
        : current.map((c) => this.normalizeCategoryLabelForInterval(c, "raw"));
      const changed = normalized.some((value, idx) => value !== current[idx]);
      if (!changed) {
        continue;
      }

      const nextPayload = {
        ...payload,
        chartData: {
          ...payload.chartData,
          categories: normalized,
        },
      };
      const dataUrl = this.drawChartSnapshotDataUrl(
        nextPayload.chartData,
        nextPayload.title,
        nextPayload.options,
        nextPayload.chartType
      );
      if (!dataUrl) {
        continue;
      }

      const image = wrapper.querySelector("img.chart-snapshot-image") as HTMLImageElement | null;
      if (image) {
        image.src = dataUrl;
      }
      wrapper.setAttribute("data-chart-payload", this.encodeChartPayload(nextPayload));
    }
  }

  private bindResizableWrapper(wrapper: HTMLElement): void {
    if (this.boundWrappers.has(wrapper)) {
      return;
    }

    this.boundWrappers.add(wrapper);
    // The handles are absolutely positioned inside the wrapper. Page content is
    // injected as raw innerHTML, so the component stylesheet does not reach it —
    // pin the containing block here instead of relying on CSS.
    if (window.getComputedStyle(wrapper).position === "static") {
      wrapper.style.position = "relative";
    }
    this.ensureResizeHandle(wrapper);
    wrapper.addEventListener("mousedown", (event) => {
      event.stopPropagation();
      this.selectResizableWrapper(wrapper);

      // Arm the move-drag here as well as on the editor: this listener stops
      // propagation, so onEditorMouseDown never sees a click on a wrapper.
      const target = event.target as Element | null;
      if (!target || target.closest(".image-handle")) {
        return; // resize / rotate / grip handles own their own gesture
      }
      if (this.isTextEditingTarget(target)) {
        return; // inside an editable table island → let text selection happen
      }
      this.armVisualDrag(this.getDraggableUnit(target) || wrapper, event);
    });
    wrapper.addEventListener("click", (event) => {
      event.stopPropagation();
      this.selectResizableWrapper(wrapper);
    });
  }

  private ensureResizeHandle(wrapper: HTMLElement): void {
    const rightHandle =
      (wrapper.querySelector(".image-resize-handle-right") as HTMLElement | null) ||
      document.createElement("span");
    if (!rightHandle.parentElement) {
      rightHandle.className =
        "image-handle image-resize-handle image-resize-handle-right";
      rightHandle.setAttribute("data-resize-dir", "x");
      rightHandle.contentEditable = "false";
      wrapper.appendChild(rightHandle);
    }

    const bottomHandle =
      (wrapper.querySelector(".image-resize-handle-bottom") as HTMLElement | null) ||
      document.createElement("span");
    if (!bottomHandle.parentElement) {
      bottomHandle.className =
        "image-handle image-resize-handle image-resize-handle-bottom";
      bottomHandle.setAttribute("data-resize-dir", "y");
      bottomHandle.contentEditable = "false";
      wrapper.appendChild(bottomHandle);
    }

    const cornerHandle =
      (wrapper.querySelector(".image-resize-handle-corner") as HTMLElement | null) ||
      document.createElement("span");
    if (!cornerHandle.parentElement) {
      cornerHandle.className =
        "image-handle image-resize-handle image-resize-handle-corner";
      cornerHandle.setAttribute("data-resize-dir", "xy");
      cornerHandle.contentEditable = "false";
      wrapper.appendChild(cornerHandle);
    }

    const rotateHandle =
      (wrapper.querySelector(".image-rotate-handle") as HTMLElement | null) ||
      document.createElement("span");
    if (!rotateHandle.parentElement) {
      rotateHandle.className = "image-handle image-rotate-handle";
      rotateHandle.setAttribute("data-handle-action", "rotate");
      rotateHandle.contentEditable = "false";
      wrapper.appendChild(rotateHandle);
    }

    // Grip: the unambiguous way to move a visual. Body-dragging still works, but
    // the grip also covers blocks whose body is editable text (report tables),
    // where a body drag has to stay a text selection. Styled inline because the
    // component stylesheet does not reach innerHTML-injected page content.
    const dragHandle =
      (wrapper.querySelector(":scope > .image-drag-handle") as HTMLElement | null) ||
      document.createElement("span");
    if (!dragHandle.parentElement) {
      dragHandle.className = "image-handle image-drag-handle";
      dragHandle.setAttribute("data-handle-action", "drag");
      dragHandle.setAttribute("title", "Drag to move");
      dragHandle.contentEditable = "false";
      dragHandle.textContent = "\u283F";
      dragHandle.style.cssText =
        "position:absolute;left:-1px;top:-1px;z-index:3;display:none;" +
        "align-items:center;justify-content:center;width:22px;height:22px;" +
        "border-radius:6px;border:1px solid #93c5fd;background:#ffffff;" +
        "color:#2563eb;font-size:12px;line-height:1;cursor:grab;" +
        "user-select:none;box-shadow:0 2px 6px rgba(15,23,42,0.18);";
      wrapper.appendChild(dragHandle);
    }

    const handles = [
      rightHandle,
      bottomHandle,
      cornerHandle,
      rotateHandle,
      dragHandle,
    ];
    for (let i = 0; i < handles.length; i += 1) {
      const handle = handles[i];
      if (this.boundHandles.has(handle)) {
        continue;
      }
      this.boundHandles.add(handle);
      handle.addEventListener("mousedown", (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const action = String(handle.getAttribute("data-handle-action") || "");
        if (action === "drag") {
          this.selectResizableWrapper(wrapper);
          this.armVisualDrag(this.getDraggableUnit(wrapper) || wrapper, event, true);
          return;
        }
        if (action === "rotate") {
          this.selectResizableWrapper(wrapper);
          this.startRotateDrag(wrapper, event);
          return;
        }

        const dir = String(handle.getAttribute("data-resize-dir") || "xy");
        this.selectResizableWrapper(wrapper);
        this.startResizeDrag(wrapper, event, dir);
      });
    }
  }

  private startResizeDrag(wrapper: HTMLElement, event: MouseEvent, direction: string): void {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = wrapper.offsetWidth;
    const startHeight = wrapper.offsetHeight;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      if (direction.includes("x")) {
        const deltaX = moveEvent.clientX - startX;
        const nextWidth = Math.max(160, startWidth + deltaX);
        wrapper.style.width = `${Math.round(nextWidth)}px`;
        wrapper.style.maxWidth = "none";
      }
      if (direction.includes("y")) {
        const deltaY = moveEvent.clientY - startY;
        const nextHeight = Math.max(120, startHeight + deltaY);
        wrapper.style.height = `${Math.round(nextHeight)}px`;
      }
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private startRotateDrag(wrapper: HTMLElement, event: MouseEvent): void {
    event.preventDefault();
    const startRotation = this.getWrapperRotation(wrapper);
    const rect = wrapper.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle =
      (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) /
      Math.PI;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const moveAngle =
        (Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX) *
          180) /
        Math.PI;
      const nextRotation = startRotation + (moveAngle - startAngle);
      this.applyWrapperRotation(wrapper, nextRotation);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private selectResizableWrapper(wrapper: HTMLElement | null): void {
    if (this.selectedResizableWrapper === wrapper) {
      return;
    }

    if (this.selectedResizableWrapper) {
      this.selectedResizableWrapper.classList.remove("is-selected");
      this.setDragHandleVisible(this.selectedResizableWrapper, false);
    }

    this.selectedResizableWrapper = wrapper;
    this.imageToolbarExpanded = false;
    if (wrapper) {
      // Resize handles behave reliably when width is fixed in px (not %/auto).
      const inlineWidth = (wrapper.style.width || "").trim();
      if (!inlineWidth || inlineWidth.endsWith("%") || inlineWidth === "auto") {
        const currentWidth = wrapper.offsetWidth;
        const containerWidth = wrapper.parentElement?.clientWidth || 0;
        const clampedWidth =
          containerWidth > 0 ? Math.min(currentWidth, containerWidth) : currentWidth;
        if (currentWidth > 0) {
          this.setWrapperWidthPx(wrapper, clampedWidth);
        }
      }
      const inlineHeight = (wrapper.style.height || "").trim();
      if (!inlineHeight || inlineHeight === "auto") {
        const currentHeight = wrapper.offsetHeight;
        if (currentHeight > 0) {
          wrapper.style.height = `${Math.round(currentHeight)}px`;
        }
      }

      // Keep selected media within the editable page width so handles stay reachable.
      const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
      const boundWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
      if (boundWidth > 0) {
        const currentWidth = this.getWrapperWidthPx(wrapper);
        if (currentWidth > boundWidth) {
          this.setWrapperWidthPx(wrapper, boundWidth);
        } else {
          this.setWrapperWidthPx(wrapper, currentWidth);
        }
        wrapper.style.maxWidth = "100%";
      }

      wrapper.classList.add("is-selected");
      this.setDragHandleVisible(wrapper, true);
      this.updateFloatingImageToolbar();
      return;
    }

    this.imageToolbarVisible = false;
    this.imageToolbarExpanded = false;
  }

  /** Reveal the move grip on the selected visual only. */
  private setDragHandleVisible(wrapper: HTMLElement, visible: boolean): void {
    const handle = wrapper.querySelector(
      ":scope > .image-drag-handle"
    ) as HTMLElement | null;
    if (handle) {
      handle.style.display = visible ? "flex" : "none";
    }
  }

  private getWrapperRotation(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.rotationDeg);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const match = transform.match(/rotate\((-?\d+(?:\.\d+)?)deg\)/i);
    return match ? Number(match[1]) : 0;
  }

  private getWrapperScale(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.scaleVal);
    if (!Number.isNaN(fromData) && fromData > 0) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const match = transform.match(/scale\((-?\d+(?:\.\d+)?)\)/i);
    const parsed = match ? Number(match[1]) : 1;
    return Number.isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  }

  private applyWrapperRotation(wrapper: HTMLElement, angle: number): void {
    const scale = this.getWrapperScale(wrapper);
    const offsetX = this.getWrapperOffsetX(wrapper);
    const offsetY = this.getWrapperOffsetY(wrapper);
    this.applyWrapperTransform(wrapper, angle, scale, offsetX, offsetY);
  }

  private applyWrapperScale(wrapper: HTMLElement, scale: number): void {
    const angle = this.getWrapperRotation(wrapper);
    const offsetX = this.getWrapperOffsetX(wrapper);
    const offsetY = this.getWrapperOffsetY(wrapper);
    this.applyWrapperTransform(wrapper, angle, scale, offsetX, offsetY);
  }

  private applyWrapperTransform(
    wrapper: HTMLElement,
    angle: number,
    scale: number,
    offsetX?: number,
    offsetY?: number
  ): void {
    const normalized = Math.round(angle * 10) / 10;
    const boundedScale = Math.max(0.3, Math.min(3, scale));
    const normalizedOffsetX = Math.round((offsetX ?? this.getWrapperOffsetX(wrapper)) * 10) / 10;
    const normalizedOffsetY = Math.round((offsetY ?? this.getWrapperOffsetY(wrapper)) * 10) / 10;
    wrapper.dataset.rotationDeg = String(normalized);
    wrapper.dataset.scaleVal = String(Math.round(boundedScale * 1000) / 1000);
    wrapper.dataset.offsetXPx = String(normalizedOffsetX);
    wrapper.dataset.offsetYPx = String(normalizedOffsetY);
    // Apply transform on the concrete content (img/report-chart) for reliable visual rotation.
    const transformTarget = this.getTransformTarget(wrapper) || wrapper;
    wrapper.style.transform = "none";
    wrapper.style.transformOrigin = "";
    transformTarget.style.transformOrigin = "center center";
    transformTarget.style.transform = `translate(${normalizedOffsetX}px, ${normalizedOffsetY}px) rotate(${normalized}deg) scale(${boundedScale})`;
    this.updateFloatingImageToolbar();
  }

  zoomSelectedImageIn(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    this.resizeWrapperByFactor(wrapper, 1.08);
    this.syncPagesFromDom();
  }

  zoomSelectedImageOut(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    this.resizeWrapperByFactor(wrapper, 1 / 1.08);
    this.syncPagesFromDom();
  }

  rotateSelectedImageLeft(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextAngle = this.getWrapperRotation(wrapper) - 15;
    this.applyWrapperRotation(wrapper, nextAngle);
    this.syncPagesFromDom();
  }

  rotateSelectedImageRight(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextAngle = this.getWrapperRotation(wrapper) + 15;
    this.applyWrapperRotation(wrapper, nextAngle);
    this.syncPagesFromDom();
  }

  resetSelectedImageTransform(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    const boundWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
    if (boundWidth > 0) {
      this.setWrapperWidthPx(wrapper, boundWidth);
    }
    this.applyWrapperTransform(wrapper, 0, 1, 0, 0);
    this.syncPagesFromDom();
  }

  slideSelectedImageLeft(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextOffset = this.getWrapperOffsetX(wrapper) - 20;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      nextOffset,
      this.getWrapperOffsetY(wrapper)
    );
    this.syncPagesFromDom();
  }

  slideSelectedImageRight(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextOffset = this.getWrapperOffsetX(wrapper) + 20;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      nextOffset,
      this.getWrapperOffsetY(wrapper)
    );
    this.syncPagesFromDom();
  }

  moveSelectedImageUp(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextOffsetY = this.getWrapperOffsetY(wrapper) - 1;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      this.getWrapperOffsetX(wrapper),
      nextOffsetY
    );
    this.syncPagesFromDom();
  }

  moveSelectedImageDown(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    const nextOffsetY = this.getWrapperOffsetY(wrapper) + 1;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      this.getWrapperOffsetX(wrapper),
      nextOffsetY
    );
    this.syncPagesFromDom();
  }

  deleteSelectedVisual(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    wrapper.remove();
    this.selectResizableWrapper(null);
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  openSelectedVisualEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    // Route to the table editor whenever the selected block holds a report table
    // (wrapped as .report-table-block, or just a .report-data-table inside a
    // chart-image/section container). Otherwise edit it as a chart.
    if (this.findSelectedTableContainer(wrapper)) {
      this.openSelectedTableEditor();
      return;
    }
    this.openSelectedChartEditor();
  }

  /**
   * Resolve the element that holds the selected report table — works whether the
   * table is wrapped as `.report-table-block` or is a bare `.report-data-table`
   * nested in a chart-image / report-block container. Returns null when the
   * selection has no table.
   */
  private findSelectedTableContainer(wrapper: HTMLElement): HTMLElement | null {
    // The wrapper may BE the table block (querySelector only matches descendants).
    if (wrapper.matches(".report-table-block")) {
      return wrapper;
    }
    const direct = wrapper.querySelector(
      ".report-table-block"
    ) as HTMLElement | null;
    if (direct) {
      return direct;
    }
    const table = wrapper.querySelector(
      "table.report-data-table, table"
    ) as HTMLElement | null;
    if (table) {
      return (
        (table.closest(
          ".report-table-block, .report-chart-image, .report-block"
        ) as HTMLElement | null) ||
        (table.parentElement as HTMLElement | null) ||
        table
      );
    }
    return null;
  }

  openSelectedChartEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    this.chartEditorMessage = "";
    if (!wrapper) {
      this.chartEditorMessage = "Select a chart first.";
      return;
    }

    const payload = this.readChartPayloadFromWrapper(wrapper);
    if (!payload) {
      this.chartEditorMessage =
        "Selected element is not an editable chart snapshot.";
      return;
    }

    this.chartEditorTargetWrapper = wrapper;
    this.chartEditorBasePayload = payload;
    // The painted title, which is blank for report charts (the name shows in the
    // heading above instead) — typing one here paints it. Charts saved before
    // titleText existed have their baked-in report name here, as before.
    this.chartEditorTitle = payload.options?.titleText ?? payload.title ?? "";
    this.chartEditorChartType =
      payload.chartType || payload.chartData.series?.[0]?.type || "bar";
    this.chartEditorTitleSize = this.normalizeChartTitleSize(
      payload.options?.titleFontSize
    );
    this.chartEditorTitleColor = payload.options?.titleColor || "#f8fafc";
    this.chartEditorBgStart = payload.options?.bgStart || "#1f2937";
    this.chartEditorBgCenter = payload.options?.bgCenter || "#2b3444";
    this.chartEditorBgEnd = payload.options?.bgEnd || "#3f3f46";
    this.chartEditorBgOuter = payload.options?.bgOuter || "#111827";
    this.chartEditorDateLabelMode =
      payload.options?.dateLabelMode === "all" ? "all" : "auto";
    this.chartEditorDateLabelRotation = this.normalizeDateLabelRotation(
      payload.options?.dateLabelRotation
    );
    this.chartEditorValueLabelSize = this.normalizeValueLabelSize(
      payload.options?.valueLabelSize
    );
    this.chartEditorValueLabelRotation = this.normalizeValueLabelRotation(
      payload.options?.valueLabelRotation
    );
    this.chartEditorLegendSize = this.normalizeLegendSize(
      payload.options?.legendFontSize
    );
    this.chartEditorXAxisLabelSize = this.normalizeAxisLabelSize(
      payload.options?.xAxisLabelSize
    );
    this.chartEditorYAxisLabelSize = this.normalizeAxisLabelSize(
      payload.options?.yAxisLabelSize
    );
    this.chartEditorTitlePadTop = this.normalizeTitlePad(payload.options?.titlePadTop);
    this.chartEditorTitlePadRight = this.normalizeTitlePad(payload.options?.titlePadRight);
    this.chartEditorTitlePadBottom = this.normalizeTitlePad(payload.options?.titlePadBottom);
    this.chartEditorTitlePadLeft = this.normalizeTitlePad(payload.options?.titlePadLeft);
    this.chartEditorMultiAxis = !!payload.options?.multiAxis;
    const savedAxisIndices = Array.isArray(payload.options?.multiAxisIndices)
      ? payload.options!.multiAxisIndices!
      : [];
    this.chartEditorSeriesAxis = payload.chartData.series.map((_, i) =>
      savedAxisIndices.length > 0 ? savedAxisIndices.includes(i) : true
    );
    this.chartEditorSeriesNames = payload.chartData.series.map(
      (s, i) => s.name || `Series ${i + 1}`
    );
    const editorPalette = this.ensureDistinctColors(
      this.defaultChartPalette,
      payload.chartData.series.length
    );
    this.chartEditorSeriesColors = payload.chartData.series.map(
      (_, i) => payload.options?.seriesColors?.[i] || editorPalette[i]
    );
    this.refreshChartEditorPreview();
    this.chartEditorOpen = true;
  }

  openSelectedTableEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    this.tableEditorMessage = "";
    if (!wrapper) {
      this.tableEditorMessage = "Select a table first.";
      return;
    }

    const tableBlock = this.findSelectedTableContainer(wrapper);
    if (!tableBlock) {
      this.tableEditorMessage = "Selected element is not a table.";
      return;
    }

    const title = tableBlock.querySelector(".report-chart-title") as HTMLElement | null;
    const headerCell = tableBlock.querySelector("thead th") as HTMLElement | null;
    const oddCell = tableBlock.querySelector("tbody tr:nth-child(odd) td") as HTMLElement | null;
    const evenCell = tableBlock.querySelector("tbody tr:nth-child(even) td") as HTMLElement | null;
    const anyCell = tableBlock.querySelector("tbody td") as HTMLElement | null;

    const titleStyle = title ? window.getComputedStyle(title) : null;
    const headerStyle = headerCell ? window.getComputedStyle(headerCell) : null;
    const oddStyle = oddCell ? window.getComputedStyle(oddCell) : null;
    const evenStyle = evenCell ? window.getComputedStyle(evenCell) : null;
    const anyStyle = anyCell ? window.getComputedStyle(anyCell) : null;
    const blockStyle = window.getComputedStyle(tableBlock);

    this.tableEditorTitleBg = this.toHexColor(titleStyle?.backgroundColor, "#0b2345");
    this.tableEditorHeaderBg = this.toHexColor(headerStyle?.backgroundColor, "#021b39");
    this.tableEditorOddRowBg = this.toHexColor(oddStyle?.backgroundColor, "#15335a");
    this.tableEditorEvenRowBg = this.toHexColor(evenStyle?.backgroundColor, "#001a3a");
    this.tableEditorTextColor = this.toHexColor(anyStyle?.color, "#f8fafc");
    this.tableEditorBorderColor = this.toHexColor(blockStyle.borderColor, "#2b3f59");

    const align = String(titleStyle?.textAlign || "left").toLowerCase();
    this.tableEditorTitleAlign =
      align === "center" || align === "right" ? align : "left";
    this.tableEditorTitleSize = this.normalizeTableTitleSize(
      titleStyle ? parseFloat(titleStyle.fontSize) : 13
    );

    this.tableEditorTargetWrapper = wrapper;
    this.refreshTableEditorPreview();
    this.tableEditorOpen = true;
  }

  closeChartEditor(): void {
    this.chartEditorOpen = false;
    this.chartEditorMessage = "";
    this.chartEditorPreviewDataUrl = "";
    this.chartEditorBasePayload = null;
    this.chartEditorTargetWrapper = null;
    this.chartEditorDateLabelMode = "auto";
    this.chartEditorDateLabelRotation = -40;
    this.chartEditorValueLabelSize = 11;
    this.chartEditorValueLabelRotation = 0;
    this.chartEditorLegendSize = 15;
    this.chartEditorXAxisLabelSize = 12;
    this.chartEditorYAxisLabelSize = 12;
    this.chartEditorTitlePadTop = 0;
    this.chartEditorTitlePadRight = 0;
    this.chartEditorTitlePadBottom = 0;
    this.chartEditorTitlePadLeft = 0;
    this.chartEditorMultiAxis = false;
    this.chartEditorSeriesAxis = [];
    this.chartEditorTitleSize = 34;
    this.chartEditorTitleColor = "#f8fafc";
    this.chartEditorChartType = "bar";
  }

  closeTableEditor(): void {
    this.tableEditorOpen = false;
    this.tableEditorMessage = "";
    this.tableEditorTargetWrapper = null;
    this.tableEditorPreviewHtml = "";
    this.tableEditorTitleAlign = "left";
    this.tableEditorTitleSize = 13;
  }

  onChartEditorFieldChange(): void {
    this.refreshChartEditorPreview();
  }

  onMultiAxisToggle(): void {
    // When enabling multi-axis with nothing selected yet, default to all series.
    if (this.chartEditorMultiAxis && !this.chartEditorSeriesAxis.some(Boolean)) {
      this.chartEditorSeriesAxis = this.chartEditorSeriesAxis.map(() => true);
    }
    this.refreshChartEditorPreview();
  }

  applyChartEditor(): void {
    const wrapper = this.chartEditorTargetWrapper;
    if (!wrapper) {
      this.chartEditorMessage = "No selected chart target.";
      return;
    }
    const nextPayload = this.buildChartEditorPayload();
    if (!nextPayload) {
      this.chartEditorMessage = "Unable to read chart data from selected item.";
      return;
    }

    const nextDataUrl = this.drawChartSnapshotDataUrl(
      nextPayload.chartData,
      nextPayload.title,
      nextPayload.options,
      nextPayload.chartType
    );
    if (!nextDataUrl) {
      this.chartEditorMessage = "Failed to regenerate chart image.";
      return;
    }

    const targetImage = wrapper.querySelector("img.chart-snapshot-image") as HTMLImageElement | null;
    if (!targetImage) {
      this.chartEditorMessage = "Target chart image not found.";
      return;
    }

    targetImage.src = nextDataUrl;
    targetImage.alt = nextPayload.title;
    wrapper.setAttribute("data-chart-payload", this.encodeChartPayload(nextPayload));

    // Keep the chart-type-* class on the outer block in sync with the new type.
    const chartImageBlock = wrapper.closest(".report-chart-image") as HTMLElement | null;
    if (chartImageBlock) {
      const staleTypeClasses = Array.from(chartImageBlock.classList).filter((cls) =>
        cls.startsWith("chart-type-")
      );
      staleTypeClasses.forEach((cls) => chartImageBlock.classList.remove(cls));
      chartImageBlock.classList.add(`chart-type-${nextPayload.chartType}`);
    }
    this.chartEditorBasePayload = nextPayload;
    this.chartEditorPreviewDataUrl = nextDataUrl;

    this.syncPagesFromDom();
    this.closeChartEditor();
  }

  applyTableEditor(): void {
    const wrapper = this.tableEditorTargetWrapper;
    if (!wrapper) {
      this.tableEditorMessage = "No selected table target.";
      return;
    }

    const tableBlock = this.findSelectedTableContainer(wrapper);
    if (!tableBlock) {
      this.tableEditorMessage = "Target table not found.";
      return;
    }

    const tableId = String(tableBlock.getAttribute("data-table-id") || "").trim();
    const allEditors = this.pageEditors?.toArray().map((ref) => ref.nativeElement) || [];
    const targets: HTMLElement[] = [];
    for (let i = 0; i < allEditors.length; i += 1) {
      const editor = allEditors[i];
      if (!tableId) {
        const direct = editor.querySelector(".report-table-block") as HTMLElement | null;
        if (direct) {
          targets.push(direct);
        }
        continue;
      }
      const blocks = Array.from(
        editor.querySelectorAll(`.report-table-block[data-table-id="${tableId}"]`)
      ) as HTMLElement[];
      targets.push(...blocks);
    }

    const uniqueTargets = Array.from(new Set(targets));
    for (let t = 0; t < uniqueTargets.length; t += 1) {
      this.applyTableStyleTo(uniqueTargets[t]);
    }

    this.syncPagesFromDom();
    this.closeTableEditor();
  }

  /** Apply the current Edit-Table-Style colors to one report-table block. */
  private applyTableStyleTo(block: HTMLElement): void {
    block.style.borderColor = this.tableEditorBorderColor;

    const title = block.querySelector(".report-chart-title") as HTMLElement | null;
    if (title) {
      title.style.background = this.tableEditorTitleBg;
      title.style.color = this.tableEditorTextColor;
      title.style.borderBottomColor = this.tableEditorBorderColor;
      title.style.textAlign = this.tableEditorTitleAlign;
      title.style.fontSize = `${this.normalizeTableTitleSize(
        this.tableEditorTitleSize
      )}px`;
    }

    const headCells = block.querySelectorAll("thead th");
    for (let i = 0; i < headCells.length; i += 1) {
      const cell = headCells[i] as HTMLElement;
      cell.style.background = this.tableEditorHeaderBg;
      cell.style.color = this.tableEditorTextColor;
      cell.style.borderColor = this.tableEditorBorderColor;
    }

    const rows = block.querySelectorAll("tbody tr");
    for (let r = 0; r < rows.length; r += 1) {
      const row = rows[r] as HTMLElement;
      const bg = r % 2 === 0 ? this.tableEditorOddRowBg : this.tableEditorEvenRowBg;
      const cells = row.querySelectorAll("th,td");
      for (let c = 0; c < cells.length; c += 1) {
        const cell = cells[c] as HTMLElement;
        cell.style.background = bg;
        cell.style.color = this.tableEditorTextColor;
        cell.style.borderColor = this.tableEditorBorderColor;
      }
    }
  }

  /** Field-change handler for the Edit Table Style dialog → refresh the preview. */
  onTableEditorFieldChange(): void {
    this.refreshTableEditorPreview();
  }

  private normalizeTableTitleSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 13;
    }
    return Math.max(10, Math.min(40, Math.round(parsed)));
  }

  /** Rebuild the live preview shown inside the Edit Table Style dialog. */
  private refreshTableEditorPreview(): void {
    const wrapper = this.tableEditorTargetWrapper;
    const container = wrapper
      ? this.findSelectedTableContainer(wrapper)
      : null;
    if (!container) {
      this.tableEditorPreviewHtml = "";
      return;
    }
    const clone = container.cloneNode(true) as HTMLElement;
    this.applyTableStyleTo(clone);
    clone.style.margin = "0";
    clone.style.maxWidth = "100%";
    this.tableEditorPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(
      clone.outerHTML
    );
  }

  private buildChartEditorPayload(): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    const base = this.chartEditorBasePayload;
    if (!base) {
      return null;
    }

    const updatedSeries = base.chartData.series.map((series, i) => ({
      ...series,
      name: String(this.chartEditorSeriesNames[i] || series.name || `Series ${i + 1}`).trim(),
      type: this.chartEditorChartType,
    }));

    const options: NonNullable<typeof base.options> = {
      ...(base.options || {}),
      bgStart: this.chartEditorBgStart || "#1f2937",
      bgCenter: this.chartEditorBgCenter || "#2b3444",
      bgEnd: this.chartEditorBgEnd || "#3f3f46",
      bgOuter: this.chartEditorBgOuter || "#111827",
      dateLabelMode: this.chartEditorDateLabelMode,
      dateLabelRotation: this.normalizeDateLabelRotation(
        this.chartEditorDateLabelRotation
      ),
      valueLabelSize: this.normalizeValueLabelSize(this.chartEditorValueLabelSize),
      valueLabelRotation: this.normalizeValueLabelRotation(
        this.chartEditorValueLabelRotation
      ),
      xAxisLabelSize: this.normalizeAxisLabelSize(this.chartEditorXAxisLabelSize),
      yAxisLabelSize: this.normalizeAxisLabelSize(this.chartEditorYAxisLabelSize),
      legendFontSize: this.normalizeLegendSize(this.chartEditorLegendSize),
      titlePadTop: this.normalizeTitlePad(this.chartEditorTitlePadTop),
      titlePadRight: this.normalizeTitlePad(this.chartEditorTitlePadRight),
      titlePadBottom: this.normalizeTitlePad(this.chartEditorTitlePadBottom),
      titlePadLeft: this.normalizeTitlePad(this.chartEditorTitlePadLeft),
      multiAxis: this.chartEditorMultiAxis,
      multiAxisIndices: base.chartData.series
        .map((_, i) => i)
        .filter((i) => this.chartEditorSeriesAxis[i]),
      titleFontSize: this.normalizeChartTitleSize(this.chartEditorTitleSize),
      titleColor: this.chartEditorTitleColor || "#f8fafc",
      titleText: this.chartEditorTitle || "",
      seriesColors: (() => {
        const palette = this.ensureDistinctColors(
          this.defaultChartPalette,
          updatedSeries.length
        );
        return updatedSeries.map(
          (_, i) => this.chartEditorSeriesColors[i] || palette[i]
        );
      })(),
    };

    return {
      ...base,
      chartType: this.chartEditorChartType,
      // `title` stays the report name — the editor's title box feeds
      // options.titleText. Overwriting it here used to silently change the
      // daily/hourly date formatting, which keys off this string.
      title: base.title || "",
      chartData: {
        ...base.chartData,
        series: updatedSeries,
      },
      options,
    };
  }

  private refreshChartEditorPreview(): void {
    const nextPayload = this.buildChartEditorPayload();
    if (!nextPayload) {
      this.chartEditorPreviewDataUrl = "";
      return;
    }
    this.chartEditorPreviewDataUrl = this.drawChartSnapshotDataUrl(
      nextPayload.chartData,
      nextPayload.title,
      nextPayload.options,
      nextPayload.chartType
    );
  }

  private resizeWrapperByFactor(wrapper: HTMLElement, factor: number): void {
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    const maxWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
    const image = wrapper.querySelector("img") as HTMLImageElement | null;
    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    const renderedImageWidth = image?.getBoundingClientRect().width || 0;
    const renderedChartWidth = chartBlock?.getBoundingClientRect().width || 0;
    const currentWidth =
      renderedImageWidth > 0
        ? renderedImageWidth
        : renderedChartWidth > 0
          ? renderedChartWidth
          : this.getWrapperWidthPx(wrapper);
    const minWidth = 120;
    let nextWidth = Math.round(currentWidth * factor);
    if (maxWidth > 0) {
      nextWidth = Math.min(nextWidth, Math.floor(maxWidth));
    }
    nextWidth = Math.max(minWidth, nextWidth);
    this.setWrapperWidthPx(wrapper, nextWidth);
    // Keep scale normalized so width controls are deterministic.
    const angle = this.getWrapperRotation(wrapper);
    this.applyWrapperTransform(wrapper, angle, 1);
    this.updateFloatingImageToolbar();
  }

  /** Expand/collapse the floating image & chart toolbar. */
  toggleImageToolbar(): void {
    this.imageToolbarExpanded = !this.imageToolbarExpanded;
    this.updateFloatingImageToolbar();
  }

  private updateFloatingImageToolbar(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      this.imageToolbarVisible = false;
      this.imageToolbarExpanded = false;
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    // Sit just above the visual when there is room, otherwise tuck inside its
    // top edge so the chip never leaves the viewport.
    const above = rect.top - 40;
    this.imageToolbarTop = above >= 8 ? above : Math.max(8, rect.top + 8);
    // Right-anchored (not left) so the collapsed chip stays pinned to the
    // visual's right corner and the expanded row opens leftwards from it.
    this.imageToolbarRight = Math.max(
      8,
      Math.min(window.innerWidth - rect.right, window.innerWidth - 48)
    );
    this.imageToolbarVisible = true;
  }

  // ----------------------------------------------------------------------------
  // Structural table editing (insert/delete rows & columns) for user tables.
  // Report data tables (.report-data-table / .report-table-block) are excluded:
  // they are regenerated/split by the pagination engine and have their own
  // style editor, so structural edits there are unsafe.
  // ----------------------------------------------------------------------------

  /** Resolve the editable table + cell that contains the given node, if any. */
  private getEditableTableFromNode(
    node: Node | null
  ): { table: HTMLTableElement; cell: HTMLTableCellElement } | null {
    if (!node) {
      return null;
    }
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    const cell = el?.closest("td,th") as HTMLTableCellElement | null;
    if (!cell) {
      return null;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return null;
    }
    // Skip report data tables and anything outside an editor.
    if (
      table.classList.contains("report-data-table") ||
      cell.closest(".report-table-block") ||
      !cell.closest(".page-editor")
    ) {
      return null;
    }
    return { table, cell };
  }

  /** Show/position the table toolbar based on the current caret/selection. */
  private updateTableToolbarFromSelection(): void {
    const selection = window.getSelection();
    const node = selection && selection.rangeCount ? selection.anchorNode : null;
    this.updateTableToolbarForNode(node);
  }

  /** Show/position the table toolbar for a specific node (e.g. a click target). */
  private updateTableToolbarForNode(node: Node | null): void {
    const found = this.getEditableTableFromNode(node);
    if (!found) {
      this.tableToolbarVisible = false;
      this.selectedTableCell = null;
      return;
    }

    this.selectedTableCell = found.cell;
    const rect = found.table.getBoundingClientRect();
    this.tableToolbarTop = Math.max(8, rect.top - 44);
    this.tableToolbarLeft = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - 380)
    );
    this.tableToolbarVisible = true;
  }

  private getCellColumnIndex(cell: HTMLTableCellElement): number {
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row) {
      return -1;
    }
    return Array.prototype.indexOf.call(row.cells, cell);
  }

  /** Re-paginate and refresh the toolbar after a structural table change. */
  private afterTableEdit(): void {
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
    setTimeout(() => this.updateTableToolbarFromSelection(), 0);
  }

  insertTableRow(above: boolean): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row || !row.parentElement) {
      return;
    }
    const columnCount = row.cells.length;
    const newRow = document.createElement("tr");
    for (let i = 0; i < columnCount; i += 1) {
      const td = document.createElement("td");
      td.innerHTML = "&nbsp;";
      newRow.appendChild(td);
    }
    if (above) {
      row.parentElement.insertBefore(newRow, row);
    } else {
      row.parentElement.insertBefore(newRow, row.nextSibling);
    }
    this.afterTableEdit();
  }

  insertTableColumn(after: boolean): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    const rows = Array.from(table.rows);
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const reference = row.cells[columnIndex] || null;
      const isHeader = reference
        ? reference.tagName === "TH"
        : !!row.querySelector("th");
      const newCell = document.createElement(isHeader ? "th" : "td");
      newCell.innerHTML = "&nbsp;";
      if (after) {
        row.insertBefore(newCell, reference ? reference.nextSibling : null);
      } else {
        row.insertBefore(newCell, reference);
      }
    }
    this.afterTableEdit();
  }

  deleteTableRow(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const row = cell.parentElement as HTMLTableRowElement | null;
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!row || !table) {
      return;
    }
    if (table.rows.length <= 1) {
      this.deleteTable();
      return;
    }
    row.remove();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  deleteTableColumn(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    const rows = Array.from(table.rows);
    const columnCount = rows[0]?.cells.length || 0;
    if (columnCount <= 1) {
      this.deleteTable();
      return;
    }
    for (let i = 0; i < rows.length; i += 1) {
      const target = rows[i].cells[columnIndex];
      if (target) {
        target.remove();
      }
    }
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  deleteTable(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    table.remove();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  /**
   * Transpose the selected table — swap its rows and columns. Lets the user flip
   * a wide "parameters-as-columns" report table into a tall "parameters-as-rows"
   * one (and back again). The original first column becomes the new header row
   * and the original header row becomes the new first column; the table is
   * rebuilt with the standard white/black styling and re-paginated.
   */
  transposeSelectedTable(): void {
    const cell = this.selectedTableCell;
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!table || !this.transposeTableElement(table)) {
      return;
    }
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  /** Transpose the table inside the currently open Edit Table Style dialog. */
  transposeTableFromEditor(): void {
    const wrapper = this.tableEditorTargetWrapper;
    if (!wrapper) {
      this.tableEditorMessage = "No selected table target.";
      return;
    }
    const container = this.findSelectedTableContainer(wrapper);
    const table = container?.querySelector(
      "table"
    ) as HTMLTableElement | null;
    if (!table || !this.transposeTableElement(table)) {
      this.tableEditorMessage = "Could not switch rows and columns.";
      return;
    }
    this.tableEditorMessage = "Switched rows and columns.";
    this.refreshTableEditorPreview();
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  /**
   * Swap a table's rows and columns in place (rebuilds its innerHTML). The
   * original first column becomes the new header row and the original header row
   * becomes the new first column, re-applying the standard white/black styling.
   * Returns false when the table is too small to transpose.
   */
  private transposeTableElement(table: HTMLTableElement): boolean {
    const rows = Array.from(table.rows);
    if (!rows.length) {
      return false;
    }
    const colCount = rows.reduce((m, r) => Math.max(m, r.cells.length), 0);
    if (colCount < 2 && rows.length < 2) {
      return false;
    }

    // Snapshot the current grid (cell HTML only — styling is re-applied below).
    const grid: string[][] = [];
    for (let r = 0; r < rows.length; r += 1) {
      grid[r] = [];
      for (let c = 0; c < colCount; c += 1) {
        const el = rows[r].cells[c] as HTMLTableCellElement | undefined;
        grid[r][c] = el ? el.innerHTML : "";
      }
    }

    const headStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const oddStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    // New header row = original first column (one cell per original row).
    let thead = "<thead><tr>";
    for (let r = 0; r < rows.length; r += 1) {
      thead += `<th style="${headStyle}">${grid[r][0] ?? ""}</th>`;
    }
    thead += "</tr></thead>";

    // New body: one row per original column (skipping column 0, now the header).
    let tbody = "<tbody>";
    for (let c = 1; c < colCount; c += 1) {
      tbody += "<tr>";
      for (let r = 0; r < rows.length; r += 1) {
        // Original header row (r === 0) becomes the new first (label) column.
        if (r === 0) {
          tbody += `<th style="${headStyle}">${grid[r][c] ?? ""}</th>`;
        } else {
          const style = c % 2 === 1 ? oddStyle : evenStyle;
          tbody += `<td style="${style}">${grid[r][c] ?? ""}</td>`;
        }
      }
      tbody += "</tr>";
    }
    tbody += "</tbody>";

    table.innerHTML = thead + tbody;
    return true;
  }

  // ----------------------------------------------------------------------------
  // Cell / row / column background color.
  // ----------------------------------------------------------------------------

  /** Capture the cells to color before the color picker steals focus. */
  captureCellColorTargets(): void {
    this.cellColorTargets = this.getCellsInSelection();
  }

  /** Apply the picked color to the captured cells (current cell or selection). */
  applyCellFill(event: Event): void {
    const color = String((event.target as HTMLInputElement)?.value || "");
    if (!color) {
      return;
    }
    this.cellFillColor = color;
    const cells = this.cellColorTargets.length
      ? this.cellColorTargets
      : this.getCellsInSelection();
    for (let i = 0; i < cells.length; i += 1) {
      cells[i].style.background = color;
    }
    this.cellColorTargets = [];
    this.syncPagesFromDom();
  }

  /** Apply the current fill color to the whole row or column of the active cell. */
  applyCellFillScope(scope: "row" | "col"): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const color = this.cellFillColor;
    if (scope === "row") {
      const row = cell.parentElement as HTMLTableRowElement | null;
      if (row) {
        const cells = Array.from(row.cells) as HTMLElement[];
        for (let i = 0; i < cells.length; i += 1) {
          cells[i].style.background = color;
        }
      }
    } else {
      const table = cell.closest("table") as HTMLTableElement | null;
      const columnIndex = this.getCellColumnIndex(cell);
      if (table && columnIndex >= 0) {
        const rows = Array.from(table.rows);
        for (let r = 0; r < rows.length; r += 1) {
          const target = rows[r].cells[columnIndex] as HTMLElement | undefined;
          if (target) {
            target.style.background = color;
          }
        }
      }
    }
    this.syncPagesFromDom();
  }

  /** Cells intersecting the current selection, or the active cell if collapsed. */
  private getCellsInSelection(): HTMLTableCellElement[] {
    const fallback = this.selectedTableCell ? [this.selectedTableCell] : [];
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return fallback;
    }
    const range = selection.getRangeAt(0);
    const table = this.selectedTableCell?.closest("table") as HTMLElement | null;
    if (range.collapsed || !table) {
      return fallback;
    }
    const cells = Array.from(
      table.querySelectorAll("td,th")
    ) as HTMLTableCellElement[];
    const selected = cells.filter((c) => range.intersectsNode(c));
    return selected.length ? selected : fallback;
  }

  // ----------------------------------------------------------------------------
  // Hover "+" controls: a button on the right of any table adds a column, a
  // button below it adds a row. Works for both inserted tables and report
  // tables (report tables apply the column to every page fragment so the column
  // count stays consistent).
  // ----------------------------------------------------------------------------

  onWorkspaceMouseMove(event: MouseEvent): void {
    if (this.tableHoverRafPending) {
      return;
    }
    const target = event.target as Element | null;
    const clientX = event.clientX;
    const clientY = event.clientY;
    this.tableHoverRafPending = true;
    requestAnimationFrame(() => {
      this.tableHoverRafPending = false;
      const table = target?.closest?.(
        ".page-editor table"
      ) as HTMLElement | null;
      const hoverCell = target?.closest?.(
        "td,th"
      ) as HTMLTableCellElement | null;
      if (table) {
        this.cancelHideTableControls();
        this.showTableControls(table);
        if (hoverCell) {
          this.showTableDeleteControls(hoverCell);
        }
      } else if (
        !target?.closest?.(".table-add-control") &&
        !target?.closest?.(".table-del-control")
      ) {
        this.scheduleHideTableControls();
      }
      // Show a resize cursor when hovering a cell's right/bottom border.
      const cell = target?.closest?.("td,th") as HTMLElement | null;
      if (cell) {
        const rect = cell.getBoundingClientRect();
        const edge = DownloadpdfComponent.TABLE_RESIZE_EDGE;
        const fromRight = rect.right - clientX;
        const fromBottom = rect.bottom - clientY;
        if (fromRight >= -2 && fromRight <= edge) {
          cell.style.cursor = "col-resize";
        } else if (fromBottom >= -2 && fromBottom <= edge) {
          cell.style.cursor = "row-resize";
        } else {
          cell.style.cursor = "";
        }
      }
    });
  }

  private showTableControls(table: HTMLElement): void {
    if (
      this.chartEditorOpen ||
      this.tableEditorOpen ||
      this.dialogType !== "none" ||
      this.exportInProgress
    ) {
      this.tableControlsVisible = false;
      return;
    }
    this.hoveredTable = table;
    const rect = table.getBoundingClientRect();
    // "+" to add a column: just outside the right edge, vertically centered.
    this.tableColAddTop = rect.top + rect.height / 2 - 13;
    this.tableColAddLeft = Math.min(rect.right + 6, window.innerWidth - 30);
    // "+" to add a row: just below the bottom edge, horizontally centered.
    this.tableRowAddTop = rect.bottom + 6;
    this.tableRowAddLeft = rect.left + rect.width / 2 - 13;
    this.tableControlsVisible = true;
  }

  cancelHideTableControls(): void {
    if (this.tableControlsHideTimer) {
      clearTimeout(this.tableControlsHideTimer);
      this.tableControlsHideTimer = null;
    }
  }

  scheduleHideTableControls(): void {
    this.cancelHideTableControls();
    this.tableControlsHideTimer = setTimeout(() => {
      this.tableControlsVisible = false;
      this.hoveredTable = null;
      this.tableDelControlsVisible = false;
      this.hoveredCell = null;
    }, 250);
  }

  /**
   * Position the per-cell "−" delete handles for the cell under the cursor: a
   * button above the table (aligned to the cell's column) deletes that column, a
   * button to the left of the table (aligned to the cell's row) deletes that row.
   * Mirrors showTableControls so add and delete affordances stay consistent.
   */
  private showTableDeleteControls(cell: HTMLTableCellElement): void {
    if (
      this.chartEditorOpen ||
      this.tableEditorOpen ||
      this.dialogType !== "none" ||
      this.exportInProgress
    ) {
      this.tableDelControlsVisible = false;
      return;
    }
    const table = cell.closest("table") as HTMLElement | null;
    if (!table) {
      this.tableDelControlsVisible = false;
      return;
    }
    this.hoveredCell = cell;
    const tableRect = table.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    // "−" to delete the column: just above the top edge, centered on the cell.
    this.tableDelColTop = Math.max(8, tableRect.top - 24);
    this.tableDelColLeft = cellRect.left + cellRect.width / 2 - 11;
    // "−" to delete the row: just left of the table, centered on the cell's row.
    this.tableDelRowTop = cellRect.top + cellRect.height / 2 - 11;
    this.tableDelRowLeft = Math.max(8, tableRect.left - 26);
    this.tableDelControlsVisible = true;
  }

  /** Delete the column under the cursor across every page fragment. */
  deleteHoveredColumn(): void {
    const cell = this.hoveredCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    // Never delete the only remaining column.
    if ((table.rows[0]?.cells.length || 0) <= 1) {
      return;
    }
    const targets = this.relatedTables(table);
    for (let t = 0; t < targets.length; t += 1) {
      const rows = Array.from((targets[t] as HTMLTableElement).rows);
      for (let r = 0; r < rows.length; r += 1) {
        const target = rows[r].cells[columnIndex];
        if (target) {
          target.remove();
        }
      }
    }
    this.tableDelControlsVisible = false;
    this.hoveredCell = null;
    this.afterHoverTableEdit(table);
  }

  /** Delete the row under the cursor (within its own page fragment). */
  deleteHoveredRow(): void {
    const cell = this.hoveredCell;
    if (!cell) {
      return;
    }
    const row = cell.closest("tr") as HTMLTableRowElement | null;
    const table = cell.closest("table") as HTMLElement | null;
    if (!row || !table) {
      return;
    }
    // Keep the header row of report tables intact — delete columns instead.
    if (row.parentElement && row.parentElement.tagName === "THEAD") {
      return;
    }
    // Never delete the last body row across all fragments of the table.
    const totalBodyRows = this.relatedTables(table).reduce(
      (sum, t) => sum + t.querySelectorAll("tbody tr").length,
      0
    );
    if (totalBodyRows <= 1) {
      return;
    }
    row.remove();
    this.tableDelControlsVisible = false;
    this.hoveredCell = null;
    this.afterHoverTableEdit(table);
  }

  addColumnToHoveredTable(): void {
    const table = this.hoveredTable;
    if (!table) {
      return;
    }
    const targets = this.relatedTables(table);
    for (let t = 0; t < targets.length; t += 1) {
      const rows = Array.from((targets[t] as HTMLTableElement).rows);
      for (let r = 0; r < rows.length; r += 1) {
        const row = rows[r];
        const isHeaderRow =
          !!row.querySelector("th") ||
          (row.parentElement && row.parentElement.tagName === "THEAD");
        const cell = document.createElement(isHeaderRow ? "th" : "td");
        const lastCell = row.cells[row.cells.length - 1] as
          | HTMLElement
          | undefined;
        if (lastCell) {
          cell.setAttribute("style", lastCell.getAttribute("style") || "");
        }
        cell.innerHTML = "&nbsp;";
        row.appendChild(cell);
      }
    }
    this.afterHoverTableEdit(table);
  }

  addRowToHoveredTable(): void {
    const table = this.hoveredTable;
    if (!table) {
      return;
    }
    const tbody = (table.querySelector("tbody") || table) as HTMLElement;
    const refRow = tbody.querySelector("tr:last-child") as
      | HTMLTableRowElement
      | null;
    const colCount = refRow
      ? refRow.cells.length
      : (table as HTMLTableElement).rows[0]?.cells.length || 1;
    const newRow = document.createElement("tr");
    for (let i = 0; i < colCount; i += 1) {
      const td = document.createElement("td");
      const refCell = refRow?.cells[i] as HTMLElement | undefined;
      if (refCell && refCell.tagName === "TD") {
        td.setAttribute("style", refCell.getAttribute("style") || "");
      }
      td.innerHTML = "&nbsp;";
      newRow.appendChild(td);
    }
    tbody.appendChild(newRow);
    this.afterHoverTableEdit(table);
  }

  /**
   * All table elements that belong to the same logical table. Report tables can
   * be split across pages into several .report-table-block fragments sharing a
   * data-table-id — a new column must be added to all of them. Plain inserted
   * tables are standalone.
   */
  private relatedTables(table: HTMLElement): HTMLElement[] {
    const block = table.closest(".report-table-block") as HTMLElement | null;
    const tableId = block?.getAttribute("data-table-id");
    if (!tableId) {
      return [table];
    }
    const result: HTMLElement[] = [];
    const editors = this.pageEditors?.toArray() || [];
    for (let e = 0; e < editors.length; e += 1) {
      const blocks = Array.from(
        editors[e].nativeElement.querySelectorAll(
          `.report-table-block[data-table-id="${tableId}"]`
        )
      );
      for (let b = 0; b < blocks.length; b += 1) {
        const t = blocks[b].querySelector("table") as HTMLElement | null;
        if (t) {
          result.push(t);
        }
      }
    }
    return result.length ? result : [table];
  }

  private afterHoverTableEdit(table: HTMLElement): void {
    const editor = table.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
    }
    this.syncPagesFromDom();
    this.reflowFrom(0);
    this.updateDocumentStats();
    // Reposition the controls against the (possibly moved) table after reflow.
    setTimeout(() => {
      if (this.hoveredTable && this.hoveredTable.isConnected) {
        this.showTableControls(this.hoveredTable);
      } else {
        this.tableControlsVisible = false;
      }
    }, 0);
  }

  // ----------------------------------------------------------------------------
  // Column / row resize by dragging a cell's right / bottom border.
  // ----------------------------------------------------------------------------

  private static readonly TABLE_RESIZE_EDGE = 6;

  /** If the pointer is on a cell's right/bottom border, begin a resize drag. */
  private maybeStartTableResize(event: MouseEvent, target: Element): boolean {
    const cell = target.closest("td,th") as HTMLTableCellElement | null;
    if (!cell || !cell.closest(".page-editor")) {
      return false;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return false;
    }
    const rect = cell.getBoundingClientRect();
    const edge = DownloadpdfComponent.TABLE_RESIZE_EDGE;
    const fromRight = rect.right - event.clientX;
    const fromBottom = rect.bottom - event.clientY;

    if (fromRight >= -2 && fromRight <= edge) {
      event.preventDefault();
      event.stopPropagation();
      this.startColumnResize(table, cell, event);
      return true;
    }
    if (fromBottom >= -2 && fromBottom <= edge) {
      event.preventDefault();
      event.stopPropagation();
      this.startRowResize(cell.parentElement as HTMLTableRowElement, event);
      return true;
    }
    return false;
  }

  private startColumnResize(
    table: HTMLTableElement,
    cell: HTMLTableCellElement,
    event: MouseEvent
  ): void {
    // Fixed layout makes per-column widths deterministic.
    table.style.tableLayout = "fixed";
    table.style.width = table.style.width || "100%";
    const columnIndex = this.getCellColumnIndex(cell);
    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const nextWidth = Math.max(24, Math.round(startWidth + (moveEvent.clientX - startX)));
      const rows = Array.from(table.rows);
      for (let r = 0; r < rows.length; r += 1) {
        const target = rows[r].cells[columnIndex] as HTMLElement | undefined;
        if (target) {
          target.style.width = `${nextWidth}px`;
        }
      }
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private startRowResize(row: HTMLTableRowElement | null, event: MouseEvent): void {
    if (!row) {
      return;
    }
    const startY = event.clientY;
    const startHeight = row.getBoundingClientRect().height;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const nextHeight = Math.max(16, Math.round(startHeight + (moveEvent.clientY - startY)));
      row.style.height = `${nextHeight}px`;
      const cells = Array.from(row.cells) as HTMLElement[];
      for (let c = 0; c < cells.length; c += 1) {
        cells[c].style.height = `${nextHeight}px`;
      }
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private getWrapperWidthPx(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.widthPx);
    if (!Number.isNaN(fromData) && fromData > 0) {
      return fromData;
    }

    const inlineWidth = String(wrapper.style.width || "").trim();
    const parsedInline = inlineWidth.endsWith("px")
      ? Number(inlineWidth.replace("px", ""))
      : Number.NaN;
    if (!Number.isNaN(parsedInline) && parsedInline > 0) {
      return parsedInline;
    }

    return wrapper.offsetWidth || Math.round(wrapper.getBoundingClientRect().width) || 900;
  }

  private getWrapperOffsetX(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.offsetXPx);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const translate2dMatch = transform.match(
      /translate\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*-?\d+(?:\.\d+)?px\s*\)/i
    );
    if (translate2dMatch) {
      return Number(translate2dMatch[1]);
    }
    const match = transform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/i);
    return match ? Number(match[1]) : 0;
  }

  private getWrapperOffsetY(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.offsetYPx);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const translate2dMatch = transform.match(
      /translate\(\s*-?\d+(?:\.\d+)?px\s*,\s*(-?\d+(?:\.\d+)?)px\s*\)/i
    );
    if (translate2dMatch) {
      return Number(translate2dMatch[1]);
    }
    const translateYMatch = transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/i);
    return translateYMatch ? Number(translateYMatch[1]) : 0;
  }

  private setWrapperWidthPx(wrapper: HTMLElement, width: number): void {
    const normalized = Math.max(120, Math.round(width));
    wrapper.dataset.widthPx = String(normalized);
    wrapper.style.width = `${normalized}px`;
    wrapper.style.maxWidth = "none";
    wrapper.style.height = "auto";

    const image = wrapper.querySelector("img") as HTMLImageElement | null;
    if (image) {
      image.style.setProperty("width", `${normalized}px`, "important");
      image.style.setProperty("max-width", "none", "important");
      image.style.setProperty("height", "auto", "important");
      image.style.display = "block";
    }

    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    if (chartBlock) {
      chartBlock.style.width = `${normalized}px`;
      chartBlock.style.maxWidth = "none";
    }
  }

  private getTransformTarget(wrapper: HTMLElement): HTMLElement | null {
    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    if (chartBlock) {
      return chartBlock;
    }

    const tableBlock = wrapper.querySelector(".report-table-block") as HTMLElement | null;
    if (tableBlock) {
      return tableBlock;
    }

    const image = wrapper.querySelector("img") as HTMLElement | null;
    if (image) {
      return image;
    }

    return null;
  }

  private placeCaretAtStart(container: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    container.focus({ preventScroll: true });
    this.scrollCaretIntoView();
  }

  private resolveImageFromTarget(target: Element): HTMLImageElement | null {
    if (target.tagName.toLowerCase() === "img") {
      return target as HTMLImageElement;
    }

    const ancestorImage = target.closest("img") as HTMLImageElement | null;
    if (ancestorImage) {
      return ancestorImage;
    }

    // For chart blocks, users may click on chart padding/background.
    // In that case, resolve the chart's own image only.
    const chartContainer = target.closest(
      ".report-chart, .report-chart-image"
    ) as HTMLElement | null;
    if (chartContainer) {
      const chartImage = chartContainer.querySelector("img");
      if (chartImage) {
        return chartImage as HTMLImageElement;
      }
    }

    return null;
  }

  private resolveImageFromEvent(event: Event): HTMLImageElement | null {
    const path = this.getEventPathElements(event);
    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      if (el.tagName.toLowerCase() === "img") {
        return el as HTMLImageElement;
      }
    }

    // Clicking a chart's padding or background: the image lives inside the
    // visual the pointer is actually in, so only visuals are searched — and the
    // walk stops at the page.
    //
    // This used to search EVERY element in the path. composedPath() carries the
    // whole ancestor chain, so .page-editor matched and returned the FIRST image
    // on the page for a click anywhere on it: every click selected an unrelated
    // chart and armed a drag on it, and armVisualDrag's preventDefault() then
    // swallowed the caret — which is why the heading above a chart could not be
    // clicked or edited.
    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      if (this.isVisualUnit(el)) {
        const img = el.querySelector("img");
        if (img) {
          return img as HTMLImageElement;
        }
      }
      if (el.classList?.contains("page-editor")) {
        break; // never resolve past the page the pointer is on
      }
    }

    return null;
  }

  private resolveWrapperFromTarget(target: Element): HTMLElement | null {
    const directWrapper = target.closest(".resizable-image") as HTMLElement | null;
    if (directWrapper) {
      return directWrapper;
    }

    const image = this.resolveImageFromTarget(target);
    if (!image) {
      return null;
    }

    return image.closest(".resizable-image") as HTMLElement | null;
  }

  private resolveWrapperFromEvent(event: Event): HTMLElement | null {
    const path = this.getEventPathElements(event);
    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      if (el.classList?.contains("resizable-image")) {
        return el as HTMLElement;
      }
    }

    const image = this.resolveImageFromEvent(event);
    if (!image) {
      return null;
    }

    return image.closest(".resizable-image") as HTMLElement | null;
  }

  private placeCaretAtEnd(container: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    container.focus({ preventScroll: true });
  }

  clearEditor(): void {
    this.pages = [{ id: 1, html: "<p><br></p>" }];
    this.nextPageId = 2;
    this.activePageIndex = 0;
    // Discard the stored draft so a cleared document is not restored later.
    void this.removeDraft();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.focusActivePage();
    }, 0);
    this.updateDocumentStats();
  }

  async closeEditor(): Promise<void> {
    // Persist the latest edits locally before leaving so nothing is lost.
    if (this.documentReady) {
      this.flushInputReflow();
      this.syncPagesFromDom();
      await this.saveDraft(false);
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(["/report-system/dashboard-group-list"], {
      queryParamsHandling: "merge",
    });
  }

  // ----------------------------------------------------------------------------
  // Local draft persistence
  // ----------------------------------------------------------------------------

  /** Storage key for the current document, scoped per dashboard group. */
  private getDraftStorageKey(): string {
    const id = this.lastLoadedDashboardGroupId;
    const suffix = id && !Number.isNaN(id) ? `group-${id}` : "standalone";
    return `downloadpdf:draft:v${this.draftVersion}:${suffix}`;
  }

  /** Toolbar "Save" handler: sync the DOM then persist immediately. */
  manualSaveDraft(): void {
    // Run any repagination still pending from the last keystroke, so Save writes
    // a settled document rather than one mid-burst.
    this.flushInputReflow();
    this.syncPagesFromDom();
    void this.saveDraft(true);
  }

  /**
   * Debounced autosave. Only runs once the document has finished its initial
   * load/pagination so we never persist transient mid-render state. saveDraft()
   * deliberately does NOT sync the DOM, so this cannot re-trigger itself.
   */
  private scheduleDraftAutosave(): void {
    if (!this.documentReady) {
      return;
    }
    // Never capture the model mid-pagination — it is intentionally stale during
    // reflow. Defer; reflowFrom re-arms this once it settles.
    if (this.reflowInProgress) {
      this.draftAutosavePending = true;
      return;
    }
    // An edit just landed → mark unsaved until the debounced write completes.
    if (this.draftState !== "saving") {
      this.draftState = "unsaved";
    }
    // Same ceiling as repagination, for the same reason: this debounce was also
    // reset by every keystroke, so a long burst of editing — holding Backspace,
    // typing a paragraph — persisted nothing at all until the user stopped. The
    // deadline makes a burst save periodically instead.
    const now = Date.now();
    if (!this.draftAutosaveDeadline) {
      this.draftAutosaveDeadline =
        now + DownloadpdfComponent.DRAFT_AUTOSAVE_MAX_WAIT_MS;
    }
    if (this.draftAutosaveTimer) {
      clearTimeout(this.draftAutosaveTimer);
    }
    const wait = Math.max(
      0,
      Math.min(
        DownloadpdfComponent.DRAFT_AUTOSAVE_DELAY_MS,
        this.draftAutosaveDeadline - now
      )
    );
    this.draftAutosaveTimer = setTimeout(() => {
      this.draftAutosaveTimer = null;
      this.draftAutosaveDeadline = 0;
      // A reflow may have started after we were scheduled — defer again rather
      // than persist a half-paginated document.
      if (this.reflowInProgress) {
        this.draftAutosavePending = true;
        return;
      }
      void this.saveDraft(false);
    }, wait);
  }

  /**
   * Persist the current page model. Best-effort. Uses IndexedDB (large quota)
   * and only falls back to localStorage if IndexedDB is unavailable or fails.
   */
  private async saveDraft(showFeedback: boolean): Promise<void> {
    const key = this.getDraftStorageKey();
    const payload = {
      version: this.draftVersion,
      savedAt: Date.now(),
      activePageIndex: this.activePageIndex,
      pages: (this.pages || []).map((page) => ({
        id: Number(page?.id) || 0,
        html: String(page?.html || ""),
      })),
    };

    // Only surface the transient "Saving…" state if the write is slow enough to
    // be worth showing. Most IndexedDB writes finish in a few ms, so flashing the
    // indicator every save just makes it flicker — skip straight to "Saved".
    const savingIndicatorTimer = setTimeout(() => {
      this.draftState = "saving";
    }, 450);

    const markSaved = () => {
      clearTimeout(savingIndicatorTimer);
      this.draftSavedAt = payload.savedAt;
      this.draftSaveError = false;
      this.draftState = "saved";
      if (showFeedback) {
        this.draftSaving = true;
        setTimeout(() => {
          this.draftSaving = false;
        }, 1400);
      }
    };

    try {
      const db = await this.openDraftDb();
      if (db) {
        await this.idbPut(db, key, payload);
        // Drop any stale localStorage copy from the previous implementation.
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
        markSaved();
        return;
      }
      // No IndexedDB → small-document fallback.
      localStorage.setItem(key, JSON.stringify(payload));
      markSaved();
    } catch (error) {
      // IndexedDB write failed → try localStorage as a last resort.
      try {
        localStorage.setItem(key, JSON.stringify(payload));
        markSaved();
      } catch (fallbackError) {
        clearTimeout(savingIndicatorTimer);
        this.draftSaveError = true;
        this.draftState = "error";
      }
    }
  }

  /** Read any saved draft for this document and offer to restore it. */
  private async checkForDraft(): Promise<void> {
    const key = this.getDraftStorageKey();
    try {
      let parsed: any = null;

      const db = await this.openDraftDb();
      if (db) {
        parsed = await this.idbGet(db, key);
      }
      // Fall back to (and migrate from) any older localStorage draft.
      if (!parsed) {
        const raw = localStorage.getItem(key);
        if (raw) {
          parsed = JSON.parse(raw);
        }
      }

      if (!parsed || !Array.isArray(parsed.pages) || !parsed.pages.length) {
        return;
      }
      const pages = parsed.pages
        .map((page: any, index: number) => ({
          id: Number(page?.id) || index + 1,
          html: String(page?.html || ""),
        }))
        .filter((page: { html: string }) => typeof page.html === "string");
      if (!pages.length) {
        return;
      }
      this.pendingDraft = {
        pages,
        savedAt: Number(parsed.savedAt) || 0,
        activePageIndex: Number(parsed.activePageIndex) || 0,
      };
      this.draftSavedAt = this.pendingDraft.savedAt;
      this.draftRestoreAvailable = true;
    } catch (error) {
    }
  }

  // --- IndexedDB plumbing (no external dependency) ---

  /** Open (and cache) the drafts database. Resolves null if IDB is unavailable. */
  private openDraftDb(): Promise<IDBDatabase | null> {
    if (this.draftDb) {
      return Promise.resolve(this.draftDb);
    }
    return new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined" || !indexedDB) {
          resolve(null);
          return;
        }
        const request = indexedDB.open(this.draftDbName, 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(this.draftStoreName)) {
            db.createObjectStore(this.draftStoreName);
          }
        };
        request.onsuccess = () => {
          this.draftDb = request.result;
          resolve(this.draftDb);
        };
        request.onerror = () => {
          resolve(null);
        };
      } catch (error) {
        resolve(null);
      }
    });
  }

  private idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readwrite");
        tx.objectStore(this.draftStoreName).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  private idbGet(db: IDBDatabase, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readonly");
        const request = tx.objectStore(this.draftStoreName).get(key);
        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  private idbDelete(db: IDBDatabase, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readwrite");
        tx.objectStore(this.draftStoreName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  /** Replace the live document with the saved draft. */
  restoreDraft(): void {
    const draft = this.pendingDraft;
    if (!draft) {
      this.draftRestoreAvailable = false;
      return;
    }

    this.draftRestoreAvailable = false;
    this.documentReady = false;
    // If the fresh API load is still in flight, its response must not overwrite
    // the draft the user just chose to restore.
    this.userRestoredDraft = true;
    this.editorLoading = false;
    this.pages = draft.pages.map((page, index) => ({
      id: Number(page.id) || index + 1,
      html: String(page.html || ""),
    }));
    this.nextPageId =
      this.pages.reduce((max, page) => Math.max(max, Number(page.id) || 0), 0) + 1;
    this.activePageIndex = Math.min(
      Math.max(0, draft.activePageIndex || 0),
      this.pages.length - 1
    );
    this.pendingDraft = null;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(0);
      this.focusActivePage();
      this.updateDocumentStats();
      this.documentReady = true;
      this.draftState = "saved";
    }, 0);
  }

  /** Dismiss the restore offer and keep the freshly generated document. */
  dismissDraft(): void {
    this.draftRestoreAvailable = false;
    this.pendingDraft = null;
  }

  /** Remove the stored draft for this document (used when clearing). */
  private async removeDraft(): Promise<void> {
    const key = this.getDraftStorageKey();
    try {
      const db = await this.openDraftDb();
      if (db) {
        await this.idbDelete(db, key);
      }
    } catch (error) {
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    this.draftSavedAt = null;
    this.draftRestoreAvailable = false;
    this.pendingDraft = null;
  }

  /** Human-readable "last saved" label for the UI. */
  get draftSavedLabel(): string {
    if (!this.draftSavedAt) {
      return "";
    }
    try {
      return new Date(this.draftSavedAt).toLocaleString();
    } catch {
      return "";
    }
  }

  /** Short clock label (HH:MM) for the inline auto-save status. */
  private get draftSavedTimeLabel(): string {
    if (!this.draftSavedAt) {
      return "";
    }
    try {
      return new Date(this.draftSavedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }

  /** Text shown in the toolbar auto-save indicator. */
  get draftStatusLabel(): string {
    switch (this.draftState) {
      case "saving":
        return "Saving…";
      case "unsaved":
        return "Unsaved";
      case "error":
        return "Save failed";
      case "saved": {
        const time = this.draftSavedTimeLabel;
        return time ? `Saved ${time}` : "Saved";
      }
      default:
        return "Auto-save";
    }
  }

  /**
   * "Edit in Word": download the document as a real .docx that opens in
   * Microsoft Word with its layout intact (cover, header logo, page numbers,
   * charts, tables, Table of Contents) and stays fully editable there. It is
   * converted in the browser from the live, laid-out pages (see word-export.ts,
   * lazy-loaded so the docx library only downloads on use). Nothing is saved
   * server-side.
   */
  async exportAsWord(): Promise<void> {
    if (this.exportInProgress) {
      return;
    }
    this.exportInProgress = true;
    this.exportCancelled = false;
    this.updateExportProgress(2, "Preparing Word document…");
    try {
      const built = await this.buildDocxForExport(1);
      if (!built) {
        return; // cancelled
      }
      const objectUrl = URL.createObjectURL(built.blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${this.sanitizeUserFileName(built.baseName)}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      this.updateExportProgress(100, "Word document downloaded.");
    } catch (error) {
      console.error("[Word] export failed", error);
      this.updateExportProgress(100, "Word export failed.");
    } finally {
      setTimeout(() => {
        this.exportInProgress = false;
      }, 600);
    }
  }

  /**
   * "Open in Google Docs": the same .docx as "Edit in Word", uploaded to the
   * user's own Google Drive — Drive converts it into a Google Doc — and opened
   * there. Access is the narrow drive.file scope (only files this app creates);
   * see google-docs-export.ts. The Google Doc is a copy: edits made there don't
   * flow back into this editor.
   */
  async openInGoogleDocs(): Promise<void> {
    if (this.exportInProgress || this.googleDocsComingSoon) {
      return;
    }
    this.exportInProgress = true;
    this.exportCancelled = false;
    this.googleDocsResult = null;
    // Cancel (export overlay) aborts the sign-in wait and the upload alike.
    const abort = new AbortController();
    this.googleDocsAbort = abort;

    // Everything up to requestGoogleToken() must run synchronously in the click:
    // browsers only allow a popup/new tab right after a user gesture. With a
    // token from earlier in the session there is no Google popup, so the click
    // can open the tab the finished doc will load into.
    let token = getCachedGoogleToken();
    let tab: Window | null = null;
    let tokenRequest: Promise<string> | null = null;
    if (token) {
      tab = window.open("", "_blank");
      tab?.document.write(
        '<title>Opening Google Docs…</title><p style="font:16px Arial,sans-serif;color:#334155;padding:32px">Preparing your Google Doc…</p>'
      );
    } else {
      this.updateExportProgress(2, "Waiting for Google sign-in…");
      tokenRequest = requestGoogleToken(environment.googleDocsClientId);
    }

    try {
      if (tokenRequest) {
        const pending = tokenRequest;
        token = await new Promise<string>((resolve, reject) => {
          abort.signal.addEventListener(
            "abort",
            () => reject(new DOMException("Cancelled", "AbortError")),
            { once: true }
          );
          pending.then(resolve, reject);
        });
      }
      if (this.exportCancelled) {
        return;
      }
      // One quick call finds a disabled Drive API before any conversion work.
      this.updateExportProgress(4, "Connecting to Google Drive…");
      await checkGoogleDriveAvailable(token!, abort.signal);
      this.updateExportProgress(5, "Preparing Google Doc…");
      // The conversion drives 0–80% of the bar, the upload the rest.
      const built = await this.buildDocxForExport(0.8);
      if (!built) {
        return; // cancelled
      }
      this.updateExportProgress(85, "Uploading to Google Drive…");
      const url = await uploadDocxAsGoogleDoc(token!, built.blob, built.baseName, abort.signal);
      this.updateExportProgress(100, "Google Doc ready.");
      setGoogleDocsUnavailable(false);
      if (tab && !tab.closed) {
        tab.opener = null;
        tab.location.href = url;
        tab = null; // handed over; keep it open
      } else {
        this.googleDocsResult = { url };
      }
    } catch (error) {
      const cancelled =
        this.exportCancelled ||
        (error as Error)?.name === "AbortError" ||
        (error instanceof GoogleDocsError && error.code === "cancelled");
      if (error instanceof GoogleDocsError && error.code === "api_disabled") {
        // Not set up on Google's side yet: show "Coming soon" rather than an
        // admin-facing error, and remember it so others aren't sent to sign in.
        setGoogleDocsUnavailable(true);
        this.googleDocsComingSoon = true;
        this.googleDocsResult = { comingSoon: true };
      } else if (!cancelled) {
        console.error("[Google Docs] export failed", error);
        this.googleDocsResult = {
          error:
            error instanceof GoogleDocsError
              ? error.message
              : "Something went wrong while creating the Google Doc. Please try again.",
        };
      }
    } finally {
      tab?.close();
      this.googleDocsAbort = null;
      setTimeout(() => {
        this.exportInProgress = false;
      }, 600);
    }
  }

  /** Warm up Google Identity Services before the click (hover/focus). */
  preloadGoogleDocs(): void {
    if (this.googleDocsComingSoon) {
      return;
    }
    preloadGoogleIdentity().catch(() => undefined);
  }

  closeGoogleDocsResult(): void {
    this.googleDocsResult = null;
  }

  /**
   * Build the editor document as a .docx from the settled, live pages. The
   * conversion reports progress up to `progressShare` of the export bar.
   * Returns null when the user cancelled.
   */
  private async buildDocxForExport(
    progressShare: number
  ): Promise<{ blob: Blob; baseName: string } | null> {
    // Snapshot the same settled layout the PDF export uses.
    await this.waitForReflowSettled();
    this.refreshTableOfContents();
    const { buildWordDocument, WordExportCancelled } = await import("./word-export");

    const baseName =
      this.currentDocumentName ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    const pages = (this.pageEditors?.toArray() || []).map((ref) => ref.nativeElement);
    // The cover page hides its header, so take the first logo actually shown.
    const headerLogo =
      Array.from(document.querySelectorAll<HTMLImageElement>(".page-header-logo")).find(
        (img) => img.offsetHeight > 0
      ) || null;

    try {
      const blob = await buildWordDocument({
        title: baseName,
        pages,
        headerLogo,
        onProgress: (percent, text) =>
          this.updateExportProgress(percent * progressShare, text),
        isCancelled: () => this.exportCancelled,
      });
      return { blob, baseName };
    } catch (error) {
      if (error instanceof WordExportCancelled) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Export the document to PDF. Renders server-side (headless Chromium worker →
   * S3) so the browser never blocks: we upload the document HTML to a presigned
   * S3 URL, start a render job, poll for completion, then download the finished
   * PDF. On any failure the export reports an error (no local fallback).
   */
  async exportAsPdf(): Promise<void> {
    if (this.exportInProgress) {
      return;
    }
    this.exportInProgress = true;
    this.exportCancelled = false;
    try {
      // Never snapshot a half-paginated document.
      await this.waitForReflowSettled();
      // Final guarantee: re-verify and fix Table of Contents page numbers against
      // the settled layout before the document is saved and exported as PDF.
      this.refreshTableOfContents();
      // Snapshot the model, dropping any trailing blank pages as we go, so
      // neither the saved document nor the PDF carries them. This syncs from the
      // DOM itself, so it replaces a bare syncPagesFromDom() here.
      this.removeTrailingEmptyPages();
      // Phase 1: persist the editable editor document to S3 (reopen + edit later).
      await this.saveDocumentToS3();
      if (this.exportCancelled) {
        this.updateExportProgress(100, "Cancelled.");
        return;
      }
      // Phase 2: export the whole document as a PDF (also stored in S3).
      await this.exportViaServer();
    } catch (error) {
      console.error("[PDF] save & export failed", error);
      this.updateExportProgress(100, "Export failed.");
    } finally {
      setTimeout(() => {
        this.exportInProgress = false;
      }, 600);
    }
  }

  /**
   * "Save & Export" phase 1: persist the full editable editor document (every
   * page's HTML + metadata) to S3 so the user can reopen and edit it later in
   * this editor. Stored under the chosen file name. Best-effort: a failed save
   * is logged but does not block the PDF export. Occupies the first ~30% of the
   * progress bar; the PDF export phase drives the remainder.
   */
  private async saveDocumentToS3(): Promise<void> {
    const pages = this.pages || [];
    // Nothing rendered (e.g. empty editor) → nothing to persist.
    if (!pages.length) {
      return;
    }
    this.updateExportProgress(10, "Saving document…");

    const baseName =
      (this.exportFileName || "").trim() ||
      this.currentDocumentName ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    // Always tag the document with a site so the (upcoming) listing can filter by
    // site and never mismatch. Prefer the group's own site; fall back to the
    // logged-in user's current site for the standalone editor (no group).
    const siteId =
      this.cachedGroupForRender?.data?.siteId ||
      this.dataShareService.getSiteId() ||
      null;
    const normalizedPages = pages.map((page) => ({
      id: Number(page?.id) || 0,
      html: String(page?.html || ""),
    }));
    // Small metadata sent to init/commit (no page HTML — that goes to S3, so a
    // multi-MB document never hits API Gateway's request-size limit).
    const meta = {
      // Set when editing a previously saved document → update in place.
      id: this.currentDocumentId,
      documentName: baseName,
      fileName: `${this.sanitizeUserFileName(baseName)}.pdf`,
      dashboardGroupId: this.lastLoadedDashboardGroupId ?? null,
      siteId,
      version: this.draftVersion,
      savedAt: Date.now(),
      activePageIndex: this.activePageIndex,
      dataFrom: this.exportWindowFrom || null,
      dataTo: this.exportWindowTo || null,
      // Per-frequency applied ranges (for the saved-documents card).
      intervalWindows: this.buildSavedIntervalWindows(),
      pageCount: normalizedPages.length,
    };

    try {
      // Step 1 — init: send metadata only, get back a presigned PUT URL.
      this.updateExportProgress(15, "Preparing save…");
      const init = await firstValueFrom(this.reportService.initDocument(meta));
      const target = init?.data;
      if (!target?.id || !target?.uploadUrl) {
        throw new Error("Document save init returned no upload URL");
      }

      // Step 2 — PUT the full document JSON (incl. pages) straight to S3
      // (bypasses the API, so multi-MB documents are fine).
      this.updateExportProgress(20, "Uploading document…");
      const documentJson = JSON.stringify({
        id: target.id,
        documentName: meta.documentName,
        fileName: meta.fileName,
        dashboardGroupId: meta.dashboardGroupId,
        siteId: meta.siteId,
        version: meta.version,
        savedAt: meta.savedAt,
        activePageIndex: meta.activePageIndex,
        dataFrom: meta.dataFrom,
        dataTo: meta.dataTo,
        pages: normalizedPages,
      });
      // Raw fetch (not HttpClient) so no Authorization header is sent to S3; the
      // presigned signature pins Content-Type, so echo the returned headers.
      const putRes = await fetch(target.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(target.uploadHeaders || {}),
        },
        body: documentJson,
      });
      if (!putRes.ok) {
        throw new Error(`document upload failed (${putRes.status})`);
      }

      // Step 3 — commit: finalize the metadata row now the JSON is in S3.
      const committed = await firstValueFrom(
        this.reportService.commitDocument(target.id, meta)
      );
      // Remember the server id so further saves this session update in place
      // rather than creating duplicates.
      const savedId = committed?.data?.id ?? target.id;
      if (savedId !== undefined && savedId !== null) {
        this.currentDocumentId = savedId;
      }
      this.currentDocumentName = baseName;
      this.updateExportProgress(30, "Document saved.");
    } catch (error) {
      console.error("[PDF] save document failed", error);
      // Non-fatal — the user still gets their PDF.
      this.updateExportProgress(30, "Document save failed; exporting…");
    }
  }

  /** Open the export dialog, prefilling a default file name. */
  openExportDialog(): void {
    if (this.exportInProgress) {
      return;
    }
    // Precedence: a reopened document keeps its saved name; otherwise a MOR report
    // defaults to its MOR-name (lines joined into one string); otherwise the
    // generic monthly-report name.
    this.exportFileName =
      this.currentDocumentName ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    this.exportDialogOpen = true;
  }

  /**
   * The MOR name as a single file-name-friendly string: the rendered title lines
   * joined and their spaces collapsed to underscores (e.g. lines "September-2025",
   * "Monthly Operational Report", "IOCL Panipat" →
   * "September-2025_Monthly_Operational_Report_IOCL_Panipat"). Empty when no MOR
   * name was entered.
   */
  private getMorExportName(): string {
    const lines = this.morNameLinesForRender();
    if (!lines.length) {
      return "";
    }
    return lines.join(" ").replace(/\s+/g, "_");
  }

  /**
   * Default export file name: `Monthly_Operational_Report_<previousMonth><year>`
   * (e.g. opened in June 2026 → "Monthly_Operational_Report_May2026"). The year
   * is the previous month's own year, so January rolls back to the prior year.
   */
  private getDefaultExportName(): string {
    const { month, year } = this.getPreviousMonth();
    return `Monthly_Operational_Report_${month}${year}`;
  }

  closeExportDialog(): void {
    this.exportDialogOpen = false;
  }

  /** Confirm the dialog → start the export with the chosen file name. */
  confirmExportDialog(): void {
    this.exportDialogOpen = false;
    void this.exportAsPdf();
  }

  /** Cancel an in-flight export (stops polling; the worker may still finish). */
  cancelExport(): void {
    this.exportCancelled = true;
    this.googleDocsAbort?.abort();
    this.updateExportProgress(100, "Export cancelled.");
  }

  /** Server-side render: upload HTML → start job → poll → download. */
  private async exportViaServer(): Promise<void> {
    this.updateExportProgress(5, "Preparing document…");
    // Use the name the user entered in the dialog; fall back to the MOR name (for
    // MOR reports) then the default monthly-report name if they cleared the field.
    const baseName =
      (this.exportFileName || "").trim() ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    const title = baseName;
    const fileName = `${this.sanitizeUserFileName(baseName)}.pdf`;
    // Inline the MOR cover image as a data URL so it renders in the off-app PDF.
    const coverImageDataUrl = this.isMorReport
      ? await this.getMorCoverImageDataUrl()
      : "";
    const html = this.buildPdfExportHtml(title, coverImageDataUrl);
    const pageCount = (this.pages || []).length;

    this.updateExportProgress(10, "Starting export…");
    const init = await firstValueFrom(
      this.reportService.initPdfExport({
        title,
        fileName,
        pageCount,
        options: {
          format: "A4",
          orientation: "portrait",
          printBackground: true,
          marginMm: 0,
          scale: 1,
        },
      })
    );
    const job = init?.data;
    if (!job?.jobId || !job?.uploadUrl) {
      throw new Error("Export init returned no job/upload URL");
    }

    // Upload the (multi-MB) HTML straight to S3 via the presigned URL. Use fetch
    // so the auth interceptor doesn't add headers S3's presigned PUT rejects.
    this.updateExportProgress(25, "Uploading document…");
    if (this.exportCancelled) {
      return;
    }
    const putRes = await fetch(job.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": "text/html", ...(job.uploadHeaders || {}) },
      body: html,
    });
    if (!putRes.ok) {
      throw new Error(`HTML upload failed (${putRes.status})`);
    }

    this.updateExportProgress(35, "Rendering…");
    await firstValueFrom(this.reportService.startPdfExport(job.jobId));

    const downloadUrl = await this.pollPdfExport(job.jobId);
    if (!downloadUrl) {
      return; // cancelled
    }

    this.updateExportProgress(98, "Downloading…");
    await this.triggerDownload(downloadUrl, fileName);
    this.updateExportProgress(100, "Export complete.");
  }

  /** Poll the render job until completed/failed/timeout. Returns the PDF URL. */
  private async pollPdfExport(jobId: string): Promise<string | null> {
    const deadline = Date.now() + 3 * 60 * 1000; // 3-minute ceiling
    let intervalMs = 1500;
    while (Date.now() < deadline) {
      if (this.exportCancelled) {
        return null;
      }
      await this.delay(intervalMs);
      if (this.exportCancelled) {
        return null;
      }
      let status;
      try {
        const res = await firstValueFrom(
          this.reportService.getPdfExportStatus(jobId)
        );
        status = res?.data;
      } catch {
        continue; // transient network blip → keep polling
      }
      if (status?.status === "completed" && status.downloadUrl) {
        return status.downloadUrl;
      }
      if (status?.status === "failed") {
        throw new Error(status.error || "Render failed");
      }
      this.updateExportProgress(
        status?.status === "processing" ? 75 : 50,
        status?.status === "processing" ? "Rendering…" : "Queued…"
      );
      intervalMs = Math.min(intervalMs + 500, 4000); // gentle backoff
    }
    throw new Error("Export timed out");
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async triggerDownload(url: string, fileName: string): Promise<void> {
    // Preferred: fetch the PDF and download it as a same-origin blob so the
    // `download` filename is honoured. Requires S3 CORS to allow GET.
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`download ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      return;
    } catch {
      // Fallback: navigate to the presigned URL directly. Its
      // content-disposition=attachment makes the browser download it. Navigation
      // is NOT subject to CORS, and same-tab (no target=_blank) avoids popup
      // blocking when this runs outside the original click gesture.
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  private sanitizeFileName(name: string): string {
    return (
      String(name || "Document")
        .replace(/[^\w.-]+/g, "_")
        .replace(/^[._-]+|[._-]+$/g, "")
        .slice(0, 80) || "Document"
    );
  }

  /** Lighter sanitiser for a user-typed file name: keep spaces, drop only the
   *  characters that are illegal in file names. */
  private sanitizeUserFileName(name: string): string {
    const cleaned = String(name || "")
      // eslint-disable-next-line no-control-regex
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\.pdf$/i, "")
      .replace(/\.+$/, "")
      .trim()
      .slice(0, 120);
    return cleaned || "Document";
  }

  /** Best-effort document title: first heading/line of page 1, else "Document". */
  /** Wait (briefly) for any in-flight reflow to settle before snapshotting. */
  private async waitForReflowSettled(): Promise<void> {
    // A repagination deferred while the user was typing must run before anything
    // snapshots the document, or the export captures a half-paginated page.
    this.flushInputReflow();
    const deadline = Date.now() + 5000;
    while (this.reflowInProgress && Date.now() < deadline) {
      await this.delay(120);
    }
  }

  private updateExportProgress(percent: number, text: string): void {
    this.exportProgress = Math.max(0, Math.min(100, Math.round(percent)));
    this.exportProgressText = text;
  }

  /**
   * Build the self-contained HTML uploaded to the server PDF renderer. Each
   * non-empty page becomes one exact A4 sheet (matching the editor's fixed pages)
   * with the header logo and a footer. All chart/image content is already inline
   * data-URLs and table/text styling is inline, so the document renders
   * identically in headless Chromium without the app's CSS. Editor-only chrome
   * (resize handles, selection outlines, toolbars) is hidden.
   */
  private buildPdfExportHtml(title: string, coverImageDataUrl = ""): string {
    const headerLogoSrc = this.getExportHeaderLogoSrc();
    const pages = (this.pages || []).filter(
      (p) => !this.isPageHtmlEffectivelyEmpty(p?.html || "")
    );
    const pageBlocks = pages
      .map((page, index) => {
        const bodyHtml = String(page?.html || "<p><br></p>");
        // The MOR cover page is a full-bleed image: no header logo, footer, or
        // body padding — the image is attached edge-to-edge to the sheet.
        if (bodyHtml.includes("mor-cover-image")) {
          // Inline the cover image as a data URL so the off-app PDF renderer
          // embeds the bytes (a plain asset URL isn't reliably fetchable there).
          const coverHtml = coverImageDataUrl
            ? bodyHtml.replace(
                /(<img\b[^>]*\bclass="[^"]*mor-cover-image[^"]*"[^>]*>)/,
                (tag) =>
                  tag.replace(/\bsrc="[^"]*"/, () => `src="${coverImageDataUrl}"`)
              )
            : bodyHtml;
          return `
          <section class="pdf-page pdf-cover-page">
            <div class="pdf-page-body pdf-cover-body">${coverHtml}</div>
          </section>`;
        }
        return `
          <section class="pdf-page">
            <div class="pdf-page-header">
              <img src="${headerLogoSrc}" alt="" class="pdf-logo" />
            </div>
            <div class="pdf-page-body">${bodyHtml}</div>
            <div class="pdf-page-footer">Page ${index + 1}</div>
          </section>`;
      })
      .join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${this.escapeHtml(title)}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body {
    font-family: Arial, sans-serif;
    color: #1d2939;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pdf-page {
    width: 210mm;
    height: 297mm;
    overflow: hidden;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    page-break-after: always;
  }
  .pdf-page:last-child { page-break-after: auto; }
  .pdf-page-header {
    height: 84px;
    padding: 14px 24px 8px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .pdf-logo { max-height: 56px; width: auto; object-fit: contain; }
  .pdf-page-body {
    flex: 1;
    padding: 0 56px;
    font-size: 16px;
    line-height: 1.5;
    overflow: hidden;
  }
  .pdf-page-footer {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: 12px;
  }
  .pdf-page-body p,
  .pdf-page-body li,
  .pdf-page-body div { margin: 0; }
  .pdf-page-body img,
  .pdf-page-body .chart-snapshot-image,
  .pdf-page-body .resizable-image img { max-width: 100%; height: auto; }
  .pdf-page-body table { width: 100%; border-collapse: collapse; }
  .pdf-page-body a { color: #1d4ed8; text-decoration: underline; }
  /* Strip editor-only chrome that may be present in the saved markup. */
  .image-resize-handle,
  .image-handle,
  .image-rotate-handle,
  .image-quick-toolbar,
  .table-add-control { display: none !important; }
  .resizable-image.is-selected { outline: none !important; }
  /* Full-bleed MOR cover page: image fills the whole sheet, no chrome, with the
     MOR name overlaid on the navy band. */
  .pdf-cover-body { padding: 0 !important; position: relative; }
  .pdf-cover-body .mor-cover { position: relative; width: 100%; height: 100%; margin: 0; }
  .pdf-cover-body .mor-cover-image {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    object-fit: fill;
    display: block;
  }
  .pdf-cover-body .mor-cover-title {
    position: absolute;
    top: 14%;
    left: 16%;
    right: 8%;
    height: 42%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    color: #ffffff;
    line-height: 1.2;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .pdf-cover-body .mor-cover-line { margin: 0 0 8px; }
  .pdf-cover-body .mor-cover-line-1 { font-size: 40px; font-weight: 800; }
  .pdf-cover-body .mor-cover-line-2 { font-size: 30px; font-weight: 500; }
  .pdf-cover-body .mor-cover-line-3 { font-size: 26px; font-weight: 700; }
</style>
</head>
<body>${pageBlocks}</body>
</html>`;
  }

  private buildExportDocumentHtml(title: string, forPrint: boolean): string {
    const headerLogoSrc = this.getExportHeaderLogoSrc();
    const headerBorderCss = forPrint ? "none" : "1px solid #e2e8f0";
    const pageBlocks = (this.pages || [])
      .map((page, index) => {
        const bodyHtml = String(page?.html || "<p><br></p>");
        return `
          <section class="export-page">
            <div class="export-page-header">
              <img src="${headerLogoSrc}" alt="Header Logo" class="export-page-logo" />
            </div>
            <div class="export-page-body">${bodyHtml}</div>
            <div class="export-page-footer">Page ${index + 1}</div>
          </section>
        `;
      })
      .join("");

    const printStyle = forPrint
      ? `
        @page { size: A4; margin: 12mm; }
        body { background: #fff; }
      `
      : "";

    return `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${this.escapeHtml(title)}</title>
        <style>
          ${printStyle}
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 16px;
            font-family: Arial, sans-serif;
            color: #0f172a;
          }
          .export-page {
            width: 100%;
            max-width: 794px;
            min-height: 1123px;
            margin: 0 auto 16px;
            border: 1px solid #d5deea;
            border-radius: 8px;
            background: #fff;
            display: flex;
            flex-direction: column;
            page-break-after: always;
          }
          .export-page-header {
            height: 76px;
            padding: 10px 18px 8px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            border-bottom: ${headerBorderCss};
          }
          .export-page-logo {
            max-height: 56px;
            width: auto;
            object-fit: contain;
          }
          .export-page:last-child { page-break-after: auto; margin-bottom: 0; }
          .export-page-body {
            flex: 1;
            padding: 24px 28px;
            font-size: 16px;
            line-height: 1.5;
            overflow: hidden;
          }
          .export-page-footer {
            text-align: center;
            padding: 8px 0 12px;
            color: #64748b;
            font-size: 13px;
          }
          .export-page-body img {
            max-width: 100%;
            height: auto;
          }
          .export-page-body table {
            width: 100%;
            border-collapse: collapse;
          }
          .export-page-body table td,
          .export-page-body table th {
            border: 1px solid #cbd5e1;
            padding: 4px 6px;
          }
        </style>
      </head>
      <body>
        ${pageBlocks}
      </body>
      </html>
    `;
  }

  private isDocPageElementEffectivelyEmpty(pageEl: HTMLElement): boolean {
    const editor = pageEl.querySelector(".page-editor") as HTMLElement | null;
    if (!editor) {
      return true;
    }

    const hasVisualContent =
      !!editor.querySelector("img[src]") ||
      !!editor.querySelector(".report-chart img.chart-snapshot-image") ||
      !!editor.querySelector(".report-table-block tbody tr") ||
      !!editor.querySelector("table tbody tr") ||
      !!editor.querySelector("hr") ||
      !!editor.querySelector("iframe[src]") ||
      !!editor.querySelector("video[src]") ||
      !!editor.querySelector("canvas") ||
      !!editor.querySelector("svg path, svg rect, svg circle, svg line, svg polyline, svg polygon");
    if (hasVisualContent) {
      return false;
    }

    const text = String(editor.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text.length === 0;
  }

  private isPageHtmlEffectivelyEmpty(html: string): boolean {
    const container = document.createElement("div");
    container.innerHTML = String(html || "");

    // A table whose body has drained away contributes nothing but its column
    // headings. Left in, those headings read as text and the page counted as
    // content — so the export carried a sheet holding a stranded table header
    // and nothing else. Drop them before judging the page.
    container.querySelectorAll("table").forEach((table) => {
      if (!table.querySelector("tbody tr")) {
        table.remove();
      }
    });

    const hasVisualContent =
      !!container.querySelector("img[src]") ||
      !!container.querySelector(".report-chart img.chart-snapshot-image") ||
      !!container.querySelector(".report-table-block tbody tr") ||
      !!container.querySelector("table tbody tr") ||
      !!container.querySelector("hr") ||
      !!container.querySelector("iframe[src]") ||
      !!container.querySelector("video[src]") ||
      !!container.querySelector("canvas") ||
      !!container.querySelector("svg path, svg rect, svg circle, svg line, svg polyline, svg polygon");
    if (hasVisualContent) {
      return false;
    }

    const text = String(container.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text.length === 0;
  }

  /**
   * Load the MOR cover image and return it as a JPEG data URL at its native size,
   * so the server-rendered PDF embeds the bytes directly (a plain asset URL isn't
   * reliably fetchable by the off-app renderer). Returns "" on any failure so the
   * export falls back to the URL src.
   */
  private getMorCoverImageDataUrl(): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve("");
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        } catch {
          resolve("");
        }
      };
      img.onerror = () => resolve("");
      img.src = this.getMorCoverImageSrc();
    });
  }

  private getExportHeaderLogoSrc(): string {
    const renderedLogo = document.querySelector(".page-header-logo") as HTMLImageElement | null;
    if (renderedLogo?.src) {
      return renderedLogo.src;
    }
    try {
      return new URL("assets/images/logo-wwtp.png", document.baseURI).toString();
    } catch {
      return "assets/images/logo-wwtp.png";
    }
  }

  private loadDashboardGroupFromQuery(): void {
    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = this.route.queryParamMap.subscribe((params) => {
      const dashboardGroupId = Number(params.get("dashboardGroupId"));
      if (!dashboardGroupId || Number.isNaN(dashboardGroupId)) {
        this.editorLoading = false;
        // Blank/standalone editor: offer any prior local draft and allow saving.
        void this.checkForDraft();
        this.documentReady = true;
        return;
      }
      if (this.lastLoadedDashboardGroupId === dashboardGroupId) {
        return;
      }
      this.lastLoadedDashboardGroupId = dashboardGroupId;
      this.userRestoredDraft = false;
      this.editorLoading = true;
      // Offer a saved draft (if any) for this group while fresh data loads.
      void this.checkForDraft();

      // Reuse the payload already fetched for the entry dialog's data config
      // instead of refetching the (potentially multi-MB) with-reports response.
      const cached = this.cachedGroupForRender;
      if (cached && cached.id === dashboardGroupId) {
        if (this.userRestoredDraft) {
          this.editorLoading = false;
          return;
        }
        void this.renderDashboardGroupIntoDocument(cached.data);
        return;
      }

      this.reportService.getDashboardGroupWithReports(dashboardGroupId).subscribe({
        next: (response) => {
          // The user restored a draft while this was loading — keep their document.
          if (this.userRestoredDraft) {
            this.editorLoading = false;
            return;
          }
          if (!response?.success || !response?.data) {
            this.editorLoading = false;
            this.documentReady = true;
            return;
          }
          void this.renderDashboardGroupIntoDocument(response.data);
        },
        error: () => {
          // Keep editor usable even if loading fails.
          this.editorLoading = false;
          this.documentReady = true;
        },
      });
    });
  }

  /** Absolute URL of the MOR cover image (resolves in the editor and re-hydrated
   *  saved documents). For the server-rendered PDF it's inlined as a data URL. */
  private getMorCoverImageSrc(): string {
    const relative = "assets/images/6628c30b-0b7a-4017-a730-78f9982e41f8-0.jpg";
    try {
      return new URL(relative, document.baseURI).toString();
    } catch {
      return relative;
    }
  }

  /**
   * HTML for the reserved MOR cover page (page 1): the cover image with the MOR
   * name overlaid. Kept as its own block so it survives reflow and exports into
   * the PDF (where the image src is swapped for an inline data URL).
   */
  private buildMorCoverPageHtml(): string {
    const src = this.getMorCoverImageSrc();
    // The MOR name lines entered in the entry dialog are stacked on the cover's
    // navy band (positioned/styled via CSS). Each line gets a hierarchy class
    // (line 1 largest, line 2 medium, line 3+ smaller-bold) to match the design.
    // Baked into the page HTML so it persists in saved documents and the PDF.
    const linesHtml = this.morNameLinesForRender()
      .map((line, i) => {
        const tierClass =
          i === 0
            ? "mor-cover-line-1"
            : i === 1
            ? "mor-cover-line-2"
            : "mor-cover-line-3";
        return `<div class="mor-cover-line ${tierClass}">${this.escapeHtml(
          line
        )}</div>`;
      })
      .join("");
    return (
      `<div class="mor-cover">` +
      `<img src="${src}" alt="MOR cover" class="mor-cover-image" />` +
      `<div class="mor-cover-title">${linesHtml}</div>` +
      `</div>`
    );
  }

  /**
   * Build the reserved page-2 Table of Contents for a MOR report: each dashboard
   * is a numbered heading (1., 2., …) and its data reports the numbered children
   * (1.1, 1.2, …). Page-number cells are placeholders here — they're resolved
   * from the paginated DOM by refreshTableOfContents() (via the shared
   * data-toc-page-for / data-report-id markers) after each reflow and before
   * save/export, so they stay correct if content later shifts pages.
   */
  private buildMorTableOfContentsHtml(): string {
    const rows: string[] = [];
    this.morTocGroups.forEach((group, gi) => {
      const dashNo = gi + 1;
      // The dashboard's page is where its first listed report lands.
      const firstReportId = group.reports[0]?.id ?? null;
      rows.push(this.morTocLevel1Row(`${dashNo}. ${group.name}`, firstReportId));
      group.reports.forEach((report, ri) => {
        rows.push(
          this.morTocLevel2Row(`${dashNo}.${ri + 1}`, report.name, report.id)
        );
      });
    });

    // data-toc-block + data-page-exclusive mark this as the (page-owning) TOC so
    // refreshTableOfContents() fills the page numbers. contenteditable=false keeps
    // the auto-managed index from being edited by hand.
    // data-report-id is a synthetic sentinel (not a real report) so the reflow's
    // block-split continuation matching keys off it cleanly when the TOC spans
    // multiple pages. It's never referenced by a page-number cell.
    return `
      <section class="report-block toc-report-block" data-report-id="mor-toc" data-toc-block="true" data-page-exclusive="1">
        <div class="imported-report-content toc-container" contenteditable="false" style="margin:6px 0 20px;padding:4px 2px 12px;background:#ffffff;">
          <h2 class="toc-title" style="margin:0 0 14px;font-size:19px;font-weight:700;color:#1f3864;text-align:left;">Table of Contents</h2>
          ${rows.join("") || '<div style="color:#94a3b8;font-size:13px;">No reports to list.</div>'}
        </div>
      </section>
    `;
  }

  /** MOR TOC dashboard heading row ("N. Dashboard …… page"). */
  private morTocLevel1Row(label: string, pageRefId: number | null): string {
    const pageAttr = pageRefId != null ? `data-toc-page-for="${pageRefId}"` : "";
    return `
      <div class="toc-row toc-l1" style="display:flex;align-items:flex-end;gap:6px;margin:12px 0 4px;font-size:14px;font-weight:700;color:#1f4e79;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #6b7a90;transform:translateY(-4px);"></span>
        <span ${pageAttr} style="flex:0 0 auto;min-width:26px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** MOR TOC report row ("N.M Report …… page"), indented under its dashboard. */
  private morTocLevel2Row(
    numLabel: string,
    name: string,
    pageRefId: number | null
  ): string {
    const pageAttr = pageRefId != null ? `data-toc-page-for="${pageRefId}"` : "";
    return `
      <div class="toc-row toc-l2" style="display:flex;align-items:flex-end;gap:6px;margin:3px 0 3px 26px;font-size:13px;font-weight:400;color:#1f3864;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"><span style="display:inline-block;min-width:38px;">${this.escapeHtml(
          numLabel
        )}</span>${this.escapeHtml(name)}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #9aa0a6;transform:translateY(-4px);"></span>
        <span ${pageAttr} style="flex:0 0 auto;min-width:24px;text-align:right;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  private async renderDashboardGroupIntoDocument(
    dashboardGroup: DashboardGroupWithReportsResponse
  ): Promise<void> {
    // Restrict the document to the data window chosen in the entry dialog.
    dashboardGroup = this.clipReportsToWindow(dashboardGroup);
    this.reportContentCache.clear();

    // Capture the render order so each Table of Contents block can index only what
    // follows it. collectRenderReports uses the same layout order + group-wide
    // dedup as the render loop below, so it lines up one-to-one with how reports
    // land in the document. Page numbers are filled in after pagination settles
    // (refreshTableOfContents).
    this.tocOrder = this.collectRenderReports(dashboardGroup).map((report) => ({
      reportId: report.reportId,
      name: (report.reportName || "Report").trim() || "Report",
      isToc: this.isTocReportName(report.reportName),
      isMorHead: this.isMorHeadingReport(report),
    }));

    const allSections: string[] = [];
    const dashboards = dashboardGroup.dashboards || [];

    // Dedupe reports across the WHOLE group, not just within a dashboard. The
    // same report can be placed on several dashboards in a group; rendering the
    // continuous stream would otherwise repeat it (e.g. a long table appearing
    // many times). getUniqueReports() only dedupes within one dashboard.
    const seenReportKeys = new Set<string>();
    // Rebuilt below for the MOR page-2 Table of Contents.
    const morTocGroups: Array<{
      name: string;
      reports: Array<{ id: number; name: string }>;
    }> = [];
    for (let i = 0; i < dashboards.length; i += 1) {
      const dashboard = dashboards[i];
      // Collect this dashboard's listed reports for the Table of Contents (data
      // reports only — text notes and section headings are excluded).
      const tocGroup = {
        name: dashboard.dashboardName || `Dashboard ${i + 1}`,
        reports: [] as Array<{ id: number; name: string }>,
      };

      const reports = this.getUniqueReports(
        this.orderReportsByLayout(dashboard)
      );

      // Lay every report out in one continuous stream (no per-text-report or
      // per-dashboard page splits). reflowFrom() only pushes overflow forward and
      // never backfills, so pre-splitting here left each report/title on its own
      // near-empty sheet. A single stream lets reflow pack each page to capacity.
      for (let r = 0; r < reports.length; r += 1) {
        const report = reports[r];
        const key = this.getReportDedupKey(report);
        if (seenReportKeys.has(key)) {
          continue;
        }
        seenReportKeys.add(key);
        const section = await this.buildReportSection(report);
        allSections.push(section);

        // Add to the Table of Contents grouping. Text/note reports ARE included;
        // only section-heading (mor_head) reports and any TOC report are skipped.
        if (
          !this.isMorHeadingReport(report) &&
          !this.isTocReportName(report.reportName)
        ) {
          // Same name the report renders its chart/table title with; fall back to
          // the first parameter's name, then a generic label.
          const reportName =
            (
              report.reportName ||
              report.parameters?.[0]?.paramName ||
              "Report"
            ).trim() || "Report";
          tocGroup.reports.push({ id: report.reportId, name: reportName });
        }
      }

      if (tocGroup.reports.length) {
        morTocGroups.push(tocGroup);
      }
    }
    this.morTocGroups = morTocGroups;

    if (!allSections.length) {
      allSections.push("<p>No reports found for this dashboard group.</p>");
    }

    // MOR reports reserve only page 1 (the cover). The Table of Contents then
    // LEADS the flowing content as a page-exclusive block, so pagination gives it
    // its own page (page 2) and, when it's too long to fit, continues it onto the
    // next page(s) — reports begin only on the page after the TOC finishes.
    this.reservedLeadingPages = this.isMorReport ? 1 : 0;

    const bodySections = this.isMorReport
      ? [this.buildMorTableOfContentsHtml(), ...allSections]
      : allSections;
    const contentHtml = this.justifyDocumentText(bodySections.join(""));
    this.pages = [];
    for (let p = 0; p < this.reservedLeadingPages; p += 1) {
      // Page 1 = MOR cover image.
      this.pages.push({ id: p + 1, html: this.buildMorCoverPageHtml() });
    }
    this.pages.push({ id: this.reservedLeadingPages + 1, html: contentHtml });
    this.nextPageId = this.reservedLeadingPages + 2;
    // Open on the cover page (page 1) so the document starts at the cover rather
    // than jumping down to the reports.
    this.activePageIndex = 0;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(this.reservedLeadingPages);
      this.focusActivePage();
      this.updateDocumentStats();
      // Heights settle a tick after reflow; dump the live DOM so we can confirm
      // how reports landed across pages and whether any page came out blank.
      this.editorLoading = false;
      // Document is now stable — enable autosave of further user edits.
      this.documentReady = true;
    }, 0);
  }

  private isTextReport(report: DashboardReportData): boolean {
    return String(report.parameters?.[0]?.graphType || "").toLowerCase() === "text";
  }

  private async buildDashboardSection(
    dashboard: DashboardWithReportsItem,
    dashboardNumber: number
  ): Promise<string> {
    const dashboardName = this.escapeHtml(
      dashboard.dashboardName || `Dashboard ${dashboardNumber}`
    );
    const reports = this.getUniqueReports(dashboard.reports || []);

    const reportSections: string[] = [];
    for (let i = 0; i < reports.length; i += 1) {
      reportSections.push(await this.buildReportSection(reports[i]));
    }

    return `
      <h2>${dashboardNumber}. ${dashboardName}</h2>
      <p>Type: ${this.escapeHtml(dashboard.dashboardType || "-")}</p>
      ${reportSections.join("") || "<p>No reports configured.</p>"}
    `;
  }

  /**
   * Order a dashboard's reports to match the on-screen grid reading order:
   * row by row top-to-bottom, left-to-right within a row.
   *
   * This mirrors the live dashboard's cell→report binding
   * (dashboard-report-view.loadDashboardLayout) so the PDF renders reports in the
   * SAME order the user sees on screen. Each grid cell (layoutConfig.items, sorted
   * by y then x) is bound to its report by, in priority:
   *   1. an explicit reportId stamped on the cell,
   *   2. the report whose section id equals the cell id
   *      (report.metadata.sectionId or report.layoutConfig.id),
   *   3. a reading-order fallback for reports that carry no section.
   * The previous implementation matched only by stamped reportId and otherwise
   * fell back to positional array index, which did NOT match the grid and made
   * reports appear shuffled.
   */
  private orderReportsByLayout(
    dashboard: DashboardWithReportsItem
  ): DashboardReportData[] {
    const reports = dashboard?.reports || [];
    const items = dashboard?.layoutConfig?.items;
    if (!Array.isArray(items) || items.length === 0 || reports.length <= 1) {
      // No usable dashboard grid → fall back to each report's own grid position.
      return this.orderReportsByOwnPosition(reports);
    }

    const reportByReportId = new Map<number, DashboardReportData>();
    reports.forEach((report) => {
      const rid = Number(report?.reportId);
      if (!Number.isNaN(rid) && !reportByReportId.has(rid)) {
        reportByReportId.set(rid, report);
      }
    });

    const reportBySectionId = new Map<number, DashboardReportData>();
    reports.forEach((report) => {
      const sid = this.getReportSectionId(report);
      if (sid !== null && !reportBySectionId.has(sid)) {
        reportBySectionId.set(sid, report);
      }
    });

    const resolved: (DashboardReportData | undefined)[] = new Array(
      items.length
    ).fill(undefined);
    const assigned = new Set<number>();

    // Pass 1: bind each cell by explicit reportId, then by section id.
    items.forEach((item, index) => {
      const stamped = Number((item as any)?.reportId);
      let match: DashboardReportData | undefined;
      if (!Number.isNaN(stamped) && reportByReportId.has(stamped)) {
        match = reportByReportId.get(stamped);
      } else {
        const cellId = Number((item as any)?.id);
        if (!Number.isNaN(cellId) && reportBySectionId.has(cellId)) {
          match = reportBySectionId.get(cellId);
        }
      }
      const rid = Number(match?.reportId);
      if (match && !Number.isNaN(rid) && !assigned.has(rid)) {
        resolved[index] = match;
        assigned.add(rid);
      }
    });

    const cellsInReadingOrder = items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort(
        (a, b) =>
          (Number((a.item as any)?.y) || 0) - (Number((b.item as any)?.y) || 0) ||
          (Number((a.item as any)?.x) || 0) - (Number((b.item as any)?.x) || 0) ||
          a.originalIndex - b.originalIndex
      );

    // Pass 2: fill still-empty cells (reading order) with reports not yet placed.
    const leftovers = reports.filter(
      (report) => !assigned.has(Number(report?.reportId))
    );
    let leftoverIdx = 0;
    cellsInReadingOrder.forEach(({ originalIndex }) => {
      if (resolved[originalIndex] === undefined && leftovers[leftoverIdx]) {
        const report = leftovers[leftoverIdx++];
        resolved[originalIndex] = report;
        assigned.add(Number(report?.reportId));
      }
    });

    // Emit reports in reading order (cells sorted by y then x).
    const result: DashboardReportData[] = [];
    const used = new Set<number>();
    cellsInReadingOrder.forEach(({ originalIndex }) => {
      const report = resolved[originalIndex];
      if (report) {
        const rid = Number(report.reportId);
        if (!used.has(rid)) {
          result.push(report);
          used.add(rid);
        }
      }
    });

    // A report not bound to any layout cell is NOT shown on the on-screen
    // dashboard (the grid / export-absolute view renders only celled reports), so
    // it must not appear in the PDF either. Appending un-celled reports here
    // surfaced reports the dashboard hides — out of sequence at the end — and made
    // re-created copies (same content, but a different reportId that never took a
    // cell) look like duplicated reports. Only when the grid bound NOTHING at all
    // (a malformed layout) do we fall back to every report in its original order,
    // so the document is never left empty.
    if (!result.length) {
      return this.orderReportsByOwnPosition(reports);
    }

    return result;
  }

  /**
   * The grid section id a report was assigned to — from the sectionId we stamp at
   * save time, or the per-report layout config id for older dashboards. Mirrors
   * dashboard-report-view.getReportSectionId. Returns null when unavailable.
   */
  private getReportSectionId(report: DashboardReportData): number | null {
    const metaSection = Number((report as any)?.metadata?.sectionId);
    if (!Number.isNaN(metaSection)) {
      return metaSection;
    }
    const layout =
      (report as any)?.layout_config ?? (report as any)?.layoutConfig;
    if (layout && layout.id !== undefined && layout.id !== null) {
      const layoutId = Number(layout.id);
      if (!Number.isNaN(layoutId)) {
        return layoutId;
      }
    }
    return null;
  }

  /**
   * Fallback ordering by each report's own grid position (layoutConfig.{y,x}),
   * used when the dashboard's layout grid is unavailable. Sorts into reading
   * order (top-to-bottom, then left-to-right). Reports without a position keep
   * their original relative order at the end.
   */
  private orderReportsByOwnPosition(
    reports: DashboardReportData[]
  ): DashboardReportData[] {
    if (!Array.isArray(reports) || reports.length <= 1) {
      return reports;
    }

    const positionOf = (report: DashboardReportData) => {
      const layout =
        (report as any)?.layout_config ?? (report as any)?.layoutConfig;
      const hasX = layout && layout.x !== undefined && layout.x !== null;
      const hasY = layout && layout.y !== undefined && layout.y !== null;
      if (!hasX && !hasY) {
        return null;
      }
      return { x: Number(layout.x) || 0, y: Number(layout.y) || 0 };
    };

    // Only reorder when every report exposes a position; otherwise the data
    // doesn't support reliable grid ordering, so leave the order untouched.
    const positioned = reports.map((report, index) => ({
      report,
      index,
      pos: positionOf(report),
    }));
    if (positioned.some((p) => p.pos === null)) {
      return reports;
    }

    return positioned
      .sort(
        (a, b) =>
          a.pos!.y - b.pos!.y || a.pos!.x - b.pos!.x || a.index - b.index
      )
      .map((p) => p.report);
  }

  private getUniqueReports(reports: DashboardReportData[]): DashboardReportData[] {
    const seen = new Set<string>();
    const unique: DashboardReportData[] = [];

    for (let i = 0; i < reports.length; i += 1) {
      const report = reports[i];
      const key = this.getReportDedupKey(report);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(report);
    }

    return unique;
  }

  private getReportDedupKey(report: DashboardReportData): string {
    const reportId = Number(report.reportId);
    if (!Number.isNaN(reportId) && reportId > 0) {
      return `id:${reportId}`;
    }

    return [
      report.reportName || "",
      report.moduleType || "",
      report.interval || "",
      report.period?.startDate || "",
      report.period?.endDate || "",
    ].join("|");
  }

  /**
   * Render the report's description beneath its chart/table, mirroring the live
   * report view's description band (chart-view.component). Returns empty when the
   * report has no description.
   */
  private renderReportDescriptionHtml(report: DashboardReportData): string {
    const description = String((report as any)?.description || "").trim();
    if (!description) {
      return "";
    }
    // Rendered in the same size and colour as the body text (not faded), sitting
    // just under the chart/table.
    return `
      <p class="report-description" style="margin:6px 2px 0;font-size:16px;line-height:1.5;color:#1d2939;text-align:left;">
        ${this.escapeHtml(description)}
      </p>
    `;
  }

  /** True when a report's NAME contains the phrase "Table of Content(s)". */
  private isTocReportName(name: string | null | undefined): boolean {
    return /table\s+of\s+contents?/i.test(String(name || ""));
  }

  /**
   * True when a report's authored CONTENT opens with a "Table of Contents"
   * heading. This is the fallback detector for reports whose configured name
   * isn't literally "Table of Contents" but whose body the user started with that
   * title — so the block is still recognised and auto-generated.
   */
  private isTocContent(html: string | null | undefined): boolean {
    const text = this.toPlainText(String(html || "")).trim();
    return /^table\s+of\s+contents?\b/i.test(text);
  }

  /** A bold, top-level (section heading) Table of Contents row. Rendered as a
   *  block-level div so the reflow engine can flow rows across page boundaries. */
  private tocLevel1Row(label: string, pageRefId: number): string {
    return `
      <div class="toc-row toc-l1" style="display:flex;align-items:flex-end;gap:6px;margin:12px 0 4px;font-size:14px;font-weight:700;color:#1f4e79;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #6b7a90;transform:translateY(-4px);"></span>
        <span data-toc-page-for="${pageRefId}" style="flex:0 0 auto;min-width:26px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** A numbered, nested (report) Table of Contents row. */
  private tocLevel2Row(num: number, label: string, pageRefId: number): string {
    return `
      <div class="toc-row toc-l2" style="display:flex;align-items:flex-end;gap:6px;margin:3px 0 3px 26px;font-size:13px;font-weight:400;color:#1f3864;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"><span style="display:inline-block;min-width:24px;">${num})</span>${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #9aa0a6;transform:translateY(-4px);"></span>
        <span data-toc-page-for="${pageRefId}" style="flex:0 0 auto;min-width:24px;text-align:right;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** True when a report is a MOR Heading (heading-only section) report. */
  private isMorHeadingReport(report: DashboardReportData): boolean {
    return (
      String(report.parameters?.[0]?.graphType || "").toLowerCase() === "mor_head"
    );
  }

  /**
   * Render a MOR Heading report as a bold, navy section heading carrying just the
   * report name. It stays in the normal editable flow (so the reports that follow
   * it sit beneath it) and carries a data-report-id so the Table of Contents can
   * resolve its page.
   */
  private buildMorHeadingSection(report: DashboardReportData): string {
    const title = this.escapeHtml(
      (
        report.reportName ||
        report.parameters?.[0]?.paramName ||
        "Section"
      )
        .toString()
        .trim() || "Section"
    );
    return `
      <section class="report-block mor-heading-block" data-report-id="${report.reportId}">
        <h2 class="mor-heading" style="margin:20px 0 10px;font-size:18px;font-weight:700;color:#1f4e79;line-height:1.4;text-align:left;">${title}</h2>
        <p><br /></p>
      </section>
    `;
  }

  /**
   * Render the Table of Contents block, driven by MOR Heading reports: each MOR
   * Heading that follows this block becomes a bold top-level row, and the normal
   * reports after it become numbered children with a single continuous counter
   * across the whole document. Only what comes AFTER this block is listed — and
   * any content the user authored under the report is ignored: the block is always
   * fully system-generated.
   *
   * Names are laid out now so the block's height is fixed up front; the page-number
   * cells are filled in later by refreshTableOfContents() once pagination settles.
   * The inner container is non-editable so the auto-managed index isn't broken.
   */
  private buildTableOfContentsSection(report: DashboardReportData): string {
    // List everything after this TOC report. If the report can't be located in the
    // captured order, fall back to listing the whole document.
    const tocId = report.reportId;
    const startIndex = this.tocOrder.findIndex(
      (entry) => entry.reportId === tocId
    );
    const entries = (
      startIndex >= 0 ? this.tocOrder.slice(startIndex + 1) : this.tocOrder
    ).filter((entry) => !entry.isToc);

    let counter = 0;
    const rows: string[] = [];
    for (const entry of entries) {
      if (entry.isMorHead) {
        // Section heading → bold top-level row (no number).
        rows.push(this.tocLevel1Row(entry.name, entry.reportId));
      } else {
        counter += 1;
        rows.push(this.tocLevel2Row(counter, entry.name, entry.reportId));
      }
    }

    // data-page-exclusive="1" tells the reflow engine this block must own its
    // page(s): no report shares a page with the TOC, and if it overflows, the
    // continuation pages stay report-free too. The content body is an
    // .imported-report-content div so the engine's text-split machinery can flow
    // the rows across page boundaries (each row is a direct child it can move).
    return `
      <section class="report-block toc-report-block" data-report-id="${report.reportId}" data-toc-block="true" data-page-exclusive="1">
        <div class="imported-report-content toc-container" contenteditable="false" style="margin:6px 0 20px;padding:4px 2px 12px;background:#ffffff;">
          <h2 class="toc-title" style="margin:0 0 14px;font-size:19px;font-weight:700;color:#1f3864;text-align:left;">Table of Contents</h2>
          ${rows.join("") || '<div style="color:#94a3b8;font-size:13px;">No reports to list.</div>'}
        </div>
        <p><br /></p>
      </section>
    `;
  }

  /** True when a block is marked page-exclusive (must own its page). */
  private isExclusiveBlock(el: Element | null | undefined): boolean {
    return (
      el instanceof HTMLElement &&
      el.getAttribute("data-page-exclusive") === "1"
    );
  }

  /**
   * True when a page holds a page-exclusive block alongside other blocks — i.e.
   * the exclusive block isn't yet alone on its page, so reflow must keep pushing
   * the other blocks forward.
   */
  private pageHasUnisolatedExclusive(editor: HTMLElement): boolean {
    if (editor.children.length <= 1) {
      return false;
    }
    for (let i = 0; i < editor.children.length; i += 1) {
      if (this.isExclusiveBlock(editor.children[i])) {
        return true;
      }
    }
    return false;
  }

  /**
   * Fill (or correct) the page numbers in every Table of Contents block from the
   * live paginated DOM: each indexed report's number is the 1-based page on which
   * it first appears. Driven purely by the data-* markers in the DOM, so it works
   * for a freshly rendered group AND a reopened saved document. Safe to call
   * repeatedly — it only rewrites the tiny page-number cells (never the row
   * layout), so it can't itself provoke another reflow. Called after every reflow
   * settles and again right before save/export.
   */
  private refreshTableOfContents(): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }
    const hasToc = editors.some((ed) =>
      ed.nativeElement.querySelector('[data-toc-block="true"]')
    );
    if (!hasToc) {
      return;
    }

    // reportId → the first (lowest) page number on which it appears.
    const pageOf = new Map<string, number>();
    for (let i = 0; i < editors.length; i += 1) {
      const blocks = editors[i].nativeElement.querySelectorAll("[data-report-id]");
      for (let b = 0; b < blocks.length; b += 1) {
        const id = blocks[b].getAttribute("data-report-id");
        if (id && !pageOf.has(id)) {
          pageOf.set(id, i + 1);
        }
      }
    }

    let changed = false;
    for (let i = 0; i < editors.length; i += 1) {
      const cells = editors[i].nativeElement.querySelectorAll("[data-toc-page-for]");
      for (let c = 0; c < cells.length; c += 1) {
        const cell = cells[c] as HTMLElement;
        const id = cell.getAttribute("data-toc-page-for");
        const page = id ? pageOf.get(id) : undefined;
        const text = page ? String(page) : "–";
        if (cell.textContent !== text) {
          cell.textContent = text;
          changed = true;
        }
      }
    }

    if (changed) {
      this.syncPagesFromDom();
    }
  }

  /**
   * A bold, left-aligned heading carrying the report's name, rendered at the top
   * of every data report section — the single place the name appears. Charts and
   * tables used to repeat it internally (baked into the snapshot image, and as a
   * table title bar) which showed the name twice; both now leave it to this
   * heading, which also covers rich-content reports that render with no name.
   */
  private buildReportNameHeading(report: DashboardReportData): string {
    const name = (report.reportName || "").toString().trim();
    if (!name) {
      return "";
    }
    return `
      <h3 class="report-name-heading" style="margin:18px 0 8px;font-size:22px;font-weight:700;color:#1f4e79;line-height:1.4;text-align:left;">${this.escapeHtml(
        name
      )}</h3>
    `;
  }

  private async buildReportSection(
    report: DashboardReportData
  ): Promise<string> {
    // A MOR Heading is a heading-only section (no data) — render its title as a
    // bold section heading and stop. The reports that follow it render normally.
    if (this.isMorHeadingReport(report)) {
      return this.buildMorHeadingSection(report);
    }

    const { content: richContent } = await this.getReportContentReportWise(report);

    // A report named "Table of Content(s)" — or whose authored body opens with a
    // "Table of Contents" heading — renders as the auto-generated, page-aware
    // index instead of its own data. The user's content is intentionally ignored.
    if (
      this.isTocReportName(report.reportName) ||
      this.isTocContent(richContent)
    ) {
      return this.buildTableOfContentsSection(report);
    }

    // Bold report-name heading shown at the top of every data report section so
    // the name always renders in the editor.
    const nameHeading = this.buildReportNameHeading(report);

    const normalizedRichContent = this.normalizeReportHtmlContent(
      richContent,
      report.reportId
    );
    const controlledRichContent = this.wrapImagesForControl(normalizedRichContent);

    // Table reports are data-driven and must be rendered fresh as a real table.
    // Their saved rich content can be a stale chart snapshot from an earlier
    // render, so never substitute it in for a table report.
    const isTableReport = this.resolvesToTableReport(report);

    // A saved chart snapshot is a pre-rendered image of the full dataset; when the
    // export window clipped this report's data, the snapshot is stale, so redraw
    // it fresh from the windowed values instead of substituting the snapshot.
    const windowClipped = !!(
      report as unknown as { __windowClipped?: boolean }
    ).__windowClipped;

    if (controlledRichContent && !isTableReport && !windowClipped) {
      return `
        <section class="report-block" data-report-id="${report.reportId}">
          ${nameHeading}
          <div class="imported-report-content">
            ${controlledRichContent}
          </div>
          <p><br /></p>
        </section>
      `;
    }

    const chartDataForType = this.extractChartDataForReport(report);
    const resolvedType = this.resolveReportChartType(report, chartDataForType);

    if (resolvedType === "table") {

      // The six explicit table variants (tbl_row/col/rfn/cfn/rfm/cfm) render
      // through their dedicated layouts. Legacy "table" reports keep the prior
      // behaviour: OPEX consumption+cost pairs use the grouped layout, others
      // use the generic date/parameter grid.
      const variant = this.tableVariantFor(report);
      const tableHtml = variant.startsWith("tbl_")
        ? this.renderTableVariantHtml(
            report,
            chartDataForType,
            report.reportName || "Report",
            variant
          )
        : this.isConsumptionCostTableReport(report)
          ? this.renderConsumptionCostTableHtml(report)
          : this.renderReportTableHtml(
              chartDataForType,
              report.reportName || "Report",
              report.reportId
            );
      if (tableHtml) {
        return `
          <section class="report-block table-report-block" data-report-id="${report.reportId}">
            ${nameHeading}
            ${tableHtml}
            ${this.renderReportDescriptionHtml(report)}
            <p><br /></p>
          </section>
        `;
      }
    }

    const chartSnapshotHtml = this.renderReportChartSnapshotHtml(report);
    if (chartSnapshotHtml) {
      const reportBlockClass =
        resolvedType === "table" ? "report-block table-report-block" : "report-block";
      return `
        <section class="${reportBlockClass}" data-report-id="${report.reportId}">
          ${nameHeading}
          ${chartSnapshotHtml}
          ${this.renderReportDescriptionHtml(report)}
          <p><br /></p>
        </section>
      `;
    }

    const parameterRows = (report.parameters || [])
      .map(
        (param, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(param.paramName || "-")}</td>
            <td>${this.escapeHtml(param.graphType || "-")}</td>
            <td>${this.escapeHtml(String(param.benchmark ?? "-"))}</td>
          </tr>
        `
      )
      .join("");

    return `
      <section class="report-block" data-report-id="${report.reportId}">
        ${nameHeading}
        <table class="doc-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Parameter</th>
              <th>Graph Type</th>
              <th>Benchmark</th>
            </tr>
          </thead>
          <tbody>
            ${parameterRows || '<tr><td colspan="4">No parameters</td></tr>'}
          </tbody>
        </table>
        <p><br /></p>
      </section>
    `;
  }

  private renderReportChartHtml(report: DashboardReportData): string {
    const chartData = this.extractChartDataForReport(report);
    if (!chartData.series.length || !chartData.categories.length) {
      return "";
    }
    const chartType = this.resolveReportChartType(report, chartData);

    if (chartType === "bar") {
      return this.renderBarChartHtml(chartData, report.reportName || "Chart");
    }

    if (chartType === "line") {
      return this.renderLineChartHtml(chartData, report.reportName || "Chart");
    }

    // Pie and donut are primarily rendered through snapshot mode.
    return this.renderLineChartHtml(chartData, report.reportName || "Chart");
  }

  private renderReportChartSnapshotHtml(report: DashboardReportData): string {
    const chartData = this.extractChartDataForReport(report);
    if (!chartData.series.length || !chartData.categories.length) {
      return "";
    }

    const title = report.reportName || "Chart";
    const chartType = this.resolveReportChartType(report, chartData);
    if (chartType === "table") {
      return this.renderReportTableHtml(chartData, title, report.reportId);
    }

    // Honour the report's multi-axis setting. The report view enables multi-axis
    // from metadata.multiAxis and, when on, gives every series its own Y-axis
    // (chart-view.component: multiAxisIndices = stats.map((_, i) => i)). Only
    // meaningful with more than one series.
    const multiAxisEnabled =
      (report.metadata as any)?.multiAxis === true &&
      chartData.series.length > 1;

    // The user-entered benchmark per parameter (aligned to the chart series), so
    // the snapshot draws each as a reference line — matching the live chart view.
    const seriesBenchmarks = this.seriesBenchmarksForReport(
      report,
      chartData.series
    ).map((b) => this.toNumberOrNull(b));

    const payload = {
      title,
      chartType,
      chartData,
      options: {
        // The report name renders as the heading above the chart, so it isn't
        // painted inside the snapshot as well. `title` above still carries it for
        // the interval detection, the alt text and the chart editor.
        titleText: "",
        bgStart: "#1f2937",
        bgCenter: "#2b3444",
        bgEnd: "#3f3f46",
        bgOuter: "#111827",
        seriesColors: this.ensureDistinctColors(
          this.defaultChartPalette,
          chartData.series.length
        ),
        multiAxis: multiAxisEnabled,
        multiAxisIndices: multiAxisEnabled
          ? chartData.series.map((_, i) => i)
          : [],
        seriesBenchmarks,
      },
    };
    const dataUrl = this.drawChartSnapshotDataUrl(
      chartData,
      title,
      payload.options,
      chartType
    );
    if (!dataUrl) {
      return "";
    }
    const encodedPayload = this.encodeChartPayload(payload);
    const chartTypeClass = `chart-type-${chartType}`;

    return `
      <div class="report-chart report-chart-image ${chartTypeClass}">
        <span class="resizable-image imported-image-control chart-image-resizable" contenteditable="false" data-chart-payload="${encodedPayload}">
          <img src="${dataUrl}" alt="${this.escapeHtml(title)}" class="chart-snapshot-image" />
        </span>
      </div>
    `;
  }

  private renderReportTableHtml(
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    reportId: number,
    // Optional per-parameter units (aligned to series). When supplied, a
    // dedicated "Unit" row is shown under the header (parameters are columns).
    units: string[] = [],
    // Optional per-parameter benchmarks, shown as a "Benchmark" row under the
    // unit row.
    benchmarks: string[] = []
  ): string {
    // White background with black text for all report tables (readable in the
    // document and when exported to PDF).
    const blockStyle =
      "margin:8px 0 20px;border:1px solid #cccccc;border-radius:8px;overflow:hidden;background:#ffffff;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const metaCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#fafafa;font-size:12px;font-weight:600;";
    const oddCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    const headers = chartData.series
      .map((s) => `<th style="${headCellStyle}">${this.escapeHtml(s.name || "Value")}</th>`)
      .join("");
    const metaRowHtml = (label: string, cells: string[]) =>
      cells.length
        ? `<tr><td style="${metaCellStyle}">${label}</td>${chartData.series
            .map(
              (_, i) =>
                `<td style="${metaCellStyle}">${this.escapeHtml(cells[i] || "")}</td>`
            )
            .join("")}</tr>`
        : "";
    const unitRowHtml = metaRowHtml("Unit", units);
    const benchmarkRowHtml = metaRowHtml("Benchmark", benchmarks);

    const rows = chartData.categories
      .map((category, rowIndex) => {
        const cellStyle = rowIndex % 2 === 0 ? oddCellStyle : evenCellStyle;
        const valueCells = chartData.series
          .map((series) => {
            const value = series.data[rowIndex];
            // Round to 2 decimals (drop trailing zeros) like the OPEX table and
            // the live report, instead of dumping raw floats (e.g. 760.4372).
            const text =
              value === null || Number.isNaN(value)
                ? "N/A"
                : this.formatTableNumber(value);
            return `<td style="${cellStyle}">${this.escapeHtml(text)}</td>`;
          })
          .join("");

        return `
          <tr>
            <td style="${cellStyle}">${this.escapeHtml(this.formatDateLikeLabel(category))}</td>
            ${valueCells}
          </tr>
        `;
      })
      .join("");

    return `
      <div class="report-table-block" data-table-id="report-${reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead>
            <tr>
              <th style="${headCellStyle}">Date</th>
              ${headers}
            </tr>
            ${unitRowHtml}
            ${benchmarkRowHtml}
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Resolve the specific table variant id for a report (tbl_row/col/rfn/cfn/
   * rfm/cfm), or "table" for the legacy generic table.
   */
  private tableVariantFor(report: DashboardReportData): string {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const tableParam = params.find(
      (p) => this.mapToSupportedChartType(p?.graphType, "line") === "table"
    );
    return String(tableParam?.graphType || "table");
  }

  /** Format a raw data value for a table cell (null/NaN → "N/A"). */
  private formatValueCell(value: number | null | undefined): string {
    return value === null || value === undefined || Number.isNaN(value)
      ? "N/A"
      : this.formatTableNumber(value);
  }

  /** Format a computed statistic (non-finite → "N/A"). */
  private formatStatNumber(value: number): string {
    return Number.isFinite(value) ? this.formatTableNumber(value) : "N/A";
  }

  /** avg/max/min/sum/stdDev over the non-null values of a series. */
  private computeSeriesStats(data: Array<number | null>): {
    avg: number;
    max: number;
    min: number;
    sum: number;
    stdDev: number;
  } {
    const valid = (data || []).filter(
      (v): v is number => v !== null && !Number.isNaN(v)
    );
    if (!valid.length) {
      return { avg: 0, max: 0, min: 0, sum: 0, stdDev: 0 };
    }
    const sum = valid.reduce((a, b) => a + b, 0);
    const avg = sum / valid.length;
    const max = Math.max(...valid);
    const min = Math.min(...valid);
    const variance =
      valid.reduce((a, b) => a + (b - avg) ** 2, 0) / valid.length;
    return { avg, max, min, sum, stdDev: Math.sqrt(variance) };
  }

  /** Human-readable label for an OPEX formula key. */
  private formulaLabelFor(key: string): string {
    switch (key) {
      case "totalCost":
        return "Total Cost";
      case "costM3":
        return "Cost m3";
      case "totalConsumption":
        return "Total Consumption";
      case "consumptionM3":
        return "Consumption m3";
      default:
        return key;
    }
  }

  /**
   * Render a styled report table from header labels and string row cells.
   * Mirrors renderReportTableHtml's white-background styling so every table
   * variant looks identical in the document and exported PDF.
   */
  private buildStyledTableHtml(
    title: string,
    reportId: number,
    headers: string[],
    rows: string[][],
    // Optional per-column units. When provided, a dedicated "Unit" row is
    // rendered directly under the header (used by the column-format tables
    // where parameters are columns, so a unit can't be its own column).
    unitRow?: string[],
    // Optional per-column benchmarks, rendered as a "Benchmark" row directly
    // under the unit row for the same column-format tables.
    benchmarkRow?: string[]
  ): string {
    const blockStyle =
      "margin:8px 0 20px;border:1px solid #cccccc;border-radius:8px;overflow:hidden;background:#ffffff;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const metaCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#fafafa;font-size:12px;font-weight:600;";
    const oddCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    const headerHtml = headers
      .map((h) => `<th style="${headCellStyle}">${this.escapeHtml(h)}</th>`)
      .join("");
    const metaRowHtml = (cells?: string[]) =>
      cells && cells.length
        ? `<tr>${cells
            .map((c) => `<td style="${metaCellStyle}">${this.escapeHtml(c)}</td>`)
            .join("")}</tr>`
        : "";
    const unitRowHtml = metaRowHtml(unitRow);
    const benchmarkRowHtml = metaRowHtml(benchmarkRow);
    const rowsHtml = rows
      .map((cells, rowIndex) => {
        const cellStyle = rowIndex % 2 === 0 ? oddCellStyle : evenCellStyle;
        const tds = cells
          .map((c) => `<td style="${cellStyle}">${this.escapeHtml(c)}</td>`)
          .join("");
        return `<tr>${tds}</tr>`;
      })
      .join("");

    return `
      <div class="report-table-block" data-table-id="report-${reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead><tr>${headerHtml}</tr>${unitRowHtml}${benchmarkRowHtml}</thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    `;
  }

  /**
   * Per-parameter units for a report's chart series, aligned to the series
   * order. Series in extractChartDataForReport are built from report.parameters
   * in order, so units match by index; a name lookup is used as a safeguard.
   */
  private seriesUnitsForReport(
    report: DashboardReportData,
    series: Array<{ name: string }>
  ): string[] {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const unitByName = new Map<string, string>();
    for (const p of params) {
      const unit = String((p as any)?.metadata?.unit || "").trim();
      if (!unit) {
        continue;
      }
      if (p?.paramName) {
        unitByName.set(String(p.paramName), unit);
      }
      if (p?.paramCode) {
        unitByName.set(String(p.paramCode), unit);
      }
    }
    return series.map((s, i) => {
      const byName = unitByName.get(String(s?.name ?? ""));
      if (byName) {
        return byName;
      }
      return String((params[i] as any)?.metadata?.unit || "").trim();
    });
  }

  /**
   * Per-parameter benchmarks (the user-entered benchmark) aligned to the chart
   * series order — mirrors seriesUnitsForReport. Drives the Benchmark column
   * (row-format tables) and the Benchmark row (column-format tables).
   */
  private seriesBenchmarksForReport(
    report: DashboardReportData,
    series: Array<{ name: string }>
  ): string[] {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const benchByName = new Map<string, string>();
    for (const p of params) {
      const bench = this.formatBenchmarkCell((p as any)?.benchmark);
      if (!bench) {
        continue;
      }
      if (p?.paramName) {
        benchByName.set(String(p.paramName), bench);
      }
      if (p?.paramCode) {
        benchByName.set(String(p.paramCode), bench);
      }
    }
    return series.map((s, i) => {
      const byName = benchByName.get(String(s?.name ?? ""));
      if (byName) {
        return byName;
      }
      return this.formatBenchmarkCell((params[i] as any)?.benchmark);
    });
  }

  /** Format a user-provided benchmark for a table cell (empty when absent). */
  private formatBenchmarkCell(
    benchmark: string | number | null | undefined
  ): string {
    if (benchmark === null || benchmark === undefined || benchmark === "") {
      return "";
    }
    return String(benchmark);
  }

  /** Collect the parameter × formula grid shared by Table 5 and Table 6. */
  private buildFormulaGridForReport(report: DashboardReportData): {
    formulaKeys: string[];
    paramCols: { code: string; name: string; unit: string; benchmark: string }[];
    cells: Map<string, string>;
  } {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const formulaKeys: string[] = [];
    const paramCols: {
      code: string;
      name: string;
      unit: string;
      benchmark: string;
    }[] = [];
    const cells = new Map<string, string>();

    for (const p of params) {
      const formula = String((p as any)?.formula || "");
      if (!formula) {
        continue;
      }
      if (!formulaKeys.includes(formula)) {
        formulaKeys.push(formula);
      }
      const code = String(p?.paramCode || p?.paramName || "");
      if (!paramCols.some((c) => c.code === code)) {
        // Keep the name clean; unit/benchmark are surfaced in their own row/col.
        const unit = String((p as any)?.metadata?.unit || "").trim();
        const baseName = String(p?.paramName || code);
        paramCols.push({
          code,
          name: baseName,
          unit,
          benchmark: this.formatBenchmarkCell((p as any)?.benchmark),
        });
      }
      const nums = (Array.isArray(p?.values) ? p.values : []).map((v) =>
        this.toNumberOrNull(v?.value)
      );
      const stats = this.computeSeriesStats(nums);
      const agg = String((p as any)?.aggregation || (p as any)?.dataType || "avg");
      const cell = agg === "sum" || agg === "raw" ? stats.sum : stats.avg;
      cells.set(`${code}__${formula}`, this.formatStatNumber(cell));
    }

    return { formulaKeys, paramCols, cells };
  }

  /** Dispatch a table report to the matching variant layout. */
  private renderTableVariantHtml(
    report: DashboardReportData,
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    variant: string
  ): string {
    // Per-parameter units and benchmarks (aligned to the chart series) drive the
    // Unit/Benchmark columns (row-format tables) and rows (column-format tables).
    // The benchmark column/row is dropped entirely when no parameter has one.
    const units = this.seriesUnitsForReport(report, chartData.series);
    const rawBenchmarks = this.seriesBenchmarksForReport(report, chartData.series);
    const benchmarks = rawBenchmarks.some((b) => b) ? rawBenchmarks : [];
    switch (variant) {
      case "tbl_row":
        return this.renderTableRowFormatHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_rfn":
        return this.renderTableRowFunctionsHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_cfn":
        return this.renderTableColumnFunctionsHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_rfm":
        return this.renderTableFormulaHtml(report, title, false);
      case "tbl_cfm":
        return this.renderTableFormulaHtml(report, title, true);
      case "tbl_col":
      default:
        return this.renderReportTableHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
    }
  }

  /** Table 1 (Row Format): parameters as rows, dates as columns. */
  private renderTableRowFormatHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null> }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const showBench = benchmarks.length > 0;
    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      ...chartData.categories,
    ];
    const rows = chartData.series.map((s, i) => [
      s.name || "Value",
      units[i] || "",
      ...(showBench ? [benchmarks[i] || ""] : []),
      ...chartData.categories.map((_, ci) => this.formatValueCell(s.data[ci])),
    ]);
    return this.buildStyledTableHtml(title, reportId, headers, rows);
  }

  /**
   * Table 3 (Row Format - Functions): parameters as rows, function columns.
   * Average & SUM aggregate the per-interval values (the series data); Max, Min
   * & STDV use every raw reading so they show the true peak/trough/spread.
   */
  private renderTableRowFunctionsHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; rawValues?: number[] }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const showBench = benchmarks.length > 0;
    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      "Average",
      "Max",
      "Min",
      "SUM",
      "STDV",
    ];
    const rows = chartData.series.map((s, i) => {
      const st = this.computeSeriesStats(s.data);
      const raw = this.computeSeriesStats(s.rawValues ?? s.data);
      return [
        s.name || "Value",
        units[i] || "",
        ...(showBench ? [benchmarks[i] || ""] : []),
        this.formatStatNumber(st.avg),
        this.formatStatNumber(raw.max),
        this.formatStatNumber(raw.min),
        this.formatStatNumber(st.sum),
        this.formatStatNumber(raw.stdDev),
      ];
    });
    return this.buildStyledTableHtml(title, reportId, headers, rows);
  }

  /**
   * Table 4 (Column Format - Functions): function rows, parameter columns.
   * Average & SUM aggregate the per-interval values; Max, Min & STDV use every
   * raw reading.
   */
  private renderTableColumnFunctionsHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; rawValues?: number[] }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const stats = chartData.series.map((s) => this.computeSeriesStats(s.data));
    const rawStats = chartData.series.map((s) =>
      this.computeSeriesStats(s.rawValues ?? s.data)
    );
    const headers = ["Function", ...chartData.series.map((s) => s.name || "Value")];
    const unitRow = ["Unit", ...chartData.series.map((_, i) => units[i] || "")];
    const benchmarkRow = benchmarks.length
      ? ["Benchmark", ...chartData.series.map((_, i) => benchmarks[i] || "")]
      : undefined;
    const rows = [
      ["Average", ...stats.map((st) => this.formatStatNumber(st.avg))],
      ["Max", ...rawStats.map((st) => this.formatStatNumber(st.max))],
      ["Min", ...rawStats.map((st) => this.formatStatNumber(st.min))],
      ["SUM", ...stats.map((st) => this.formatStatNumber(st.sum))],
      ["STDV", ...rawStats.map((st) => this.formatStatNumber(st.stdDev))],
    ];
    return this.buildStyledTableHtml(title, reportId, headers, rows, unitRow, benchmarkRow);
  }

  /**
   * Table 5 / Table 6 (Formula). column=false → parameters as rows, formulas
   * as columns; column=true → formulas as rows, parameters as columns.
   */
  private renderTableFormulaHtml(
    report: DashboardReportData,
    title: string,
    column: boolean
  ): string {
    const { formulaKeys, paramCols, cells } =
      this.buildFormulaGridForReport(report);
    const showBench = paramCols.some((c) => c.benchmark !== "");

    if (column) {
      const headers = ["Formula", ...paramCols.map((c) => c.name)];
      const unitRow = ["Unit", ...paramCols.map((c) => c.unit)];
      const benchmarkRow = showBench
        ? ["Benchmark", ...paramCols.map((c) => c.benchmark)]
        : undefined;
      const rows = formulaKeys.map((fk) => [
        this.formulaLabelFor(fk),
        ...paramCols.map((c) => cells.get(`${c.code}__${fk}`) ?? "N/A"),
      ]);
      return this.buildStyledTableHtml(
        title,
        report.reportId,
        headers,
        rows,
        unitRow,
        benchmarkRow
      );
    }

    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      ...formulaKeys.map((k) => this.formulaLabelFor(k)),
    ];
    const rows = paramCols.map((c) => [
      c.name,
      c.unit,
      ...(showBench ? [c.benchmark] : []),
      ...formulaKeys.map((fk) => cells.get(`${c.code}__${fk}`) ?? "N/A"),
    ]);
    return this.buildStyledTableHtml(title, report.reportId, headers, rows);
  }

  /** A report renders as a table when any parameter's graphType is "table". */
  private resolvesToTableReport(report: DashboardReportData): boolean {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    return params.some(
      (p) => this.mapToSupportedChartType(p?.graphType, "line") === "table"
    );
  }

  /**
   * OPEX chemical reports carry, for each chemical, a "totalConsumption" param
   * and a "totalCost" param. Those get grouped into one row per chemical with
   * a consumption column and a cost column (image 2).
   */
  private isConsumptionCostTableReport(report: DashboardReportData): boolean {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const hasCost = params.some((p) => /cost/i.test(String(p?.formula || "")));
    const hasConsumption = params.some((p) =>
      /consumption/i.test(String(p?.formula || ""))
    );
    return hasCost && hasConsumption;
  }

  private sumParameterValues(param: DashboardReportParameter): number {
    const values = Array.isArray(param?.values) ? param.values : [];
    let sum = 0;
    let hasValue = false;
    for (let i = 0; i < values.length; i += 1) {
      const n = this.toNumberOrNull(values[i]?.value);
      if (n !== null) {
        sum += n;
        hasValue = true;
      }
    }
    return hasValue ? sum : 0;
  }

  private formatTableNumber(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "0";
    }
    // Trim to 2 decimals, drop trailing zeros, no thousands separators (e.g.
    // 3.96, 1445.4, 11151) — matches the live report table.
    return String(Math.round(value * 100) / 100);
  }

  /**
   * Format a chart axis tick / value label. Keeps integers and large values as
   * whole numbers, but preserves decimals for fractional axes (e.g. a multi-axis
   * scale of 0.3, 0.6, 0.9, 1.2, 1.5) instead of rounding them to 0, 0, 0, 1, 1.
   */
  private formatAxisTick(value: number): string {
    if (!Number.isFinite(value)) {
      return "0";
    }
    const rounded = Math.round(value * 100) / 100;
    if (Number.isInteger(rounded)) {
      return String(rounded);
    }
    // Up to 2 decimals, trimming trailing zeros (0.30 → 0.3, 1.50 → 1.5).
    return rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  }

  /**
   * Render an OPEX chemical "consumption + total cost" table: one row per
   * chemical (grouped by paramCode), pairing the parameter that carries the
   * "totalConsumption" formula with the one carrying "totalCost".
   */
  private renderConsumptionCostTableHtml(report: DashboardReportData): string {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    if (!params.length) {
      return "";
    }

    const order: string[] = [];
    const groups = new Map<
      string,
      { name: string; consumption: number | null; cost: number | null }
    >();
    for (let i = 0; i < params.length; i += 1) {
      const param = params[i];
      const code = String(param?.paramCode || param?.paramName || "").trim();
      if (!code) {
        continue;
      }
      if (!groups.has(code)) {
        groups.set(code, {
          name: String(param?.paramName || code),
          consumption: null,
          cost: null,
        });
        order.push(code);
      }
      const group = groups.get(code)!;
      const value = this.sumParameterValues(param);
      const formula = String(param?.formula || "").toLowerCase();
      if (formula.includes("cost")) {
        if (group.cost === null) {
          group.cost = value;
        }
      } else {
        // totalConsumption (or anything that is not a cost) → consumption column.
        if (group.consumption === null) {
          group.consumption = value;
        }
      }
    }

    const firstParam = params[0];
    const moduleName =
      String(firstParam?.moduleName || "Chemicals").trim() || "Chemicals";
    const unit = String(firstParam?.metadata?.unit || "").trim();
    const nameHeader = `${moduleName} Names`;
    const consumptionHeader = unit
      ? `Consumption of ${moduleName} in ${unit.toUpperCase()}`
      : `Consumption of ${moduleName}`;
    const costHeader = "Total Cost";

    // White background with black text (consistent with all report tables).
    const blockStyle =
      "margin:8px 0 20px;background:#ffffff;border:0;overflow:hidden;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:8px 12px;background:#f2f2f2;color:#000000;font-weight:700;font-size:13px;text-align:center;border:1px solid #cccccc;";
    const nameCellStyle =
      "padding:7px 12px;color:#000000;font-size:12px;font-weight:700;text-align:center;border:1px solid #cccccc;";
    const numCellStyle =
      "padding:7px 12px;color:#000000;font-size:12px;text-align:center;border:1px solid #cccccc;";

    const rows = order
      .map((code, index) => {
        const group = groups.get(code)!;
        const rowBg = index % 2 === 0 ? "#ffffff" : "#f7f7f7";
        return `
          <tr>
            <td style="${nameCellStyle};background:${rowBg}">${this.escapeHtml(group.name)}</td>
            <td style="${numCellStyle};background:${rowBg}">${this.escapeHtml(this.formatTableNumber(group.consumption))}</td>
            <td style="${numCellStyle};background:${rowBg}">${this.escapeHtml(this.formatTableNumber(group.cost))}</td>
          </tr>
        `;
      })
      .join("");

    return `
      <div class="report-table-block" data-table-id="report-${report.reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead>
            <tr>
              <th style="${headCellStyle};width:40%">${this.escapeHtml(nameHeader)}</th>
              <th style="${headCellStyle};width:32%">${this.escapeHtml(consumptionHeader)}</th>
              <th style="${headCellStyle};width:28%">${this.escapeHtml(costHeader)}</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  private drawChartSnapshotDataUrl(
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    styleOptions?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // The text to paint. "" paints no title; undefined falls back to `title`.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
      // Per-series benchmark values (aligned to chartData.series). Each non-null
      // value is drawn as a horizontal reference line in the series colour.
      seriesBenchmarks?: Array<number | null>;
    },
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"
  ): string {
    const width = 900;
    const inferredType =
      chartType ||
      (chartData.series.some((s) => s.type === "text")
        ? "text"
        : chartData.series.some((s) => s.type === "gauge")
          ? "gauge"
          : chartData.series.some((s) => s.type === "dot")
            ? "dot"
            : chartData.series.some((s) => s.type === "bar_label")
              ? "bar_label"
              : chartData.series.some((s) => s.type === "line_label")
                ? "line_label"
                : chartData.series.some((s) => s.type === "numeric")
                  ? "numeric"
                  : chartData.series.some((s) => s.type === "table")
                    ? "table"
                    : chartData.series.some((s) => s.type === "box")
                      ? "box"
                      : chartData.series.some((s) => s.type === "donut")
                        ? "donut"
                        : chartData.series.some((s) => s.type === "pie")
                          ? "pie"
                          : chartData.series.some((s) => s.type === "bar")
                            ? "bar"
                            : "line");
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return "";
    }

    const bgStart = styleOptions?.bgStart || "#1f2937";
    const bgCenter = styleOptions?.bgCenter || "#2b3444";
    const bgEnd = styleOptions?.bgEnd || "#3f3f46";
    const bgOuter = styleOptions?.bgOuter || "#111827";
    const titleColor = styleOptions?.titleColor || "#f8fafc";
    const titleFontSize = this.normalizeChartTitleSize(
      styleOptions?.titleFontSize
    );
    const legendTextColor = styleOptions?.legendTextColor || "#e5e7eb";
    const axisTextColor = styleOptions?.axisTextColor || "#e2e8f0";
    const gridColor = styleOptions?.gridColor || "rgba(209, 213, 219, 0.35)";
    const dateLabelMode = styleOptions?.dateLabelMode === "all" ? "all" : "auto";
    const dateLabelRotation = this.normalizeDateLabelRotation(
      styleOptions?.dateLabelRotation
    );
    const valueLabelSize = this.normalizeValueLabelSize(
      styleOptions?.valueLabelSize
    );
    const valueLabelRotation = this.normalizeValueLabelRotation(
      styleOptions?.valueLabelRotation
    );
    const legendFontSize = this.normalizeLegendSize(
      styleOptions?.legendFontSize
    );
    const xAxisLabelSize = this.normalizeAxisLabelSize(
      styleOptions?.xAxisLabelSize
    );
    const yAxisLabelSize = this.normalizeAxisLabelSize(
      styleOptions?.yAxisLabelSize
    );
    const titlePadTop = this.normalizeTitlePad(styleOptions?.titlePadTop);
    const titlePadRight = this.normalizeTitlePad(styleOptions?.titlePadRight);
    const titlePadBottom = this.normalizeTitlePad(styleOptions?.titlePadBottom);
    const titlePadLeft = this.normalizeTitlePad(styleOptions?.titlePadLeft);

    // What gets painted, as opposed to `title` — which always carries the report
    // name, since the interval detection just below keys off it. The report name
    // already renders as the heading above the chart, so report renders pass
    // titleText:"" to stop it showing twice; a title typed into the chart editor
    // paints as normal. Undefined (charts saved before titleText existed) falls
    // back to the report name so their baked-in title is preserved.
    const displayTitle =
      styleOptions?.titleText === undefined
        ? String(title || "")
        : String(styleOptions.titleText || "");
    const hasTitle = !!displayTitle.trim();

    // Wrap long titles across multiple lines so they are never clipped at the
    // canvas edges. The extra lines are folded into titleExtraSpace so they
    // enlarge the top area (and the canvas height) instead of overlapping the plot.
    const titleLineHeight = Math.round(titleFontSize * 1.25);
    const titleWrapWidth = Math.max(120, width - 96);
    ctx.font = `700 ${titleFontSize}px Arial`;
    const titleLines = hasTitle
      ? this.wrapTextToWidth(ctx, displayTitle, titleWrapWidth)
      : [];
    const titleWrapExtra = Math.max(0, titleLines.length - 1) * titleLineHeight;
    // With no title there is nothing for the title padding to space out, so it
    // collapses too — otherwise a stored pad would leave a gap above an absent title.
    const titleExtraSpace = hasTitle
      ? titlePadTop + titlePadBottom + titleWrapExtra
      : 0;
    const multiAxis = !!styleOptions?.multiAxis;
    const isDailyByTitle = /\bdaily\b/i.test(String(title || ""));
    const isHourlyByTitle = /\bhourly\b/i.test(String(title || ""));
    const isRawByTitle = /\braw\b/i.test(String(title || ""));
    const isTimestampByTitle = isHourlyByTitle || isRawByTitle;
    const displayCategories = isDailyByTitle
      ? chartData.categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
      : isTimestampByTitle
        ? chartData.categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "raw"))
        : chartData.categories;
    const categoriesCount = Math.max(1, chartData.categories.length);

    const isCartesianChart =
      inferredType !== "text" &&
      inferredType !== "numeric" &&
      inferredType !== "table" &&
      inferredType !== "gauge" &&
      inferredType !== "pie" &&
      inferredType !== "donut" &&
      inferredType !== "box";

    // Widen the left gutter when the Y-axis labels are enlarged so the tick
    // numbers never clip against the canvas edge (no effect at the 12px default).
    const yAxisGutter = 56 + Math.max(0, yAxisLabelSize - 12) * 2.5;
    const margin = {
      // The 56px top band exists to hold the title; with no title it collapses to
      // a plain gutter (kept wide enough for the top Y-axis tick label) and the
      // plot grows into the freed space. The canvas height is unchanged either
      // way, so page layout doesn't shift.
      top: (hasTitle ? 56 : 24) + titleExtraSpace,
      right: 22,
      bottom: 86,
      left: yAxisGutter,
    };
    if (isCartesianChart && displayCategories.length) {
      // Measure with the chosen X-axis label size so the reserved label depth
      // and sampling step match what is actually drawn below.
      ctx.font = `600 ${xAxisLabelSize}px Arial`;
      const basePlotWidth = width - margin.left - margin.right;
      const xStepForLayout = dateLabelMode === "all"
        ? 1
        : this.computeAutoDateLabelStep(
            ctx,
            displayCategories,
            basePlotWidth,
            isTimestampByTitle,
            dateLabelRotation
          );
      const labelDepth = this.computeRotatedDateLabelDepth(
        ctx,
        displayCategories,
        xStepForLayout,
        isTimestampByTitle,
        dateLabelRotation
      );
      const legendReserve = Math.max(38, legendFontSize + 22);
      margin.bottom = Math.max(86, Math.ceil(20 + labelDepth + legendReserve));
    }

    // Multi-axis: one independent Y-axis per SELECTED series (mirrors report behaviour).
    // Only series listed in multiAxisIndices get their own axis; the rest share the
    // first selected axis. First 3 axes go on the right, any remaining on the left.
    // Per-series benchmark values (aligned to series order). A finite value is
    // drawn as a horizontal reference line and folded into that series' Y span so
    // the line always stays within the plot.
    const seriesBenchmarks = chartData.series.map((_, i) => {
      const raw = styleOptions?.seriesBenchmarks?.[i];
      const num = this.toNumberOrNull(raw as any);
      return num !== null && Number.isFinite(num) ? num : null;
    });
    const seriesSpans = chartData.series.map((s, i) => {
      const maxData = (s.data || []).reduce<number>(
        (m, v) => (v !== null && !Number.isNaN(v) && Number(v) > m ? Number(v) : m),
        0
      );
      const bench = seriesBenchmarks[i];
      return Math.max(1, maxData, bench !== null ? bench : 0);
    });
    // Field present (even if empty) → honour exactly what the user selected, so
    // unchecking every parameter turns multi-axis off. Field absent (legacy payload
    // that only stored multiAxis:true) → fall back to all series.
    const selectedAxisIndices = Array.isArray(styleOptions?.multiAxisIndices)
      ? styleOptions!.multiAxisIndices!.filter(
          (idx) => Number.isInteger(idx) && idx >= 0 && idx < chartData.series.length
        )
      : chartData.series.map((_, i) => i);
    const applyMultiAxis =
      multiAxis && isCartesianChart && selectedAxisIndices.length >= 1;
    const axisGap = 50;
    const axisPosForSeries = (origIndex: number): number => {
      const pos = selectedAxisIndices.indexOf(origIndex);
      return pos >= 0 ? pos : 0;
    };
    const spanForSeries = (origIndex: number): number => {
      if (!applyMultiAxis) {
        return span;
      }
      const axisSeries = selectedAxisIndices[axisPosForSeries(origIndex)];
      return seriesSpans[axisSeries] ?? span;
    };
    if (applyMultiAxis) {
      const rightAxesCount = Math.min(selectedAxisIndices.length, 3);
      const leftAxesCount = Math.max(0, selectedAxisIndices.length - 3);
      margin.right += rightAxesCount * axisGap;
      margin.left += leftAxesCount * axisGap;
    }

    // Reserve vertical space for EVERY legend row. The legend wraps when the
    // labels are long (multi-series charts), and the base reservation above only
    // accounts for a single row — so extra rows previously overlapped the date
    // labels. Compute the wrapped row count with the same logic the legend
    // drawing uses (final margins), then grow the bottom margin (and thus the
    // canvas height) to fit all rows plus a gap.
    const reserveLegendRows =
      (isCartesianChart ||
        inferredType === "pie" ||
        inferredType === "donut") &&
      chartData.series.length > 1;
    if (reserveLegendRows) {
      ctx.font = `600 ${legendFontSize}px Arial`;
      const maxLegendWidth = width - margin.left - margin.right;
      const legendRowHeight = Math.max(22, legendFontSize + 10);
      let legendRowCount = 1;
      let rowWidth = 0;
      for (let i = 0; i < chartData.series.length; i += 1) {
        const itemWidth = Math.max(
          110,
          ctx.measureText(String(chartData.series[i].name || "")).width + 36
        );
        if (i > 0 && rowWidth + itemWidth > maxLegendWidth) {
          legendRowCount += 1;
          rowWidth = itemWidth;
        } else {
          rowWidth += itemWidth;
        }
      }
      if (legendRowCount > 1) {
        margin.bottom += (legendRowCount - 1) * legendRowHeight + 10;
      }
    }

    const isPieType = inferredType === "pie" || inferredType === "donut";
    const dynamicTableHeight =
      inferredType === "table"
        ? Math.max(390, 118 + (Math.max(1, chartData.categories.length) + 1) * 32 + 20)
        : isPieType
          ? 640 + Math.max(0, margin.bottom - 86)
          : 390 + Math.max(0, margin.bottom - 86);
    const height = dynamicTableHeight + titleExtraSpace;
    canvas.height = height;
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, bgOuter);
    gradient.addColorStop(0.22, bgStart);
    gradient.addColorStop(0.62, bgCenter);
    gradient.addColorStop(1, bgEnd);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const baseColors =
      styleOptions?.seriesColors && styleOptions.seriesColors.length
        ? styleOptions.seriesColors
        : this.defaultChartPalette;
    // Guarantee one visually-distinct color per series. The base palette only
    // holds 6 entries, so charts with more series (e.g. a 12-slice pie) would
    // otherwise wrap via `colors[i % len]` and repeat the first colors. Extend
    // it to the series count with well-separated generated hues.
    const colors = this.ensureDistinctColors(
      baseColors,
      chartData.series.length
    );
    const resolvedChartType = inferredType;

    if (resolvedChartType === "box") {
      return "";
    }

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    const edgePadding = Math.min(22, Math.max(8, plotWidth * 0.02));
    const plotStartX = margin.left + edgePadding;
    const plotUsableWidth = Math.max(1, plotWidth - edgePadding * 2);
    const groupWidth = plotUsableWidth / categoriesCount;
    // Include benchmark values so the shared axis scales to keep their reference
    // lines within the plot (multi-axis charts use per-series spans instead).
    const benchmarkValues = seriesBenchmarks.filter(
      (v): v is number => v !== null
    );
    const maxValue =
      values.length || benchmarkValues.length
        ? Math.max(...values, ...benchmarkValues, 0)
        : 0;
    const minValue = 0;
    const span = Math.max(1, maxValue - minValue);

    // Title (with configurable spacing around it). Drawn line-by-line so a wrapped
    // long title stacks vertically instead of being clipped at the canvas edges.
    ctx.fillStyle = titleColor;
    ctx.font = `700 ${titleFontSize}px Arial`;
    ctx.textAlign = "center";
    const titleX = width / 2 + (titlePadLeft - titlePadRight);
    for (let tl = 0; tl < titleLines.length; tl += 1) {
      ctx.fillText(
        titleLines[tl],
        titleX,
        36 + titlePadTop + tl * titleLineHeight
      );
    }

    if (resolvedChartType === "text") {
      const textLines = chartData.series
        .map((s) => s.name)
        .filter((s) => !!String(s || "").trim())
        .slice(0, 4);
      if (!textLines.length) {
        textLines.push(title);
      }
      ctx.fillStyle = legendTextColor;
      ctx.font = "600 22px Arial";
      ctx.textAlign = "center";
      const startY = margin.top + 70;
      for (let i = 0; i < textLines.length; i += 1) {
        ctx.fillText(textLines[i], width / 2, startY + i * 34);
      }
    } else if (resolvedChartType === "numeric") {
      if (!values.length) {
        return "";
      }
      // Render one value card per series (matches the dashboard view, which shows
      // every numeric parameter side by side instead of a single number).
      const cardCount = Math.max(1, chartData.series.length);
      const cols = Math.min(cardCount, 4);
      const rows = Math.ceil(cardCount / cols);
      const gap = 16;
      const cardW = (plotWidth - gap * (cols - 1)) / cols;
      const cardH = (plotHeight - gap * (rows - 1)) / rows;

      for (let s = 0; s < cardCount; s += 1) {
        const series = chartData.series[s];
        const col = s % cols;
        const row = Math.floor(s / cols);
        const cardX = margin.left + col * (cardW + gap);
        const cardY = margin.top + row * (cardH + gap);
        const color = colors[s % colors.length];

        // Card panel.
        this.drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 10);
        ctx.fillStyle = "rgba(255, 255, 255, 0.06)";
        ctx.fill();
        ctx.strokeStyle = "rgba(148, 163, 184, 0.4)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Parameter name (top of card).
        const nameFont = Math.max(12, Math.min(16, Math.round(cardW * 0.085)));
        ctx.fillStyle = "#cbd5e1";
        ctx.font = `600 ${nameFont}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(
          this.shortenLabel(series?.name || `Value ${s + 1}`, 22),
          cardX + cardW / 2,
          cardY + Math.max(22, cardH * 0.22)
        );

        // Value (centered, colored, auto-fit to the card width).
        const value = this.getSeriesRepresentativeValue(series?.data || []);
        const valueText = this.formatNumericValue(value);
        let valueFont = Math.max(20, Math.min(64, Math.round(cardH * 0.34)));
        ctx.font = `700 ${valueFont}px Arial`;
        const maxTextWidth = cardW * 0.86;
        while (valueFont > 14 && ctx.measureText(valueText).width > maxTextWidth) {
          valueFont -= 2;
          ctx.font = `700 ${valueFont}px Arial`;
        }
        ctx.fillStyle = color;
        ctx.fillText(valueText, cardX + cardW / 2, cardY + cardH * 0.62 + valueFont / 3);
      }
    } else if (resolvedChartType === "table") {
      const visibleRows = categoriesCount;
      const visibleSeries = Math.max(1, Math.min(chartData.series.length, 3));
      const tableLeft = margin.left - 6;
      const tableTop = margin.top + 6;
      const tableWidth = width - margin.left - margin.right + 12;
      const rowHeight = 32;
      const tableHeight = rowHeight * (visibleRows + 1);
      const colWidth = tableWidth / (visibleSeries + 1);
      const border = "rgba(148, 163, 184, 0.45)";

      // Table outer shell like report card.
      ctx.fillStyle = "rgba(71, 85, 105, 0.55)";
      ctx.fillRect(tableLeft, tableTop, tableWidth, tableHeight);

      // Header background.
      ctx.fillStyle = "#021b39";
      ctx.fillRect(tableLeft, tableTop, tableWidth, rowHeight);

      // Row zebra backgrounds.
      for (let r = 0; r < visibleRows; r += 1) {
        const y = tableTop + rowHeight * (r + 1);
        ctx.fillStyle = r % 2 === 0 ? "#15335a" : "#001a3a";
        ctx.fillRect(tableLeft, y, tableWidth, rowHeight);
      }

      // Grid lines.
      ctx.strokeStyle = border;
      ctx.lineWidth = 1;
      for (let c = 0; c <= visibleSeries + 1; c += 1) {
        const x = tableLeft + c * colWidth;
        ctx.beginPath();
        ctx.moveTo(x, tableTop);
        ctx.lineTo(x, tableTop + tableHeight);
        ctx.stroke();
      }
      for (let r = 0; r <= visibleRows + 1; r += 1) {
        const y = tableTop + r * rowHeight;
        ctx.beginPath();
        ctx.moveTo(tableLeft, y);
        ctx.lineTo(tableLeft + tableWidth, y);
        ctx.stroke();
      }

      // Header text.
      ctx.fillStyle = "#f8fafc";
      ctx.textAlign = "left";
      ctx.font = "700 12px Arial";
      ctx.fillText("Date", tableLeft + 12, tableTop + 21);
      for (let c = 0; c < visibleSeries; c += 1) {
        ctx.fillText(
          this.shortenLabel(chartData.series[c].name, 22),
          tableLeft + (c + 1) * colWidth + 8,
          tableTop + 21
        );
      }

      // Cell text.
      ctx.font = "600 11px Arial";
      for (let r = 0; r < visibleRows; r += 1) {
        const y = tableTop + (r + 1) * rowHeight + 21;
        ctx.fillStyle = "#ffffff";
        ctx.fillText(
          this.shortenLabel(this.formatDateLikeLabel(chartData.categories[r]), 16),
          tableLeft + 12,
          y
        );
        for (let c = 0; c < visibleSeries; c += 1) {
          const v = chartData.series[c].data[r];
          ctx.fillText(
            v === null || Number.isNaN(v) ? "N/A" : `${v}`,
            tableLeft + (c + 1) * colWidth + 8,
            y
          );
        }
      }

      // full table rendered; no row truncation note needed.
    } else if (resolvedChartType === "gauge") {
      if (!values.length) {
        return "";
      }
      const radius = Math.min(plotWidth, plotHeight) * 0.5;
      const centerX = width / 2;
      const centerY = margin.top + plotHeight / 2 + 64;
      const gaugeValue = this.getSeriesRepresentativeValue(chartData.series[0]?.data || []);
      const gaugeMaxBase = Math.max(maxValue, gaugeValue, 1);
      const gaugeMax = Math.max(1, Math.ceil((gaugeMaxBase * 1.5) / 10) * 10);
      const ratio = Math.max(0, Math.min(1, gaugeValue / gaugeMax));
      const startAngle = Math.PI;
      const endAngle = 2 * Math.PI;
      const sweep = endAngle - startAngle;
      const innerRadius = radius - 22;

      // Background track
      ctx.lineWidth = 22;
      ctx.lineCap = "round";
      ctx.strokeStyle = "rgba(148, 163, 184, 0.28)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
      ctx.stroke();

      // Colored zones (red -> yellow -> cyan -> green)
      const zoneStops = [0, 0.28, 0.52, 0.76, 1];
      const zoneColors = ["#ff6b78", "#f4d35e", "#4fd1ff", "#7ef7ae"];
      ctx.lineWidth = 16;
      ctx.lineCap = "butt";
      for (let zi = 0; zi < zoneColors.length; zi += 1) {
        const a1 = startAngle + zoneStops[zi] * sweep;
        const a2 = startAngle + zoneStops[zi + 1] * sweep;
        ctx.strokeStyle = zoneColors[zi];
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, a1, a2, false);
        ctx.stroke();
      }

      // Minor/major ticks
      const majorCount = 4;
      const minorPerMajor = 3;
      for (let i = 0; i <= majorCount * minorPerMajor; i += 1) {
        const t = i / (majorCount * minorPerMajor);
        const angle = startAngle + t * sweep;
        const isMajor = i % minorPerMajor === 0;
        const outer = radius - 2;
        const tickLen = isMajor ? 18 : 10;
        const inner = outer - tickLen;
        const x1 = centerX + Math.cos(angle) * outer;
        const y1 = centerY + Math.sin(angle) * outer;
        const x2 = centerX + Math.cos(angle) * inner;
        const y2 = centerY + Math.sin(angle) * inner;

        ctx.strokeStyle = isMajor ? "#f8fafc" : "rgba(226, 232, 240, 0.8)";
        ctx.lineWidth = isMajor ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Numeric labels on major ticks
      ctx.fillStyle = "#e2e8f0";
      ctx.font = "600 14px Arial";
      ctx.textAlign = "center";
      for (let i = 0; i <= majorCount; i += 1) {
        const t = i / majorCount;
        const angle = startAngle + t * sweep;
        const labelValue = (gaugeMax * t);
        const lx = centerX + Math.cos(angle) * (radius + 20);
        const ly = centerY + Math.sin(angle) * (radius + 20);
        const text = `${Math.round(labelValue).toLocaleString("en-US")}`;
        ctx.fillText(text, lx, ly + 4);
      }

      // Needle pointer
      const needleAngle = startAngle + ratio * sweep;
      const tipR = radius - 26;
      const baseR = innerRadius - 26;
      const halfWidth = 8;
      const tipX = centerX + Math.cos(needleAngle) * tipR;
      const tipY = centerY + Math.sin(needleAngle) * tipR;
      const baseX = centerX + Math.cos(needleAngle) * baseR;
      const baseY = centerY + Math.sin(needleAngle) * baseR;
      const leftX = baseX + Math.cos(needleAngle - Math.PI / 2) * halfWidth;
      const leftY = baseY + Math.sin(needleAngle - Math.PI / 2) * halfWidth;
      const rightX = baseX + Math.cos(needleAngle + Math.PI / 2) * halfWidth;
      const rightY = baseY + Math.sin(needleAngle + Math.PI / 2) * halfWidth;

      ctx.fillStyle = "#4fd1ff";
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "#0f172a";
      ctx.beginPath();
      ctx.arc(centerX, centerY, 7, 0, Math.PI * 2);
      ctx.fill();

      // Center value + parameter name
      ctx.fillStyle = legendTextColor;
      const valueFontSize = Math.max(26, Math.min(44, Math.round(radius * 0.42)));
      ctx.font = `700 ${valueFontSize}px Arial`;
      ctx.textAlign = "center";
      ctx.fillText(`${gaugeValue.toFixed(2)}`, centerX, centerY - 6);
      const nameFontSize = Math.max(14, Math.min(28, Math.round(radius * 0.22)));
      ctx.font = `600 ${nameFontSize}px Arial`;
      ctx.fillStyle = "#dbeafe";
      ctx.fillText(
        this.shortenLabel(chartData.series[0]?.name || "Value", 26),
        centerX,
        centerY + Math.max(40, Math.round(radius * 0.36))
      );
    } else if (resolvedChartType === "pie" || resolvedChartType === "donut") {
      if (!values.length) {
        return "";
      }
      const slices = chartData.series.map((series) =>
        this.getSeriesRepresentativeValue(series.data)
      );
      const total = slices.reduce((sum, v) => sum + v, 0);
      if (total > 0) {
        // Fill the plot area: use ~half the available height (minus a little room
        // for the leader-line % labels top/bottom) and clamp so the side labels
        // still fit within the canvas width.
        const labelRoom = 30;
        const radiusByHeight = plotHeight / 2 - labelRoom;
        const radiusByWidth = (plotWidth - 150) / 2;
        const radius = Math.max(150, Math.min(radiusByHeight, radiusByWidth));
        const centerX = width / 2;
        const centerY = margin.top + plotHeight / 2 + 4;
        const innerRadius = radius * 0.56;

        // Pre-compute each slice's angles so we can draw the slice and then its
        // leader-line label from the same geometry.
        const sliceInfo: Array<{ index: number; start: number; sweep: number; mid: number; value: number }> = [];
        let angleCursor = -Math.PI / 2;
        for (let i = 0; i < slices.length; i += 1) {
          const value = slices[i];
          if (value <= 0) {
            continue;
          }
          const sweep = (value / total) * Math.PI * 2;
          sliceInfo.push({ index: i, start: angleCursor, sweep, mid: angleCursor + sweep / 2, value });
          angleCursor += sweep;
        }

        // Draw the slices.
        for (let s = 0; s < sliceInfo.length; s += 1) {
          const slice = sliceInfo[s];
          ctx.fillStyle = colors[slice.index % colors.length];
          if (resolvedChartType === "donut") {
            const endAngle = slice.start + slice.sweep;
            // Draw as ring segment so center is never sliced.
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, slice.start, endAngle, false);
            ctx.arc(centerX, centerY, innerRadius, endAngle, slice.start, true);
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, slice.start, slice.start + slice.sweep);
            ctx.closePath();
            ctx.fill();
          }
        }

        // Leader-line percentage labels around the pie (matches the live report
        // view's labelled slices). Names stay in the legend below; the slice label
        // shows the share. Negligible slices (<2%) are skipped to avoid clutter.
        const pctLabelSize = 16;
        ctx.font = `700 ${pctLabelSize}px Arial`;
        ctx.textBaseline = "middle";
        for (let s = 0; s < sliceInfo.length; s += 1) {
          const slice = sliceInfo[s];
          const pct = (slice.value / total) * 100;
          if (pct < 2) {
            continue;
          }
          const cos = Math.cos(slice.mid);
          const sin = Math.sin(slice.mid);
          const onRight = cos >= 0;
          const x1 = centerX + cos * radius;
          const y1 = centerY + sin * radius;
          const x2 = centerX + cos * (radius + 16);
          const y2 = centerY + sin * (radius + 16);
          const x3 = x2 + (onRight ? 24 : -24);
          ctx.strokeStyle = colors[slice.index % colors.length];
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.lineTo(x3, y2);
          ctx.stroke();
          ctx.fillStyle = legendTextColor;
          ctx.textAlign = onRight ? "left" : "right";
          ctx.fillText(`${this.formatAxisTick(pct)}%`, x3 + (onRight ? 6 : -6), y2);
        }
      }
    } else {
      // Axes and grid
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      const yTicks = 6;
      for (let t = 0; t <= yTicks; t += 1) {
        const ratio = t / yTicks;
        const y = margin.top + plotHeight - ratio * plotHeight;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + plotWidth, y);
        ctx.stroke();

        if (!applyMultiAxis) {
          const tickValue = minValue + ratio * span;
          ctx.fillStyle = axisTextColor;
          ctx.font = `700 ${yAxisLabelSize}px Arial`;
          ctx.textAlign = "right";
          ctx.fillText(`${this.formatAxisTick(tickValue)}`, margin.left - 8, y + 4);
        }
      }

      // Draw bars and lines/dots
      const barSeries = chartData.series.filter((s) =>
        s.type === "bar" || s.type === "bar_label"
      );
      const lineSeries = chartData.series.filter((s) =>
        s.type === "line" || s.type === "line_label" || s.type === "dot"
      );
      const barCount = Math.max(1, barSeries.length);
      // Cap the bar width so charts with few categories don't get absurdly thick
      // bars. Charts with many categories have a small groupWidth, so the cap
      // never kicks in and they render exactly as before.
      const maxBarWidth = 80;
      // Per-bar slot inside the ~75%-of-group cluster. The drawn bar fills most of
      // its slot and the remainder becomes the gap between adjacent parameter bars,
      // so grouped bars (multiple parameters per category) are visually separated
      // instead of touching. Single-series charts keep no inner gap.
      const barSlot = (groupWidth * 0.75) / barCount;
      const barGap = barCount > 1 ? Math.min(barSlot * 0.25, 14) : 0;
      const barWidth = Math.min(maxBarWidth, Math.max(3, barSlot - barGap));
      // Total width occupied by a category's bar slots, used to centre them in the slot.
      const groupBarsWidth = barSlot * barCount;
      const showBarLabels = resolvedChartType === "bar_label";
      const showLineLabels = resolvedChartType === "line_label";
      const dotOnly = resolvedChartType === "dot";
      const showPointMarkers = dotOnly;

      for (let ci = 0; ci < categoriesCount; ci += 1) {
        for (let si = 0; si < barSeries.length; si += 1) {
          const value = barSeries[si].data[ci];
          if (value === null || Number.isNaN(value)) {
            continue;
          }
          const barOrigIndex = chartData.series.indexOf(barSeries[si]);
          const barSpan = spanForSeries(barOrigIndex >= 0 ? barOrigIndex : si);
          const colorIndex = barOrigIndex >= 0 ? barOrigIndex : si;
          const h = ((value - minValue) / barSpan) * plotHeight;
          // Centre the (possibly capped) bar group within its category slot so
          // bars sit in the middle instead of hugging the left edge.
          const groupStartX =
            plotStartX + ci * groupWidth + (groupWidth - groupBarsWidth) / 2;
          // Centre the (narrower) bar inside its slot so the leftover slot width
          // shows as an even gap on each side, separating adjacent parameter bars.
          const x = groupStartX + si * barSlot + (barSlot - barWidth) / 2;
          const y = margin.top + plotHeight - h;
          ctx.fillStyle = colors[colorIndex % colors.length];
          ctx.fillRect(x, y, barWidth, h);
          if (showBarLabels) {
            this.drawValueLabel(
              ctx,
              `${this.formatAxisTick(value)}`,
              x + barWidth / 2,
              Math.max(margin.top + 10, y - 4),
              valueLabelSize,
              valueLabelRotation,
              axisTextColor
            );
          }
        }
      }

      for (let li = 0; li < lineSeries.length; li += 1) {
        const series = lineSeries[li];
        const lineOrigIndex = chartData.series.indexOf(series);
        const lineSpan = spanForSeries(lineOrigIndex >= 0 ? lineOrigIndex : li);
        const color =
          applyMultiAxis && lineOrigIndex >= 0
            ? colors[lineOrigIndex % colors.length]
            : colors[(barSeries.length + li) % colors.length];
        if (!dotOnly) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2.2;
          ctx.beginPath();
        }
        let started = false;
        for (let i = 0; i < categoriesCount; i += 1) {
          const value = series.data[i];
          if (value === null || Number.isNaN(value)) {
            continue;
          }
          const x = plotStartX + i * groupWidth + groupWidth / 2;
          const y = margin.top + plotHeight - (((value - minValue) / lineSpan) * plotHeight);
          if (!dotOnly) {
            if (!started) {
              ctx.moveTo(x, y);
              started = true;
            } else {
              ctx.lineTo(x, y);
            }
          }
          if (showPointMarkers) {
            ctx.beginPath();
            ctx.fillStyle = color;
            ctx.arc(x, y, 3.8, 0, Math.PI * 2);
            ctx.fill();
          }
          if (showLineLabels || dotOnly) {
            this.drawValueLabel(
              ctx,
              `${this.formatAxisTick(value)}`,
              x,
              y - 8,
              valueLabelSize,
              valueLabelRotation,
              axisTextColor
            );
          }
        }
        if (started && !dotOnly) {
          ctx.stroke();
        }
      }

      // Benchmark reference lines: a horizontal line at each series' benchmark
      // value, in the series colour, with a labelled value chip near the right
      // edge (mirrors the live chart's benchmark markLine).
      for (let bi = 0; bi < chartData.series.length; bi += 1) {
        const bench = seriesBenchmarks[bi];
        if (bench === null) {
          continue;
        }
        const bSpan = spanForSeries(bi);
        const by =
          margin.top + plotHeight - (((bench - minValue) / bSpan) * plotHeight);
        if (by < margin.top - 0.5 || by > margin.top + plotHeight + 0.5) {
          continue;
        }
        const color = colors[bi % colors.length];
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(margin.left, by);
        ctx.lineTo(margin.left + plotWidth, by);
        ctx.stroke();

        const label = `${
          chartData.series[bi].name || `Series ${bi + 1}`
        } Benchmark: ${this.formatAxisTick(bench)}`;
        ctx.font = "700 12px Arial";
        const padX = 6;
        const padY = 4;
        const chipW = ctx.measureText(label).width + padX * 2;
        const chipH = 12 + padY * 2;
        let chipX = margin.left + plotWidth - chipW - 2;
        if (chipX < margin.left) {
          chipX = margin.left;
        }
        // Prefer just above the line (insideEndTop); flip below if it'd clip the top.
        let chipY = by - chipH - 2;
        if (chipY < margin.top) {
          chipY = by + 2;
        }
        this.drawRoundedRect(ctx, chipX, chipY, chipW, chipH, 4);
        ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(label, chipX + padX, chipY + chipH / 2);
        ctx.restore();
      }

      // Multi-axis: draw one colored Y-axis per series.
      if (applyMultiAxis) {
        const yTicks = 6;
        ctx.textBaseline = "alphabetic";
        ctx.font = `700 ${yAxisLabelSize}px Arial`;
        const plotRight = margin.left + plotWidth;
        for (let p = 0; p < selectedAxisIndices.length; p += 1) {
          const seriesIdx = selectedAxisIndices[p];
          const color = colors[seriesIdx % colors.length];
          const sSpan = seriesSpans[seriesIdx];
          const onRight = p <= 2;
          const axisX = onRight
            ? plotRight + p * axisGap
            : margin.left - (p - 2) * axisGap;
          const align: CanvasTextAlign = onRight ? "left" : "right";

          // Axis line.
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(axisX, margin.top);
          ctx.lineTo(axisX, margin.top + plotHeight);
          ctx.stroke();

          // Tick labels (colored to match the series).
          ctx.fillStyle = color;
          ctx.textAlign = align;
          for (let t = 0; t <= yTicks; t += 1) {
            const ratio = t / yTicks;
            const y = margin.top + plotHeight - ratio * plotHeight;
            const labelValue = ratio * sSpan;
            const tx = onRight ? axisX + 6 : axisX - 6;
            ctx.fillText(`${this.formatAxisTick(labelValue)}`, tx, y + 4);
          }
        }
      }

      // X labels (sampled). Force one-date-per-point for daily charts.
      if (isDailyByTitle) {
      }
      if (isRawByTitle) {
      }
      const xStep = dateLabelMode === "all"
        ? 1
        : this.computeAutoDateLabelStep(
            ctx,
            displayCategories,
            plotWidth,
            isTimestampByTitle,
            dateLabelRotation
          );
      ctx.fillStyle = legendTextColor;
      ctx.font = `600 ${xAxisLabelSize}px Arial`;
      for (let i = 0; i < categoriesCount; i += 1) {
        if (i % xStep !== 0 && i !== categoriesCount - 1) {
          continue;
        }
        const label = isTimestampByTitle
          ? this.formatHourlyTimestampLabel(displayCategories[i])
          : this.formatDateLikeLabel(displayCategories[i]);
        // Bars (and line points) are centred in each category slot, so the tick
        // and date label go at the slot centre to line up under them.
        const x = plotStartX + i * groupWidth + groupWidth / 2;
        const axisY = margin.top + plotHeight;
        ctx.strokeStyle = "rgba(203, 213, 225, 0.55)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, axisY);
        ctx.lineTo(x, axisY + 7);
        ctx.stroke();
        const absRot = Math.abs(dateLabelRotation);
        const baseLabelY = margin.top + plotHeight + 16;
        const y = baseLabelY + (absRot >= 85 ? 10 : absRot >= 75 ? 6 : 0);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((dateLabelRotation * Math.PI) / 180);
        if (absRot >= 80) {
          // Keep vertical labels flowing downward from x-axis.
          ctx.textAlign = dateLabelRotation >= 0 ? "left" : "right";
        } else if (absRot >= 75) {
          ctx.textAlign = "center";
        } else if (dateLabelRotation <= -12) {
          ctx.textAlign = "right";
        } else if (dateLabelRotation >= 12) {
          ctx.textAlign = "left";
        } else {
          ctx.textAlign = "center";
        }
        ctx.textBaseline = "top";
        ctx.fillText(label, 0, 2);
        ctx.restore();
      }
    }

    if (resolvedChartType === "table" || resolvedChartType === "gauge") {
      return canvas.toDataURL("image/png");
    }

    // Legend at bottom, centered.
    ctx.textAlign = "left";
    ctx.font = `600 ${legendFontSize}px Arial`;
    // Keep in sync with the legend-row reservation in drawChartSnapshotDataUrl so
    // multi-row legends get the same vertical gap they were given space for.
    const legendRowHeight = Math.max(22, legendFontSize + 10);
    const legendBottomY = height - 12;
    const maxLegendWidth = width - margin.left - margin.right;
    const legendItems = chartData.series.map((series, i) => {
      // Render the full parameter name (no truncation/ellipsis).
      const label = String(series.name || "");
      const itemWidth = Math.max(110, ctx.measureText(label).width + 36);
      return { i, label, itemWidth };
    });

    const legendRows: Array<Array<{ i: number; label: string; itemWidth: number }>> = [];
    let currentRow: Array<{ i: number; label: string; itemWidth: number }> = [];
    let currentRowWidth = 0;

    for (let i = 0; i < legendItems.length; i += 1) {
      const item = legendItems[i];
      if (currentRow.length > 0 && currentRowWidth + item.itemWidth > maxLegendWidth) {
        legendRows.push(currentRow);
        currentRow = [item];
        currentRowWidth = item.itemWidth;
      } else {
        currentRow.push(item);
        currentRowWidth += item.itemWidth;
      }
    }
    if (currentRow.length) {
      legendRows.push(currentRow);
    }

    for (let rowIndex = 0; rowIndex < legendRows.length; rowIndex += 1) {
      const row = legendRows[rowIndex];
      const rowWidth = row.reduce((sum, item) => sum + item.itemWidth, 0);
      let legendX = (width - rowWidth) / 2;
      const legendY = legendBottomY - (legendRows.length - 1 - rowIndex) * legendRowHeight;

      for (let ri = 0; ri < row.length; ri += 1) {
        const item = row[ri];
        const color = colors[item.i % colors.length];
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(legendX + 7, legendY - 5, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = legendTextColor;
        ctx.fillText(item.label, legendX + 20, legendY);
        legendX += item.itemWidth;
      }
    }

    return canvas.toDataURL("image/png");
  }

  /**
   * Return at least `count` visually-distinct colors. The base palette (default
   * brand colors or user-chosen series colors) is kept as-is for the leading
   * entries so existing charts look unchanged; any shortfall is filled by
   * placing each new color at the hue that is FARTHEST from every hue already in
   * use (greedy farthest-point on the color wheel). This is what keeps generated
   * colors away from the base brand hues too — a naive fixed-step generator
   * would land a new green next to the brand green, etc. Lightness/saturation
   * also shift as the wheel fills so that, once hues must crowd together, near
   * neighbours still differ in brightness. Exact-duplicate hexes are dropped.
   */
  private ensureDistinctColors(base: string[], count: number): string[] {
    const out: string[] = [];
    const usedHexes = new Set<string>();
    const usedHues: number[] = [];
    const add = (hex: string): void => {
      const key = hex.toLowerCase();
      if (usedHexes.has(key)) {
        return;
      }
      usedHexes.add(key);
      usedHues.push(this.hexToHue(hex));
      out.push(hex);
    };

    for (const color of base) {
      if (out.length >= count) {
        break;
      }
      if (color) {
        add(color);
      }
    }

    const circularDistance = (a: number, b: number): number => {
      const d = Math.abs(a - b) % 360;
      return Math.min(d, 360 - d);
    };

    let guard = 0;
    while (out.length < count && guard < count * 4) {
      guard += 1;
      // Scan the wheel for the hue with the largest minimum distance to every
      // hue already used (empty palette → fall back to 0°).
      let bestHue = 0;
      let bestScore = -1;
      for (let h = 0; h < 360; h += 2) {
        let minDistance = 360;
        for (let u = 0; u < usedHues.length; u += 1) {
          minDistance = Math.min(minDistance, circularDistance(h, usedHues[u]));
        }
        if (minDistance > bestScore) {
          bestScore = minDistance;
          bestHue = h;
        }
      }
      // Each full pass around the wheel drops lightness so re-used hue regions
      // read as a different shade.
      const cycle = Math.floor(out.length / 6);
      const saturation = 70 - (cycle % 2) * 14;
      const lightness = 60 - (cycle % 3) * 10;
      let hex = this.hslToHex(bestHue, saturation, lightness);
      if (usedHexes.has(hex.toLowerCase())) {
        hex = this.hslToHex(bestHue, saturation, Math.max(20, lightness - 12));
      }
      const before = out.length;
      add(hex);
      if (out.length === before) {
        // Guard against a stuck loop if a duplicate still slipped through.
        usedHues.push(bestHue);
      }
    }

    return out;
  }

  /** Extract the HSL hue (0–360°) from a #rrggbb / #rgb hex color. */
  private hexToHue(hex: string): number {
    let value = (hex || "").replace("#", "").trim();
    if (value.length === 3) {
      value = value
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (value.length < 6) {
      return 0;
    }
    const r = parseInt(value.substring(0, 2), 16) / 255;
    const g = parseInt(value.substring(2, 4), 16) / 255;
    const b = parseInt(value.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta === 0) {
      return 0;
    }
    let hue: number;
    if (max === r) {
      hue = ((g - b) / delta) % 6;
    } else if (max === g) {
      hue = (b - r) / delta + 2;
    } else {
      hue = (r - g) / delta + 4;
    }
    hue *= 60;
    return hue < 0 ? hue + 360 : hue;
  }

  /** Convert an HSL color (h in degrees, s/l in percent) to a #rrggbb hex string. */
  private hslToHex(h: number, s: number, l: number): string {
    const sat = s / 100;
    const light = l / 100;
    const c = (1 - Math.abs(2 * light - 1)) * sat;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0;
    let g = 0;
    let b = 0;
    if (hp >= 0 && hp < 1) {
      r = c;
      g = x;
    } else if (hp < 2) {
      r = x;
      g = c;
    } else if (hp < 3) {
      g = c;
      b = x;
    } else if (hp < 4) {
      g = x;
      b = c;
    } else if (hp < 5) {
      r = x;
      b = c;
    } else {
      r = c;
      b = x;
    }
    const m = light - c / 2;
    const toHex = (v: number): string =>
      Math.round((v + m) * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private encodeChartPayload(payload: {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  }): string {
    try {
      return encodeURIComponent(JSON.stringify(payload));
    } catch {
      return "";
    }
  }

  private decodeChartPayload(encoded: string): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    if (!encoded) {
      return null;
    }
    try {
      const decoded = decodeURIComponent(encoded);
      const parsed = JSON.parse(decoded);
      if (
        !parsed ||
        typeof parsed !== "object" ||
        !parsed.chartData ||
        !Array.isArray(parsed.chartData.series) ||
        !Array.isArray(parsed.chartData.categories)
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private readChartPayloadFromWrapper(wrapper: HTMLElement): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // Absent on charts saved before this existed → falls back to `title`.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    const encoded = String(wrapper.getAttribute("data-chart-payload") || "");
    return this.decodeChartPayload(encoded);
  }

  /**
   * Group a reading's timestamp into its interval bucket key so raw
   * per-timestamp readings collapse into one value per interval (mirrors the
   * dashboard's getTimelineBucketKey). Raw (unaggregated) reports keep their
   * exact timestamp key so the existing raw-timestamp handling is untouched.
   */
  private intervalBucketKey(
    startStr: string,
    interval: string,
    aggregation?: string
  ): string {
    if (String(aggregation) === "raw") {
      return startStr;
    }
    const date = new Date(startStr);
    if (Number.isNaN(date.getTime())) {
      return startStr;
    }
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    if (interval === "monthly") {
      return `${yyyy}-${mm}`;
    }
    if (
      interval === "15minutes" ||
      interval === "15_min" ||
      interval === "30minutes" ||
      interval === "30_min" ||
      interval === "1hour" ||
      interval === "hourly"
    ) {
      const hh = String(date.getUTCHours()).padStart(2, "0");
      const mi = String(date.getUTCMinutes()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    }
    // Daily and weekly both key by day: a weekly report's readings carry the
    // week-start day, so day-keying preserves the existing weekly grouping.
    return `${yyyy}-${mm}-${dd}`;
  }

  private extractChartDataForReport(report: DashboardReportData): {
    categories: string[];
    series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"; rawValues?: number[] }>;
  } {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const hasParamValues = params.some(
      (param) =>
        Array.isArray(param?.values) &&
        param.values.some((v) => v && v.value !== undefined)
    );

    const rawTimestampCategories = this.isRawReport(report)
      ? this.extractRawTimestampCategories(report)
      : [];

    if (hasParamValues) {
      const baseParam = params.find(
        (param) => Array.isArray(param?.values) && param.values.length > 0
      );
      const baseValues = Array.isArray(baseParam?.values) ? baseParam.values : [];
      const interval = this.inferIntervalHint(report, baseValues);

      // Build a unified timeline across ALL parameters (mirrors the dashboard's
      // buildUnifiedTimeline). Using only the first parameter's values dropped
      // periods (e.g. a month) that other parameters had but it didn't.
      const timelineByKey = new Map<string, any>();
      for (const param of params) {
        const pInterval = String(param?.interval || interval || "daily");
        for (const v of Array.isArray(param?.values) ? param.values : []) {
          const key = this.intervalBucketKey(
            String(v?.start ?? ""),
            pInterval,
            (param as any)?.aggregation
          );
          if (key && !timelineByKey.has(key)) {
            timelineByKey.set(key, v);
          }
        }
      }
      const timeline = Array.from(timelineByKey.entries())
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => {
          const da = this.parseDateSafe(a.key)?.getTime() ?? 0;
          const db = this.parseDateSafe(b.key)?.getTime() ?? 0;
          return da - db;
        });

      let categories = timeline.map((t) =>
        this.buildCategoryLabel(t.value, interval)
      );
      if (this.isRawReport(report)) {
        if (rawTimestampCategories.length > 0) {
          const targetLen =
            timeline.length || baseValues.length || rawTimestampCategories.length;
          categories = this.alignCategoriesToLength(
            rawTimestampCategories,
            targetLen,
            categories
          );
        } else {
          categories = categories.map((c) =>
            this.normalizeCategoryLabelForInterval(String(c), "raw")
          );
        }
      } else if (this.isHourlyReport(report)) {
        categories = categories.map((c) =>
          this.normalizeCategoryLabelForInterval(String(c), "hourly")
        );
      } else if (this.isDailyReport(report)) {
        categories = this.normalizeDailyCategorySequence(
          categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
        );
      }

      const reportType = this.mapToSupportedChartType(report.parameters?.[0]?.graphType, "line");
      const series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"; rawValues?: number[] }> = params.map((param) => {
        // The backend may send raw readings per timestamp, so an interval bucket
        // can hold many readings. Collect them per bucket and aggregate by the
        // parameter's data type (sum adds, avg/raw averages) instead of letting
        // the last reading win. `rawValues` keeps every reading so the function
        // tables (Table 3/4) can compute Max/Min/STDV over the true readings.
        const pInterval = String(param?.interval || interval || "daily");
        const agg = String((param as any)?.aggregation || (param as any)?.dataType || "avg");
        const bucketValues = new Map<string, number[]>();
        const rawValues: number[] = [];
        for (const v of Array.isArray(param?.values) ? param.values : []) {
          const num = this.toNumberOrNull(v?.value);
          if (num === null) {
            continue;
          }
          const key = this.intervalBucketKey(
            String(v?.start ?? ""),
            pInterval,
            (param as any)?.aggregation
          );
          const bucket = bucketValues.get(key);
          if (bucket) {
            bucket.push(num);
          } else {
            bucketValues.set(key, [num]);
          }
          rawValues.push(num);
        }
        return {
          name: param?.paramName || param?.paramCode || "Series",
          data: timeline.map((t) => {
            const arr = bucketValues.get(t.key);
            if (!arr || arr.length === 0) {
              return null;
            }
            const sum = arr.reduce((a, b) => a + b, 0);
            return agg === "sum" ? sum : sum / arr.length;
          }),
          type: this.mapToSupportedChartType(param?.graphType, reportType),
          rawValues,
        };
      });

      return { categories, series };
    }

    const chartData = (report.metadata as any)?.chart_data || {};
    const rawSeries = Array.isArray(chartData.series) ? chartData.series : [];
    const rawCategories = Array.isArray(chartData.categories)
      ? chartData.categories
      : [];

    const series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> = rawSeries.map((s: any, idx: number) => ({
      name: s?.name || `Series ${idx + 1}`,
      data: (Array.isArray(s?.data) ? s.data : []).map((point: any) => {
        if (typeof point === "number") {
          return point;
        }
        if (typeof point === "string") {
          const value = Number(point);
          return Number.isNaN(value) ? null : value;
        }
        if (point && typeof point === "object" && "value" in point) {
          return this.toNumberOrNull((point as any).value);
        }
        return null;
      }),
      type:
        this.mapToSupportedChartType(s?.type || s?.graphType || chartData?.graphType || chartData?.chartType, "line"),
    }));

    const maxLen = Math.max(0, ...series.map((s) => s.data.length));
    const interval = this.inferIntervalHint(report, []);
    const pointCategories = this.extractCategoriesFromSeriesData(rawSeries);
    let sourceCategories =
      pointCategories.length === maxLen && pointCategories.some((c) => String(c || "").trim())
        ? pointCategories
        : rawCategories;

    if (this.isRawReport(report) && rawTimestampCategories.length > 0) {
      sourceCategories = rawTimestampCategories;
    }

    let categories =
      sourceCategories.length > 0
        ? sourceCategories.map((c: any) =>
            this.normalizeCategoryLabelForInterval(String(c), interval)
          )
        : Array.from({ length: maxLen }, (_, i) => String(i + 1));
    if (this.isRawReport(report)) {
      categories = rawTimestampCategories.length > 0
        ? this.alignCategoriesToLength(rawTimestampCategories, maxLen || categories.length, categories)
        : categories.map((c) =>
            this.normalizeCategoryLabelForInterval(String(c), "raw")
          );
    } else if (this.isHourlyReport(report)) {
      categories = categories.map((c) =>
        this.normalizeCategoryLabelForInterval(String(c), "hourly")
      );
    } else if (this.isDailyReport(report)) {
      categories = this.normalizeDailyCategorySequence(
        categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
      );
    }

    return { categories, series };
  }

  private isDailyReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("daily") || raw.includes("day")) {
        return true;
      }
    }
    return false;
  }

  private isHourlyReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("hourly") || raw.includes("hour")) {
        return true;
      }
    }
    return false;
  }

  private isRawReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("raw")) {
        return true;
      }
    }
    return false;
  }

  private getMetadataChartCategories(report: DashboardReportData): string[] {
    const categories = (report.metadata as any)?.chart_data?.categories;
    if (!Array.isArray(categories)) {
      return [];
    }
    return categories.map((c: any) => String(c ?? ""));
  }

  private alignCategoriesToLength(
    normalizedCategories: string[],
    targetLen: number,
    fallbackCategories: string[]
  ): string[] {
    const target = Math.max(0, targetLen);
    if (!target) {
      return normalizedCategories;
    }
    if (normalizedCategories.length >= target) {
      return normalizedCategories.slice(0, target);
    }
    const fallback = Array.isArray(fallbackCategories) ? fallbackCategories : [];
    const merged = [...normalizedCategories];
    for (let i = normalizedCategories.length; i < target; i += 1) {
      merged.push(String(fallback[i] || fallback[fallback.length - 1] || ""));
    }
    return merged;
  }

  private extractRawTimestampCategories(report: DashboardReportData): string[] {
    const candidateSets: string[][] = [];

    const addCandidateSet = (values: unknown[]): void => {
      if (!Array.isArray(values) || !values.length) {
        return;
      }
      const normalized = values
        .map((value) => this.normalizeRawTimestampValue(value))
        .filter((value) => !!value);
      if (normalized.length) {
        candidateSets.push(normalized);
      }
    };

    addCandidateSet(this.getMetadataChartCategories(report));

    const chartData = (report.metadata as any)?.chart_data || {};
    addCandidateSet(Array.isArray(chartData?.xAxis?.categories) ? chartData.xAxis.categories : []);

    const rawSeries = Array.isArray(chartData.series) ? chartData.series : [];
    for (let i = 0; i < rawSeries.length; i += 1) {
      const data = Array.isArray(rawSeries[i]?.data) ? rawSeries[i].data : [];
      const fromPoints = data.map((p: any) =>
        p && typeof p === "object"
          ? (p.x ?? p.time ?? p.timestamp ?? p.dateTime ?? p.datetime ?? p.date ?? p.start ?? p.label ?? p.category ?? "")
          : p
      );
      addCandidateSet(fromPoints);
    }

    const params = Array.isArray(report.parameters) ? report.parameters : [];
    for (let p = 0; p < params.length; p += 1) {
      const values = Array.isArray(params[p]?.values) ? params[p].values : [];
      const fromValues = values.map((v: any) =>
        v && typeof v === "object"
          ? (v.timestamp ?? v.time ?? v.dateTime ?? v.datetime ?? v.date ?? v.start ?? v.label ?? v.category ?? "")
          : v
      );
      addCandidateSet(fromValues);
    }

    if (!candidateSets.length) {
      return [];
    }

    let best: string[] = [];
    let bestScore = -1;
    for (let i = 0; i < candidateSets.length; i += 1) {
      const set = candidateSets[i];
      const score = set.reduce((sum, value) => sum + (this.isLikelyTimeText(value) ? 1 : 0), 0);
      if (score > bestScore) {
        best = set;
        bestScore = score;
      }
    }

    return best;
  }

  private normalizeRawTimestampValue(value: unknown): string {
    const parsed = this.parseRawTimestamp(value);
    if (parsed) {
      return this.formatHourlyTimestamp(parsed);
    }
    return this.shortenLabel(String(value ?? "").trim(), 48);
  }

  private parseRawTimestamp(value: unknown): Date | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (typeof value === "number") {
      const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : NaN;
      if (!Number.isNaN(ms)) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          return d;
        }
      }
      return null;
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        return this.parseRawTimestamp(n);
      }
    }
    return this.parseDateTimeSafe(raw);
  }

  private extractCategoriesFromSeriesData(rawSeries: any[]): string[] {
    if (!Array.isArray(rawSeries)) {
      return [];
    }
    for (let i = 0; i < rawSeries.length; i += 1) {
      const data = Array.isArray(rawSeries[i]?.data) ? rawSeries[i].data : [];
      if (!data.length) {
        continue;
      }
      const categories = data.map((point: any) => {
        if (point === null || point === undefined) {
          return "";
        }
        if (typeof point === "string") {
          return point;
        }
        if (typeof point === "object") {
          return String(
            point.start ??
              point.date ??
              point.x ??
              point.category ??
              point.label ??
              point.name ??
              ""
          );
        }
        return "";
      });
      if (categories.some((c) => String(c || "").trim())) {
        return categories;
      }
    }
    return [];
  }

  private normalizeDailyCategorySequence(categories: string[]): string[] {
    if (!Array.isArray(categories) || !categories.length) {
      return [];
    }
    // Do not synthesize missing days here. Keep only API-provided labels,
    // normalized when parseable, otherwise preserved as-is.
    return categories.map((raw) => {
      const text = String(raw || "").trim();
      if (!text) {
        return "";
      }
      const parsed = this.parseDateSafe(text) || this.parseDateFromRangeLikeLabel(text);
      return parsed ? this.toIsoDate(parsed) : text;
    });
  }

  private inferIntervalHint(report: DashboardReportData, values: any[]): string {
    const normalizeHint = (raw: unknown): "raw" | "hourly" | "daily" | "weekly" | "monthly" | "" => {
      const v = String(raw || "").toLowerCase();
      if (!v) {
        return "";
      }
      if (v.includes("raw")) {
        return "raw";
      }
      if (v.includes("hourly") || v.includes("hour")) {
        return "hourly";
      }
      if (v.includes("daily") || v.includes("day")) {
        return "daily";
      }
      if (v.includes("weekly") || v.includes("week")) {
        return "weekly";
      }
      if (v.includes("monthly") || v.includes("month")) {
        return "monthly";
      }
      return "";
    };

    const fromInterval = normalizeHint(report.interval);
    if (fromInterval) {
      return fromInterval;
    }

    const chartMeta = ((report.metadata as any)?.chart_data || {}) as Record<string, any>;
    const metaCandidates = [
      chartMeta.interval,
      chartMeta.frequency,
      chartMeta.period,
      chartMeta.rangeType,
      chartMeta.groupBy,
      chartMeta.xAxisLabelFormat,
      chartMeta.title,
      chartMeta.subTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
    ];
    for (let i = 0; i < metaCandidates.length; i += 1) {
      const hint = normalizeHint(metaCandidates[i]);
      if (hint) {
        return hint;
      }
    }

    const nameCandidates = [
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report as any)?.heading,
    ];
    for (let i = 0; i < nameCandidates.length; i += 1) {
      const hint = normalizeHint(nameCandidates[i]);
      if (hint) {
        return hint;
      }
    }

    const spans: number[] = [];
    for (let i = 0; i < values.length; i += 1) {
      const start = this.parseDateSafe(values[i]?.start);
      const end = this.parseDateSafe(values[i]?.end);
      if (!start || !end) {
        continue;
      }
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / msPerDay)) + 1;
      spans.push(diffDays);
    }
    if (spans.length) {
      const avg = spans.reduce((a, b) => a + b, 0) / spans.length;
      if (avg <= 1 / 24) {
        return "hourly";
      }
      if (avg <= 2) {
        return "daily";
      }
      if (avg >= 27) {
        return "monthly";
      }
      return "weekly";
    }

    return "";
  }

  private normalizeCategoryLabelForInterval(rawValue: string, interval: string): string {
    const raw = String(rawValue || "").trim();
    if (!raw) {
      return "";
    }

    if (interval.includes("raw")) {
      const parsed = this.parseDateTimeSafe(raw);
      if (parsed) {
        return this.formatHourlyTimestamp(parsed);
      }
      return this.shortenLabel(raw, 48);
    }

    if (interval.includes("hour")) {
      const parsed = this.parseDateTimeSafe(raw) || this.parseDateSafe(raw) || this.parseDateFromRangeLikeLabel(raw);
      if (parsed) {
        return this.formatHourlyTimestamp(parsed);
      }
      return this.shortenLabel(raw, 36);
    }

    if (interval.includes("day")) {
      const parsed = this.parseDateSafe(raw) || this.parseDateFromRangeLikeLabel(raw);
      if (parsed) {
        return this.toIsoDate(parsed);
      }
    }

    return this.formatDateLikeLabel(raw);
  }

  private mapToSupportedChartType(
    rawType: unknown,
    fallback: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" = "line"
  ): "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" {
    const normalized = String(rawType || "").toLowerCase();
    if (!normalized) {
      return fallback;
    }
    // The six table variants (tbl_row/col/rfn/cfn/rfm/cfm) all render as tables.
    if (normalized.startsWith("tbl_")) {
      return "table";
    }
    if (normalized.includes("bar_label") || normalized.includes("bar label")) {
      return "bar_label";
    }
    if (normalized.includes("line_label") || normalized.includes("line label")) {
      return "line_label";
    }
    if (normalized.includes("dot")) {
      return "dot";
    }
    if (normalized.includes("donut") || normalized.includes("doughnut")) {
      return "donut";
    }
    if (normalized.includes("pie")) {
      return "pie";
    }
    if (normalized.includes("gauge")) {
      return "gauge";
    }
    if (normalized.includes("text")) {
      return "text";
    }
    if (normalized.includes("bar") || normalized.includes("column")) {
      return "bar";
    }
    if (
      normalized.includes("line") ||
      normalized.includes("spline") ||
      normalized.includes("area")
    ) {
      return "line";
    }
    if (normalized.includes("numeric")) {
      return "numeric";
    }
    if (normalized.includes("table")) {
      return "table";
    }
    if (normalized.includes("box")) {
      return "box";
    }
    return fallback;
  }

  private resolveReportChartType(
    report: DashboardReportData,
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    }
  ): "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" {
    const candidates = new Set<
      "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"
    >();

    const reportLevel = this.mapToSupportedChartType(
      (report.metadata as any)?.chart_data?.graphType || (report.metadata as any)?.chart_data?.chartType,
      "line"
    );
    candidates.add(reportLevel);

    const params = Array.isArray(report.parameters) ? report.parameters : [];
    for (let i = 0; i < params.length; i += 1) {
      candidates.add(this.mapToSupportedChartType(params[i]?.graphType, reportLevel));
    }

    for (let i = 0; i < chartData.series.length; i += 1) {
      candidates.add(chartData.series[i].type);
    }

    const priority: Array<
      "text" | "gauge" | "pie" | "donut" | "table" | "numeric" | "bar_label" | "line_label" | "bar" | "line" | "dot" | "box"
    > = [
      "text",
      "gauge",
      "pie",
      "donut",
      "table",
      "numeric",
      "bar_label",
      "line_label",
      "bar",
      "line",
      "dot",
      "box",
    ];

    for (let i = 0; i < priority.length; i += 1) {
      const type = priority[i];
      if (candidates.has(type)) {
        return type;
      }
    }

    return "line";
  }

  private getSeriesRepresentativeValue(values: Array<number | null>): number {
    for (let i = values.length - 1; i >= 0; i -= 1) {
      const v = values[i];
      if (v !== null && !Number.isNaN(v)) {
        return Math.max(0, Number(v));
      }
    }
    return 0;
  }

  private renderBarChartHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> },
    title: string
  ): string {
    const width = 640;
    const height = 280;
    const margin = { top: 28, right: 16, bottom: 60, left: 42 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const colors = ["#2563eb", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    if (!values.length) {
      return "";
    }
    const max = Math.max(...values, 0);
    const categoriesCount = Math.max(chartData.categories.length, 1);
    const barSeries = chartData.series.filter((s) => s.type === "bar");
    const lineSeries = chartData.series.filter((s) => s.type !== "bar");
    const seriesCount = Math.max(barSeries.length, 1);
    const groupWidth = plotWidth / categoriesCount;
    const barWidth = Math.max(3, (groupWidth * 0.8) / seriesCount);

    const bars: string[] = [];
    const lines: string[] = [];
    for (let ci = 0; ci < categoriesCount; ci += 1) {
      for (let si = 0; si < barSeries.length; si += 1) {
        const value = barSeries[si].data[ci];
        if (value === null || Number.isNaN(value)) {
          continue;
        }
        const h = max > 0 ? (value / max) * plotHeight : 0;
        const x = margin.left + ci * groupWidth + (groupWidth * 0.1) + si * barWidth;
        const y = margin.top + (plotHeight - h);
        bars.push(
          `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${barWidth.toFixed(
            2
          )}" height="${h.toFixed(2)}" fill="${colors[si % colors.length]}" fill-opacity="0.95" />`
        );
      }
    }

    for (let li = 0; li < lineSeries.length; li += 1) {
      const series = lineSeries[li];
      const points = series.data
        .map((v, i) => {
          if (v === null || Number.isNaN(v)) {
            return "";
          }
          const x = margin.left + i * groupWidth + groupWidth / 2;
          const y = margin.top + (plotHeight - ((v / max) * plotHeight));
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .filter(Boolean)
        .join(" ");
      if (!points) {
        continue;
      }
      lines.push(
        `<polyline points="${points}" fill="none" stroke="${
          colors[(barSeries.length + li) % colors.length]
        }" stroke-width="2.5" />`
      );
    }

    const labelStep = Math.max(1, Math.ceil(chartData.categories.length / 10));
    const labels = chartData.categories
      .map((c, i) => {
        if (i % labelStep !== 0 && i !== chartData.categories.length - 1) {
          return "";
        }
        const x = margin.left + i * groupWidth + groupWidth / 2;
        return `<text x="${x.toFixed(2)}" y="${height - 16}" text-anchor="end" transform="rotate(-40 ${x.toFixed(
          2
        )} ${height - 16})">${this.escapeHtml(this.shortenLabel(c, 10))}</text>`;
      })
      .join("");

    const legend = this.renderChartLegend(chartData.series, colors);
    const yTicks = Array.from({ length: 7 }, (_, i) => (max / 6) * i)
      .map((tick) => {
        const y = margin.top + plotHeight - ((tick / max) * plotHeight || 0);
        return `
          <line x1="${margin.left}" y1="${y.toFixed(2)}" x2="${margin.left + plotWidth}" y2="${y.toFixed(2)}" class="grid-line"></line>
          <text x="${margin.left - 6}" y="${(y + 4).toFixed(2)}" text-anchor="end" class="y-tick">${Math.round(tick)}</text>
        `;
      })
      .join("");

    return `
      <div class="report-chart">
        <div class="report-chart-title">${this.escapeHtml(title)}</div>
        ${legend}
        <svg viewBox="0 0 ${width} ${height}" class="report-chart-svg" role="img" aria-label="${this.escapeHtml(
          title
        )}">
          <defs>
            <linearGradient id="chartBgGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#1f2937" />
              <stop offset="100%" stop-color="#3f3f46" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="${width}" height="${height}" fill="url(#chartBgGradient)"></rect>
          ${yTicks}
          <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${
      margin.top + plotHeight
    }" class="axis-line"></line>
          <line x1="${margin.left}" y1="${margin.top + plotHeight}" x2="${
      margin.left + plotWidth
    }" y2="${margin.top + plotHeight}" class="axis-line"></line>
          ${bars.join("")}
          ${lines.join("")}
          <g class="axis-labels">${labels}</g>
        </svg>
      </div>
    `;
  }

  private renderLineChartHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> },
    title: string
  ): string {
    const width = 640;
    const height = 260;
    const margin = { top: 20, right: 20, bottom: 50, left: 48 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const colors = ["#2563eb", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    if (!values.length) {
      return "";
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const pointsCount = Math.max(chartData.categories.length - 1, 1);

    const lines = chartData.series
      .map((series, idx) => {
        const pts = series.data
          .map((v, i) => {
            if (v === null || Number.isNaN(v)) {
              return "";
            }
            const x = margin.left + (i / pointsCount) * plotWidth;
            const y = margin.top + ((max - v) / span) * plotHeight;
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          })
          .filter(Boolean)
          .join(" ");

        if (!pts) {
          return "";
        }

        return `<polyline points="${pts}" fill="none" stroke="${
          colors[idx % colors.length]
        }" stroke-width="2" />`;
      })
      .join("");

    const labels = chartData.categories
      .map((c, i) => {
        if (i % Math.ceil(Math.max(chartData.categories.length, 1) / 8) !== 0) {
          return "";
        }
        const x = margin.left + (i / Math.max(chartData.categories.length - 1, 1)) * plotWidth;
        return `<text x="${x.toFixed(2)}" y="${height - 16}" text-anchor="middle">${this.escapeHtml(
          this.shortenLabel(c, 12)
        )}</text>`;
      })
      .join("");

    const legend = this.renderChartLegend(chartData.series, colors);

    return `
      <div class="report-chart">
        <div class="report-chart-title">${this.escapeHtml(title)}</div>
        <svg viewBox="0 0 ${width} ${height}" class="report-chart-svg" role="img" aria-label="${this.escapeHtml(
          title
        )}">
          <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${
      margin.top + plotHeight
    }" class="axis-line"></line>
          <line x1="${margin.left}" y1="${margin.top + plotHeight}" x2="${
      margin.left + plotWidth
    }" y2="${margin.top + plotHeight}" class="axis-line"></line>
          ${lines}
          <g class="axis-labels">${labels}</g>
        </svg>
        ${legend}
      </div>
    `;
  }

  private renderChartLegend(
    series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>,
    colors: string[]
  ): string {
    const items = series
      .map(
        (s, i) => `
          <span class="legend-item">
            <span class="legend-swatch" style="background:${colors[i % colors.length]}"></span>
            <span class="legend-label">${this.escapeHtml(s.name || `Series ${i + 1}`)}</span>
          </span>
        `
      )
      .join("");

    return `<div class="report-chart-legend">${items}</div>`;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  private shortenLabel(value: string, max = 18): string {
    const text = String(value || "").trim();
    if (text.length <= max) {
      return text;
    }
    return `${text.slice(0, max - 1)}...`;
  }

  private formatDateLikeLabel(value: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }

    if (this.looksLikeWeeklyLabel(raw)) {
      return raw;
    }

    const dateOnly = raw.match(/\d{4}-\d{2}-\d{2}/);
    if (dateOnly) {
      return dateOnly[0];
    }
    return this.shortenLabel(raw, 28);
  }

  private formatHourlyTimestampLabel(value: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }
    const parsed = this.parseDateTimeSafe(raw);
    if (parsed) {
      return this.formatHourlyTimestamp(parsed);
    }
    return this.shortenLabel(raw, 36);
  }

  private buildCategoryLabel(value: any, interval: string): string {
    const rawStart = String(value?.start || "").trim();
    const rawEnd = String(value?.end || "").trim();
    const isRaw = interval.includes("raw");
    const isHourly = interval.includes("hour") || isRaw;
    const isDaily = interval.includes("day");

    if (this.looksLikeWeeklyLabel(rawStart) && !isDaily) {
      return rawStart;
    }

    const startDate = this.parseDateSafe(rawStart);
    const endDate = this.parseDateSafe(rawEnd);

    if (isHourly) {
      const hourlyDate =
        this.parseDateTimeSafe(rawStart) ||
        (!isRaw ? startDate : null) ||
        this.parseDateTimeSafe(rawEnd) ||
        (!isRaw ? endDate : null);
      if (hourlyDate) {
        return this.formatHourlyTimestamp(hourlyDate);
      }
      return this.shortenLabel(rawStart || rawEnd, isRaw ? 48 : 36);
    }

    if (isDaily) {
      const dailyDate =
        startDate ||
        this.parseDateFromRangeLikeLabel(rawStart) ||
        this.parseDateSafe(rawEnd) ||
        this.parseDateFromRangeLikeLabel(rawEnd);
      if (dailyDate) {
        return this.toIsoDate(dailyDate);
      }
    }

    if (interval.includes("week") && startDate && endDate) {
      return this.formatWeeklyRangeLabel(startDate, endDate);
    }

    if (interval.includes("month") && startDate) {
      return this.formatMonthlyLabel(startDate);
    }

    if (interval.includes("day") && startDate) {
      return this.toIsoDate(startDate);
    }

    if (interval.includes("day")) {
      const parsedRangeDate = this.parseDateFromRangeLikeLabel(rawStart);
      if (parsedRangeDate) {
        return this.toIsoDate(parsedRangeDate);
      }
      return this.formatDateLikeLabel(rawStart);
    }

    if (!!rawEnd && startDate && endDate) {
      const inferred = this.inferRangeGranularity(startDate, endDate);
      if (inferred === "daily") {
        return this.toIsoDate(startDate);
      }
      if (inferred === "monthly") {
        return this.formatMonthlyLabel(startDate);
      }
      return this.formatWeeklyRangeLabel(startDate, endDate);
    }

    if (startDate) {
      return this.toIsoDate(startDate);
    }

    if (rawStart) {
      return this.formatDateLikeLabel(rawStart);
    }

    return this.shortenLabel(String(value?.label || value?.category || ""), 28);
  }

  private parseDateFromRangeLikeLabel(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }

    const months: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };

    let m = text.match(/^\(?\s*(\d{1,2})\s*-\s*(\d{1,2})\s*\)?\s*([A-Za-z]{3,9})\s*'?\s*(\d{2,4})$/i);
    if (m) {
      const day = Number(m[1]);
      const mon = months[String(m[3]).slice(0, 3).toLowerCase()];
      const y = Number(m[4]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    m = text.match(/^\(?\s*(\d{1,2})\s+([A-Za-z]{3,9})\s*-\s*(\d{1,2})\s+([A-Za-z]{3,9})\s*'?\s*(\d{2,4})\s*\)?$/i);
    if (m) {
      const day = Number(m[1]);
      const mon = months[String(m[2]).slice(0, 3).toLowerCase()];
      const y = Number(m[5]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    return null;
  }

  private inferRangeGranularity(
    start: Date,
    end: Date
  ): "daily" | "weekly" | "monthly" {
    const msPerDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / msPerDay)) + 1;
    if (diffDays <= 1) {
      return "daily";
    }
    if (diffDays >= 27) {
      return "monthly";
    }
    return "weekly";
  }

  private areDailyCategories(categories: string[]): boolean {
    if (!categories.length) {
      return false;
    }
    return categories.every((c) => /^\d{4}-\d{2}-\d{2}$/.test(String(c || "").trim()));
  }

  private computeAutoDateLabelStep(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    plotWidth: number,
    isTimestamp: boolean,
    rotationDeg = -40
  ): number {
    const count = Math.max(1, labels.length);
    if (count <= 1 || plotWidth <= 0) {
      return 1;
    }
    // Strict UX rule: from 28 points onward, always alternate labels.
    if (count >= 28) {
      return 2;
    }

    const spacing = plotWidth / count;
    let maxWidth = 0;
    for (let i = 0; i < labels.length; i += 1) {
      const label = isTimestamp
        ? this.formatHourlyTimestampLabel(labels[i])
        : this.formatDateLikeLabel(labels[i]);
      const width = ctx.measureText(label).width;
      if (width > maxWidth) {
        maxWidth = width;
      }
    }

    const absRad = (Math.abs(rotationDeg) * Math.PI) / 180;
    const textHeight = 12;
    const projectedWidth =
      maxWidth * Math.abs(Math.cos(absRad)) + textHeight * Math.abs(Math.sin(absRad));
    const padded = projectedWidth + 14;
    const byWidth = Math.max(1, Math.ceil(padded / Math.max(spacing, 1)));
    const byCount = isTimestamp
      ? Math.max(1, Math.ceil(count / 18))
      : Math.max(1, Math.ceil(count / 12));
    return Math.max(1, byWidth, byCount);
  }

  private computeRotatedDateLabelDepth(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    step: number,
    isTimestamp: boolean,
    rotationDeg: number
  ): number {
    if (!labels.length) {
      return 0;
    }
    const rad = (Math.abs(rotationDeg) * Math.PI) / 180;
    const sin = Math.sin(rad);
    const cos = Math.abs(Math.cos(rad));
    const approxTextHeight = 12;
    let maxDepth = 0;

    for (let i = 0; i < labels.length; i += 1) {
      if (i % Math.max(1, step) !== 0 && i !== labels.length - 1) {
        continue;
      }
      const label = isTimestamp
        ? this.formatHourlyTimestampLabel(labels[i])
        : this.formatDateLikeLabel(labels[i]);
      const width = ctx.measureText(label).width;
      const depth = width * sin + approxTextHeight * cos;
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    }

    return Math.ceil(maxDepth);
  }

  private normalizeDateLabelRotation(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return -40;
    }
    return Math.max(-180, Math.min(180, Math.round(parsed)));
  }

  private normalizeChartTitleSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 24;
    }
    return Math.max(16, Math.min(64, Math.round(parsed)));
  }

  /**
   * Greedily wrap `text` into lines that each fit within `maxWidth` for the
   * canvas context's current font. Used to keep long chart titles fully visible
   * instead of clipping them at the canvas edges. A single word wider than
   * maxWidth is kept on its own line (it cannot be broken without hyphenation).
   */
  private wrapTextToWidth(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ): string[] {
    const trimmed = String(text || "").trim();
    if (!trimmed) {
      return [""];
    }
    if (ctx.measureText(trimmed).width <= maxWidth) {
      return [trimmed];
    }

    const words = trimmed.split(/\s+/);
    const lines: string[] = [];
    let current = "";
    for (let i = 0; i < words.length; i += 1) {
      const word = words[i];
      const candidate = current ? `${current} ${word}` : word;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) {
      lines.push(current);
    }
    return lines.length ? lines : [trimmed];
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.arcTo(x + width, y, x + width, y + r, r);
    ctx.lineTo(x + width, y + height - r);
    ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
    ctx.lineTo(x + r, y + height);
    ctx.arcTo(x, y + height, x, y + height - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  private formatNumericValue(value: number): string {
    if (Number.isNaN(value)) {
      return "0";
    }
    const rounded = Math.round(value * 100) / 100;
    return rounded.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }

  private drawValueLabel(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    fontSize: number,
    rotationDeg: number,
    color: string
  ): void {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = `700 ${fontSize}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    if (rotationDeg) {
      ctx.translate(x, y);
      ctx.rotate((rotationDeg * Math.PI) / 180);
      ctx.fillText(text, 0, 0);
    } else {
      ctx.fillText(text, x, y);
    }
    ctx.restore();
  }

  private normalizeValueLabelSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 11;
    }
    return Math.max(8, Math.min(36, Math.round(parsed)));
  }

  private normalizeValueLabelRotation(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return Math.max(-90, Math.min(90, Math.round(parsed)));
  }

  private normalizeLegendSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 15;
    }
    return Math.max(10, Math.min(40, Math.round(parsed)));
  }

  private normalizeAxisLabelSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 12;
    }
    return Math.max(7, Math.min(30, Math.round(parsed)));
  }

  private normalizeTitlePad(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return Math.max(0, Math.min(200, Math.round(parsed)));
  }

  private looksLikeWeeklyLabel(value: string): boolean {
    const raw = this.normalizeDateLabelText(value);
    if (!raw) {
      return false;
    }
    return (
      /^\(?\d{1,2}\s*-\s*\d{1,2}\)?\s*[A-Za-z]{3,9}\s*'?\d{2,4}$/i.test(raw) ||
      /^\(?\d{1,2}\s+[A-Za-z]{3,9}\s*-\s*\d{1,2}\s+[A-Za-z]{3,9}\s*'?\d{2,4}\)?$/i.test(raw)
    );
  }

  private parseDateSafe(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }
    const dateOnly = text.match(/\d{4}-\d{2}-\d{2}/);
    if (!dateOnly && !this.isLikelyDateText(text)) {
      return null;
    }
    const candidate = dateOnly ? dateOnly[0] : text;
    const d = new Date(candidate);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }

    const m = text.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s*'?\s*(\d{2,4})$/i);
    if (m) {
      const day = Number(m[1]);
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
      };
      const mon = monthMap[String(m[2]).slice(0, 3).toLowerCase()];
      const y = Number(m[3]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    return null;
  }

  private parseDateTimeSafe(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }
    if (!this.isLikelyDateText(text) || !this.isLikelyTimeText(text)) {
      return null;
    }
    const manual = text.match(
      /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{2,4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/i
    );
    if (manual) {
      const day = Number(manual[1]);
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
      };
      const mon = monthMap[String(manual[2]).slice(0, 3).toLowerCase()];
      const y = Number(manual[3]);
      const year = y < 100 ? 2000 + y : y;
      const hh = Number(manual[4] || 0);
      const mm = Number(manual[5] || 0);
      const ss = Number(manual[6] || 0);
      if (!Number.isNaN(day) && mon !== undefined && !Number.isNaN(year)) {
        return new Date(year, mon, day, hh, mm, ss);
      }
    }
    const d = new Date(text);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  private isLikelyDateText(text: string): boolean {
    const raw = String(text || '').trim();
    if (!raw) {
      return false;
    }
    return (
      /\d{4}-\d{1,2}-\d{1,2}/.test(raw) ||
      /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(raw) ||
      /\d{1,2}\s+[A-Za-z]{3,9}\s+\d{2,4}/.test(raw) ||
      /[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{2,4}/.test(raw)
    );
  }
  private isLikelyTimeText(text: string): boolean {
    const raw = String(text || '').trim();
    if (!raw) {
      return false;
    }
    return (
      /(?:^|\s|T)\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?(?:\s|$)/.test(raw) ||
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)
    );
  }

  private normalizeDateLabelText(raw: string): string {
    return String(raw || "")
      .trim()
      .replace(/[’‘`]/g, "'")
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ");
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  private formatHourlyTimestamp(date: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(date.getDate()).padStart(2, "0");
    const mon = months[date.getMonth()];
    const year = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${day} ${mon} ${year}, ${hh}:${mm}`;
  }

  private formatWeeklyRangeLabel(start: Date, end: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const sDay = start.getDate();
    const eDay = end.getDate();
    const sMon = months[start.getMonth()];
    const eMon = months[end.getMonth()];
    const sYear = start.getFullYear();
    const eYear = end.getFullYear();
    const eYY = String(eYear).slice(-2);
    const sYY = String(sYear).slice(-2);

    if (sYear === eYear && sMon === eMon) {
      return `(${sDay}-${eDay}) ${sMon}'${eYY}`;
    }
    if (sYear === eYear) {
      return `(${sDay} ${sMon}-${eDay} ${eMon} '${eYY})`;
    }
    return `(${sDay} ${sMon}-${eDay} ${eMon} '${eYY})`;
  }

  private formatMonthlyLabel(date: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = months[date.getMonth()];
    const yy = String(date.getFullYear()).slice(-2);
    return `${mon}'${yy}`;
  }

  private toHexColor(value: string | undefined, fallback: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return fallback;
    }
    if (raw.startsWith("#")) {
      return raw.length === 7 ? raw : fallback;
    }
    const match = raw.match(
      /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
    );
    if (!match) {
      return fallback;
    }
    const toHex = (n: number): string =>
      Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
    return `#${toHex(Number(match[1]))}${toHex(Number(match[2]))}${toHex(Number(match[3]))}`;
  }

  private getReportRichContent(report: DashboardReportData): string {
    const isAiReport = report.parameters?.[0]?.graphType === "ai";
    if (isAiReport) {
      return report.metadata?.ai_graph_details?.ai_response || "";
    }
    return report.metadata?.text_content || "";
  }

  private getReportRichContentByReportId(
    report: DashboardReportData
  ): { content: string; source: string } {
    const metadata = (report.metadata || {}) as Record<string, unknown>;
    const reportIdKey = String(report.reportId);

    const idMappedContent = this.extractIdMappedContent(metadata, reportIdKey);
    if (idMappedContent) {
      return { content: idMappedContent, source: "id-mapped" };
    }

    const listMappedContent = this.extractListMappedContent(metadata, report.reportId);
    if (listMappedContent) {
      return { content: listMappedContent, source: "list-mapped" };
    }

    const fallback = this.getReportRichContent(report);
    return { content: fallback, source: "direct-fallback" };
  }

  private async getReportContentReportWise(
    report: DashboardReportData
  ): Promise<{ content: string; source: string }> {
    const reportId = Number(report.reportId);
    if (!Number.isNaN(reportId) && reportId > 0) {
      const cached = this.reportContentCache.get(reportId);
      if (typeof cached === "string") {
        return { content: cached, source: "report-id-cache" };
      }

      try {
        const response = await firstValueFrom(
          this.reportService.getReportById(reportId)
        );
        const data = response?.data as any;
        const fromReportById = this.extractContentFromReportByIdPayload(data);
        if (fromReportById) {
          this.reportContentCache.set(reportId, fromReportById);
          return { content: fromReportById, source: "report-id-api" };
        }
      } catch (error) {
      }
    }

    return this.getReportRichContentByReportId(report);
  }

  private extractContentFromReportByIdPayload(data: any): string {
    if (!data || typeof data !== "object") {
      return "";
    }

    const metadata = (data.metadata || {}) as Record<string, unknown>;
    const reportId = Number(data.reportId || data.id || 0);
    const reportIdKey = reportId > 0 ? String(reportId) : "";

    if (reportIdKey) {
      const idMapped = this.extractIdMappedContent(metadata, reportIdKey);
      if (idMapped) {
        return idMapped;
      }

      const listMapped = this.extractListMappedContent(metadata, reportId);
      if (listMapped) {
        return listMapped;
      }
    }

    const firstGraphType =
      data?.parameters?.[0]?.graphType || data?.parameters?.[0]?.graph_type;
    const isAi = String(firstGraphType || "").toLowerCase() === "ai";

    if (isAi) {
      return String(metadata["ai_graph_details"]?.["ai_response"] || "");
    }

    return String(metadata["text_content"] || "");
  }

  private extractIdMappedContent(
    metadata: Record<string, unknown>,
    reportIdKey: string
  ): string {
    const maps = [
      metadata["report_content_by_id"],
      metadata["report_contents_by_id"],
      metadata["text_content_by_id"],
      metadata["ai_response_by_id"],
      metadata["reportContentById"],
      metadata["textContentById"],
      metadata["aiResponseById"],
      metadata["text_content"],
      metadata["ai_response"],
    ];

    for (let i = 0; i < maps.length; i += 1) {
      const map = maps[i] as unknown;
      if (!map || typeof map !== "object" || Array.isArray(map)) {
        continue;
      }

      const byId = map as Record<string, unknown>;
      const candidate = byId[reportIdKey];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    return "";
  }

  private extractListMappedContent(
    metadata: Record<string, unknown>,
    reportId: number
  ): string {
    const lists = [
      metadata["report_contents"],
      metadata["report_content"],
      metadata["reports_content"],
      metadata["sections"],
      metadata["items"],
    ];

    for (let i = 0; i < lists.length; i += 1) {
      const list = lists[i] as unknown;
      if (!Array.isArray(list)) {
        continue;
      }

      for (let j = 0; j < list.length; j += 1) {
        const item = list[j] as Record<string, unknown>;
        if (!item || typeof item !== "object") {
          continue;
        }

        const id = Number(item["reportId"] ?? item["report_id"] ?? item["id"]);
        if (id !== reportId) {
          continue;
        }

        const candidate =
          item["content"] ??
          item["html"] ??
          item["text_content"] ??
          item["ai_response"];

        if (typeof candidate === "string" && candidate.trim()) {
          return candidate;
        }
      }
    }

    return "";
  }

  private sanitizeImportedHtml(html: string): string {
    if (!html) {
      return "";
    }

    return String(html)
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "")
      .replace(/javascript:/gi, "");
  }

  /**
   * Justify the body text of the freshly generated document so the user doesn't
   * have to align each block by hand. Applies to paragraphs and list items only,
   * skips text inside charts/tables (they manage their own alignment) and any
   * block that already carries an explicit alignment.
   */
  private justifyDocumentText(html: string): string {
    if (!html) {
      return html;
    }
    const container = document.createElement("div");
    container.innerHTML = html;

    const blocks = Array.from(
      container.querySelectorAll("p, li")
    ) as HTMLElement[];
    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];
      if (
        block.closest(
          ".report-chart, .report-chart-image, .report-table-block, table"
        )
      ) {
        continue;
      }
      const text = String(block.textContent || "").trim();
      if (!text) {
        continue;
      }
      if (!block.style.textAlign) {
        block.style.textAlign = "justify";
      }
    }

    return container.innerHTML;
  }

  private normalizeReportHtmlContent(html: string, reportId: number): string {
    const sanitized = this.sanitizeImportedHtml(html);
    if (!sanitized) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = sanitized;

    // Some legacy/saved report payloads include complete editor pages repeatedly.
    // Keep only unique page blocks by text signature.
    const pageNodes = Array.from(
      container.querySelectorAll(".doc-page, .editor-page, .page")
    );

    if (pageNodes.length > 1) {
      const seen = new Set<string>();
      let removed = 0;

      for (let i = 0; i < pageNodes.length; i += 1) {
        const node = pageNodes[i] as HTMLElement;
        const signature = this.previewText(node.innerText || node.textContent || "", 200)
          .toLowerCase()
          .trim();

        if (!signature) {
          continue;
        }

        if (seen.has(signature)) {
          node.remove();
          removed += 1;
          continue;
        }

        seen.add(signature);
      }

      if (removed > 0) {
      }
    }

    this.relaxNonBreakingSpaces(container);

    return container.innerHTML;
  }

  /**
   * Saved/pasted report content often joins words with non-breaking spaces
   * (&nbsp;), which the browser will not wrap at — causing whole lines to either
   * overflow the page or get broken mid-word. Convert single non-breaking spaces
   * back to regular spaces so text wraps naturally at word boundaries. Runs of
   * 2+ non-breaking spaces are kept as-is (those are usually intentional padding
   * for alignment, e.g. "Subject     :").
   */
  private relaxNonBreakingSpaces(container: HTMLElement): void {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let current = walker.nextNode();
    while (current) {
      textNodes.push(current as Text);
      current = walker.nextNode();
    }

    for (let i = 0; i < textNodes.length; i += 1) {
      const node = textNodes[i];
      const value = node.nodeValue || "";
      if (value.indexOf(" ") === -1) {
        continue;
      }
      node.nodeValue = value.replace(/ +/g, (run) =>
        run.length === 1 ? " " : run
      );
    }
  }

  private wrapImagesForControl(html: string): string {
    if (!html) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = html;
    const images = Array.from(container.querySelectorAll("img"));

    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      const parent = img.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image imported-image-control";
      wrapper.setAttribute("contenteditable", "false");

      const attrWidth = img.getAttribute("width");
      const cssWidth = img.style.width;
      if (attrWidth && /^\d+$/.test(attrWidth)) {
        wrapper.style.width = `${attrWidth}px`;
      } else if (attrWidth) {
        wrapper.style.width = attrWidth;
      } else if (cssWidth) {
        wrapper.style.width = cssWidth;
      }

      img.removeAttribute("width");
      img.removeAttribute("height");
      img.style.maxWidth = "100%";
      img.style.height = "auto";

      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    }

    return container.innerHTML;
  }

  private toPlainText(html: string): string {
    if (!html) {
      return "";
    }

    const normalized = String(html)
      .replace(/<\/h[1-6]>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/&nbsp;/gi, " ");

    const temp = document.createElement("div");
    temp.innerHTML = normalized;

    const orderedLists = Array.from(temp.querySelectorAll("ol"));
    for (let i = 0; i < orderedLists.length; i += 1) {
      const list = orderedLists[i];
      const items = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );

      for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
        const li = items[itemIndex] as HTMLElement;
        li.insertBefore(
          document.createTextNode(`${itemIndex + 1}. `),
          li.firstChild
        );
        li.appendChild(document.createTextNode("\n"));
      }

      list.insertAdjacentText("beforebegin", "\n");
      list.insertAdjacentText("afterend", "\n");
    }

    const unorderedLists = Array.from(temp.querySelectorAll("ul"));
    for (let i = 0; i < unorderedLists.length; i += 1) {
      const list = unorderedLists[i];
      const items = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );

      for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
        const li = items[itemIndex] as HTMLElement;
        li.insertBefore(document.createTextNode("- "), li.firstChild);
        li.appendChild(document.createTextNode("\n"));
      }

      list.insertAdjacentText("beforebegin", "\n");
      list.insertAdjacentText("afterend", "\n");
    }

    return (temp.textContent || temp.innerText || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private previewText(html: string, max = 120): string {
    const text = String(html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.slice(0, max);
  }

  /** A page is "blank" when it has no visible text and no media/table/chart. */
  private isBlankPageEl(editor: HTMLElement): boolean {
    const text = (editor.textContent || "").replace(/ /g, " ").trim();
    if (text.length > 0) {
      return false;
    }
    return !editor.querySelector(
      "img, table, svg, canvas, hr, .report-chart, .report-table-block, .resizable-image"
    );
  }

  /**
   * String (model) counterpart of isBlankPageEl: true only when the page HTML has
   * no media/structural element AND no visible text. Used by trailing-page cleanup
   * so a media-only page (chart/image/table with no text) is never dropped.
   */
  private isBlankPageHtml(html: string): boolean {
    const s = String(html || "");
    if (/<(img|table|svg|canvas|hr|iframe)[\s/>]/i.test(s)) {
      return false;
    }
    if (
      /class\s*=\s*["'][^"']*(report-chart|report-table-block|resizable-image)/i.test(
        s
      )
    ) {
      return false;
    }
    const text = s
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/\s+/g, "")
      .trim();
    return text.length === 0;
  }

  private getEventPathElements(event: Event): Element[] {
    const path = (event.composedPath?.() || []) as Array<EventTarget>;
    return path.filter((p): p is Element => p instanceof Element);
  }

}
